'use client';

import {
    clampGradientAngle,
    isGradientColorScheme,
} from '@/utils/gradientDirection';
import { getLegalDocumentFields } from '@/utils/legalDocument';
import { normalizeMapEmbed } from '@/utils/normalizeMapEmbed';
import { normalizeSvgFileForUpload } from '@/utils/normalizeSvgUpload';
import { REVIEW_SOURCE_ICON_PATHS } from '@/utils/reviewSourceAlt';
import { Listbox, Transition } from '@headlessui/react';
import {
    ArrowLeftIcon,
    CursorArrowRaysIcon,
    LinkIcon,
    PlusIcon,
    RectangleGroupIcon,
    SparklesIcon,
    SwatchIcon,
} from '@heroicons/react/20/solid';
import {
    ArrowsUpDownIcon,
    ArrowUpTrayIcon,
    CheckIcon,
    ChevronDownIcon,
    CodeBracketIcon,
    PencilSquareIcon,
    TrashIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';

import React from 'react';
import { EditorDrawer } from './EditorDrawer';
import ImageUpload from './ImageUpload';
import JobPricingCalculatorEditor from './JobPricingCalculatorEditor';
import { LoadingSpinner } from './LoadingSpinner';
import { MARKDOWN_EDITOR_WHITELIST, MDEditor } from './atoms/MDEditor';
import { TextArea } from './atoms/TextAreaAutoSize';
import { useEditor } from './context/EditorContext/EditorContext';
import { useNotifications } from './context/NotificationContext/NotificationContext';

import { MediaPanel } from './MediaPanel';
import { renameImageAsset } from './helpers/imageRename';
import {
    deepFindAlignmentKey,
    getAllowedAlignmentsForKey,
    readAlignmentAtPath,
} from './helpers/alignment';
import { getSupportedAnimationGroups } from './helpers/animation';
import {
    CARD_COLOR_SCHEME_GROUPS,
    COMPONENT_COLOR_SCHEME_GROUPS,
    componentSchemeToSwatch,
    MAP_COLOR_SCHEME_GROUPS,
    mapSchemeToSwatch,
    normalizeCardColorScheme,
    normalizeComponentColorScheme,
    normalizeMapColorScheme,
} from './helpers/colorScheme';
import {
    getAllowedEngageFromProps,
    isEngageKey,
    normalizeEngage,
} from './helpers/engage';
import AlignmentButtons from './ui/AlignmentButtons';
import AnimationControls from './ui/AnimationControls';
import ColorSchemeSelect from './ui/ColorSchemeSelect';
import EngageButtons from './ui/EngageButtons';
import FontAwesomeIconPicker from './ui/FontAwesomeIconPicker';
import PaymentOptionsEditor from './ui/PaymentOptionsEditor';
import PromptInput from './ui/PromptInput';
import ShapeDividerControls from './ui/ShapeDividerControls';
import SocialLinksEditor from './ui/SocialLinksEditor';
import VerticalSpacingRow from './ui/VerticalSpacingRow';

/* ───────────────────────────── Image-key helpers ───────────────────────────── */

const IMAGE_BASE_HINTS = new Set(['image', 'media']);
const IMAGE_EXCLUDE_HINTS = new Set([
    'position',
    'alttext',
    'caption',
    'variant',
    'video',
    'link',
]);
const IMAGE_URL_HINTS = new Set([
    'src',
    'url',
    'imageurl',
    'file',
    'filename',
    'path',
]);

function looksLikeImageKey(k) {
    const tokens = String(k)
        .replace(/[.[\]-]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);

    if (tokens.some((t) => IMAGE_EXCLUDE_HINTS.has(t))) return false;

    const hasBase = tokens.some((t) => IMAGE_BASE_HINTS.has(t));
    const hasUrlish = tokens.some((t) => IMAGE_URL_HINTS.has(t));
    const hasIcon = tokens.includes('icon');
    const hasAuthor = tokens.includes('author');

    if (hasIcon && hasAuthor) return true; // authorIcon/author.icon counts
    if (hasIcon && !hasAuthor) return false;

    if (hasBase) return true;
    if (hasBase && hasUrlish) return true;

    return false;
}

/* ─────────────────────────── Key/label utilities ─────────────────────────── */

function keyLeaf(key) {
    const k = String(key).replace(/\[\d+\]$/, ''); // drop trailing [n]
    return k.split('.').pop();
}

function parseFieldOrderValue(value) {
    return Array.from(
        new Set(
            String(value ?? '')
                .split(',')
                .map((item) => item.trim().toLowerCase())
                .filter(Boolean)
        )
    );
}

function applyLeafOrder(keys, orderedLeaves) {
    if (!Array.isArray(keys) || !Array.isArray(orderedLeaves)) return keys;
    if (orderedLeaves.length === 0) return keys;

    const orderedKeys = orderedLeaves.map(
        (leaf) =>
            keys.find((key) => keyLeaf(key).toLowerCase() === leaf) || null
    );
    const filteredOrderedKeys = orderedKeys.filter(Boolean);
    const remainingKeys = keys.filter(
        (key) => !filteredOrderedKeys.includes(key)
    );
    return [...filteredOrderedKeys, ...remainingKeys];
}

const MEDIA_POSITION_COMPONENTS = new Set([
    'CTAHero',
    'HeroFullBG',
    'ItemGrid',
]);
const HERO_SUB_HEADER_COMPONENTS = new Set([
    'AboutPageHero',
    'FaqHero',
    'Hero',
    'HeroFullBG',
    'HeroHalf',
    'ReviewPage',
    'WorkPage',
]);
const HERO_HEADER_SIZE_OPTIONS = [
    'xx-small',
    'x-small',
    'small',
    'medium',
    'large',
];
const HERO_HEADER_SIZE_LABELS = {
    'xx-small': 'XX-Small',
    'x-small': 'X-Small',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
};
const MEDIA_POSITION_GENERIC_COMPONENTS = new Set([
    'AboutFeature',
    'Gallery',
    'Hero',
    'ImageGallery',
    'ImageSlider',
    'Reviews',
    'ReviewSlider',
    'ServiceTabs',
    'Services',
    'SecondaryServices',
    'Team',
]);
const IMAGE_POSITION_CENTER_COMPONENTS = new Set(['AboutFeature', 'Service']);
const MEDIA_POSITION_KEY_BY_COMPONENT = {
    CTAHero: 'ImagePosition',
    HeroFullBG: 'ImagePosition',
    ItemGrid: 'ImagePosition',
    Service: 'MediaPosition',
};
const MEDIA_POSITION_MEDIA_LEAVES = {
    CTAHero: new Set(['imageurl', 'media']),
    HeroFullBG: new Set(['imageurl']),
    ItemGrid: new Set(['imageurl']),
    Service: new Set(['media', 'beforeimage', 'afterimage']),
};

function isTopLevelKey(key) {
    const raw = String(key);
    return !raw.includes('.') && !raw.includes('[');
}

function getMediaPositionKey(componentName, mediaKey) {
    const leaf = keyLeaf(mediaKey).toLowerCase();
    const pathLower = String(mediaKey).toLowerCase();

    if (leaf === 'icon' && pathLower.includes('.author.')) return null;

    if (componentName === 'Service') {
        if (leaf === 'beforeimage')
            return replaceLeafInPath(mediaKey, 'BeforeImagePosition');
        if (leaf === 'afterimage')
            return replaceLeafInPath(mediaKey, 'AfterImagePosition');
        if (leaf === 'media')
            return replaceLeafInPath(mediaKey, 'MediaPosition');
        return null;
    }

    if (componentName === 'ItemGrid' && leaf === 'cardimageurl') {
        return replaceLeafInPath(mediaKey, 'CardImagePosition');
    }

    if (componentName === 'CallToAction' || componentName === 'HeroHalf') {
        if (leaf === 'imageurl' || leaf === 'image')
            return replaceLeafInPath(mediaKey, 'ImageObjectPosition');
        return null;
    }

    if (componentName === 'Popup') {
        if (leaf === 'imageurl' || leaf === 'image')
            return replaceLeafInPath(mediaKey, 'ImageObjectPosition');
        return null;
    }

    if (componentName === 'HeroFullBG') {
        if (leaf === 'leadimageurl')
            return replaceLeafInPath(mediaKey, 'LeadImagePosition');
        if (leaf === 'imageurl')
            return replaceLeafInPath(mediaKey, 'ImagePosition');
        return null;
    }

    if (componentName === 'ComparisonTable') {
        if (leaf === 'featureblockbackgroundimage') {
            return replaceLeafInPath(
                mediaKey,
                'FeatureBlockBackgroundImagePosition'
            );
        }
    }

    const positionKey = MEDIA_POSITION_KEY_BY_COMPONENT[componentName];
    if (positionKey) {
        const allowedLeaves = MEDIA_POSITION_MEDIA_LEAVES[componentName];
        if (allowedLeaves && !allowedLeaves.has(leaf)) return null;
        if (
            MEDIA_POSITION_COMPONENTS.has(componentName) &&
            !isTopLevelKey(mediaKey)
        )
            return null;
        return positionKey;
    }

    if (!MEDIA_POSITION_GENERIC_COMPONENTS.has(componentName)) return null;

    if (leaf === 'backgroundimageurl' || leaf === 'backgroundimage')
        return replaceLeafInPath(mediaKey, 'BackgroundImagePosition');
    if (leaf === 'media') return replaceLeafInPath(mediaKey, 'MediaPosition');
    if (leaf === 'imageurl' || leaf === 'image')
        return replaceLeafInPath(mediaKey, 'ImagePosition');
    return null;
}

function prettyLabel(rawKey) {
    const leaf = keyLeaf(rawKey);

    const OVERRIDES = {
        CTA: 'CTA',
        CustomCTALink: 'CTA Link',
        HideCTAButton: 'Hide CTA Button',
        CTAOpenInNewTab: 'Open in new tab',
        BookingLinkOpenInNewTab: 'Open in new tab',
        DirectionsOpenInNewTab: 'Open in new tab',
        ButtonText: 'Button',
        ButtonLink: 'Button Link',
        ButtonOpenInNewTab: 'Open in new tab',
        SecondaryButtonText: 'Secondary Button',
        SecondaryButtonLink: 'Secondary Link',
        SecondaryButtonOpenInNewTab: 'Open in new tab',
        OutlineButtonText: 'Outline Button',
        OutlineButtonLink: 'Outline Link',
        OutlineButtonOpenInNewTab: 'Open in new tab',
        ThirdButtonText: 'Outline Button',
        ThirdButtonLink: 'Outline Link',
        ThirdButtonOpenInNewTab: 'Open in new tab',
        SmallCTAOpenInNewTab: 'Open in new tab',
        SmallSecondaryButtonOpenInNewTab: 'Open in new tab',
        SmallOutlineButtonOpenInNewTab: 'Open in new tab',
        ColorScheme: 'Color Scheme',
        VerticalSpacing: 'Vertical Spacing',
        AnimationPreset: 'Animation',
        MapColorScheme: 'Map Color Scheme',
        ImageUrl: 'Media',
        LeadImageUrl: 'Lead Media',
        LeadImageUseSiteRadius: 'Use Site Corner Radius',
        HeaderSize: 'Header Size',
        SliderTransition: 'Animation',
        UnpublishRedirectTo: 'Unpublish Redirect (Optional)',
    };
    if (OVERRIDES[leaf]) return OVERRIDES[leaf];

    const spaced = leaf
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();

    return spaced.replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatComponentName(name) {
    return String(name || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim();
}

function prettyArrayTitle(rawKey, index) {
    const baseLeaf = keyLeaf(String(rawKey).replace(/\[\d+\]$/, ''));
    const singular = baseLeaf.endsWith('s') ? baseLeaf.slice(0, -1) : baseLeaf;
    return `${prettyLabel(singular)} ${index + 1}`;
}

function coerceBoolean(val) {
    return typeof val === 'string'
        ? val.toLowerCase().trim() === 'true'
        : !!val;
}

function schemaEntryForKey(schema, key) {
    if (!schema) return null;
    const rootKey = String(key)
        .split(/[.[\]]/)
        .filter(Boolean)[0];
    if (!rootKey) return null;
    return schema[rootKey] || null;
}

const COLLAPSIBLE_ARRAY_EDITOR_CONFIG = {
    HeroFullBG: {
        images: {
            itemLabel: 'Background Image',
            titleFields: ['ImageUrl'],
            defaultExpanded: false,
            tone: 'gradient',
            useImageBackground: true,
            backgroundImageFields: ['ImageUrl'],
            mediaFilenamePill: true,
        },
    },
    Service: {
        features: {
            itemLabel: 'Feature',
            titleFields: ['Title', 'Description'],
            defaultExpanded: false,
            tone: 'secondary',
        },
        medias: {
            itemLabel: 'Media',
            titleFields: ['AltText', 'Media'],
            defaultExpanded: false,
            tone: 'gradient',
            useImageBackground: true,
            backgroundImageFields: ['Media'],
        },
    },
    Services: {
        services: {
            itemLabel: 'Service',
            titleFields: ['Tagline', 'Header', 'Anchor'],
            defaultExpanded: false,
            tone: 'primary',
            useImageBackground: true,
            backgroundImageFields: ['Image'],
        },
        features: {
            itemLabel: 'Feature',
            titleFields: ['Title', 'Description'],
            defaultExpanded: false,
            tone: 'secondary',
        },
    },
    SecondaryServices: {
        services: {
            itemLabel: 'Service',
            titleFields: ['Service', 'Header', 'Anchor'],
            defaultExpanded: false,
            tone: 'primary',
            useImageBackground: true,
            backgroundImageFields: ['ImageUrl'],
        },
    },
    ServiceTabs: {
        services: {
            itemLabel: 'Service',
            titleFields: ['TabName', 'Tagline', 'Header', 'Anchor'],
            fieldOrder: ['Anchor'],
            defaultExpanded: false,
            tone: 'primary',
        },
        features: {
            itemLabel: 'Feature',
            titleFields: ['Title', 'Description'],
            defaultExpanded: false,
            tone: 'secondary',
        },
        medias: {
            itemLabel: 'Media',
            titleFields: ['AltText', 'Media'],
            defaultExpanded: false,
            tone: 'gradient',
            useImageBackground: true,
            backgroundImageFields: ['Media'],
        },
    },
    ComparisonTable: {
        sections: {
            itemLabel: 'Section',
            titleFields: ['Title'],
            defaultExpanded: false,
            tone: 'primary',
        },
    },
    USP: {
        features: {
            itemLabel: 'Feature',
            titleFields: ['Title', 'Description'],
            defaultExpanded: false,
            tone: 'secondary',
        },
    },
    ItemGrid: {
        features: {
            itemLabel: 'Feature',
            titleFields: ['Title', 'Description'],
            defaultExpanded: false,
            tone: 'secondary',
            useImageBackground: true,
            backgroundImageFields: ['CardImageUrl'],
        },
    },
    Pricing: {
        packages: {
            itemLabel: 'Package',
            titleFields: ['Title', 'Description', 'Price'],
            defaultExpanded: false,
            tone: 'primary',
        },
        features: {
            itemLabel: 'Feature',
            titleFields: ['Item', 'Title', 'Description'],
            defaultExpanded: false,
            tone: 'secondary',
        },
    },
    Reviews: {
        reviews: {
            itemLabel: 'Review',
            titleFields: ['Author.Name', 'Body'],
            defaultExpanded: false,
            tone: 'primary',
            headerEditableField: 'Author.Name',
        },
    },
    ReviewSlider: {
        reviews: {
            itemLabel: 'Review',
            titleFields: ['Author.Name', 'Body'],
            defaultExpanded: false,
            tone: 'primary',
            headerEditableField: 'Author.Name',
        },
    },
    ReviewHero: {
        reviews: {
            itemLabel: 'Review',
            titleFields: ['Author.Name', 'Body'],
            defaultExpanded: false,
            tone: 'primary',
            headerEditableField: 'Author.Name',
            hideItemNumber: true,
        },
    },
    Credentials: {
        badges: {
            itemLabel: 'Badge',
            titleFields: ['Name', 'Description', 'AltText'],
            defaultExpanded: false,
            tone: 'primary',
            useImageBackground: true,
            backgroundImageFields: ['Image'],
            hiddenFields: ['Name', 'Description'],
            maxItems: 9,
        },
    },
    CredentialsLine: {
        badges: {
            itemLabel: 'Badge',
            titleFields: ['Name', 'Description', 'AltText'],
            defaultExpanded: false,
            tone: 'primary',
            useImageBackground: true,
            backgroundImageFields: ['Image'],
        },
    },
    ImageGallery: {
        images: {
            itemLabel: 'Image',
            titleFields: ['AltText', 'ImageUrl'],
            defaultExpanded: false,
            tone: 'gradient',
            useImageBackground: true,
            backgroundImageFields: ['ImageUrl'],
        },
    },
    Gallery: {
        images: {
            itemLabel: 'Image',
            titleFields: ['AltText', 'Caption', 'ImageUrl'],
            hiddenFields: ['Caption'],
            defaultExpanded: false,
            tone: 'gradient',
            useImageBackground: true,
            backgroundImageFields: ['ImageUrl'],
        },
    },
    ItemCounter: {
        features: {
            itemLabel: 'Counter',
            titleFields: ['Title', 'Numbers', 'Description'],
            defaultExpanded: false,
            tone: 'primary',
            maxItems: 6,
        },
    },
    ImageSlider: {
        images: {
            itemLabel: 'Image',
            titleFields: ['AltText', 'ImageUrl'],
            defaultExpanded: false,
            tone: 'gradient',
            useImageBackground: true,
            backgroundImageFields: ['ImageUrl'],
        },
    },
    List: {
        features: {
            itemLabel: 'Item',
            titleFields: ['Title', 'Description'],
            defaultExpanded: false,
            tone: 'primary',
        },
    },
    Process: {
        steps: {
            itemLabel: 'Step',
            titleFields: ['Title', 'Description'],
            defaultExpanded: false,
            tone: 'primary',
        },
    },
    Faq: {
        faqs: {
            itemLabel: 'FAQ',
            titleFields: ['Question', 'Answer'],
            defaultExpanded: false,
            tone: 'primary',
            maxItems: 20,
        },
    },
    Footer: {
        medias: {
            itemLabel: 'Media',
            titleFields: ['AltText', 'Media'],
            defaultExpanded: false,
            tone: 'gradient',
            useImageBackground: true,
            backgroundImageFields: ['Media'],
            allowEmpty: true,
        },
        socials: {
            itemLabel: 'Social',
            titleFields: ['Name', 'Link'],
            defaultExpanded: false,
            tone: 'primary',
            editorStyle: 'inline-edit',
        },
        services: {
            itemLabel: 'Service',
            titleFields: ['Name', 'Anchor'],
            defaultExpanded: false,
            tone: 'primary',
            editorStyle: 'inline-edit',
        },
        links: {
            itemLabel: 'Link',
            titleFields: ['Name', 'Link'],
            defaultExpanded: false,
            tone: 'primary',
            editorStyle: 'inline-edit',
        },
        phones: {
            itemLabel: 'Phone',
            titleFields: ['Number'],
            defaultExpanded: false,
            tone: 'primary',
            editorStyle: 'inline-edit',
        },
        textfield: {
            itemLabel: 'Text',
            titleFields: ['Label', 'Link'],
            defaultExpanded: false,
            tone: 'primary',
            editorStyle: 'inline-edit',
        },
        policylinks: {
            itemLabel: 'Policy Link',
            titleFields: ['Name', 'Link'],
            defaultExpanded: false,
            tone: 'primary',
            editorStyle: 'inline-edit',
        },
    },
    Nav: {
        services: {
            itemLabel: 'Service',
            titleFields: ['Name', 'Anchor'],
            defaultExpanded: false,
            tone: 'primary',
            editorStyle: 'inline-edit',
            hideMetaLabel: true,
        },
        dropdown: {
            itemLabel: 'Additional Dropdown',
            titleFields: ['Header'],
            defaultExpanded: false,
            tone: 'primary',
            itemNumberOffset: 2,
            headerEditableField: 'Header',
            emptyTitlePlaceholder: 'Add a header to make it appear',
        },
        links: {
            itemLabel: 'Link',
            titleFields: ['Name', 'Link'],
            defaultExpanded: false,
            tone: 'primary',
            editorStyle: 'inline-edit',
        },
        enddropdowns: {
            itemLabel: 'End Dropdown',
            titleFields: ['Header'],
            defaultExpanded: false,
            tone: 'primary',
            headerEditableField: 'Header',
            emptyTitlePlaceholder: 'Add a header to make it appear',
        },
        items: {
            itemLabel: 'Item',
            titleFields: ['Name', 'Anchor', 'Link'],
            defaultExpanded: false,
            tone: 'secondary',
            editorStyle: 'inline-edit',
        },
    },
    Privacy: {
        list: {
            itemLabel: 'Item',
            titleFields: ['Description'],
            defaultExpanded: false,
            tone: 'primary',
        },
        accesslist: {
            itemLabel: 'Item',
            titleFields: ['Description'],
            defaultExpanded: false,
            tone: 'primary',
        },
    },
    Team: {
        people: {
            itemLabel: 'Person',
            titleFields: ['Person.Name', 'Person.Role', 'Person.Description'],
            defaultExpanded: false,
            tone: 'primary',
            useImageBackground: true,
            backgroundImageFields: ['Person.ImageURL'],
        },
        socials: {
            itemLabel: 'Social',
            titleFields: ['Name', 'Link'],
            defaultExpanded: false,
            tone: 'secondary',
            editorStyle: 'inline-edit',
        },
    },
    AboutFeature: {
        features: {
            itemLabel: 'Feature',
            titleFields: ['Title', 'Description'],
            defaultExpanded: false,
            tone: 'primary',
        },
        medias: {
            itemLabel: 'Media',
            titleFields: ['AltText', 'Media'],
            defaultExpanded: false,
            tone: 'gradient',
            useImageBackground: true,
            backgroundImageFields: ['Media'],
        },
    },
    Basic: {
        sections: {
            itemLabel: 'Section',
            titleFields: ['Header', 'Subheader', 'Tagline', 'Body'],
            defaultExpanded: false,
            tone: 'primary',
        },
        features: {
            itemLabel: 'Feature',
            titleFields: ['Title', 'Description'],
            defaultExpanded: false,
            tone: 'primary',
        },
    },
};

function truncateEditorTitle(value, maxLength = 72) {
    const text = String(value || '').trim();
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 3).trimEnd()}...`;
}

function getArrayEditorConfig(componentName, key) {
    const componentConfig = COLLAPSIBLE_ARRAY_EDITOR_CONFIG[componentName];
    if (!componentConfig) return null;
    const arrayLeaf = keyLeaf(
        String(key).replace(/\[\d+\]$/, '')
    ).toLowerCase();
    return componentConfig[arrayLeaf] || null;
}

function resolveArrayEditorConfig(componentName, key) {
    const baseConfig = getArrayEditorConfig(componentName, key);
    if (!baseConfig) return null;
    return { ...baseConfig };
}

function getArrayItemEditorNumber(index, config) {
    return index + (config?.itemNumberOffset ?? 1);
}

function getArrayItemEditorFallbackTitle(rawKey, index, config) {
    if (config?.itemLabel) {
        if (config.hideItemNumber) return config.itemLabel;
        return `${config.itemLabel} ${getArrayItemEditorNumber(index, config)}`;
    }
    return prettyArrayTitle(rawKey, index);
}

function getArrayItemEditorMetaLabel(index, config) {
    if (!config || config.hideMetaLabel) return '';
    if (!config.itemLabel) return '';
    if (config.hideItemNumber) return config.itemLabel;
    return `${config.itemLabel} ${getArrayItemEditorNumber(index, config)}`;
}

function getArrayItemEditorTitle(item, config, fallbackTitle) {
    if (!item || typeof item !== 'object' || !config) return fallbackTitle;

    for (const field of config.titleFields || []) {
        const rawValue = field.includes('.')
            ? getByPath(item, field)
            : item?.[field];
        const resolvedValue =
            rawValue && typeof rawValue === 'object' && 'value' in rawValue
                ? rawValue.value
                : rawValue;
        if (
            typeof resolvedValue === 'string' ||
            typeof resolvedValue === 'number'
        ) {
            const title = truncateEditorTitle(resolvedValue);
            if (title) return title;
        }
    }

    if (config?.emptyTitlePlaceholder) {
        return config.emptyTitlePlaceholder;
    }

    return fallbackTitle;
}

function getArrayItemEditorSubtitle(item, config, primaryTitle = '') {
    if (!item || typeof item !== 'object' || !config) return '';

    for (const field of config.titleFields || []) {
        const rawValue = field.includes('.')
            ? getByPath(item, field)
            : item?.[field];
        const resolvedValue =
            rawValue && typeof rawValue === 'object' && 'value' in rawValue
                ? rawValue.value
                : rawValue;
        if (
            typeof resolvedValue === 'string' ||
            typeof resolvedValue === 'number'
        ) {
            const subtitle = truncateEditorTitle(resolvedValue);
            if (subtitle && subtitle !== primaryTitle) return subtitle;
        }
    }

    return '';
}

function svgMarkupToDataUrl(markup) {
    const value = String(markup || '').trim();
    if (!value) return '';
    try {
        const utf8 =
            typeof window !== 'undefined' && typeof window.btoa === 'function'
                ? window.btoa(unescape(encodeURIComponent(value)))
                : btoa(unescape(encodeURIComponent(value)));
        return `data:image/svg+xml;base64,${utf8}`;
    } catch {
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(value)}`;
    }
}

function normalizeCollapsibleArrayBackgroundImageValue(input) {
    if (!input) return '';
    if (typeof input === 'string') {
        const value = input.trim();
        if (/<\s*svg[\s>]/i.test(value)) {
            return svgMarkupToDataUrl(value);
        }
        return value;
    }
    if (typeof input === 'object') {
        const candidate = input.url || input.src || input.path || '';
        if (typeof candidate !== 'string') return '';
        const value = candidate.trim();
        if (/<\s*svg[\s>]/i.test(value)) {
            return svgMarkupToDataUrl(value);
        }
        return value;
    }
    return '';
}

function isSelectedEditorMedia(input) {
    const mediaUrl = normalizeCollapsibleArrayBackgroundImageValue(input);
    if (!mediaUrl) return false;
    return !/placeholder\.svg(?:\?.*)?$/i.test(mediaUrl);
}

function isAltTextFieldKey(key) {
    const leaf = keyLeaf(key).toLowerCase();
    return (
        leaf === 'alttext' ||
        leaf === 'alt' ||
        leaf === 'leadimagealttext' ||
        leaf === 'beforealttext' ||
        leaf === 'beforealt' ||
        leaf === 'afteralttext' ||
        leaf === 'afteralt'
    );
}

function inferAltTextSourceImageField(path, rootObj) {
    if (!isAltTextFieldKey(path)) return null;

    const leaf = keyLeaf(path).toLowerCase();
    const candidateLeaves =
        leaf === 'leadimagealttext'
            ? ['LeadImageUrl']
            : leaf === 'beforealttext' || leaf === 'beforealt'
            ? ['BeforeImage', 'BeforeImageUrl']
            : leaf === 'afteralttext' || leaf === 'afteralt'
            ? ['AfterImage', 'AfterImageUrl']
            : [
                  'Media',
                  'ImageUrl',
                  'Image',
                  'CardImageUrl',
                  'BackgroundImageUrl',
                  'BackgroundImage',
                  'BeforeImage',
                  'AfterImage',
              ];

    for (const candidateLeaf of candidateLeaves) {
        const candidateKey = replaceLeafInPath(path, candidateLeaf);
        const candidateValue = getByPath(rootObj, candidateKey);
        if (!isSelectedEditorMedia(candidateValue)) continue;
        return {
            key: candidateKey,
            value: normalizeCollapsibleArrayBackgroundImageValue(
                candidateValue
            ),
        };
    }

    return null;
}

function inferAltTextFieldForImage(path, rootObj) {
    const leaf = keyLeaf(path).toLowerCase();
    const candidateLeaves =
        leaf === 'leadimage' || leaf === 'leadimageurl'
            ? ['LeadImageAltText']
            : leaf === 'beforeimage' || leaf === 'beforeimageurl'
            ? ['BeforeAltText', 'BeforeAlt']
            : leaf === 'afterimage' || leaf === 'afterimageurl'
            ? ['AfterAltText', 'AfterAlt']
            : ['AltText', 'Alt'];

    for (const candidateLeaf of candidateLeaves) {
        const candidateKey = replaceLeafInPath(path, candidateLeaf);
        const candidateValue = getByPath(rootObj, candidateKey);
        if (candidateValue === undefined) continue;
        return {
            key: candidateKey,
            value: candidateValue,
        };
    }

    return null;
}

function inferStandaloneMediaVariantField(componentName, path, rootObj) {
    if (componentName !== 'HeroFullBG') return null;

    const leaf = keyLeaf(path).toLowerCase();
    if (leaf !== 'leadimageurl') return null;

    const variantKey = replaceLeafInPath(path, 'LeadImageVariant');
    const variantValue = getByPath(rootObj, variantKey);
    if (variantValue === undefined) return null;

    return {
        key: variantKey,
        value: variantValue,
        allowedDisplays: [
            'original',
            'contain',
            'landscape',
            'portrait',
            'square',
        ],
        label: 'Size & Proportions',
    };
}

function inferStandaloneMediaRadiusField(componentName, path, rootObj) {
    if (componentName !== 'HeroFullBG') return null;

    const leaf = keyLeaf(path).toLowerCase();
    if (leaf !== 'leadimageurl') return null;

    const radiusKey = replaceLeafInPath(path, 'LeadImageUseSiteRadius');
    const radiusValue = getByPath(rootObj, radiusKey);
    if (radiusValue === undefined) return null;

    return {
        key: radiusKey,
        value: radiusValue,
        label: 'Use Site Corner Radius',
    };
}

function getCollapsibleArrayBackgroundImage(item, config) {
    if (!config?.useImageBackground || !item || typeof item !== 'object') {
        return '';
    }

    for (const field of config.backgroundImageFields || []) {
        const rawValue = field.includes('.')
            ? getByPath(item, field)
            : item?.[field];
        if (!isSelectedEditorMedia(rawValue)) continue;
        return normalizeCollapsibleArrayBackgroundImageValue(rawValue);
    }

    for (const [field, rawValue] of Object.entries(item)) {
        if (!looksLikeImageKey(field)) continue;
        if (!isSelectedEditorMedia(rawValue)) continue;
        return normalizeCollapsibleArrayBackgroundImageValue(rawValue);
    }

    return '';
}

function getCollapsibleArrayToneClasses(tone = 'secondary') {
    if (tone === 'cta') {
        return {
            wrapper: 'border-primary-700 bg-primary-700',
            headerOpenBorder: 'border-white/15',
            buttonHover: 'hover:bg-primary-600/90',
            metaText: 'text-white/80',
            chevronText: 'text-white',
        };
    }

    if (tone === 'gradient') {
        return {
            wrapper:
                'border-primary-200 bg-gradient-to-r from-primary-50 via-white to-secondary-50',
            headerOpenBorder: 'border-primary-200',
            buttonHover: 'hover:bg-white/50',
            metaText: 'text-primary-700',
            chevronText: 'text-primary-700',
        };
    }

    if (tone === 'primary') {
        return {
            wrapper: 'border-primary-200 bg-primary-50',
            headerOpenBorder: 'border-primary-200',
            buttonHover: 'hover:bg-primary-100/70',
            metaText: 'text-primary-700',
            chevronText: 'text-primary-700',
        };
    }

    return {
        wrapper: 'border-secondary-200 bg-secondary-50',
        headerOpenBorder: 'border-secondary-200',
        buttonHover: 'hover:bg-secondary-100/70',
        metaText: 'text-secondary-700',
        chevronText: 'text-secondary-700',
    };
}

function getEmptyMediaCollapsibleToneClasses() {
    return {
        wrapper: 'border-neutral-300 bg-neutral-100',
        headerOpenBorder: 'border-neutral-300',
        buttonHover: 'hover:bg-transparent',
        metaText: 'text-neutral-600',
        chevronText: 'text-neutral-600',
    };
}

function getDropPlaceholderClasses(tone = 'primary') {
    if (tone === 'secondary') {
        return 'bg-secondary-50/80 border-secondary-300';
    }
    return 'bg-primary-50/80 border-primary-300';
}

function arrayPathFromItemKey(key) {
    return String(key).replace(/\[\d+\]$/, '');
}

function moveLeafKeyToFront(keys, leafName) {
    const match = keys.find((key) => keyLeaf(key).toLowerCase() === leafName);
    if (!match) return keys;
    return [match, ...keys.filter((key) => key !== match)];
}

function isDirectArrayItemPath(path, arrayKey) {
    const escapedArrayKey = String(arrayKey).replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
    );
    const matcher = new RegExp(`^${escapedArrayKey}\\[(\\d+)\\]$`);
    return matcher.test(String(path));
}

function getArrayItemIndex(path) {
    const match = String(path).match(/\[(\d+)\]$/);
    return match ? Number(match[1]) : -1;
}

function getArrayItemAnimationNode(node) {
    if (!node || typeof node.querySelector !== 'function') return node;
    return node.querySelector('[data-draggable-array-item="true"]') || node;
}

function findInlineEditPrimaryKey(item = {}) {
    const candidates = ['Name', 'Label', 'Header', 'Title', 'Number'];
    return candidates.find((candidate) =>
        Object.prototype.hasOwnProperty.call(item, candidate)
    );
}

function findInlineEditLinkKey(item = {}, candidateKeys = ['Link', 'Anchor']) {
    const candidates = Array.isArray(candidateKeys) ? candidateKeys : ['Link'];
    return candidates.find((candidate) =>
        Object.prototype.hasOwnProperty.call(item, candidate)
    );
}

function normalizeAdjacentDropPosition(fromIndex, overIndex, overPosition) {
    if (overPosition === 'before' && overIndex === fromIndex + 1) {
        return 'after';
    }
    if (overPosition === 'after' && overIndex === fromIndex - 1) {
        return 'before';
    }
    return overPosition;
}

function isNoOpDropPosition(fromIndex, overIndex, overPosition) {
    const insertionIndex = overPosition === 'after' ? overIndex + 1 : overIndex;
    const adjustedInsertionIndex =
        fromIndex < insertionIndex ? insertionIndex - 1 : insertionIndex;
    return fromIndex === adjustedInsertionIndex;
}

const INLINE_ADD_BUTTON_CLASSNAME =
    'focus-visible:ring-primary-500/60 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full text-neutral-500 transition-all hover:bg-primary-100 hover:text-primary-700 focus:outline-none focus-visible:ring-2 disabled:opacity-40';
const ROUND_DANGER_ICON_BUTTON_CLASSNAME =
    'focus-visible:ring-primary-500/60 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full text-neutral-500 transition-all hover:bg-red-100 hover:text-red-600 focus:outline-none focus-visible:ring-2 disabled:opacity-40';
const SMALL_DANGER_ICON_BUTTON_CLASSNAME =
    'focus-visible:ring-primary-500/60 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full text-neutral-500 transition-all hover:bg-red-100 hover:text-red-600 focus:outline-none focus-visible:ring-2 disabled:opacity-40';
const INLINE_EDIT_BUTTON_CLASSNAME =
    'focus-visible:ring-primary-500/60 group-focus-visible-within:opacity-100 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full text-neutral-500 opacity-0 transition-all hover:bg-primary-100 hover:text-primary-700 focus:outline-none focus-visible:ring-2 group-hover:opacity-100';
const INLINE_GENERATE_BUTTON_CLASSNAME =
    'inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-primary-200 bg-white px-2 text-[10px] font-semibold text-primary-700 transition-all hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 disabled:cursor-not-allowed disabled:opacity-50';
const ALT_TEXT_GENERATION_ENABLED = false;
const COMPONENT_ICON_SIZE_OPTIONS = [
    { key: 'default', label: 'Default' },
    { key: 'x-small', label: 'X-small' },
    { key: 'small', label: 'Small' },
    { key: 'medium', label: 'Medium' },
    { key: 'large', label: 'Large' },
    { key: 'x-large', label: 'X-large' },
];
const GRADIENT_DIRECTION_FIELD = 'GradientDirection';

/* ─────────────────────────────── Key guards ─────────────────────────────── */

function isAnimationKey(key) {
    const leaf = keyLeaf(key).toLowerCase();
    return leaf === 'animation' || leaf === 'animationpreset';
}
function isDurationKey(key) {
    return keyLeaf(key).toLowerCase() === 'duration';
}
function isColorSchemeKey(key) {
    return keyLeaf(key).toLowerCase() === 'colorscheme';
}
function isGradientDirectionKey(key) {
    return keyLeaf(key).toLowerCase() === 'gradientdirection';
}
function isCardsColorSchemeKey(key) {
    return keyLeaf(key).toLowerCase() === 'cardscolorscheme';
}
function isAlignmentKey(key) {
    return keyLeaf(key).toLowerCase() === 'alignment';
}
function isVerticalSpacingKey(key) {
    return keyLeaf(key).toLowerCase() === 'verticalspacing';
}
function isFooterBottomSpacingKey(key) {
    return keyLeaf(key).toLowerCase() === 'bottomrowspacing';
}
function isImagePositionKey(key) {
    return keyLeaf(key).toLowerCase() === 'imageposition';
}
function isImageVariantKey(key) {
    return keyLeaf(key).toLowerCase() === 'imagevariant';
}
function isAnimationGroupKey(key) {
    return keyLeaf(key).toLowerCase() === 'animationgroup';
}
function isMapColorSchemeKey(key) {
    return keyLeaf(key).toLowerCase() === 'mapcolorscheme';
}
function isGoogleMapKey(key) {
    return keyLeaf(key).toLowerCase() === 'googlemap';
}
function isLogoOverflowKey(key) {
    return keyLeaf(key).toLowerCase() === 'logooverflow';
}
function isBehaviorKey(key) {
    return keyLeaf(key).toLowerCase() === 'behavior';
}
function isNavHeightKey(key) {
    return keyLeaf(key).toLowerCase() === 'navheight';
}
function isIconSizeKey(key) {
    const leaf = keyLeaf(key).toLowerCase();
    return leaf === 'iconsize' || leaf === 'featureiconsize';
}
function isSocialLinksKey(key) {
    const leaf = keyLeaf(key).toLowerCase();
    return leaf === 'social' || leaf === 'socials';
}

function isStyleKey(key, componentName) {
    const leaf = keyLeaf(key).toLowerCase();
    const isPopupPosition = componentName === 'Popup' && leaf === 'position';
    return (
        isPopupPosition ||
        isColorSchemeKey(key) ||
        isGradientDirectionKey(key) ||
        isCardsColorSchemeKey(key) ||
        isMapColorSchemeKey(key) ||
        isAnimationKey(key) ||
        isDurationKey(key) ||
        isAnimationGroupKey(key) ||
        isAlignmentKey(key) ||
        isEngageKey(key) ||
        isVerticalSpacingKey(key) ||
        isFooterBottomSpacingKey(key) ||
        isImagePositionKey(key) ||
        isImageVariantKey(key) ||
        isLogoOverflowKey(key) ||
        isBehaviorKey(key) ||
        isNavHeightKey(key) ||
        isIconSizeKey(key)
    );
}

function normalizeAngleValue(value) {
    const parsed =
        typeof value === 'number' ? value : Number.parseFloat(String(value));
    if (!Number.isFinite(parsed)) return 0;
    const normalized = Math.round(parsed) % 360;
    return normalized < 0 ? normalized + 360 : normalized;
}

function AngleStepControl({ label, value, onStep }) {
    return (
        <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-[11px] font-medium leading-5 text-neutral-500">
                {label}
            </span>
            <div className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white p-1 shadow-sm">
                <button
                    type="button"
                    onClick={() => onStep(-15)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-sm text-neutral-500 transition hover:bg-neutral-50 hover:text-neutral-900"
                    aria-label={`${label} rotate left`}
                    title="Rotate left"
                >
                    ↺
                </button>
                <div className="inline-flex h-8 min-w-[72px] items-center justify-center rounded-md bg-neutral-50 px-3 text-sm font-semibold leading-5 text-neutral-700">
                    {normalizeAngleValue(value)}°
                </div>
                <button
                    type="button"
                    onClick={() => onStep(15)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-sm text-neutral-500 transition hover:bg-neutral-50 hover:text-neutral-900"
                    aria-label={`${label} rotate right`}
                    title="Rotate right"
                >
                    ↻
                </button>
            </div>
        </div>
    );
}

const BUTTON_KEYS_LOWER = new Set([
    'cta',
    'customctalink',
    'ctalink',
    'ctaopeninnewtab',
    'buttontext',
    'buttonlink',
    'buttonopeninnewtab',
    'secondarybuttontext',
    'secondarybuttonlink',
    'secondarybuttonopeninnewtab',
    'outlinebuttontext',
    'outlinebuttonlink',
    'outlinebuttonopeninnewtab',
    'reviewbuttontext',
    'thirdbuttontext',
    'thirdbuttonlink',
    'thirdbuttonopeninnewtab',
    'smallcta',
    'smallctalink',
    'smallctaopeninnewtab',
    'smallsecondarybuttontext',
    'smallsecondarybuttonlink',
    'smallsecondarybuttonopeninnewtab',
    'smalloutlinebuttontext',
    'smalloutlinebuttonlink',
    'smalloutlinebuttonopeninnewtab',
]);

const BUTTON_ICON_LEAF_BY_TEXT_LEAF = {
    cta: 'CTAIcon',
    secondarybuttontext: 'SecondaryButtonIcon',
    outlinebuttontext: 'OutlineButtonIcon',
    thirdbuttontext: 'ThirdButtonIcon',
    smallcta: 'SmallCTAIcon',
    smallsecondarybuttontext: 'SmallSecondaryButtonIcon',
    smalloutlinebuttontext: 'SmallOutlineButtonIcon',
};

const BUTTON_ICON_KEYS_LOWER = new Set(
    Object.values(BUTTON_ICON_LEAF_BY_TEXT_LEAF).map((key) => key.toLowerCase())
);

const BUTTON_ICON_VISIBLE_KEYS_LOWER = new Set(
    [...BUTTON_ICON_KEYS_LOWER].map((key) => `${key}visible`)
);

const NAV_BEHAVIOR_OPTIONS = [
    {
        value: 'static',
        label: 'Static — sits at top of the page',
    },
    {
        value: 'sticky',
        label: 'Sticky — pins while scrolling',
    },
    {
        value: 'sticky-after-hero',
        label: 'Sticky after hero — pins after hero',
    },
    {
        value: 'show-after-hero',
        label: 'Show after hero — hidden on load, pins after hero',
    },
    {
        value: 'shrink',
        label: 'Shrink — compacts on scroll',
    },
];

const NAV_HEIGHT_OPTIONS = [
    {
        value: 'slim',
        label: 'Slim',
    },
    {
        value: 'compact',
        label: 'Compact',
    },
    {
        value: 'standard',
        label: 'Standard',
    },
    {
        value: 'large',
        label: 'Large',
    },
];

const BANNER_COLOR_SCHEMES = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'gradient', label: 'Gradient' },
    { value: 'dark-gradient', label: 'Dark Gradient' },
    { value: 'light-gradient', label: 'Light Gradient' },
    { value: 'mid-gradient', label: 'Mid Gradient' },
    { value: 'light-primary', label: 'Light Primary' },
    { value: 'light-secondary', label: 'Light Secondary' },
    { value: 'dark-primary', label: 'Dark Primary' },
    { value: 'dark-secondary', label: 'Dark Secondary' },
];

const REVIEW_SOURCE_OPTIONS = [
    {
        value: 'google',
        label: 'Google',
        icon: REVIEW_SOURCE_ICON_PATHS.google,
    },
    {
        value: 'nicejob',
        label: 'NiceJob',
        icon: REVIEW_SOURCE_ICON_PATHS.nicejob,
    },
    {
        value: 'facebook',
        label: 'Facebook',
        icon: REVIEW_SOURCE_ICON_PATHS.facebook,
    },
    {
        value: 'custom',
        label: 'Custom Asset',
        icon: null,
    },
];

const PAGE_SCHEMA_MARKUP_KEY = 'SchemaMarkup';
const PAGE_CUSTOM_CODE_HEAD_KEY = 'InsideHeadTag';
const PAGE_CUSTOM_CODE_BODY_KEY = 'BeforeBodyTag';
const PAGE_PUBLISHED_KEY = 'Published';
const PAGE_UNPUBLISH_REDIRECT_KEY = 'UnpublishRedirectTo';

function parsePublishedValue(value) {
    if (value == null) return true;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return Boolean(value);
}

function normalizeUnpublishRedirectTarget(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return '';

    try {
        const parsed = /^https?:\/\//i.test(raw)
            ? new URL(raw)
            : new URL(raw.startsWith('/') ? raw : `/${raw}`, 'https://local');
        const pathname = (parsed.pathname || '/').replace(/\/+$/, '') || '/';
        return `${pathname}${parsed.search || ''}${parsed.hash || ''}`;
    } catch (error) {
        return '';
    }
}

function extractMediaFilename(value) {
    const raw = normalizeCollapsibleArrayBackgroundImageValue(value);
    if (!raw) return '';
    if (/^data:/i.test(raw)) return '';
    if (
        /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
            raw
        )
    )
        return '';

    try {
        const parsed = /^https?:\/\//i.test(raw)
            ? new URL(raw)
            : new URL(raw.startsWith('/') ? raw : `/${raw}`, 'https://local');
        const pathname = parsed.pathname || '';
        const basename = pathname.split('/').pop() || '';
        return decodeURIComponent(basename);
    } catch (error) {
        const basename =
            raw.split('/').pop()?.split('?')[0]?.split('#')[0] || '';
        try {
            return decodeURIComponent(basename);
        } catch (decodeError) {
            return basename;
        }
    }
}

function replaceMediaFilename(value, nextFilename) {
    const raw = normalizeCollapsibleArrayBackgroundImageValue(value);
    const trimmedFilename = String(nextFilename || '')
        .trim()
        .replace(/[\\/]+/g, '');

    if (!raw || !trimmedFilename) return raw;
    if (/^data:/i.test(raw)) return raw;
    if (
        /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
            raw
        )
    )
        return raw;

    try {
        if (/^https?:\/\//i.test(raw)) {
            const parsed = new URL(raw);
            const parts = parsed.pathname.split('/');
            parts[parts.length - 1] = encodeURIComponent(trimmedFilename);
            parsed.pathname = parts.join('/');
            return parsed.toString();
        }

        const parsed = new URL(
            raw.startsWith('/') ? raw : `/${raw}`,
            'https://local'
        );
        const parts = parsed.pathname.split('/');
        parts[parts.length - 1] = encodeURIComponent(trimmedFilename);
        parsed.pathname = parts.join('/');
        const rebuilt = `${parsed.pathname}${parsed.search}${parsed.hash}`;
        return raw.startsWith('/') ? rebuilt : rebuilt.replace(/^\//, '');
    } catch (error) {
        return raw;
    }
}

function splitFilenameParts(filename) {
    const trimmed = String(filename || '').trim();
    if (!trimmed) {
        return { name: '', extension: '' };
    }
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot <= 0) {
        return { name: trimmed, extension: '' };
    }
    return {
        name: trimmed.slice(0, lastDot),
        extension: trimmed.slice(lastDot),
    };
}

function buildFilenameFromParts(name, extension = '') {
    const baseName = String(name || '')
        .trim()
        .replace(/[\\/]+/g, '');
    const normalizedExtension = String(extension || '').trim();
    if (!baseName) return '';
    return `${baseName}${normalizedExtension}`;
}

function readSiteImageAltText(image) {
    return String(
        image?.alt_text ?? image?.altText ?? image?.alt ?? image?.AltText ?? ''
    );
}

function normalizeNavBehavior(value) {
    const raw = String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[_\s]+/g, '-');
    switch (raw) {
        case 'static':
            return 'static';
        case 'reveal-on-scroll-up':
        case 'reveal':
        case 'reveal-up':
        case 'reveal-on-scroll':
        case 'reveal-scroll-up':
        case 'reveal-scroll':
            return 'sticky'; // reveal removed; fall back to sticky
        case 'sticky-after-hero':
        case 'after-hero':
            return 'sticky-after-hero';
        case 'show-after-hero':
        case 'hidden-until-after-hero':
            return 'show-after-hero';
        case 'shrink':
            return 'shrink';
        case 'sticky':
        default:
            return 'sticky';
    }
}

function normalizeNavHeight(value) {
    const raw = String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[_\s]+/g, '-');
    switch (raw) {
        case 'slim':
        case 'tight':
        case 'thin':
        case 'minimal':
        case 'xs':
        case 'x-small':
            return 'slim';
        case 'large':
        case 'taller':
        case 'extra-tall':
        case 'x-tall':
        case 'xl':
        case 'bigger':
        case 'big':
            return 'large';
        case 'standard':
        case 'tall':
            return 'standard';
        case 'compact':
        case 'default':
        case 'normal':
        case 'small':
        case 'sm':
        default:
            return 'compact';
    }
}

function mapSourceToIconVal(source) {
    const valLower = String(source || '').toLowerCase();
    if (valLower.includes('google')) return REVIEW_SOURCE_ICON_PATHS.google;
    if (valLower.includes('nicejob')) return REVIEW_SOURCE_ICON_PATHS.nicejob;
    if (valLower.includes('facebook')) return REVIEW_SOURCE_ICON_PATHS.facebook;
    return '';
}

function isButtonKey(key) {
    return BUTTON_KEYS_LOWER.has(keyLeaf(key).toLowerCase());
}

function isButtonIconKey(key) {
    return BUTTON_ICON_KEYS_LOWER.has(keyLeaf(key).toLowerCase());
}

function isButtonIconVisibleKey(key) {
    return BUTTON_ICON_VISIBLE_KEYS_LOWER.has(keyLeaf(key).toLowerCase());
}

function isReviewsLegacyButtonKey(key, componentName) {
    return (
        ['Reviews', 'ReviewHero', 'ReviewSlider', 'TrustBadge'].includes(
            componentName
        ) && ['buttontext', 'buttonlink'].includes(keyLeaf(key).toLowerCase())
    );
}

function isLinkLikeKey(key) {
    const leaf = keyLeaf(key).toLowerCase();
    return (
        leaf === 'link' ||
        leaf === 'url' ||
        leaf === 'href' ||
        leaf.endsWith('link') ||
        leaf.endsWith('url') ||
        leaf.endsWith('href')
    );
}

function isPromptableKey(key, componentName) {
    return !(
        isStyleKey(key, componentName) ||
        isLinkLikeKey(key) ||
        isButtonKey(key) ||
        isButtonIconKey(key) ||
        isButtonIconVisibleKey(key) ||
        isReviewsLegacyButtonKey(key, componentName)
    );
}

const SHAPE_DIVIDER_KEYS = new Set(['shape', 'colorfill', 'flip']);
function isShapeDividerKey(key) {
    return SHAPE_DIVIDER_KEYS.has(keyLeaf(key).toLowerCase());
}

/* ───────────────────────── Keys ───────────────────────── */

function findKeyByLeaf(allKeys, leafLower) {
    return allKeys.find((k) => keyLeaf(k).toLowerCase() === leafLower);
}

const NAV_PHONE_CONTROL_LEAVES = new Set([
    'phone',
    'showphonenumber',
    'phoneicon',
    'showphoneicondesktop',
    'phoneiconposition',
    'phonemobileicon',
]);

function resolveNavPhoneButtonPair(allKeys, componentName = '') {
    if (componentName !== 'Nav') return null;
    const phoneKey = findKeyByLeaf(allKeys, 'phone');
    if (!phoneKey) return null;

    const resolvePhoneKey = (leaf) =>
        findKeyByLeaf(allKeys, leaf.toLowerCase()) ||
        replaceLeafInPath(phoneKey, leaf);

    return {
        kind: 'nav-phone',
        textKey: phoneKey,
        showTextKey: resolvePhoneKey('ShowPhoneNumber'),
        iconKey: resolvePhoneKey('PhoneIcon'),
        iconVisibleKey: resolvePhoneKey('ShowPhoneIconDesktop'),
        iconPositionKey: resolvePhoneKey('PhoneIconPosition'),
        mobileIconKey: resolvePhoneKey('PhoneMobileIcon'),
        label: 'Phone Button',
    };
}

function resolveButtonPairs(allKeys, componentName = '') {
    const pairs = [];
    const ctaTextKey = findKeyByLeaf(allKeys, 'cta');
    const ctaLinkKey =
        findKeyByLeaf(allKeys, 'customctalink') ||
        findKeyByLeaf(allKeys, 'ctalink') ||
        (ctaTextKey ? replaceLeafInPath(ctaTextKey, 'CustomCTALink') : null);
    if (ctaTextKey)
        pairs.push({
            textKey: ctaTextKey,
            linkKey: ctaLinkKey,
            iconKey: resolveButtonIconKey(ctaTextKey, allKeys),
            iconVisibleKey: resolveButtonIconVisibleKey(ctaTextKey, allKeys),
            openInNewTabKey: resolveButtonOpenInNewTabKey(ctaTextKey, allKeys),
        });

    const secondaryTextKey = findKeyByLeaf(allKeys, 'secondarybuttontext');
    const secondaryLinkKey =
        findKeyByLeaf(allKeys, 'secondarybuttonlink') ||
        (secondaryTextKey
            ? replaceLeafInPath(secondaryTextKey, 'SecondaryButtonLink')
            : null);
    if (secondaryTextKey)
        pairs.push({
            textKey: secondaryTextKey,
            linkKey: secondaryLinkKey,
            iconKey: resolveButtonIconKey(secondaryTextKey, allKeys),
            iconVisibleKey: resolveButtonIconVisibleKey(
                secondaryTextKey,
                allKeys
            ),
            openInNewTabKey: resolveButtonOpenInNewTabKey(
                secondaryTextKey,
                allKeys
            ),
        });

    // ReviewStories renders ReviewButtonText as its outline-style button.
    // Keep using the component's existing key so editing this field updates
    // the button that is actually rendered instead of the unused generic CTA
    // fallback field.
    const reviewButtonTextKey =
        componentName === 'ReviewStories'
            ? findKeyByLeaf(allKeys, 'reviewbuttontext')
            : null;
    const outlineTextKey = reviewButtonTextKey
        ? null
        : findKeyByLeaf(allKeys, 'outlinebuttontext');
    const outlineLinkKey =
        findKeyByLeaf(allKeys, 'outlinebuttonlink') ||
        (outlineTextKey
            ? replaceLeafInPath(outlineTextKey, 'OutlineButtonLink')
            : null);
    if (outlineTextKey)
        pairs.push({
            textKey: outlineTextKey,
            linkKey: outlineLinkKey,
            iconKey: resolveButtonIconKey(outlineTextKey, allKeys),
            iconVisibleKey: resolveButtonIconVisibleKey(
                outlineTextKey,
                allKeys
            ),
            openInNewTabKey: resolveButtonOpenInNewTabKey(
                outlineTextKey,
                allKeys
            ),
        });

    if (reviewButtonTextKey)
        pairs.push({
            textKey: reviewButtonTextKey,
            linkKey: null,
            label: 'Outline Button',
        });

    if (!outlineTextKey) {
        const legacyTextKey = findKeyByLeaf(allKeys, 'buttontext');
        const legacyLinkKey =
            findKeyByLeaf(allKeys, 'buttonlink') ||
            (legacyTextKey
                ? replaceLeafInPath(legacyTextKey, 'ButtonLink')
                : null);
        if (legacyTextKey)
            pairs.push({
                textKey: legacyTextKey,
                linkKey: legacyLinkKey,
                label:
                    componentName === 'Reviews' ||
                    componentName === 'ReviewHero'
                        ? 'Outline Button'
                        : 'Button',
                linkLabel:
                    componentName === 'Reviews' ||
                    componentName === 'ReviewHero'
                        ? 'Outline Link'
                        : 'Button Link',
                iconKey: resolveButtonIconKey(legacyTextKey, allKeys),
                iconVisibleKey: resolveButtonIconVisibleKey(
                    legacyTextKey,
                    allKeys
                ),
                openInNewTabKey: resolveButtonOpenInNewTabKey(
                    legacyTextKey,
                    allKeys
                ),
            });
    }

    // Legacy third button (rendered as outline)
    const thirdTextKey = findKeyByLeaf(allKeys, 'thirdbuttontext');
    const thirdLinkKey =
        findKeyByLeaf(allKeys, 'thirdbuttonlink') ||
        (thirdTextKey
            ? replaceLeafInPath(thirdTextKey, 'ThirdButtonLink')
            : null);
    if (thirdTextKey)
        pairs.push({
            textKey: thirdTextKey,
            linkKey: thirdLinkKey,
            iconKey: resolveButtonIconKey(thirdTextKey, allKeys),
            iconVisibleKey: resolveButtonIconVisibleKey(thirdTextKey, allKeys),
            openInNewTabKey: resolveButtonOpenInNewTabKey(
                thirdTextKey,
                allKeys
            ),
        });

    // Small buttons (e.g., Pricing cards)
    const smallCtaTextKey = findKeyByLeaf(allKeys, 'smallcta');
    const smallCtaLinkKey =
        findKeyByLeaf(allKeys, 'smallctalink') ||
        findKeyByLeaf(allKeys, 'smallctaLink') ||
        (smallCtaTextKey
            ? replaceLeafInPath(smallCtaTextKey, 'SmallCTALink')
            : null);
    if (smallCtaTextKey)
        pairs.push({
            textKey: smallCtaTextKey,
            linkKey: smallCtaLinkKey,
            iconKey: resolveButtonIconKey(smallCtaTextKey, allKeys),
            iconVisibleKey: resolveButtonIconVisibleKey(
                smallCtaTextKey,
                allKeys
            ),
            openInNewTabKey: resolveButtonOpenInNewTabKey(
                smallCtaTextKey,
                allKeys
            ),
        });

    const smallSecondaryTextKey = findKeyByLeaf(
        allKeys,
        'smallsecondarybuttontext'
    );
    const smallSecondaryLinkKey =
        findKeyByLeaf(allKeys, 'smallsecondarybuttonlink') ||
        (smallSecondaryTextKey
            ? replaceLeafInPath(
                  smallSecondaryTextKey,
                  'SmallSecondaryButtonLink'
              )
            : null);
    if (smallSecondaryTextKey)
        pairs.push({
            textKey: smallSecondaryTextKey,
            linkKey: smallSecondaryLinkKey,
            iconKey: resolveButtonIconKey(smallSecondaryTextKey, allKeys),
            iconVisibleKey: resolveButtonIconVisibleKey(
                smallSecondaryTextKey,
                allKeys
            ),
            openInNewTabKey: resolveButtonOpenInNewTabKey(
                smallSecondaryTextKey,
                allKeys
            ),
        });

    const smallOutlineTextKey = findKeyByLeaf(
        allKeys,
        'smalloutlinebuttontext'
    );
    const smallOutlineLinkKey =
        findKeyByLeaf(allKeys, 'smalloutlinebuttonlink') ||
        (smallOutlineTextKey
            ? replaceLeafInPath(smallOutlineTextKey, 'SmallOutlineButtonLink')
            : null);
    if (smallOutlineTextKey)
        pairs.push({
            textKey: smallOutlineTextKey,
            linkKey: smallOutlineLinkKey,
            iconKey: resolveButtonIconKey(smallOutlineTextKey, allKeys),
            iconVisibleKey: resolveButtonIconVisibleKey(
                smallOutlineTextKey,
                allKeys
            ),
            openInNewTabKey: resolveButtonOpenInNewTabKey(
                smallOutlineTextKey,
                allKeys
            ),
        });

    return pairs;
}

function resolveButtonIconKey(textKey, allKeys = []) {
    const textLeaf = keyLeaf(textKey).toLowerCase();
    const iconLeaf = BUTTON_ICON_LEAF_BY_TEXT_LEAF[textLeaf];
    if (!iconLeaf) return null;
    return (
        findKeyByLeaf(allKeys, iconLeaf.toLowerCase()) ||
        replaceLeafInPath(textKey, iconLeaf)
    );
}

function resolveButtonIconVisibleKey(textKey, allKeys = []) {
    const iconKey = resolveButtonIconKey(textKey, allKeys);
    if (!iconKey) return null;
    const iconLeaf = keyLeaf(iconKey);
    const visibleLeaf = iconLeaf.endsWith('Visible')
        ? iconLeaf
        : `${iconLeaf}Visible`;
    return (
        findKeyByLeaf(allKeys, visibleLeaf.toLowerCase()) ||
        replaceLeafInPath(iconKey, visibleLeaf)
    );
}

function resolveButtonOpenInNewTabKey(textKey, allKeys = []) {
    const textLeaf = keyLeaf(textKey);
    const openLeaf =
        textLeaf === 'CTA'
            ? 'CTAOpenInNewTab'
            : textLeaf.endsWith('Text')
            ? `${textLeaf.slice(0, -4)}OpenInNewTab`
            : `${textLeaf}OpenInNewTab`;
    return (
        findKeyByLeaf(allKeys, openLeaf.toLowerCase()) ||
        replaceLeafInPath(textKey, openLeaf)
    );
}

// Hide link inputs until their paired text exists (covers nested items too)
function shouldHideButtonLinkField(path, rootObj) {
    const leaf = keyLeaf(path).toLowerCase();
    const map = {
        customctalink: ['cta'],
        ctalink: ['cta'],
        secondarybuttonlink: ['secondarybuttontext'],
        outlinebuttonlink: ['outlinebuttontext', 'thirdbuttontext'],
        thirdbuttonlink: ['thirdbuttontext', 'outlinebuttontext'],
        smallctalink: ['smallcta'],
        smallsecondarybuttonlink: ['smallsecondarybuttontext'],
        smalloutlinebuttonlink: ['smalloutlinebuttontext'],
    };
    const textLeaves = map[leaf];
    if (!textLeaves) return false;

    // Walk to the object that actually contains the sibling text field,
    // then match its key case-insensitively — real field names (e.g. "CTA")
    // don't share the lowercase casing used by `leaf`/`map` above.
    const parts = [];
    String(path).replace(/([^[.\]]+)|\[(\d+)\]/g, (_, k, i) =>
        parts.push(i != null ? Number(i) : k)
    );
    parts.pop();
    let parentObj = rootObj;
    for (const p of parts) {
        if (parentObj == null) break;
        parentObj = parentObj[p];
    }
    if (!parentObj || typeof parentObj !== 'object') return false;
    const siblingKeys = Object.keys(parentObj);

    return !textLeaves.some((textLeaf) => {
        const siblingKey = siblingKeys.find(
            (k) => k.toLowerCase() === textLeaf
        );
        if (!siblingKey) return false;
        const val = readValueAtPath(parentObj, siblingKey);
        return String(val ?? '').trim().length > 0;
    });
}

function readValueAtPath(root, path) {
    const val = getByPath(root, path);
    if (val && typeof val === 'object' && 'value' in val)
        return val.value ?? '';
    return val ?? '';
}

/* ───────────── Image position helpers (missing earlier) ───────────── */

function getAllowedImagePositions() {
    // Keep in sync with ImagePositionButtons allowed prop
    return ['left', 'center', 'right'];
}

function normalizeImagePosition(
    incoming,
    allowed = getAllowedImagePositions()
) {
    const v = String(
        incoming && typeof incoming === 'object' && 'value' in incoming
            ? incoming.value
            : incoming ?? ''
    ).toLowerCase();
    const allowedSet = new Set(allowed);
    if (allowedSet.has(v)) return v;
    if (allowedSet.has('left')) return 'left';
    return allowed[0];
}

function normalizePopupPosition(incoming) {
    const raw =
        incoming && typeof incoming === 'object' && 'value' in incoming
            ? incoming.value
            : incoming;
    const v = String(raw || '').toLowerCase();
    if (v === 'bottom') return 'bottom';
    return 'center';
}

/* ─────────────────────────── Image position UI ─────────────────────────── */

function ImagePosIcon({ type }) {
    const W = 20,
        H = 16;
    if (type === 'full') {
        return (
            <svg
                width="20"
                height="16"
                viewBox={`0 0 ${W} ${H}`}
                aria-hidden="true"
                className="text-current"
            >
                <rect
                    x="1"
                    y="1"
                    width={W - 2}
                    height={H - 2}
                    rx="2"
                    fill="currentColor"
                />
                <rect
                    x="4"
                    y="11"
                    width="12"
                    height="2"
                    rx="1"
                    fill="white"
                    opacity="0.85"
                />
            </svg>
        );
    }
    const P = 2,
        GAP = 2;
    const pw = 7,
        ph = 10,
        py = 3;
    const pwC = 9,
        phC = 8,
        pyC = 3;
    const px =
        type === 'left' ? W - pw - P : type === 'right' ? P : (W - pwC) / 2;
    const line = (x, y, w) => (
        <rect
            x={x}
            y={y}
            width={Math.max(0, w)}
            height="2"
            rx="1"
            fill="currentColor"
            opacity="0.8"
        />
    );

    const yA = py,
        yB = py + 4;
    const rightX = (type === 'center' ? (W - pwC) / 2 + pwC : px + pw) + GAP;
    const rightW = W - P - rightX;

    const leftX = P;
    const leftW = Math.max(
        0,
        (type === 'center' ? (W - pwC) / 2 : px) - leftX - GAP
    );

    return (
        <svg
            width="20"
            height="16"
            viewBox={`0 0 ${W} ${H}`}
            aria-hidden="true"
            className="text-current"
        >
            {type === 'center' ? (
                <rect
                    x={(W - pwC) / 2}
                    y={pyC}
                    width={pwC}
                    height={phC}
                    rx="2"
                    fill="currentColor"
                />
            ) : (
                <rect
                    x={px}
                    y={py}
                    width={pw}
                    height={ph}
                    rx="2"
                    fill="currentColor"
                />
            )}
            {type === 'left' && (
                <>
                    {line(leftX, yA, leftW)}
                    {line(leftX, yB, Math.max(0, leftW - 3))}
                </>
            )}
            {type === 'right' && (
                <>
                    {line(rightX, yA, rightW)}
                    {line(rightX, yB, Math.max(0, rightW - 3))}
                </>
            )}
            {type === 'center' && (
                <>{line((W - 8) / 2, Math.min(pyC + phC + 2, H - 3), 8)}</>
            )}
        </svg>
    );
}

function PopupPositionIcon({ type }) {
    const W = 24,
        H = 18;
    const outerR = 2;
    const modalW = 12;
    const modalH = 6;
    const padding = 3;
    const centerY = (H - modalH) / 2;
    const bottomY = H - modalH - padding;
    const modalY = type === 'bottom' ? bottomY : centerY;
    return (
        <svg
            width={W}
            height={H}
            viewBox={`0 0 ${W} ${H}`}
            aria-hidden="true"
            className="text-current"
        >
            <rect
                x="0.5"
                y="0.5"
                width={W - 1}
                height={H - 1}
                rx={outerR}
                stroke="currentColor"
                strokeWidth="1"
                fill="none"
                opacity="0.4"
            />
            <rect
                x={(W - modalW) / 2}
                y={modalY}
                width={modalW}
                height={modalH}
                rx="1.5"
                fill="currentColor"
                opacity="0.9"
            />
            <rect
                x={(W - modalW) / 2}
                y={modalY + modalH / 2 - 1}
                width={modalW}
                height="1.5"
                rx="0.75"
                fill="white"
                opacity="0.7"
            />
        </svg>
    );
}

function PopupPositionButtons({ value, onChange }) {
    const opts = ['center', 'bottom'];
    return (
        <div className="flex flex-wrap gap-2">
            {opts.map((opt) => {
                const active = value === opt;
                const colorClass = active
                    ? 'text-primary-600'
                    : 'text-neutral-500';
                return (
                    <span
                        key={opt}
                        className={[
                            'relative inline-flex rounded-xl p-[2px]',
                            'bg-neutral-300',
                            'transition-colors duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)]',
                            'motion-reduce:transition-none',
                        ].join(' ')}
                    >
                        <button
                            type="button"
                            onClick={() => onChange(opt)}
                            aria-pressed={active}
                            aria-label={`Popup ${opt}`}
                            className={[
                                'inline-flex h-9 w-14 items-center justify-center rounded-[10px] bg-white',
                                'transition-[opacity,box-shadow] duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)]',
                                'motion-reduce:transition-none',
                                'shadow-sm hover:shadow',
                                active
                                    ? 'ring-2 ring-primary-600 ring-offset-1 ring-offset-white'
                                    : 'ring-2 ring-neutral-200',
                                'focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-1',
                                colorClass,
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            <PopupPositionIcon type={opt} />
                        </button>
                    </span>
                );
            })}
        </div>
    );
}

function ImagePositionButtons({
    value,
    allowed = ['left', 'center', 'right'],
    hideUnsupported = false,
    onChange,
}) {
    const ALL = ['left', 'center', 'right', 'full'];
    const order = hideUnsupported
        ? ALL.filter((k) => allowed.includes(k))
        : ALL;

    return (
        <div className="flex flex-wrap gap-2">
            {order.map((opt) => {
                const active = value === opt;
                const disabled = !allowed.includes(opt);
                const colorClass = disabled
                    ? 'text-neutral-300'
                    : active
                    ? 'text-primary-600'
                    : 'text-neutral-500';

                return (
                    <span
                        key={opt}
                        className={[
                            'relative inline-flex rounded-xl p-[2px]',
                            disabled ? 'bg-neutral-200' : 'bg-neutral-300',
                            'transition-colors duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)]',
                            'motion-reduce:transition-none',
                        ].join(' ')}
                    >
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={() => !disabled && onChange(opt)}
                            aria-pressed={active}
                            aria-label={`Image ${opt}`}
                            title={`Image ${opt}`}
                            className={[
                                'inline-flex h-9 w-12 items-center justify-center rounded-[10px] bg-white',
                                'transition-[opacity,box-shadow] duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)]',
                                'motion-reduce:transition-none',
                                'shadow-sm',
                                !disabled && 'hover:shadow',
                                disabled && 'cursor-default opacity-50',
                                active
                                    ? 'ring-2 ring-primary-600 ring-offset-1 ring-offset-white'
                                    : 'ring-2 ring-neutral-200',
                                'focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-1',
                                colorClass,
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            <ImagePosIcon type={opt} />
                        </button>
                    </span>
                );
            })}
        </div>
    );
}

// helpers to read sibling fields
function getByPath(root, path) {
    const parts = [];
    path.replace(/([^[.\]]+)|\[(\d+)\]/g, (_, k, i) =>
        parts.push(i != null ? Number(i) : k)
    );
    let cur = root;
    for (const p of parts) {
        if (cur == null) return undefined;
        cur = cur[p];
    }
    return cur;
}
function replaceLeafInPath(path, newLeaf) {
    return String(path).replace(/([^.[]+)(\s*)$/, () => newLeaf);
}
function inferLinkedFieldKey(path, rootObj) {
    // Avoid accelerating anchor ID fields as link targets (e.g., component section anchors).
    const candidates = ['Link', 'URL', 'Url', 'Href'];
    for (const leaf of candidates) {
        const candidate = replaceLeafInPath(path, leaf);
        const val = getByPath(rootObj, candidate);
        if (val != null && String(val).trim() !== '')
            return { key: candidate, value: val };
    }
    // also try if the text field is Name/Title/etc.
    const leafLower = keyLeaf(path).toLowerCase();

    if (
        [
            'name',
            'title',
            'label',
            'header',
            'question',
            'answer',
            'tagline',
            'role',
        ].includes(leafLower)
    ) {
        for (const leaf of candidates) {
            const candidate = replaceLeafInPath(path, leaf);
            const val = getByPath(rootObj, candidate);
            if (val != null && String(val).trim() !== '')
                return { key: candidate, value: val };
        }
    }
    return null;
}

/* ─────────────────────────── Chips & small buttons ─────────────────────────── */

function VSChip({ active, disabled, children, onClick, srText }) {
    const colorClass = disabled
        ? 'text-neutral-300'
        : active
        ? 'text-primary-600'
        : 'text-neutral-600';

    return (
        <span
            className={[
                'group relative inline-flex rounded-xl p-[2px]',
                'transition-colors duration-300 ease-out',
                disabled
                    ? 'bg-neutral-200'
                    : active
                    ? 'bg-primary-600'
                    : 'bg-neutral-300 hover:bg-gradient-to-br hover:from-primary-600 hover:to-secondary-600',
                !disabled &&
                    'focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600',
            ].join(' ')}
        >
            <button
                type="button"
                disabled={disabled}
                onClick={disabled ? undefined : onClick}
                aria-pressed={active}
                className={[
                    'inline-flex h-9 items-center justify-center rounded-[10px] bg-white px-3 text-xs md:text-sm',
                    'transition-[opacity,box-shadow] duration-300 ease-out',
                    'shadow-sm',
                    !disabled && 'hover:shadow',
                    disabled ? 'cursor-default opacity-50' : '',
                    active
                        ? 'ring-2 ring-primary-600 ring-offset-1 ring-offset-white'
                        : 'ring-2 ring-neutral-200',
                    'focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-1',
                    colorClass,
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                {srText && <span className="sr-only">{srText}</span>}
                {children}
            </button>
        </span>
    );
}

function normalizeTagPills(value) {
    const source = Array.isArray(value)
        ? value
        : typeof value === 'string'
        ? value.split(/[,;\n]/)
        : [];

    return Array.from(
        new Set(source.map((item) => String(item ?? '').trim()).filter(Boolean))
    );
}

function TagPillsInput({
    value,
    onChange,
    placeholder = 'Optional Filter Tag, Empty Will Display All Reviews.',
}) {
    const inputRef = React.useRef(null);
    const [draft, setDraft] = React.useState('');
    const tags = React.useMemo(
        () => normalizeTagPills(value).slice(0, 1),
        [value]
    );

    const commitDraft = React.useCallback(
        (rawDraft = draft) => {
            const nextTag = String(rawDraft ?? '').trim();
            if (!nextTag) return;

            if (tags[0]?.toLowerCase() === nextTag.toLowerCase()) {
                setDraft('');
                return;
            }

            onChange?.([nextTag]);
            setDraft('');
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        },
        [draft, onChange, tags]
    );

    const removeTag = React.useCallback(
        (tagToRemove) => {
            const next = tags.filter(
                (tag) => tag.toLowerCase() !== String(tagToRemove).toLowerCase()
            );
            onChange?.(next);
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        },
        [onChange, tags]
    );

    return (
        <div className="mt-2 max-w-[16rem] rounded-lg bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
            <div className="flex min-h-8 flex-wrap items-center gap-1 rounded-md bg-white px-2 py-1">
                {tags.map((tag) => (
                    <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700 ring-1 ring-primary-200"
                    >
                        <span className="max-w-[12rem] truncate">{tag}</span>
                        <button
                            type="button"
                            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-primary-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                            onClick={() => removeTag(tag)}
                            aria-label={`Remove ${tag}`}
                        >
                            <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                        if (
                            event.key === 'Enter' ||
                            event.key === ' ' ||
                            event.key === 'Tab'
                        ) {
                            if (draft.trim()) {
                                event.preventDefault();
                                commitDraft();
                            }
                            return;
                        }

                        if (event.key === ',' && draft.trim()) {
                            event.preventDefault();
                            commitDraft();
                            return;
                        }

                        if (
                            event.key === 'Backspace' &&
                            !draft &&
                            tags.length
                        ) {
                            event.preventDefault();
                            removeTag(tags[tags.length - 1]);
                        }
                    }}
                    onBlur={() => {
                        if (draft.trim()) commitDraft();
                    }}
                    onPaste={(event) => {
                        const text = event.clipboardData.getData('text');
                        if (!/[,\n;]/.test(text)) return;
                        event.preventDefault();
                        const nextTag = normalizeTagPills(
                            text.split(/[,;\n]/)
                        )[0];
                        if (nextTag) onChange?.([nextTag]);
                        setDraft('');
                    }}
                    placeholder={tags.length ? '' : placeholder}
                    className="min-w-[7rem] flex-1 border-0 p-0.5 text-xs leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                />
            </div>
        </div>
    );
}

/* Small square button (used by VerticalAlignButtons) */
function VSButton({ active, disabled, srText, children, onClick }) {
    const colorClass = disabled
        ? 'text-neutral-300'
        : active
        ? 'text-primary-600'
        : 'text-neutral-500';

    return (
        <span
            className={[
                'group relative inline-flex rounded-xl p-[2px]',
                'transition-colors duration-300 ease-out',
                disabled
                    ? 'bg-neutral-200'
                    : active
                    ? 'bg-primary-600'
                    : 'bg-neutral-300 hover:bg-gradient-to-br hover:from-primary-600 hover:to-secondary-600',
                !disabled &&
                    'focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600',
            ].join(' ')}
        >
            <button
                type="button"
                disabled={disabled}
                onClick={disabled ? undefined : onClick}
                aria-pressed={active}
                className={[
                    'inline-flex h-9 w-12 items-center justify-center rounded-[10px] bg-white',
                    'transition-[opacity,box-shadow] duration-300 ease-out',
                    'shadow-sm',
                    !disabled && 'hover:shadow',
                    disabled ? 'cursor-default opacity-50' : '',
                    disabled
                        ? 'ring-2 ring-neutral-200'
                        : active
                        ? 'ring-2 ring-primary-600 ring-offset-1 ring-offset-white'
                        : 'ring-2 ring-neutral-200',
                    'focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-1',
                    colorClass,
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <span className="sr-only">{srText}</span>
                {children}
            </button>
        </span>
    );
}

/* ────────────────────── ImageVariant (display/valign/shadow) ────────────────────── */

function VerticalAlignButtons({ value = 'top', onChange, disabled = false }) {
    return (
        <div className="inline-flex gap-2">
            <VSButton
                active={value === 'top'}
                disabled={disabled}
                srText="Vertical Top"
                onClick={() => onChange?.('top')}
            >
                <i
                    className="fa-solid fa-objects-align-top text-sm"
                    aria-hidden="true"
                />
            </VSButton>

            <VSButton
                active={value === 'center'}
                disabled={disabled}
                srText="Vertical Center"
                onClick={() => onChange?.('center')}
            >
                <i
                    className="fa-solid fa-objects-align-center-vertical text-sm"
                    aria-hidden="true"
                />
            </VSButton>
        </div>
    );
}

const DISPLAYS = ['cover', 'contain', 'landscape', 'portrait', 'square'];

function normalizeVariantTokens(v = '') {
    const original = String(v).trim();
    const raw = String(v)
        .split('-')
        .map((t) => t.trim())
        .filter(Boolean);
    const combined = [];
    for (let i = 0; i < raw.length; i++) {
        const t = raw[i];
        const n = raw[i + 1];
        if (t === 'vertical' && n === 'center') {
            combined.push('vertical-center');
            i++;
            continue;
        }
        if (t === 'no' && n === 'shadow') {
            combined.push('no-shadow');
            i++;
            continue;
        }
        combined.push(t);
    }
    const seen = new Set();
    const tokens = [];
    // include the original string first so hyphenated variants (e.g., slider-full) match
    if (original) tokens.push(original);
    for (const t of combined)
        if (!seen.has(t)) {
            seen.add(t);
            tokens.push(t);
        }
    return tokens;
}

function parseVariant(
    v = '',
    allowedDisplays = DISPLAYS,
    allowVerticalAlign = true
) {
    const normalizedInput = String(v || '')
        .toLowerCase()
        .trim();
    const displays =
        Array.isArray(allowedDisplays) && allowedDisplays.length > 0
            ? allowedDisplays
            : DISPLAYS;
    const tokens = normalizeVariantTokens(v);
    const tokenFirstMatch = tokens.find((t) => displays.includes(t));
    const display =
        tokenFirstMatch ||
        displays.find((d) => tokens.includes(d)) ||
        displays[0];
    const valignRaw = tokens.includes('vertical-center') ? 'center' : 'top';
    const valign = allowVerticalAlign ? valignRaw : 'top';
    const shadow = !tokens.includes('no-shadow');
    const extras = tokens
        .filter(
            (t) =>
                t !== normalizedInput &&
                !displays.includes(t) &&
                !DISPLAYS.includes(t) && // drop any known display tokens we don't support here
                t !== 'vertical-center' &&
                t !== 'no-shadow' &&
                !t.includes('no-shadow')
        )
        // avoid keeping split fragments of the chosen display (e.g., "slider" or "full" from "slider-full")
        .filter((t) => !(display?.includes('-') && display?.includes(t)));
    return { display, valign, shadow, extras };
}

function buildVariant(
    { display, valign, shadow, extras },
    allowedDisplays = DISPLAYS,
    allowVerticalAlign = true
) {
    const displays =
        Array.isArray(allowedDisplays) && allowedDisplays.length > 0
            ? allowedDisplays
            : DISPLAYS;
    const cleanDisplay = displays.includes(display)
        ? display
        : displays[0] || 'cover';
    const cleanExtras = (extras ?? []).filter(
        (t) =>
            !displays.includes(t) &&
            t !== 'vertical-center' &&
            t !== 'no-shadow'
    );
    const out = [];
    if (cleanDisplay) out.push(cleanDisplay);
    out.push(...cleanExtras);
    const valignToUse =
        allowVerticalAlign && valign === 'center' ? 'center' : 'top';
    if (valignToUse === 'center') out.push('vertical-center');
    if (!shadow) out.push('no-shadow');
    const seen = new Set();
    const ordered = [];
    for (const t of out) {
        if (!seen.has(t)) {
            seen.add(t);
            ordered.push(t);
        }
    }
    return ordered.join('-');
}

function Switch({ checked, onChange, ariaLabel, size = 'default' }) {
    const isSmall = size === 'sm';
    return (
        <label className="relative inline-flex cursor-pointer items-center">
            <input
                type="checkbox"
                className="peer sr-only"
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                aria-label={ariaLabel}
            />
            <div
                className={
                    isSmall
                        ? "peer h-4 w-7 rounded-full bg-neutral-300 after:absolute after:left-[2px] after:top-0.5 after:h-3 after:w-3 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-3 peer-checked:after:border-white peer-focus:ring-1 peer-focus:ring-blue-300"
                        : "peer h-5 w-9 rounded-full bg-neutral-300 after:absolute after:left-[3px] after:top-0.5 after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-4 peer-checked:after:border-white peer-focus:ring-1 peer-focus:ring-blue-300"
                }
            />
        </label>
    );
}

function ImageVariantControls({
    value,
    onChange,
    allowedDisplays = DISPLAYS,
    enableVerticalAlign = true,
    disableVerticalAlign = false,
    enableShadow = true,
}) {
    const displays = React.useMemo(() => {
        if (Array.isArray(allowedDisplays) && allowedDisplays.length > 0) {
            return allowedDisplays;
        }
        return DISPLAYS;
    }, [allowedDisplays]);

    const { display, valign, shadow, extras } = React.useMemo(
        () => parseVariant(value, displays, enableVerticalAlign),
        [value, displays, enableVerticalAlign]
    );

    const setPart = (part, next) => {
        const shadowToUse = enableShadow ? shadow : true;
        const valignToUse = enableVerticalAlign ? valign : 'top';
        const nextStr = buildVariant(
            {
                display: part === 'display' ? next : display,
                valign:
                    enableVerticalAlign && part === 'valign'
                        ? next
                        : valignToUse,
                shadow: enableShadow && part === 'shadow' ? next : shadowToUse,
                extras,
            },
            displays,
            enableVerticalAlign
        );
        onChange?.(nextStr);
    };

    return (
        <div>
            {/* Display */}
            <div className="mb-4">
                <div className="mb-1 text-xs font-semibold leading-5 text-primary-700">
                    Image Display
                </div>
                <div className="inline-flex flex-wrap gap-2">
                    {displays.map((opt) => (
                        // render friendly labels (e.g., slider-full -> Slider full)
                        <VSChip
                            key={opt}
                            active={display === opt}
                            disabled={false}
                            srText={`Display ${opt}`}
                            onClick={() => setPart('display', opt)}
                        >
                            {opt
                                .split('-')
                                .map((word) =>
                                    word.length
                                        ? word[0].toUpperCase() + word.slice(1)
                                        : ''
                                )
                                .join(' ')}
                        </VSChip>
                    ))}
                </div>
            </div>

            {/* Vertical + Shadow */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                {enableVerticalAlign && (
                    <div>
                        <div
                            className={`mb-1 text-xs font-semibold leading-5 ${
                                disableVerticalAlign
                                    ? 'text-neutral-400'
                                    : 'text-primary-700'
                            }`}
                        >
                            Vertical Alignment
                        </div>
                        <VerticalAlignButtons
                            value={valign}
                            disabled={disableVerticalAlign}
                            onChange={(next) => setPart('valign', next)}
                        />
                    </div>
                )}

                {enableShadow && (
                    <div className="flex items-center gap-3">
                        <div className="text-xs font-semibold leading-5 text-primary-700">
                            Shadow
                        </div>
                        <Switch
                            checked={shadow === true}
                            onChange={(checked) => setPart('shadow', checked)}
                            ariaLabel="Toggle shadow"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─────────────────────────── Hide Site Branding ─────────────────────────── */

const HIDE_FOR_COMPONENT = {
    AboutFeature: ['MediaPosition'],
    CallToAction: ['ImageObjectPosition'],
    Footer: ['Brand', 'BrandUrl', 'BrandName', 'BrandLogo'],
    CTAHero: ['ImagePosition', 'Video'],
    Gallery: ['ImagePosition'],
    HeroFullBG: [
        'AltText',
        'ContentOrder',
        'LeadImageAltText',
        'LeadImagePosition',
        'Video',
    ],
    HeroHalf: ['ImageObjectPosition'],
    Hero: ['ImagePosition'],
    ImageGallery: ['ImagePosition'],
    ImageSlider: ['ImagePosition'],
    ItemGrid: ['ImagePosition', 'CardImagePosition'],
    Nav: ['Shadow'],
    Popup: ['ImageObjectPosition'],
    ComparisonTable: [
        'BackgroundImage',
        'BackgroundImagePosition',
        'BackgroundFixed',
        'BackgroundImageUrl',
        'GridSeparators',
        'FeatureBlockBackgroundImagePosition',
    ],
    Reviews: ['BackgroundImagePosition'],
    ReviewSlider: ['BackgroundImagePosition'],
    Service: [
        'MediaPosition',
        'BeforeImagePosition',
        'AfterImagePosition',
        'StickyMedia',
    ],
    ServiceTabs: ['MediaPosition', 'StickyMedia'],
    Services: ['ImagePosition'],
    SecondaryServices: ['ImagePosition', 'Video', 'VideoTitle'],
    Team: ['ImagePosition'],
};

const HERO_FULL_BG_REORDERABLE_LEAVES = [
    'leadimageurl',
    'header',
    'subheader',
    'body',
    'ctabuttons',
];

function shouldHideProp(componentName, key) {
    const path = String(key);
    const leaf = keyLeaf(path);

    const toHide = HIDE_FOR_COMPONENT[componentName];
    if (
        Array.isArray(toHide) &&
        toHide.some((k) => k.toLowerCase() === leaf.toLowerCase())
    ) {
        return true;
    }

    if (componentName === 'Footer') {
        return /(^|\.|\[)Brand(\.|$)/i.test(path);
    }

    return false;
}

/* ───────────────────── Color scheme display filters ───────────────────── */

function filterColorSchemeGroups(groups, componentName, componentProps = {}) {
    const name = String(componentName || '').toLowerCase();
    const normalizeLabel = (label) => String(label || '').toLowerCase();
    if (name === 'itemgrid') {
        return groups
            .map((group) => {
                const label = normalizeLabel(group?.label);
                if (label === 'box effects') return null;
                if (label === 'effects') {
                    const onlyClear = (group?.options || []).filter(
                        (opt) => opt.value === 'clear'
                    );
                    return onlyClear.length
                        ? { ...group, options: onlyClear }
                        : null;
                }
                return group;
            })
            .filter(Boolean);
    }

    if (name === 'nav') {
        const hideValues = new Set(['white', 'white-secondary']);
        const hideLabels = new Set(['box effects', 'effects']);
        return groups
            .map((group) => {
                const label = normalizeLabel(group?.label);
                if (hideLabels.has(label)) return null;
                const options = (group?.options || []).filter(
                    (opt) => !hideValues.has(opt?.value)
                );
                return options.length ? { ...group, options } : null;
            })
            .filter(Boolean);
    }

    if (name === 'reviews') {
        return groups
            .map((group) => {
                const label = normalizeLabel(group?.label);
                if (label === 'transparent' || label === 'float') return null;
                if (label === 'effects') {
                    const options = (group?.options || []).filter((opt) =>
                        ['blur', 'dark-blur'].includes(opt?.value)
                    );
                    return options.length ? { ...group, options } : null;
                }
                return group;
            })
            .filter(Boolean);
    }

    if (name === 'comparisontable' || name === 'services') {
        const hideLabels = new Set([
            'float',
            'transparent',
            'effects',
            'box effects',
        ]);
        return groups.filter((group) => {
            const label = normalizeLabel(group?.label);
            return !hideLabels.has(label);
        });
    }

    if (name === 'ctahero') {
        const layout = String(componentProps?.Layout || '')
            .trim()
            .toLowerCase();
        return groups.filter((group) => {
            const label = normalizeLabel(group?.label);
            return (
                label !== 'box effects' &&
                label !== 'float' &&
                label !== 'transparent' &&
                (layout !== 'box' || label !== 'effects')
            );
        });
    }

    const hideEffectsFor = new Set([
        'aboutfeature',
        'basic',
        'calltoaction',
        'credentials',
        'itemlist',
        'list',
        'nav',
        'secondaryservices',
        'aboutpagehero',
        'popup',
    ]);
    if (hideEffectsFor.has(name)) {
        return groups.filter((group) => {
            const label = normalizeLabel(group?.label);
            return label !== 'box effects' && label !== 'effects';
        });
    }

    return groups;
}

function isOverflowSchemeValue(value) {
    return String(value || '')
        .toLowerCase()
        .includes('overflow');
}

function fallbackFromOverflow(current, previous = null) {
    if (previous && !isOverflowSchemeValue(previous)) return previous;
    const v = String(current || '').toLowerCase();
    if (v.startsWith('dark')) return 'dark';
    if (
        v.includes('primary') ||
        v.includes('secondary') ||
        v.includes('gradient')
    ) {
        return v.startsWith('light') ? 'light' : 'dark';
    }
    return 'light';
}

function buildPrivacyDocumentPrompt(documentText) {
    return [
        'Use the uploaded client legal document as source material for this Privacy component.',
        'Rewrite the component content to match the document closely while preserving the existing JSON structure and field names.',
        'Return only valid component data for the Privacy component.',
        'Use markdown where helpful in paragraph fields, keep headings concise, and convert suitable bullet-like clauses into List or AccessList items with fa-solid fa-circle-check icons.',
        '',
        'Client document text:',
        documentText,
    ].join('\n');
}

function normalizePrivacyDocumentText(value) {
    return String(value || '')
        .replace(/\r\n/g, '\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function getPrivacyDocumentFields(documentText) {
    const normalizedText = normalizePrivacyDocumentText(documentText);
    const lines = normalizedText.split('\n');
    const titleIndex = lines.findIndex((line) => line.trim());
    const title = titleIndex >= 0 ? lines[titleIndex].trim() : 'Privacy Policy';
    const content = lines
        .slice(titleIndex >= 0 ? titleIndex + 1 : 0)
        .join('\n')
        .trim();

    return {
        Tagline: 'Legal Information',
        Header: title,
        Body: '',
        DocumentContent: content || normalizedText,
    };
}

function PrivacyDocumentImport({ file, componentName, onChange }) {
    const editor = useEditor();
    const notifications = useNotifications();
    const [documentText, setDocumentText] = React.useState('');
    const [documentName, setDocumentName] = React.useState('');
    const [isExtracting, setIsExtracting] = React.useState(false);
    const isGenerating = editor.components.generating({ file });
    const hasDocumentText = documentText.trim().length > 0;

    const handleDocumentUpload = React.useCallback(
        async (event) => {
            const uploadedFile = event.target.files?.[0];
            event.target.value = '';
            if (!uploadedFile) return;

            setIsExtracting(true);

            try {
                const formData = new FormData();
                formData.append('file', uploadedFile);

                const response = await fetch('/api/document-text/extract', {
                    method: 'POST',
                    body: formData,
                });
                const payload = await response.json().catch(() => ({}));

                if (!response.ok) {
                    throw new Error(
                        payload?.error || 'Failed to extract document text.'
                    );
                }

                setDocumentText(payload.text || '');
                setDocumentName(payload.fileName || uploadedFile.name);
                onChange({
                    file,
                    value: getPrivacyDocumentFields(payload.text || ''),
                });
                notifications.push({
                    title: 'Document applied to Privacy',
                    message:
                        'The first line is now the H1 and the rest is page content.',
                    variant: 'success',
                    ttl: 8,
                });
            } catch (error) {
                notifications.push({
                    title: 'Document upload failed',
                    message:
                        error instanceof Error
                            ? error.message
                            : 'Failed to extract document text.',
                    variant: 'error',
                    ttl: 20,
                    error,
                });
            } finally {
                setIsExtracting(false);
            }
        },
        [file, notifications, onChange]
    );

    const handleApplyPrivacyDocument = React.useCallback(() => {
        const trimmedText = documentText.trim();
        if (!trimmedText) return;

        onChange({
            file,
            value: getPrivacyDocumentFields(trimmedText),
        });
        notifications.push({
            title: 'Document applied to Privacy',
            message: 'The Privacy page content was updated.',
            variant: 'success',
            ttl: 8,
        });
    }, [documentText, file, notifications, onChange]);

    const handleRewritePrivacy = React.useCallback(() => {
        const trimmedText = documentText.trim();
        if (!trimmedText) return;

        editor.components.generate({
            file,
            component_name: componentName,
            prompt: buildPrivacyDocumentPrompt(trimmedText),
            temperature: 0.2,
            use_gpt4: true,
            include_page_context: true,
        });
    }, [componentName, documentText, editor.components, file]);

    return (
        <div className="bg-primary-50/60 mt-4 rounded-xl border border-primary-100 p-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-xs font-semibold text-primary-800">
                        Import client policy document
                    </div>
                    <p className="mt-1 text-xs leading-5 text-neutral-600">
                        Upload a .doc, .docx, .txt, or .md file to populate the
                        H1 and page copy from the document.
                    </p>
                </div>
                <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-700 shadow-sm ring-1 ring-primary-200 transition-colors hover:bg-primary-50">
                    {isExtracting ? (
                        <LoadingSpinner size="small" color="#4338ca" />
                    ) : (
                        <ArrowUpTrayIcon className="h-4 w-4" />
                    )}
                    <span>Upload</span>
                    <input
                        type="file"
                        accept=".doc,.docx,.txt,.md,text/plain,text/markdown,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="sr-only"
                        disabled={isExtracting || isGenerating}
                        onChange={handleDocumentUpload}
                    />
                </label>
            </div>

            {documentName ? (
                <div className="mt-2 truncate text-[11px] font-medium text-neutral-500">
                    {documentName}
                </div>
            ) : null}

            <TextArea
                className="mt-3 block max-h-56 min-h-[96px] w-full rounded-lg border-0 bg-white px-3 py-2 text-xs leading-5 text-neutral-800 shadow-sm ring-1 ring-inset ring-primary-100 placeholder:text-neutral-400 focus:ring-1 focus:ring-inset focus:ring-primary-500"
                placeholder="Extracted document text will appear here. You can also paste policy text directly."
                value={documentText}
                onChange={(event) => setDocumentText(event.target.value)}
            />

            <div className="mt-3 flex justify-end gap-2">
                <button
                    type="button"
                    onClick={handleApplyPrivacyDocument}
                    disabled={!hasDocumentText || isExtracting || isGenerating}
                    className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-primary-700 shadow-sm ring-1 ring-primary-200 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <CheckIcon className="h-4 w-4" />
                    <span>Apply to page</span>
                </button>
                <button
                    type="button"
                    onClick={handleRewritePrivacy}
                    disabled={!hasDocumentText || isExtracting || isGenerating}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {isGenerating ? (
                        <LoadingSpinner size="small" color="#FFFFFF" />
                    ) : (
                        <SparklesIcon className="h-4 w-4" />
                    )}
                    <span>Rewrite with AI</span>
                </button>
            </div>
        </div>
    );
}

function TermsDocumentImport({ file, onChange }) {
    const notifications = useNotifications();
    const [documentText, setDocumentText] = React.useState('');
    const [documentName, setDocumentName] = React.useState('');
    const [isExtracting, setIsExtracting] = React.useState(false);
    const hasDocumentText = documentText.trim().length > 0;

    const handleDocumentUpload = React.useCallback(
        async (event) => {
            const uploadedFile = event.target.files?.[0];
            event.target.value = '';
            if (!uploadedFile) return;

            setIsExtracting(true);

            try {
                const formData = new FormData();
                formData.append('file', uploadedFile);

                const response = await fetch('/api/document-text/extract', {
                    method: 'POST',
                    body: formData,
                });
                const payload = await response.json().catch(() => ({}));

                if (!response.ok) {
                    throw new Error(
                        payload?.error || 'Failed to extract document text.'
                    );
                }

                setDocumentText(payload.text || '');
                setDocumentName(payload.fileName || uploadedFile.name);
                onChange({
                    file,
                    value: getLegalDocumentFields(payload.text || '', {
                        fallbackTitle: 'Terms & Conditions',
                        tagline: 'Legal Information',
                    }),
                });
                notifications.push({
                    title: 'Document applied to Terms & Conditions',
                    message:
                        'The title, headings, and bullet lists were formatted automatically.',
                    variant: 'success',
                    ttl: 8,
                });
            } catch (error) {
                notifications.push({
                    title: 'Document upload failed',
                    message:
                        error instanceof Error
                            ? error.message
                            : 'Failed to extract document text.',
                    variant: 'error',
                    ttl: 20,
                    error,
                });
            } finally {
                setIsExtracting(false);
            }
        },
        [file, notifications, onChange]
    );

    const handleApplyTermsDocument = React.useCallback(() => {
        const trimmedText = documentText.trim();
        if (!trimmedText) return;

        onChange({
            file,
            value: getLegalDocumentFields(trimmedText, {
                fallbackTitle: 'Terms & Conditions',
                tagline: 'Legal Information',
            }),
        });
        notifications.push({
            title: 'Document applied to Terms & Conditions',
            message: 'The Terms & Conditions page content was updated.',
            variant: 'success',
            ttl: 8,
        });
    }, [documentText, file, notifications, onChange]);

    return (
        <div className="bg-primary-50/60 mt-4 rounded-xl border border-primary-100 p-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-xs font-semibold text-primary-800">
                        Import client legal document
                    </div>
                    <p className="mt-1 text-xs leading-5 text-neutral-600">
                        Upload a .doc, .docx, .txt, or .md file, or paste the
                        document below, and the page will format headings,
                        subheadings, and bullet lists automatically.
                    </p>
                </div>
                <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-700 shadow-sm ring-1 ring-primary-200 transition-colors hover:bg-primary-50">
                    {isExtracting ? (
                        <LoadingSpinner size="small" color="#4338ca" />
                    ) : (
                        <ArrowUpTrayIcon className="h-4 w-4" />
                    )}
                    <span>Upload</span>
                    <input
                        type="file"
                        accept=".doc,.docx,.txt,.md,text/plain,text/markdown,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="sr-only"
                        disabled={isExtracting}
                        onChange={handleDocumentUpload}
                    />
                </label>
            </div>

            {documentName ? (
                <div className="mt-2 truncate text-[11px] font-medium text-neutral-500">
                    {documentName}
                </div>
            ) : null}

            <TextArea
                className="mt-3 block max-h-56 min-h-[120px] w-full rounded-lg border-0 bg-white px-3 py-2 text-xs leading-5 text-neutral-800 shadow-sm ring-1 ring-inset ring-primary-100 placeholder:text-neutral-400 focus:ring-1 focus:ring-inset focus:ring-primary-500"
                placeholder="Paste the client's terms document here or upload a file."
                value={documentText}
                onChange={(event) => setDocumentText(event.target.value)}
            />

            <div className="mt-3 flex justify-end gap-2">
                <button
                    type="button"
                    onClick={handleApplyTermsDocument}
                    disabled={!hasDocumentText || isExtracting}
                    className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-primary-700 shadow-sm ring-1 ring-primary-200 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <CheckIcon className="h-4 w-4" />
                    <span>Apply to page</span>
                </button>
            </div>
        </div>
    );
}

/* ───────────────────────────── Main ComponentEditor ───────────────────────────── */

export default function ComponentEditor(props) {
    const {
        component_props,
        file,
        componentName,
        open,
        onSave,
        onClose,
        onCancel,
        onChange,
        onDeleteComponent,
        mediaFieldRequest,
    } = props;
    const editor = useEditor();
    const notifications = useNotifications();
    const isPageComponent = (componentName || '').toLowerCase() === 'page';
    const isShapeDivider = componentName === 'ShapeDivider';
    const componentSchema = React.useMemo(() => {
        try {
            return require(`@/components/${componentName}.schema.json`);
        } catch (error) {
            return null;
        }
    }, [componentName]);

    React.useEffect(() => {
        if (!open || componentName !== 'HeroFullBG' || !component_props) return;

        const repeatableImages = Array.isArray(component_props?.Images)
            ? component_props.Images
            : [];
        const hasRepeatableImage = repeatableImages.some((image) =>
            isSelectedEditorMedia(image?.ImageUrl ?? image?.imageUrl ?? image)
        );
        if (hasRepeatableImage) return;

        const legacyImage = component_props?.ImageUrl;
        const migratedImage = isSelectedEditorMedia(legacyImage)
            ? legacyImage
            : '';
        if (!migratedImage && repeatableImages.length > 0) return;

        onChange({
            file,
            key: 'Images',
            value: [
                {
                    ImageUrl: migratedImage,
                    ImagePosition: component_props?.ImagePosition ?? '50',
                },
            ],
        });
    }, [
        componentName,
        component_props?.ImagePosition,
        component_props?.ImageUrl,
        component_props?.Images,
        file,
        onChange,
        open,
    ]);

    const [activeMediaField, setActiveMediaField] = React.useState(null); // { key, value }
    const [mediaOriginalValue, setMediaOriginalValue] = React.useState(null);
    const [mediaWasApplied, setMediaWasApplied] = React.useState(false);
    const [schemaMarkupGenerating, setSchemaMarkupGenerating] =
        React.useState(false);
    const [altTextGeneratingKey, setAltTextGeneratingKey] =
        React.useState(null);
    const [expandedArrayItems, setExpandedArrayItems] = React.useState({});
    const [inlineFieldDrafts, setInlineFieldDrafts] = React.useState({});
    const [draggedArrayItem, setDraggedArrayItem] = React.useState(null);
    const [draggedTopLevelField, setDraggedTopLevelField] =
        React.useState(null);
    const suppressDragClickRef = React.useRef(false);
    const suppressDragClickTimeoutRef = React.useRef(null);
    const dragPreviewNodeRef = React.useRef(null);
    const topLevelFieldDragPreviewNodeRef = React.useRef(null);
    const arrayItemNodesRef = React.useRef(new Map());
    const pendingArrayAnimationRef = React.useRef(null);
    const animationFrameIdsRef = React.useRef([]);
    const animationTimeoutIdsRef = React.useRef([]);
    const editorScrollRef = React.useRef(null);
    const mediaScrollRestoreRef = React.useRef(0);
    const prevActiveMediaRef = React.useRef(null);
    const mediaUploadInputRefs = React.useRef({});
    const siteImageByUrl = React.useMemo(() => {
        const entries = Array.isArray(editor.site.data?.images)
            ? editor.site.data.images
                  .filter((image) => image?.url)
                  .map((image) => [String(image.url).trim(), image])
            : [];
        return new Map(entries);
    }, [editor.site.data?.images]);

    React.useLayoutEffect(() => {
        const wasActive = Boolean(prevActiveMediaRef.current);
        const isActive = Boolean(activeMediaField);
        if (wasActive && !isActive && editorScrollRef.current) {
            const node = editorScrollRef.current;
            const maxScroll = Math.max(
                0,
                node.scrollHeight - node.clientHeight
            );
            node.scrollTop = Math.min(mediaScrollRestoreRef.current, maxScroll);
        }
        prevActiveMediaRef.current = activeMediaField;
    }, [activeMediaField]);

    const getSiteImageByUrl = React.useCallback(
        (url) => {
            const normalized = String(url || '').trim();
            if (!normalized) return null;
            return siteImageByUrl.get(normalized) || null;
        },
        [siteImageByUrl]
    );
    const syncImageAltText = React.useCallback(
        ({ imageUrl, altText, componentAltKey = null }) => {
            const normalizedAltText = String(altText ?? '');
            const siteImage = getSiteImageByUrl(imageUrl);

            if (siteImage?.image_id) {
                editor.site.updateImage?.({
                    image_id: siteImage.image_id,
                    data: {
                        alt_text: normalizedAltText,
                    },
                });
            }

            if (componentAltKey) {
                onChange({
                    file,
                    key: componentAltKey,
                    value: normalizedAltText,
                });
            }
        },
        [editor.site, file, getSiteImageByUrl, onChange]
    );

    React.useLayoutEffect(() => {
        const pending = pendingArrayAnimationRef.current;
        if (!pending) return;

        const currentEntries = Array.from(arrayItemNodesRef.current.entries())
            .filter(([path, node]) => {
                return (
                    node &&
                    node.isConnected &&
                    isDirectArrayItemPath(path, pending.arrayKey)
                );
            })
            .sort((a, b) => getArrayItemIndex(a[0]) - getArrayItemIndex(b[0]));

        if (
            pending.type === 'insert' &&
            currentEntries.length <= pending.beforeRects.length
        ) {
            pendingArrayAnimationRef.current = null;
            return;
        }
        if (
            pending.type === 'delete' &&
            currentEntries.length >= pending.beforeRects.length
        ) {
            pendingArrayAnimationRef.current = null;
            return;
        }

        currentEntries.forEach(([, node], newIndex) => {
            if (!node) return;

            if (pending.type === 'insert' && newIndex === pending.insertIndex) {
                const animatedNode = getArrayItemAnimationNode(node);
                if (!animatedNode) return;

                animatedNode.style.opacity = '0';
                animatedNode.style.transform = 'translateY(-24px) scale(0.88)';
                animatedNode.style.filter = 'blur(6px)';
                animatedNode.style.willChange = 'opacity, transform, filter';
                animatedNode.style.transition = 'none';

                const startId = window.requestAnimationFrame(() => {
                    const animateId = window.setTimeout(() => {
                        animatedNode.style.transition =
                            'opacity 420ms cubic-bezier(0.22, 1, 0.36, 1), transform 420ms cubic-bezier(0.22, 1, 0.36, 1), filter 420ms cubic-bezier(0.22, 1, 0.36, 1)';
                        animatedNode.style.opacity = '1';
                        animatedNode.style.transform = 'translateY(0) scale(1)';
                        animatedNode.style.filter = 'blur(0)';
                    }, 34);
                    animationTimeoutIdsRef.current.push(animateId);
                });
                animationFrameIdsRef.current.push(startId);

                const cleanupId = window.setTimeout(() => {
                    animatedNode.style.opacity = '';
                    animatedNode.style.transform = '';
                    animatedNode.style.filter = '';
                    animatedNode.style.willChange = '';
                    animatedNode.style.transition = '';
                }, 520);
                animationTimeoutIdsRef.current.push(cleanupId);
                return;
            }

            if (typeof node.animate !== 'function') return;

            const previousIndex =
                pending.type === 'insert'
                    ? newIndex > pending.insertIndex
                        ? newIndex - 1
                        : newIndex
                    : newIndex >= pending.removeIndex
                    ? newIndex + 1
                    : newIndex;
            const previousRect = pending.beforeRects[previousIndex];
            if (!previousRect) return;

            const currentRect = node.getBoundingClientRect();
            const deltaX = previousRect.left - currentRect.left;
            const deltaY = previousRect.top - currentRect.top;
            if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;

            node.animate(
                [
                    {
                        transform: `translate(${deltaX}px, ${deltaY}px)`,
                    },
                    {
                        transform: 'translate(0, 0)',
                    },
                ],
                {
                    duration: 220,
                    easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                    fill: 'both',
                }
            );
        });

        pendingArrayAnimationRef.current = null;
    }, [component_props]);

    React.useEffect(() => {
        return () => {
            if (suppressDragClickTimeoutRef.current) {
                window.clearTimeout(suppressDragClickTimeoutRef.current);
            }
            if (dragPreviewNodeRef.current) {
                dragPreviewNodeRef.current.remove();
                dragPreviewNodeRef.current = null;
            }
            animationFrameIdsRef.current.forEach((id) =>
                window.cancelAnimationFrame(id)
            );
            animationTimeoutIdsRef.current.forEach((id) =>
                window.clearTimeout(id)
            );
            animationFrameIdsRef.current = [];
            animationTimeoutIdsRef.current = [];
        };
    }, []);

    // inside ComponentEditor, has access to { file, onChange } from props
    const handleComponentPropChange = React.useCallback(
        ({ key, value }) => {
            const navComponent = editor.components.data.find(
                (component) => component.name === 'Nav'
            );
            const footerComponent = editor.components.data.find(
                (component) => component.name === 'Footer'
            );
            const isNavPhoneField =
                componentName === 'Nav' &&
                keyLeaf(key).toLowerCase() === 'phone';
            const isFooterPrimaryPhoneField =
                componentName === 'Footer' &&
                /^Phones\[0\]\.Number$/i.test(String(key));

            onChange({ file, key, value });

            if (isNavPhoneField && footerComponent) {
                const currentPhones = Array.isArray(
                    footerComponent.props?.Phones
                )
                    ? structuredClone(footerComponent.props.Phones)
                    : [];
                if (currentPhones.length === 0) {
                    currentPhones.push({ Number: value });
                } else {
                    currentPhones[0] = {
                        ...(currentPhones[0] || {}),
                        Number: value,
                    };
                }
                editor.components.change({
                    file: footerComponent.data_file,
                    key: 'Phones',
                    value: currentPhones,
                });
            }

            if (isFooterPrimaryPhoneField && navComponent) {
                editor.components.change({
                    file: navComponent.data_file,
                    key: 'Phone',
                    value,
                });
            }
        },
        [
            componentName,
            editor.components.data,
            editor.components.change,
            file,
            onChange,
        ]
    );

    const handleCleanChange = React.useCallback(
        (event, key) => {
            const raw = event?.target?.value ?? '';
            const value =
                keyLeaf(key).toLowerCase() === 'googlemap'
                    ? normalizeMapEmbed(raw)
                    : raw;

            handleComponentPropChange({ key, value });
        },
        [handleComponentPropChange]
    );

    const handleBooleanToggle = React.useCallback(
        (key, checked) => {
            const leaf = keyLeaf(key).toLowerCase();
            const isPublishedToggle =
                isPageComponent && leaf === PAGE_PUBLISHED_KEY.toLowerCase();

            if (!isPublishedToggle) {
                onChange({ file, key, value: checked });
                return;
            }

            if (checked) {
                onChange({
                    file,
                    key: PAGE_UNPUBLISH_REDIRECT_KEY,
                    value: '',
                });
            }

            onChange({ file, key, value: checked });
        },
        [file, isPageComponent, onChange]
    );

    const handlePopupTest = React.useCallback(() => {
        if (typeof window === 'undefined') return;

        window.dispatchEvent(
            new CustomEvent('editor:preview-popup', {
                detail: { file },
            })
        );
    }, [file]);

    if (!component_props) return null;

    const alignKey = deepFindAlignmentKey(component_props);
    const supportsAlignment = Boolean(alignKey);
    const isCTAHeroBoxLayout =
        componentName === 'CTAHero' &&
        String(component_props?.Layout || '')
            .trim()
            .toLowerCase() === 'box';
    let allowedAlignments = supportsAlignment
        ? getAllowedAlignmentsForKey(component_props, alignKey)
        : ['left', 'center', 'right'];
    const showOverflowAlignment = componentName === 'Hero';
    if (componentName === 'Nav') {
        allowedAlignments = ['left', 'right'];
    }
    if (componentName === 'ServiceTabs') {
        // ServiceTabs only supports left/center alignment
        allowedAlignments = ['left', 'center'];
    }
    if (componentName === 'ItemGrid') {
        allowedAlignments = ['left', 'center'];
    }
    if (componentName === 'List' || componentName === 'ItemList') {
        allowedAlignments = ['left', 'center'];
    }
    if (componentName === 'SecondaryServices') {
        allowedAlignments = ['left', 'center'];
    }
    if (componentName === 'Hero') {
        allowedAlignments = [
            'left',
            'center',
            'right',
            'left-overflow',
            'right-overflow',
        ];
    }
    if (isCTAHeroBoxLayout) {
        allowedAlignments = ['center'];
    }
    const alignmentValue = supportsAlignment
        ? readAlignmentAtPath(component_props, alignKey, allowedAlignments)
        : 'left';

    const allKeys = Object.keys(component_props);
    if (componentName === 'FloatingAction') {
        ['IconPackOverride', 'IconStyleOverride'].forEach((internalKey) => {
            const internalIndex = allKeys.indexOf(internalKey);
            if (internalIndex >= 0) allKeys.splice(internalIndex, 1);
        });
        const legacyActionIndex = allKeys.indexOf('Action');
        if (!allKeys.includes('Type') && legacyActionIndex >= 0) {
            allKeys.splice(legacyActionIndex, 1, 'Type');
        } else if (!allKeys.includes('Type')) {
            allKeys.splice(allKeys.includes('Enabled') ? 1 : 0, 0, 'Type');
        } else if (legacyActionIndex >= 0) {
            allKeys.splice(legacyActionIndex, 1);
        }
    }
    if (componentName === 'Reviews') {
        ['ReviewSource', 'NiceJobLayout'].forEach((schemaKey) => {
            if (componentSchema?.[schemaKey] && !allKeys.includes(schemaKey)) {
                allKeys.push(schemaKey);
            }
        });
    }
    if (componentName === 'Footer' && !allKeys.includes('Medias')) {
        allKeys.unshift('Medias');
    }
    const reviewSourceValue = String(
        component_props?.ReviewSource ??
            componentSchema?.ReviewSource?.value ??
            'manual'
    )
        .toLowerCase()
        .trim();
    const useNiceJobReviews =
        componentName === 'Reviews' && reviewSourceValue === 'nicejob';
    const isPagePublished = isPageComponent
        ? parsePublishedValue(component_props?.[PAGE_PUBLISHED_KEY])
        : true;
    const unpublishRedirectValue = String(
        component_props?.[PAGE_UNPUBLISH_REDIRECT_KEY] ?? ''
    );

    let contentKeys = allKeys.filter(
        (k) =>
            !isStyleKey(k, componentName) &&
            !isButtonKey(k) &&
            !isButtonIconKey(k) &&
            !isButtonIconVisibleKey(k) &&
            !isReviewsLegacyButtonKey(k, componentName) &&
            k !== 'company' &&
            !(
                componentName === 'Nav' &&
                keyLeaf(k).toLowerCase() === 'embedlink'
            ) &&
            !(
                componentName === 'Nav' &&
                keyLeaf(k).toLowerCase() === 'logotext'
            ) &&
            !(
                isPageComponent &&
                keyLeaf(k).toLowerCase() ===
                    PAGE_UNPUBLISH_REDIRECT_KEY.toLowerCase()
            ) &&
            !(
                componentName === 'HeroFullBG' &&
                isTopLevelKey(k) &&
                keyLeaf(k).toLowerCase() === 'imageurl'
            ) &&
            !shouldHideProp(componentName, k) &&
            !(isShapeDivider && isShapeDividerKey(k))
    );
    if (isPageComponent && !contentKeys.includes(PAGE_SCHEMA_MARKUP_KEY)) {
        contentKeys = [...contentKeys, PAGE_SCHEMA_MARKUP_KEY];
    }
    if (isPageComponent && !contentKeys.includes(PAGE_PUBLISHED_KEY)) {
        contentKeys = [...contentKeys, PAGE_PUBLISHED_KEY];
    }
    if (componentName === 'Nav') {
        const getLeaf = (k) => keyLeaf(k).toLowerCase();
        contentKeys = contentKeys.filter(
            (k) =>
                !NAV_PHONE_CONTROL_LEAVES.has(getLeaf(k)) &&
                getLeaf(k) !== 'ctamobileicon'
        );
        contentKeys = contentKeys.filter(
            (k) => getLeaf(k) !== 'servicesheader'
        );
        const preferredNavOrder = [
            'services',
            'dropdown',
            'links',
            'enddropdowns',
        ];
        const orderedNavKeys = preferredNavOrder
            .map((leaf) => contentKeys.find((k) => getLeaf(k) === leaf))
            .filter(Boolean);
        const remainingNavKeys = contentKeys.filter(
            (k) => !orderedNavKeys.includes(k)
        );
        contentKeys = [...orderedNavKeys, ...remainingNavKeys];
    }
    if (componentName === 'ReviewHero') {
        const preferredReviewHeroOrder = ['header', 'icon', 'reviews'];
        const orderedReviewHeroKeys = preferredReviewHeroOrder
            .map((leaf) =>
                contentKeys.find((k) => keyLeaf(k).toLowerCase() === leaf)
            )
            .filter(Boolean);
        const remainingReviewHeroKeys = contentKeys.filter(
            (k) => !orderedReviewHeroKeys.includes(k)
        );
        contentKeys = [...orderedReviewHeroKeys, ...remainingReviewHeroKeys];
    }
    if (componentName === 'Reviews') {
        const hiddenReviewLeaves = useNiceJobReviews
            ? new Set([
                  'reviews',
                  'layout',
                  'featureicon',
                  'featureiconpack',
                  'featureiconstyle',
              ])
            : new Set(['nicejoblayout']);
        contentKeys = contentKeys.filter(
            (key) => !hiddenReviewLeaves.has(keyLeaf(key).toLowerCase())
        );
        contentKeys = applyLeafOrder(contentKeys, [
            'tagline',
            'header',
            'reviewsource',
            'nicejoblayout',
            'reviews',
            'layout',
            'featureicon',
            'featureiconpack',
            'featureiconstyle',
        ]);
    }
    if (componentName === 'HeroFullBG') {
        const preferredHeroFullBGOrder = ['leadimageurl', 'header'];
        contentKeys = applyLeafOrder(contentKeys, preferredHeroFullBGOrder);
        if (!contentKeys.includes('CTAButtons')) {
            contentKeys.push('CTAButtons');
        }
        const customHeroFullBGOrder = parseFieldOrderValue(
            readValueAtPath(component_props, 'ContentOrder') ?? ''
        );
        contentKeys = applyLeafOrder(contentKeys, customHeroFullBGOrder);
        const backgroundImagesKey = contentKeys.find(
            (key) => keyLeaf(key).toLowerCase() === 'images'
        );
        const sliderTransitionKey = contentKeys.find(
            (key) => keyLeaf(key).toLowerCase() === 'slidertransition'
        );
        if (backgroundImagesKey && sliderTransitionKey) {
            contentKeys = contentKeys.filter(
                (key) =>
                    key !== backgroundImagesKey && key !== sliderTransitionKey
            );
            contentKeys.push(backgroundImagesKey, sliderTransitionKey);
        }
    }
    if (componentName === 'Footer') {
        contentKeys = contentKeys.filter(
            (k) =>
                !['servicesheader', 'linksheader', 'infoheader'].includes(
                    keyLeaf(k).toLowerCase()
                )
        );
    }
    if (componentName === 'USP') {
        const anchorKey = contentKeys.find(
            (k) => keyLeaf(k).toLowerCase() === 'anchor'
        );
        const taglineKey = contentKeys.find(
            (k) => keyLeaf(k).toLowerCase() === 'tagline'
        );
        const headerKey = contentKeys.find(
            (k) => keyLeaf(k).toLowerCase() === 'header'
        );
        if (anchorKey && taglineKey && headerKey) {
            contentKeys = contentKeys.filter((k) => k !== taglineKey);
            const anchorIndex = contentKeys.indexOf(anchorKey);
            const headerIndex = contentKeys.indexOf(headerKey);
            const insertIndex =
                anchorIndex !== -1 &&
                headerIndex !== -1 &&
                anchorIndex < headerIndex
                    ? anchorIndex + 1
                    : headerIndex;
            contentKeys.splice(insertIndex, 0, taglineKey);
        }
    }
    if (HERO_SUB_HEADER_COMPONENTS.has(componentName)) {
        if (componentSchema?.HeaderSize) {
            contentKeys = contentKeys.filter(
                (k) => keyLeaf(k).toLowerCase() !== 'headersize'
            );
            const nextHeaderIndex = contentKeys.findIndex(
                (k) => keyLeaf(k).toLowerCase() === 'header'
            );
            const insertIndex =
                nextHeaderIndex !== -1 ? nextHeaderIndex + 1 : 0;
            contentKeys.splice(insertIndex, 0, 'HeaderSize');
        }
        if (componentSchema?.SubHeader && componentName !== 'HeroFullBG') {
            contentKeys = contentKeys.filter(
                (k) => keyLeaf(k).toLowerCase() !== 'subheader'
            );
            const headerSizeIndex = contentKeys.findIndex(
                (k) => keyLeaf(k).toLowerCase() === 'headersize'
            );
            const nextHeaderIndex = contentKeys.findIndex(
                (k) => keyLeaf(k).toLowerCase() === 'header'
            );
            const insertIndex =
                headerSizeIndex !== -1
                    ? headerSizeIndex + 1
                    : nextHeaderIndex !== -1
                    ? nextHeaderIndex + 1
                    : 0;
            contentKeys.splice(insertIndex, 0, 'SubHeader');
        }
    }
    if (isPageComponent) {
        const schemaIdx = contentKeys.indexOf(PAGE_SCHEMA_MARKUP_KEY);
        const metaDescriptionIdx = contentKeys.findIndex(
            (k) => keyLeaf(k).toLowerCase() === 'metadescription'
        );
        if (schemaIdx !== -1 && metaDescriptionIdx !== -1) {
            contentKeys.splice(schemaIdx, 1);
            contentKeys.splice(
                metaDescriptionIdx + 1,
                0,
                PAGE_SCHEMA_MARKUP_KEY
            );
        }
    }
    contentKeys = moveLeafKeyToFront(contentKeys, 'anchor');
    const pageCustomCodeHeadKey = isPageComponent
        ? contentKeys.find(
              (k) =>
                  keyLeaf(k).toLowerCase() ===
                  PAGE_CUSTOM_CODE_HEAD_KEY.toLowerCase()
          )
        : null;
    const pageCustomCodeBodyKey = isPageComponent
        ? contentKeys.find(
              (k) =>
                  keyLeaf(k).toLowerCase() ===
                  PAGE_CUSTOM_CODE_BODY_KEY.toLowerCase()
          )
        : null;
    const pageCustomCodeKeys = [
        pageCustomCodeHeadKey,
        pageCustomCodeBodyKey,
    ].filter(Boolean);
    if (pageCustomCodeKeys.length > 0) {
        contentKeys = contentKeys.filter(
            (k) => !pageCustomCodeKeys.includes(k)
        );
    }
    const pagePublishedKey = isPageComponent
        ? contentKeys.find(
              (k) =>
                  keyLeaf(k).toLowerCase() === PAGE_PUBLISHED_KEY.toLowerCase()
          )
        : null;
    const pageNoIndexKey = isPageComponent
        ? contentKeys.find((k) => keyLeaf(k).toLowerCase() === 'noindex')
        : null;
    const pageNameKeys = isPageComponent
        ? contentKeys.filter((k) => keyLeaf(k).toLowerCase() === 'pagename')
        : [];
    const pageMenuManagedKeys = [
        pagePublishedKey,
        pageNoIndexKey,
        ...pageNameKeys,
    ].filter(Boolean);
    let contentKeysForNodes = isPageComponent
        ? contentKeys.filter((k) => !pageMenuManagedKeys.includes(k))
        : contentKeys;
    const ctaHeroLayoutKey =
        componentName === 'CTAHero'
            ? contentKeysForNodes.find(
                  (k) => keyLeaf(k).toLowerCase() === 'layout'
              ) || null
            : null;
    if (ctaHeroLayoutKey) {
        contentKeysForNodes = contentKeysForNodes.filter(
            (k) => k !== ctaHeroLayoutKey
        );
    }
    const navBannerLeafNames = new Set([
        'bannertitle',
        'bannertext',
        'bannerbuttontext',
        'bannerbuttonicon',
        'bannerbuttonlink',
        'bannercolorscheme',
    ]);
    const navBannerKeys =
        componentName === 'Nav'
            ? contentKeysForNodes.filter((k) =>
                  navBannerLeafNames.has(keyLeaf(k).toLowerCase())
              )
            : [];
    const navBannerAnchorKey = navBannerKeys[0] || null;
    const navBannerTitleKey = navBannerKeys.find(
        (k) => keyLeaf(k).toLowerCase() === 'bannertitle'
    );
    const navBannerTextKey = navBannerKeys.find(
        (k) => keyLeaf(k).toLowerCase() === 'bannertext'
    );
    const navPhoneKey =
        componentName === 'Nav'
            ? contentKeysForNodes.find(
                  (k) => keyLeaf(k).toLowerCase() === 'phone'
              ) || null
            : null;
    const footerLinksKey =
        componentName === 'Footer'
            ? contentKeysForNodes.find(
                  (k) => keyLeaf(k).toLowerCase() === 'links'
              ) || null
            : null;
    const footerInfoLeafNames = new Set([
        'emaillabel',
        'email',
        'phones',
        'textfield',
        'address',
        'businesshour',
        'license',
    ]);
    const footerInfoKeys =
        componentName === 'Footer'
            ? contentKeysForNodes.filter((k) =>
                  footerInfoLeafNames.has(keyLeaf(k).toLowerCase())
              )
            : [];
    const footerInfoAnchorKey = footerInfoKeys[0] || null;

    const colorKey = allKeys.find((k) => isColorSchemeKey(k));
    const gradientDirectionKey =
        allKeys.find((k) => isGradientDirectionKey(k)) ||
        GRADIENT_DIRECTION_FIELD;
    const cardsColorKey = allKeys.find((k) => isCardsColorSchemeKey(k));
    const mapColorKey = allKeys.find((k) => isMapColorSchemeKey(k));
    const googleMapKey = allKeys.find((k) => isGoogleMapKey(k));
    const logoOverflowKey = allKeys.find((k) => isLogoOverflowKey(k));
    const behaviorKey = allKeys.find((k) => isBehaviorKey(k));
    const navHeightKey = allKeys.find((k) => isNavHeightKey(k));
    const embedLinkKey = allKeys.find(
        (k) => keyLeaf(k).toLowerCase() === 'embedlink'
    );
    const hasMap = !!String(component_props[googleMapKey] ?? '').trim();
    const behaviorRawValue =
        behaviorKey && component_props[behaviorKey] !== undefined
            ? typeof component_props[behaviorKey] === 'object' &&
              component_props[behaviorKey] !== null &&
              'value' in component_props[behaviorKey]
                ? component_props[behaviorKey].value
                : component_props[behaviorKey]
            : 'sticky';
    const navBehaviorValue = normalizeNavBehavior(behaviorRawValue || 'sticky');
    const navHeightRawValue =
        navHeightKey && component_props[navHeightKey] !== undefined
            ? typeof component_props[navHeightKey] === 'object' &&
              component_props[navHeightKey] !== null &&
              'value' in component_props[navHeightKey]
                ? component_props[navHeightKey].value
                : component_props[navHeightKey]
            : 'standard';
    const navHeightValue = normalizeNavHeight(navHeightRawValue || 'standard');
    const popupPositionKey =
        componentName === 'Popup'
            ? allKeys.find((k) => keyLeaf(k).toLowerCase() === 'position')
            : null;

    const isBeforeImageKey = (k) => {
        const leaf = keyLeaf(k).toLowerCase();
        return leaf === 'beforeimage' || leaf === 'beforeimageurl';
    };
    const isAfterImageKey = (k) => {
        const leaf = keyLeaf(k).toLowerCase();
        return leaf === 'afterimage' || leaf === 'afterimageurl';
    };
    const isBeforeAltKey = (k) => {
        const leaf = keyLeaf(k).toLowerCase();
        return leaf === 'beforealttext' || leaf === 'beforealt';
    };
    const isAfterAltKey = (k) => {
        const leaf = keyLeaf(k).toLowerCase();
        return leaf === 'afteralttext' || leaf === 'afteralt';
    };
    const beforeImageKey = contentKeys.find((k) => isBeforeImageKey(k));
    const afterImageKey = contentKeys.find((k) => isAfterImageKey(k));
    const beforeAltKey = contentKeys.find((k) => isBeforeAltKey(k));
    const afterAltKey = contentKeys.find((k) => isAfterAltKey(k));
    const beforeAfterAnchorKey =
        beforeImageKey && afterImageKey
            ? contentKeys.indexOf(beforeImageKey) <=
              contentKeys.indexOf(afterImageKey)
                ? beforeImageKey
                : afterImageKey
            : null;

    const hasAnimKey = allKeys.some((k) => isAnimationKey(k));
    const hasDurKey = allKeys.some((k) => isDurationKey(k));
    const hasAnimGroupKey = allKeys.some((k) => isAnimationGroupKey(k));
    const animGroups = getSupportedAnimationGroups(
        component_props,
        componentName
    );
    let hasAnimationControls =
        hasAnimKey || hasDurKey || hasAnimGroupKey || animGroups.length > 1;
    if (
        componentName === 'ReviewSlider' ||
        componentName === 'Popup' ||
        componentName === 'Nav' ||
        componentName === 'Footer'
    ) {
        hasAnimationControls = false;
    }

    const engageKey = allKeys.find((k) => isEngageKey(k));

    const vspaceKey = allKeys.find((k) => isVerticalSpacingKey(k));
    const footerBottomSpacingKey =
        componentName === 'Footer'
            ? allKeys.find((k) => isFooterBottomSpacingKey(k))
            : null;
    const iconSizeKey = useNiceJobReviews
        ? null
        : allKeys.find((k) => isIconSizeKey(k)) ||
          (componentSchema?.FeatureIconSize ? 'FeatureIconSize' : null) ||
          (componentSchema?.IconSize ? 'IconSize' : null);
    const imagePosKey = allKeys.find((k) => isImagePositionKey(k));
    const stickyMediaKey =
        componentName === 'Service' || componentName === 'ServiceTabs'
            ? allKeys.find((k) => keyLeaf(k).toLowerCase() === 'stickymedia') ||
              'StickyMedia'
            : null;
    const imageVariantKey = allKeys.find((k) => isImageVariantKey(k));
    const isGallery = componentName === 'Gallery';
    const imageVariantDisplays = isGallery
        ? ['masonry', 'square', 'contain', 'slider', 'slider-full', 'bento']
        : componentName === 'Financial'
        ? ['cover', 'contain', 'square']
        : componentName === 'ImageSlider'
        ? ['regular', 'full']
        : undefined;
    const imageVariantValue = imageVariantKey
        ? String(component_props[imageVariantKey] ?? '')
              .toLowerCase()
              .trim()
        : '';
    const imageVariantVerticalAlign =
        componentName !== 'Financial' &&
        !isGallery &&
        componentName !== 'ImageSlider' &&
        componentName !== 'SecondaryServices';
    const selectedImageAlignment =
        imagePosKey &&
        !MEDIA_POSITION_COMPONENTS.has(componentName) &&
        !shouldHideProp(componentName, imagePosKey)
            ? normalizeImagePosition(component_props[imagePosKey], [
                  'left',
                  'center',
                  'right',
              ])
            : null;
    const serviceImageAlignment =
        (componentName === 'Service' || componentName === 'ServiceTabs') &&
        imagePosKey
            ? normalizeImagePosition(component_props[imagePosKey], [
                  'left',
                  'center',
                  'right',
              ])
            : null;
    const showStickyMediaToggle =
        (componentName === 'Service' || componentName === 'ServiceTabs') &&
        stickyMediaKey &&
        serviceImageAlignment &&
        serviceImageAlignment !== 'center';
    const disableImageVariantVerticalAlign =
        selectedImageAlignment === 'center';
    const imageVariantShadow = !isGallery && componentName !== 'ImageSlider';

    const handleInputChange = (event, key) => {
        const value = event.target.value;
        onChange({ file, key, value });

        // Auto-set review icon when Source includes "google" (ReviewHero)
        const leaf = keyLeaf(key).toLowerCase();
        if (componentName === 'ReviewHero' && leaf === 'source') {
            const iconKey = replaceLeafInPath(key, 'Icon');
            const mapped = mapSourceToIconVal(value);
            if (mapped) {
                onChange({ file, key: iconKey, value: mapped });
            } else if (!value) {
                onChange({ file, key: iconKey, value: '' });
            }
        }
    };

    const handleGenerateSchemaMarkup = React.useCallback(
        async ({ key }) => {
            if (!isPageComponent || schemaMarkupGenerating) return;

            setSchemaMarkupGenerating(true);
            try {
                const pageName = editor?.page?.name || 'index';
                const response = await fetch(
                    `/api/schema-markup/generate?page_name=${encodeURIComponent(
                        pageName
                    )}`
                );
                if (!response.ok) {
                    throw new Error('Failed to generate schema markup');
                }
                const data = await response.json();
                if (!data?.schema_markup) {
                    throw new Error('No schema markup returned');
                }
                onChange({ file, key, value: data.schema_markup });
                notifications.push({
                    title: 'Schema markup generated',
                    variant: 'success',
                    ttl: 10,
                });
            } catch (error) {
                notifications.push({
                    title: 'Failed to generate schema markup',
                    variant: 'error',
                    ttl: 20,
                    error,
                });
            } finally {
                setSchemaMarkupGenerating(false);
            }
        },
        [
            editor,
            file,
            isPageComponent,
            notifications,
            onChange,
            schemaMarkupGenerating,
        ]
    );

    const handleGenerateAltText = React.useCallback(
        async ({ key, imageUrl, applyAltText = null }) => {
            if (!ALT_TEXT_GENERATION_ENABLED) return;
            const normalizedImageUrl =
                normalizeCollapsibleArrayBackgroundImageValue(imageUrl);
            if (!normalizedImageUrl || !isSelectedEditorMedia(imageUrl)) {
                notifications.push({
                    title: 'Select an image first',
                    variant: 'error',
                    ttl: 10,
                });
                return;
            }

            setAltTextGeneratingKey(key);
            try {
                const response = await fetch('/api/alt-text/generate', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        imageUrl: normalizedImageUrl,
                    }),
                });
                if (!response.ok) {
                    const errorPayload = await response
                        .json()
                        .catch(() => null);
                    throw new Error(
                        errorPayload?.error || 'Failed to generate alt text'
                    );
                }
                const data = await response.json();
                const nextAltText = String(
                    data?.alt_text || data?.altText || ''
                )
                    .trim()
                    .replace(/^["']+|["']+$/g, '');
                if (!nextAltText) {
                    throw new Error('No alt text returned');
                }
                if (typeof applyAltText === 'function') {
                    applyAltText(nextAltText);
                } else {
                    onChange({ file, key, value: nextAltText });
                }
            } catch (error) {
                notifications.push({
                    title: 'Failed to generate alt text',
                    message:
                        error instanceof Error
                            ? error.message
                            : 'Unknown error',
                    variant: 'error',
                    ttl: 20,
                    error,
                });
            } finally {
                setAltTextGeneratingKey((current) =>
                    current === key ? null : current
                );
            }
        },
        [file, notifications, onChange]
    );

    const handleOpenMediaPanel = React.useCallback((key, currentValue) => {
        if (editorScrollRef.current) {
            mediaScrollRestoreRef.current = editorScrollRef.current.scrollTop;
        }
        setActiveMediaField({ key, value: currentValue });
        setMediaOriginalValue(currentValue ?? '');
        setMediaWasApplied(false);
    }, []);

    const handledMediaRequestRef = React.useRef(null);
    React.useEffect(() => {
        if (!open || !mediaFieldRequest?.key) return;

        const requestId = mediaFieldRequest.id || mediaFieldRequest.key;
        if (handledMediaRequestRef.current === requestId) return;

        handledMediaRequestRef.current = requestId;
        if (mediaFieldRequest.inline && mediaFieldRequest.isIcon) {
            setActiveMediaField(null);
            const requestedKey = String(mediaFieldRequest.key);
            const arrayItemMatch = requestedKey.match(/^(.+?\[\d+\])/);
            if (arrayItemMatch) {
                setExpandedArrayItems((previous) => ({
                    ...previous,
                    [arrayItemMatch[1]]: true,
                }));
            }
            let settleFrame = 0;
            let finalFrame = 0;
            const scrollFrame = window.requestAnimationFrame(() => {
                settleFrame = window.requestAnimationFrame(() => {
                    finalFrame = window.requestAnimationFrame(() => {
                        const scrollContainer = editorScrollRef.current;
                        if (!scrollContainer) return;
                        const targetNode = Array.from(
                            scrollContainer.querySelectorAll(
                                '[data-inline-icon-field]'
                            )
                        ).find(
                            (node) =>
                                node.getAttribute('data-inline-icon-field') ===
                                requestedKey
                        );
                        if (!targetNode) return;

                        // The picker is collapsed when an icon already has a
                        // value. Open only the requested picker before scrolling.
                        targetNode
                            .querySelector('button[aria-expanded="false"]')
                            ?.click();

                        const containerRect =
                            scrollContainer.getBoundingClientRect();
                        const targetRect = targetNode.getBoundingClientRect();
                        scrollContainer.scrollTo({
                            top:
                                scrollContainer.scrollTop +
                                targetRect.top -
                                containerRect.top -
                                8,
                            behavior: 'smooth',
                        });
                    });
                });
            });
            return () => {
                window.cancelAnimationFrame(scrollFrame);
                window.cancelAnimationFrame(settleFrame);
                window.cancelAnimationFrame(finalFrame);
            };
        }

        if (mediaFieldRequest.inline) {
            setActiveMediaField(null);
            const requestedKey = String(mediaFieldRequest.key);
            const topLevelKey = requestedKey.split('.')[0].split('[')[0];
            const expansionKeys = new Set();

            // Standalone media fields use a dedicated media pill.
            if (
                topLevelKey &&
                Object.prototype.hasOwnProperty.call(
                    component_props || {},
                    topLevelKey
                )
            ) {
                expansionKeys.add(`${topLevelKey}.__media__`);
            }

            // Images inside an array item live in that item's collapsible pill.
            const arrayItemMatch = requestedKey.match(/^(.+?\[\d+\])/);
            if (arrayItemMatch) expansionKeys.add(arrayItemMatch[1]);

            if (expansionKeys.size) {
                setExpandedArrayItems((previous) => {
                    const next = { ...previous };
                    expansionKeys.forEach((path) => {
                        next[path] = true;
                    });
                    return next;
                });
            }

            const scrollFrame = window.requestAnimationFrame(() => {
                const settleFrame = window.requestAnimationFrame(() => {
                    const scrollContainer = editorScrollRef.current;
                    if (!scrollContainer) return;

                    const arrayItemMatch = requestedKey.match(/^(.+?\[\d+\])/);
                    const arrayItemNode = arrayItemMatch
                        ? arrayItemNodesRef.current.get(arrayItemMatch[1])
                        : null;
                    const mediaSectionId = `component-editor-${String(
                        `${topLevelKey}.__media__`
                    ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                    const mediaSectionNode =
                        scrollContainer.ownerDocument.getElementById(
                            mediaSectionId
                        );
                    const targetNode = arrayItemNode || mediaSectionNode;
                    if (!targetNode) return;

                    const containerRect =
                        scrollContainer.getBoundingClientRect();
                    const targetRect = targetNode.getBoundingClientRect();
                    scrollContainer.scrollTo({
                        top:
                            scrollContainer.scrollTop +
                            targetRect.top -
                            containerRect.top -
                            8,
                        behavior: 'smooth',
                    });
                });
                return settleFrame;
            });

            return () => window.cancelAnimationFrame(scrollFrame);
        }

        handleOpenMediaPanel(
            mediaFieldRequest.key,
            mediaFieldRequest.value ?? ''
        );
    }, [
        component_props,
        handleOpenMediaPanel,
        mediaFieldRequest,
        open,
        setExpandedArrayItems,
    ]);

    const handleMediaPreview = (val) => {
        if (!activeMediaField) return;
        onChange({ file, key: activeMediaField.key, value: val });
    };

    const handleMediaSelect = (val) => {
        if (!activeMediaField) return;
        onChange({ file, key: activeMediaField.key, value: val });
        setMediaWasApplied(true);
        setActiveMediaField(null);
    };

    const handleInlineMediaUpload = React.useCallback(
        async (targetKey, event) => {
            const image = event?.target?.files?.[0];
            if (!image) return;

            const normalizedImage = await normalizeSvgFileForUpload(image);
            const formData = new FormData();
            formData.append('image', normalizedImage);
            const companyId = editor.global.data.company_id;
            const url =
                `https://image-search-ytnjkd5upa-uc.a.run.app/upload/image/${companyId}?` +
                new URLSearchParams({
                    filename: image.name.replace(/ +/g, '-').toLowerCase(),
                });

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    body: formData,
                });
                if (!response.ok) {
                    throw new Error(
                        `Image upload failed: ${response.statusText}`
                    );
                }

                const data = await response.json();
                editor.site.addImage?.({
                    ...data,
                    size_bytes: image.size,
                });
                onChange({ file, key: targetKey, value: data.url });
            } catch (error) {
                notifications.push({
                    title: 'Image upload failed',
                    variant: 'error',
                    error,
                });
            } finally {
                if (event?.target) {
                    event.target.value = '';
                }
            }
        },
        [
            editor.global.data.company_id,
            editor.site,
            file,
            notifications,
            onChange,
        ]
    );

    const handleCloseMediaPanel = () => {
        if (activeMediaField && !mediaWasApplied) {
            onChange({
                file,
                key: activeMediaField.key,
                value: mediaOriginalValue ?? '',
            });
        }
        setActiveMediaField(null);
        setMediaOriginalValue(null);
        setMediaWasApplied(false);
    };

    const isArrayItemExpanded = React.useCallback(
        (path, defaultExpanded = true) =>
            Object.prototype.hasOwnProperty.call(expandedArrayItems, path)
                ? expandedArrayItems[path]
                : defaultExpanded,
        [expandedArrayItems]
    );

    const toggleArrayItemExpanded = React.useCallback(
        (path, defaultExpanded = true) => {
            setExpandedArrayItems((prev) => ({
                ...prev,
                [path]: !(Object.prototype.hasOwnProperty.call(prev, path)
                    ? prev[path]
                    : defaultExpanded),
            }));
        },
        []
    );

    const setArrayItemExpanded = React.useCallback((path, nextValue) => {
        setExpandedArrayItems((prev) => ({
            ...prev,
            [path]: nextValue,
        }));
    }, []);

    const setInlineFieldDraft = React.useCallback((path, value) => {
        setInlineFieldDrafts((prev) => ({
            ...prev,
            [path]: value,
        }));
    }, []);

    const clearInlineFieldDraft = React.useCallback((path) => {
        setInlineFieldDrafts((prev) => {
            if (!Object.prototype.hasOwnProperty.call(prev, path)) return prev;
            const next = { ...prev };
            delete next[path];
            return next;
        });
    }, []);

    const readInlineFieldDraft = React.useCallback(
        (path, fallback = '') =>
            Object.prototype.hasOwnProperty.call(inlineFieldDrafts, path)
                ? inlineFieldDrafts[path]
                : fallback,
        [inlineFieldDrafts]
    );

    const readDraggedArrayItem = React.useCallback(
        (event) => {
            if (draggedArrayItem) return draggedArrayItem;
            const payloads = [
                event?.dataTransfer?.getData('application/json'),
                event?.dataTransfer?.getData('text/plain'),
            ].filter(Boolean);
            for (const raw of payloads) {
                try {
                    const parsed = JSON.parse(raw);
                    if (
                        parsed &&
                        typeof parsed.arrayKey === 'string' &&
                        typeof parsed.fromIndex === 'number'
                    ) {
                        return parsed;
                    }
                } catch (error) {
                    continue;
                }
            }
            return null;
        },
        [draggedArrayItem]
    );

    const reorderArrayItems = React.useCallback(
        (arrayKey, fromIndex, insertionIndex) => {
            const currentValue = readValueAtPath(component_props, arrayKey);
            if (!Array.isArray(currentValue)) return;
            const nextValue = structuredClone(currentValue);
            const [movedItem] = nextValue.splice(fromIndex, 1);
            if (movedItem === undefined) return;
            const adjustedInsertionIndex =
                fromIndex < insertionIndex
                    ? insertionIndex - 1
                    : insertionIndex;
            if (fromIndex === adjustedInsertionIndex) return;
            nextValue.splice(adjustedInsertionIndex, 0, movedItem);
            onChange({
                file,
                key: arrayKey,
                value: nextValue,
            });
        },
        [component_props, file, onChange]
    );

    const handleArrayItemDragStart = React.useCallback(
        (event, arrayKey, fromIndex) => {
            const payload = {
                arrayKey,
                fromIndex,
                overIndex: fromIndex,
                overPosition: 'before',
            };
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData(
                'application/json',
                JSON.stringify(payload)
            );
            event.dataTransfer.setData('text/plain', JSON.stringify(payload));
            const sourceCard = event.currentTarget.closest(
                '[data-draggable-array-item="true"]'
            );
            if (sourceCard) {
                const rect = sourceCard.getBoundingClientRect();
                const previewNode = sourceCard.cloneNode(true);
                const offsetX = Math.max(
                    0,
                    Math.min(rect.width - 1, event.clientX - rect.left)
                );
                const offsetY = Math.max(
                    0,
                    Math.min(rect.height - 1, event.clientY - rect.top)
                );
                previewNode.style.position = 'fixed';
                previewNode.style.top = '-10000px';
                previewNode.style.left = '-10000px';
                previewNode.style.width = `${rect.width}px`;
                previewNode.style.maxWidth = `${rect.width}px`;
                previewNode.style.margin = '0';
                previewNode.style.pointerEvents = 'none';
                previewNode.style.borderRadius = '16px';
                previewNode.style.overflow = 'hidden';
                previewNode.style.boxShadow =
                    '0 18px 36px rgba(15, 23, 42, 0.18)';
                previewNode.style.transform = 'rotate(0deg)';
                previewNode.style.opacity = '0.98';
                previewNode.style.background = 'white';
                document.body.appendChild(previewNode);
                dragPreviewNodeRef.current = previewNode;
                event.dataTransfer.setDragImage(previewNode, offsetX, offsetY);
            }
            setDraggedArrayItem(payload);
        },
        []
    );

    const handleArrayItemDragOver = React.useCallback(
        (event, arrayKey, overIndex) => {
            const payload = readDraggedArrayItem(event);
            if (!payload || payload.arrayKey !== arrayKey) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            const targetRect = event.currentTarget.getBoundingClientRect();
            const pointerY = event.clientY - targetRect.top;
            const rawOverPosition =
                pointerY < targetRect.height / 2 ? 'before' : 'after';
            const overPosition = normalizeAdjacentDropPosition(
                payload.fromIndex,
                overIndex,
                rawOverPosition
            );
            setDraggedArrayItem((prev) => {
                if (
                    prev &&
                    prev.arrayKey === payload.arrayKey &&
                    prev.fromIndex === payload.fromIndex &&
                    prev.overIndex === overIndex &&
                    prev.overPosition === overPosition
                ) {
                    return prev;
                }
                return {
                    arrayKey,
                    fromIndex: payload.fromIndex,
                    overIndex,
                    overPosition,
                };
            });
        },
        [readDraggedArrayItem]
    );

    const handleArrayItemDrop = React.useCallback(
        (event, arrayKey, overIndex) => {
            const payload = readDraggedArrayItem(event);
            if (!payload || payload.arrayKey !== arrayKey) return;
            event.preventDefault();
            const targetRect = event.currentTarget.getBoundingClientRect();
            const pointerY = event.clientY - targetRect.top;
            const rawOverPosition =
                pointerY < targetRect.height / 2 ? 'before' : 'after';
            const overPosition = normalizeAdjacentDropPosition(
                payload.fromIndex,
                overIndex,
                rawOverPosition
            );
            const insertionIndex =
                overPosition === 'after' ? overIndex + 1 : overIndex;
            reorderArrayItems(arrayKey, payload.fromIndex, insertionIndex);
            setDraggedArrayItem(null);
        },
        [readDraggedArrayItem, reorderArrayItems]
    );

    const handleArrayItemDragEnd = React.useCallback(() => {
        setDraggedArrayItem(null);
        if (dragPreviewNodeRef.current) {
            dragPreviewNodeRef.current.remove();
            dragPreviewNodeRef.current = null;
        }
        suppressDragClickRef.current = true;
        if (suppressDragClickTimeoutRef.current) {
            window.clearTimeout(suppressDragClickTimeoutRef.current);
        }
        suppressDragClickTimeoutRef.current = window.setTimeout(() => {
            suppressDragClickRef.current = false;
            suppressDragClickTimeoutRef.current = null;
        }, 0);
    }, []);

    const handleTopLevelFieldDragEnd = React.useCallback(() => {
        setDraggedTopLevelField(null);
        if (topLevelFieldDragPreviewNodeRef.current) {
            topLevelFieldDragPreviewNodeRef.current.remove();
            topLevelFieldDragPreviewNodeRef.current = null;
        }
        suppressDragClickRef.current = true;
        if (suppressDragClickTimeoutRef.current) {
            window.clearTimeout(suppressDragClickTimeoutRef.current);
        }
        suppressDragClickTimeoutRef.current = window.setTimeout(() => {
            suppressDragClickRef.current = false;
            suppressDragClickTimeoutRef.current = null;
        }, 0);
    }, []);

    const handleTopLevelFieldDragStart = React.useCallback(
        (event, fieldLeaf) => {
            event.stopPropagation();
            const payload = {
                fieldLeaf,
                overLeaf: null,
                overPosition: null,
            };
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData(
                'application/json',
                JSON.stringify(payload)
            );
            event.dataTransfer.setData('text/plain', JSON.stringify(payload));
            const sourceCard = event.currentTarget.closest(
                '[data-draggable-hero-field="true"]'
            );
            if (sourceCard) {
                const previewSource =
                    sourceCard.querySelector('[data-hero-lead-media-card]') ||
                    sourceCard.firstElementChild?.firstElementChild ||
                    sourceCard.firstElementChild ||
                    sourceCard;
                const rect = previewSource.getBoundingClientRect();
                const previewNode = previewSource.cloneNode(true);
                previewNode.style.position = 'fixed';
                previewNode.style.top = '-10000px';
                previewNode.style.left = '-10000px';
                previewNode.style.width = `${rect.width}px`;
                previewNode.style.maxWidth = `${rect.width}px`;
                previewNode.style.margin = '0';
                previewNode.style.pointerEvents = 'none';
                previewNode.style.borderRadius = '16px';
                previewNode.style.overflow = 'hidden';
                previewNode.style.boxShadow =
                    '0 18px 36px rgba(15, 23, 42, 0.18)';
                previewNode.style.opacity = '0.98';
                previewNode.style.background = 'white';
                document.body.appendChild(previewNode);
                topLevelFieldDragPreviewNodeRef.current = previewNode;
                event.dataTransfer.setDragImage(
                    previewNode,
                    Math.max(24, rect.width / 2),
                    24
                );
            }
            setDraggedTopLevelField(payload);
        },
        []
    );

    const registerArrayItemNode = React.useCallback((path, node) => {
        if (!path) return;
        if (node) {
            arrayItemNodesRef.current.set(path, node);
            return;
        }
        arrayItemNodesRef.current.delete(path);
    }, []);

    const handleAnimatedInsert = React.useCallback(
        ({ arrayKey, insertIndex, insert }) => {
            const beforeRects = Array.from(arrayItemNodesRef.current.entries())
                .filter(([path, node]) => {
                    return (
                        node &&
                        node.isConnected &&
                        isDirectArrayItemPath(path, arrayKey)
                    );
                })
                .sort(
                    (a, b) => getArrayItemIndex(a[0]) - getArrayItemIndex(b[0])
                )
                .map(([, node]) => node.getBoundingClientRect());

            pendingArrayAnimationRef.current = {
                type: 'insert',
                arrayKey,
                insertIndex,
                beforeRects,
            };
            insert();
        },
        []
    );

    const handleAnimatedDelete = React.useCallback(
        ({ arrayKey, itemPath, removeIndex, remove }) => {
            const beforeEntries = Array.from(
                arrayItemNodesRef.current.entries()
            )
                .filter(([path, node]) => {
                    return (
                        node &&
                        node.isConnected &&
                        isDirectArrayItemPath(path, arrayKey)
                    );
                })
                .sort(
                    (a, b) => getArrayItemIndex(a[0]) - getArrayItemIndex(b[0])
                );
            const beforeRects = beforeEntries.map(([, node]) =>
                node.getBoundingClientRect()
            );
            const targetNode = arrayItemNodesRef.current.get(itemPath);

            if (targetNode && typeof document !== 'undefined') {
                const ghostSource = getArrayItemAnimationNode(targetNode);
                const rect = ghostSource.getBoundingClientRect();
                const ghost = ghostSource.cloneNode(true);

                ghost.style.position = 'fixed';
                ghost.style.left = `${rect.left}px`;
                ghost.style.top = `${rect.top}px`;
                ghost.style.width = `${rect.width}px`;
                ghost.style.height = `${rect.height}px`;
                ghost.style.margin = '0';
                ghost.style.pointerEvents = 'none';
                ghost.style.zIndex = '9999';
                ghost.style.transformOrigin = 'center top';
                ghost.style.overflow = 'hidden';
                ghost.style.background =
                    window.getComputedStyle(ghostSource).backgroundColor;
                ghost.style.opacity = '1';
                ghost.style.transform = 'translateY(0) scale(1)';
                ghost.style.filter = 'blur(0)';
                ghost.style.willChange = 'opacity, transform, filter';
                ghost.style.transition = 'none';
                document.body.appendChild(ghost);

                const startId = window.requestAnimationFrame(() => {
                    const animateId = window.setTimeout(() => {
                        ghost.style.transition =
                            'opacity 220ms ease-out, transform 220ms ease-out, filter 220ms ease-out';
                        ghost.style.opacity = '0';
                        ghost.style.transform = 'translateY(-10px) scale(0.92)';
                        ghost.style.filter = 'blur(4px)';
                    }, 24);
                    animationTimeoutIdsRef.current.push(animateId);
                });
                animationFrameIdsRef.current.push(startId);

                const cleanupId = window.setTimeout(() => {
                    if (ghost.isConnected) {
                        ghost.remove();
                    }
                }, 320);
                animationTimeoutIdsRef.current.push(cleanupId);
            }

            pendingArrayAnimationRef.current = {
                type: 'delete',
                arrayKey,
                removeIndex,
                beforeRects,
            };
            remove();
        },
        []
    );

    const renderInput = (value, key, level = 0, ctx = {}) => {
        const {
            index: ctxIndex,
            parentLength,
            skipButtons,
            previewSize,
            layout,
            midContent,
            compactRow,
            hiddenFieldPaths = [],
            hideImagePreview = false,
            hideImageActionButtons = false,
            hideInlineMediaPanel = false,
            forceInlineMediaPanel = false,
            inlineMediaPanelProps = null,
            inlineMediaAltProps = null,
            inlineMediaCaptionProps = null,
            hideLabel = false,
            customLabel = null,
        } = ctx;
        const is_array = Array.isArray(value);
        const is_object =
            typeof value === 'object' && !is_array && value !== null;
        const isNiceJobFiltersField =
            componentName === 'Reviews' &&
            keyLeaf(key).toLowerCase() === 'nicejobfilters';

        if (isShapeDivider && isShapeDividerKey(key)) return null;
        const containerClassName = compactRow ? 'w-full' : 'mb-2 w-full';
        if (key === 'company') return null;
        if (
            isPageComponent &&
            keyLeaf(key).toLowerCase() ===
                PAGE_UNPUBLISH_REDIRECT_KEY.toLowerCase()
        ) {
            return null;
        }
        if (isStyleKey(key, componentName)) return null;
        if (
            skipButtons &&
            (isButtonKey(key) ||
                isButtonIconKey(key) ||
                isButtonIconVisibleKey(key))
        )
            return null;
        if (shouldHideProp(componentName, key)) return null;
        if (componentName === 'FloatingAction') {
            const action = String(
                component_props?.Type || component_props?.Action || 'text'
            )
                .trim()
                .toLowerCase();
            const leaf = keyLeaf(key).toLowerCase();
            if (
                (leaf === 'bookinglink' ||
                    leaf === 'bookinglinkopeninnewtab') &&
                action !== 'book'
            ) {
                return null;
            }
            if (
                leaf === 'bookinglinkopeninnewtab' &&
                !String(component_props?.BookingLink || '').trim()
            ) {
                return null;
            }
            if (leaf === 'messengerlink' && action !== 'messenger') {
                return null;
            }
            if (
                ['emailaddress', 'emailsubject', 'emailmessage'].includes(
                    leaf
                ) &&
                action !== 'email'
            ) {
                return null;
            }
            if (
                ['directionsdestination', 'directionsopeninnewtab'].includes(
                    leaf
                ) &&
                action !== 'directions'
            ) {
                return null;
            }
            if (
                leaf === 'directionsopeninnewtab' &&
                !String(component_props?.DirectionsDestination || '').trim()
            ) {
                return null;
            }
            if (
                leaf === 'phonenumber' &&
                !['text', 'call', 'whatsapp'].includes(action)
            ) {
                return null;
            }
            if (
                leaf === 'textmessage' &&
                !['text', 'whatsapp'].includes(action)
            ) {
                return null;
            }
        }
        if (componentName === 'Popup') {
            const popupHideCTAButtonRaw = component_props?.HideCTAButton;
            const popupHideCTAButton =
                popupHideCTAButtonRaw &&
                typeof popupHideCTAButtonRaw === 'object' &&
                'value' in popupHideCTAButtonRaw
                    ? popupHideCTAButtonRaw.value
                    : popupHideCTAButtonRaw;
            const popupShouldHidePrimaryCTA =
                popupHideCTAButton === true ||
                String(popupHideCTAButton || '')
                    .trim()
                    .toLowerCase() === 'true';
            if (
                popupShouldHidePrimaryCTA &&
                keyLeaf(key).toLowerCase() === 'cta'
            ) {
                return null;
            }
            if (keyLeaf(key).toLowerCase() === 'hidectabutton') {
                return null;
            }
        }

        if (
            componentName === 'Footer' &&
            keyLeaf(key).toLowerCase() === 'creditcards'
        ) {
            return (
                <PaymentOptionsEditor
                    key={key}
                    label="Payment Methods"
                    value={value}
                    onChange={(next) => onChange({ file, key, value: next })}
                />
            );
        }

        if (isNiceJobFiltersField) {
            return null;
        }

        if (is_array) {
            if (
                componentName === 'Footer' &&
                keyLeaf(key).toLowerCase() === 'medias' &&
                value.length === 0
            ) {
                const firstMediaKey = `${key}[0].Media`;
                return (
                    <div key={key + level} className={containerClassName}>
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            Images
                        </label>
                        <button
                            type="button"
                            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-primary-300 bg-primary-50 px-4 py-3 text-xs font-semibold text-primary-700 transition-colors hover:border-primary-400 hover:bg-primary-100"
                            onClick={() => {
                                onChange({
                                    file,
                                    key,
                                    value: [{ Media: '', AltText: '' }],
                                });
                                handleOpenMediaPanel(firstMediaKey, '');
                            }}
                        >
                            <PlusIcon className="h-4 w-4" aria-hidden="true" />
                            Add image
                        </button>
                    </div>
                );
            }

            if (
                isSocialLinksKey(key) &&
                (componentName === 'Team' || componentName === 'Footer')
            ) {
                return (
                    <SocialLinksEditor
                        key={key}
                        label="Social"
                        value={value}
                        onChange={(next) =>
                            onChange({ file, key, value: next })
                        }
                    />
                );
            }

            const isComparisonTable = componentName === 'ComparisonTable';
            const keyPath = String(key);
            const comparisonRowsMatch = keyPath.match(
                /^Sections\[(\d+)\]\.Rows$/
            );
            const comparisonValuesMatch = keyPath.match(
                /^Sections\[(\d+)\]\.Rows\[(\d+)\]\.Values$/
            );
            const isComparisonPlans = keyPath === 'Plans';

            if (isComparisonTable && isComparisonPlans) {
                const plans = Array.isArray(value) ? value : [];
                const toText = (v) =>
                    v == null || typeof v === 'boolean' ? '' : String(v);
                const normalizeBadgeColor = (raw) => {
                    const trimmed = String(raw ?? '').trim();
                    if (!trimmed) return '';
                    const withHash = trimmed.startsWith('#')
                        ? trimmed
                        : `#${trimmed}`;
                    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(withHash)
                        ? withHash.toUpperCase()
                        : '';
                };
                const colorPickerFallback = (() => {
                    const scheme = String(
                        component_props?.ColorScheme || ''
                    ).toLowerCase();
                    return scheme.includes('light') ? '#DBEAFE' : '#FFFFFF';
                })();
                const normalizePlan = (plan) => ({
                    Name: toText(plan?.Name ?? ''),
                    Price: toText(plan?.Price ?? ''),
                    Description: toText(plan?.Description ?? ''),
                    Badge: toText(plan?.Badge ?? ''),
                    BadgeColor: normalizeBadgeColor(toText(plan?.BadgeColor)),
                    CTA: toText(plan?.CTA ?? ''),
                    CTALink: toText(plan?.CTALink ?? ''),
                });
                const updatePlan = (planIndex, patch) => {
                    const nextPlans = Array.isArray(value)
                        ? structuredClone(value)
                        : [];
                    while (nextPlans.length <= planIndex) {
                        nextPlans.push(normalizePlan({}));
                    }
                    nextPlans[planIndex] = {
                        ...normalizePlan(nextPlans[planIndex]),
                        ...patch,
                    };
                    onChange({ file, key, value: nextPlans });
                };
                const planToneClasses =
                    getCollapsibleArrayToneClasses('primary');
                const planDropPlaceholder = (
                    <div
                        className={`my-3 h-16 rounded-xl border-2 border-dashed ${getDropPlaceholderClasses(
                            'primary'
                        )}`}
                        aria-hidden="true"
                    />
                );

                return (
                    <div key={key + level} className={containerClassName}>
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            Plans
                        </label>
                        <div className="mt-2">
                            {plans.map((plan, index) => {
                                const planPath = `${key}[${index}]`;
                                const planVal = normalizePlan(plan);
                                const isExpanded = isArrayItemExpanded(
                                    planPath,
                                    false
                                );
                                const detailsId = `component-editor-${String(
                                    planPath
                                ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                                const isDraggedItem =
                                    draggedArrayItem?.arrayKey === key &&
                                    draggedArrayItem?.fromIndex === index;
                                const isDropTarget =
                                    draggedArrayItem?.arrayKey === key &&
                                    draggedArrayItem?.fromIndex !== index &&
                                    draggedArrayItem?.overIndex === index;
                                const showDropPlaceholderBefore =
                                    isDropTarget &&
                                    draggedArrayItem?.overPosition ===
                                        'before' &&
                                    !isNoOpDropPosition(
                                        draggedArrayItem?.fromIndex,
                                        index,
                                        'before'
                                    );
                                const showDropPlaceholderAfter =
                                    isDropTarget &&
                                    draggedArrayItem?.overPosition ===
                                        'after' &&
                                    !isNoOpDropPosition(
                                        draggedArrayItem?.fromIndex,
                                        index,
                                        'after'
                                    );

                                return (
                                    <div
                                        key={`comparison-plan-${planPath}`}
                                        ref={(node) =>
                                            registerArrayItemNode(
                                                planPath,
                                                node
                                            )
                                        }
                                        onDragOver={(event) =>
                                            handleArrayItemDragOver(
                                                event,
                                                key,
                                                index
                                            )
                                        }
                                        onDrop={(event) =>
                                            handleArrayItemDrop(
                                                event,
                                                key,
                                                index
                                            )
                                        }
                                    >
                                        {showDropPlaceholderBefore
                                            ? planDropPlaceholder
                                            : null}
                                        <div
                                            data-draggable-array-item="true"
                                            className={`overflow-hidden rounded-xl border shadow-sm transition-all ${
                                                planToneClasses.wrapper
                                            } ${
                                                isDraggedItem
                                                    ? 'scale-[0.985] opacity-70'
                                                    : ''
                                            }`}
                                        >
                                            <div
                                                className={`flex items-center gap-2 px-3 py-2 ${
                                                    isExpanded
                                                        ? `border-b ${planToneClasses.headerOpenBorder}`
                                                        : ''
                                                }`}
                                            >
                                                <button
                                                    type="button"
                                                    draggable
                                                    className={`${planToneClasses.buttonHover} flex min-w-0 flex-1 cursor-grab items-center gap-3 rounded-lg p-1 text-left transition-colors ease-in focus-visible:outline-none active:cursor-grabbing`}
                                                    aria-expanded={isExpanded}
                                                    aria-controls={detailsId}
                                                    onDragStart={(event) =>
                                                        handleArrayItemDragStart(
                                                            event,
                                                            key,
                                                            index
                                                        )
                                                    }
                                                    onDragEnd={
                                                        handleArrayItemDragEnd
                                                    }
                                                    onClick={() => {
                                                        if (
                                                            suppressDragClickRef.current
                                                        ) {
                                                            suppressDragClickRef.current = false;
                                                            return;
                                                        }
                                                        toggleArrayItemExpanded(
                                                            planPath,
                                                            false
                                                        );
                                                    }}
                                                >
                                                    <div className="min-w-0">
                                                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">
                                                            {`Plan ${
                                                                index + 1
                                                            }`}
                                                        </div>
                                                        <div className="truncate text-sm font-semibold text-neutral-900">
                                                            {planVal.Name.trim() ||
                                                                `Plan ${
                                                                    index + 1
                                                                }`}
                                                        </div>
                                                    </div>
                                                </button>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        title="Add plan after"
                                                        className={
                                                            INLINE_ADD_BUTTON_CLASSNAME
                                                        }
                                                        onClick={() =>
                                                            handleAnimatedInsert(
                                                                {
                                                                    arrayKey:
                                                                        key,
                                                                    insertIndex:
                                                                        index +
                                                                        1,
                                                                    insert: () =>
                                                                        editor.components.nested.add(
                                                                            {
                                                                                file,
                                                                                key,
                                                                                index:
                                                                                    index +
                                                                                    1,
                                                                            }
                                                                        ),
                                                                }
                                                            )
                                                        }
                                                    >
                                                        <PlusIcon className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={
                                                            ROUND_DANGER_ICON_BUTTON_CLASSNAME
                                                        }
                                                        disabled={
                                                            plans.length <= 1
                                                        }
                                                        onClick={() =>
                                                            handleAnimatedDelete(
                                                                {
                                                                    arrayKey:
                                                                        key,
                                                                    itemPath:
                                                                        planPath,
                                                                    removeIndex:
                                                                        index,
                                                                    remove: () =>
                                                                        editor.components.nested.delete(
                                                                            {
                                                                                file,
                                                                                key: planPath,
                                                                            }
                                                                        ),
                                                                }
                                                            )
                                                        }
                                                    >
                                                        <TrashIcon className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title={
                                                            isExpanded
                                                                ? 'Collapse plan'
                                                                : 'Expand plan'
                                                        }
                                                        className={
                                                            INLINE_ADD_BUTTON_CLASSNAME
                                                        }
                                                        aria-expanded={
                                                            isExpanded
                                                        }
                                                        aria-controls={
                                                            detailsId
                                                        }
                                                        onClick={() =>
                                                            toggleArrayItemExpanded(
                                                                planPath,
                                                                false
                                                            )
                                                        }
                                                    >
                                                        <ChevronDownIcon
                                                            className={`h-3 w-3 ${
                                                                planToneClasses.chevronText
                                                            } transition-transform duration-200 ${
                                                                isExpanded
                                                                    ? 'rotate-180'
                                                                    : ''
                                                            }`}
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                            {isExpanded ? (
                                                <div
                                                    id={detailsId}
                                                    className="grid gap-3 bg-white p-3 md:grid-cols-2"
                                                >
                                                    <div>
                                                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                                            Name
                                                        </label>
                                                        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <input
                                                                type="text"
                                                                value={
                                                                    planVal.Name
                                                                }
                                                                onChange={(e) =>
                                                                    updatePlan(
                                                                        index,
                                                                        {
                                                                            Name: e
                                                                                .target
                                                                                .value,
                                                                        }
                                                                    )
                                                                }
                                                                className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                                            Price
                                                        </label>
                                                        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <input
                                                                type="text"
                                                                value={
                                                                    planVal.Price
                                                                }
                                                                onChange={(e) =>
                                                                    updatePlan(
                                                                        index,
                                                                        {
                                                                            Price: e
                                                                                .target
                                                                                .value,
                                                                        }
                                                                    )
                                                                }
                                                                className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="md:col-span-2">
                                                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                                            Description
                                                        </label>
                                                        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <input
                                                                type="text"
                                                                value={
                                                                    planVal.Description
                                                                }
                                                                onChange={(e) =>
                                                                    updatePlan(
                                                                        index,
                                                                        {
                                                                            Description:
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                        }
                                                                    )
                                                                }
                                                                className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                                            Badge
                                                        </label>
                                                        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <input
                                                                type="text"
                                                                value={
                                                                    planVal.Badge
                                                                }
                                                                onChange={(e) =>
                                                                    updatePlan(
                                                                        index,
                                                                        {
                                                                            Badge: e
                                                                                .target
                                                                                .value,
                                                                        }
                                                                    )
                                                                }
                                                                className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                                            Badge Color
                                                        </label>
                                                        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <div className="flex h-[38px] items-stretch overflow-hidden rounded-[10px] bg-white">
                                                                <div className="relative h-full w-9 shrink-0">
                                                                    <div
                                                                        className="h-full w-full"
                                                                        style={{
                                                                            backgroundColor:
                                                                                normalizeBadgeColor(
                                                                                    planVal.BadgeColor
                                                                                ) ||
                                                                                colorPickerFallback,
                                                                        }}
                                                                    />
                                                                    <input
                                                                        type="color"
                                                                        value={
                                                                            normalizeBadgeColor(
                                                                                planVal.BadgeColor
                                                                            ) ||
                                                                            colorPickerFallback
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            updatePlan(
                                                                                index,
                                                                                {
                                                                                    BadgeColor:
                                                                                        e
                                                                                            .target
                                                                                            .value,
                                                                                }
                                                                            )
                                                                        }
                                                                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 focus:outline-none"
                                                                        aria-label="Badge color picker"
                                                                    />
                                                                </div>

                                                                <input
                                                                    type="text"
                                                                    spellCheck={
                                                                        false
                                                                    }
                                                                    placeholder="Default"
                                                                    value={
                                                                        planVal.BadgeColor
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updatePlan(
                                                                            index,
                                                                            {
                                                                                BadgeColor:
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                            }
                                                                        )
                                                                    }
                                                                    className="flex-1 border-0 bg-transparent px-2 text-sm leading-6 text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                                                                    aria-label="Badge color hex input"
                                                                />
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="mt-1 text-[11px] font-semibold text-neutral-500 hover:text-neutral-700"
                                                            onClick={() =>
                                                                updatePlan(
                                                                    index,
                                                                    {
                                                                        BadgeColor:
                                                                            '',
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            Use default badge
                                                            style
                                                        </button>
                                                    </div>

                                                    <div>
                                                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                                            CTA
                                                        </label>
                                                        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <input
                                                                type="text"
                                                                value={
                                                                    planVal.CTA
                                                                }
                                                                onChange={(e) =>
                                                                    updatePlan(
                                                                        index,
                                                                        {
                                                                            CTA: e
                                                                                .target
                                                                                .value,
                                                                        }
                                                                    )
                                                                }
                                                                className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                                            CTA Link
                                                        </label>
                                                        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <input
                                                                type="text"
                                                                value={
                                                                    planVal.CTALink
                                                                }
                                                                onChange={(e) =>
                                                                    updatePlan(
                                                                        index,
                                                                        {
                                                                            CTALink:
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                        }
                                                                    )
                                                                }
                                                                className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                        {showDropPlaceholderAfter
                                            ? planDropPlaceholder
                                            : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }

            if (isComparisonTable && comparisonValuesMatch) {
                const toText = (v) =>
                    v == null || typeof v === 'boolean' ? '' : String(v).trim();

                const normalizeComparisonCell = (cell) => {
                    const normalizeStatus = (
                        rawStatus,
                        rawIcon = '',
                        rawValue = ''
                    ) => {
                        const merged = toText(
                            rawStatus || rawIcon || rawValue
                        ).toLowerCase();
                        if (
                            [
                                'check',
                                'yes',
                                'true',
                                'included',
                                'available',
                                'supported',
                                '1',
                            ].includes(merged) ||
                            merged.includes('fa-check')
                        ) {
                            return 'check';
                        }
                        if (
                            [
                                'x',
                                'no',
                                'false',
                                'not included',
                                'excluded',
                                'unavailable',
                                'not supported',
                                '0',
                            ].includes(merged) ||
                            merged.includes('fa-xmark')
                        ) {
                            return 'x';
                        }
                        return '';
                    };

                    if (
                        cell &&
                        typeof cell === 'object' &&
                        !Array.isArray(cell)
                    ) {
                        return {
                            Status: normalizeStatus(
                                cell.Status,
                                cell.Icon,
                                cell.Value
                            ),
                            Text: toText(cell.Text ?? ''),
                        };
                    }

                    const raw = toText(cell);
                    const status = normalizeStatus(raw, raw, raw);
                    return status
                        ? { Status: status, Text: '' }
                        : { Status: '', Text: raw };
                };

                const planNames = (
                    Array.isArray(component_props?.Plans)
                        ? component_props.Plans
                        : []
                ).map((plan, idx) => {
                    const name =
                        plan && typeof plan === 'object' ? plan.Name : '';
                    const normalizedName = toText(name);
                    return normalizedName || `Plan ${idx + 1}`;
                });

                const rowsCount = Array.isArray(value) ? value.length : 0;
                const columnsCount = Math.max(planNames.length, rowsCount, 1);
                const featurePath = keyPath.replace(/\.Values$/, '.Feature');
                const featureName =
                    toText(readValueAtPath(component_props, featurePath)) ||
                    'Feature';
                const hasMismatch = rowsCount !== planNames.length;

                const updateCell = (cellIndex, patch) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    const nextValues = structuredClone(currentValues);

                    while (nextValues.length <= cellIndex) {
                        nextValues.push({ Status: '', Text: '' });
                    }

                    const normalizedCell = normalizeComparisonCell(
                        nextValues[cellIndex]
                    );
                    nextValues[cellIndex] = {
                        ...normalizedCell,
                        ...patch,
                    };

                    onChange({ file, key, value: nextValues });
                };

                const syncToPlans = () => {
                    const targetLength = Math.max(1, planNames.length);
                    const currentValues = Array.isArray(value) ? value : [];
                    const nextValues = Array.from(
                        { length: targetLength },
                        (_, idx) =>
                            normalizeComparisonCell(
                                currentValues[idx] ?? { Status: '', Text: '' }
                            )
                    );
                    onChange({ file, key, value: nextValues });
                };

                return (
                    <div key={key + level} className={containerClassName}>
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            Values for: {featureName}
                        </label>

                        <div className="mt-2 space-y-2 rounded-lg border border-neutral-300 bg-white p-3">
                            {hasMismatch && (
                                <div className="mb-1 flex items-center justify-between gap-2 rounded-md bg-neutral-50 p-2">
                                    <span className="text-xs text-neutral-600">
                                        Plans and values count do not match.
                                    </span>
                                    <button
                                        type="button"
                                        className="rounded-md bg-primary-700 px-2 py-1 text-xs font-semibold text-white transition-all ease-in hover:bg-primary-800"
                                        onClick={syncToPlans}
                                    >
                                        Sync to plans
                                    </button>
                                </div>
                            )}

                            {Array.from({ length: columnsCount }).map(
                                (_, colIndex) => {
                                    const cellValue = normalizeComparisonCell(
                                        (Array.isArray(value) ? value : [])[
                                            colIndex
                                        ]
                                    );
                                    return (
                                        <div
                                            key={`${keyPath}-value-${colIndex}`}
                                            className="rounded-md border border-neutral-200 p-2"
                                        >
                                            <div className="mb-2 text-xs font-semibold text-neutral-700">
                                                {planNames[colIndex] ||
                                                    `Plan ${colIndex + 1}`}
                                            </div>
                                            <div className="grid gap-3 md:grid-cols-2">
                                                <div>
                                                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                                        Indicator
                                                    </label>
                                                    <div className="inline-flex overflow-hidden rounded-md border border-neutral-200">
                                                        {[
                                                            {
                                                                value: '',
                                                                label: 'None',
                                                                iconClass:
                                                                    'fa-solid fa-minus',
                                                            },
                                                            {
                                                                value: 'check',
                                                                label: 'Check',
                                                                iconClass:
                                                                    'fa-solid fa-check',
                                                            },
                                                            {
                                                                value: 'x',
                                                                label: 'X',
                                                                iconClass:
                                                                    'fa-solid fa-xmark',
                                                            },
                                                        ].map((option) => {
                                                            const isActive =
                                                                cellValue.Status ===
                                                                option.value;
                                                            return (
                                                                <button
                                                                    key={`${keyPath}-status-${colIndex}-${option.value}`}
                                                                    type="button"
                                                                    className={`inline-flex h-7 w-7 items-center justify-center p-0 text-[10px] font-semibold transition-all ease-in ${
                                                                        isActive
                                                                            ? 'bg-primary-700 text-white'
                                                                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                                                                    } ${
                                                                        option.value ===
                                                                        ''
                                                                            ? ''
                                                                            : 'border-l border-neutral-200'
                                                                    }`}
                                                                    onClick={() =>
                                                                        updateCell(
                                                                            colIndex,
                                                                            {
                                                                                Status: option.value,
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    <i
                                                                        className={`${option.iconClass} text-[10px]`}
                                                                        aria-hidden="true"
                                                                    />
                                                                    <span className="sr-only">
                                                                        {
                                                                            option.label
                                                                        }
                                                                    </span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                                        Text (optional)
                                                    </label>
                                                    <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                        <input
                                                            type="text"
                                                            value={
                                                                cellValue.Text
                                                            }
                                                            onChange={(e) =>
                                                                updateCell(
                                                                    colIndex,
                                                                    {
                                                                        Text: e
                                                                            .target
                                                                            .value,
                                                                    }
                                                                )
                                                            }
                                                            placeholder="Ex: Included or Starts at $200"
                                                            className="block h-7 w-full rounded-[10px] border-0 px-2 text-[10px] leading-4 text-neutral-800 placeholder:text-[10px] placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    </div>
                );
            }

            if (isComparisonTable && comparisonRowsMatch) {
                const sectionIndex = Number(comparisonRowsMatch[1]);
                const sectionTitle =
                    String(
                        readValueAtPath(
                            component_props,
                            `Sections[${sectionIndex}].Title`
                        ) ?? ''
                    ).trim() || `Section ${sectionIndex + 1}`;
                const rows = Array.isArray(value) ? value : [];

                const rowToneClasses =
                    getCollapsibleArrayToneClasses('secondary');
                const rowDropPlaceholder = (
                    <div
                        className={`my-3 h-16 rounded-xl border-2 border-dashed ${getDropPlaceholderClasses(
                            'secondary'
                        )}`}
                        aria-hidden="true"
                    />
                );

                return (
                    <div key={key + level} className={containerClassName}>
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            Rows ({sectionTitle})
                        </label>

                        <div className="mt-2">
                            {rows.map((item, index) => {
                                const itemPath = `${key}[${index}]`;
                                const featurePath = `${itemPath}.Feature`;
                                const featureName =
                                    String(
                                        readValueAtPath(
                                            component_props,
                                            featurePath
                                        ) ?? ''
                                    ).trim() || `Feature ${index + 1}`;
                                const isExpanded = isArrayItemExpanded(
                                    itemPath,
                                    false
                                );
                                const detailsId = `component-editor-${String(
                                    itemPath
                                ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                                const isDraggedItem =
                                    draggedArrayItem?.arrayKey === key &&
                                    draggedArrayItem?.fromIndex === index;
                                const isDropTarget =
                                    draggedArrayItem?.arrayKey === key &&
                                    draggedArrayItem?.fromIndex !== index &&
                                    draggedArrayItem?.overIndex === index;
                                const showDropPlaceholderBefore =
                                    isDropTarget &&
                                    draggedArrayItem?.overPosition ===
                                        'before' &&
                                    !isNoOpDropPosition(
                                        draggedArrayItem?.fromIndex,
                                        index,
                                        'before'
                                    );
                                const showDropPlaceholderAfter =
                                    isDropTarget &&
                                    draggedArrayItem?.overPosition ===
                                        'after' &&
                                    !isNoOpDropPosition(
                                        draggedArrayItem?.fromIndex,
                                        index,
                                        'after'
                                    );

                                return (
                                    <div
                                        key={`comparison-row-${itemPath}`}
                                        ref={(node) =>
                                            registerArrayItemNode(
                                                itemPath,
                                                node
                                            )
                                        }
                                        onDragOver={(event) =>
                                            handleArrayItemDragOver(
                                                event,
                                                key,
                                                index
                                            )
                                        }
                                        onDrop={(event) =>
                                            handleArrayItemDrop(
                                                event,
                                                key,
                                                index
                                            )
                                        }
                                    >
                                        {showDropPlaceholderBefore
                                            ? rowDropPlaceholder
                                            : null}
                                        <div
                                            data-draggable-array-item="true"
                                            className={`overflow-hidden rounded-xl border shadow-sm transition-all ${
                                                rowToneClasses.wrapper
                                            } ${
                                                isDraggedItem
                                                    ? 'scale-[0.985] opacity-70'
                                                    : ''
                                            }`}
                                        >
                                            <div
                                                className={`flex items-center gap-2 px-3 py-2 ${
                                                    isExpanded
                                                        ? `border-b ${rowToneClasses.headerOpenBorder}`
                                                        : ''
                                                }`}
                                            >
                                                <button
                                                    type="button"
                                                    draggable
                                                    className={`${rowToneClasses.buttonHover} flex min-w-0 flex-1 cursor-grab items-center gap-3 rounded-lg p-1 text-left transition-colors ease-in focus-visible:outline-none active:cursor-grabbing`}
                                                    aria-expanded={isExpanded}
                                                    aria-controls={detailsId}
                                                    onDragStart={(event) =>
                                                        handleArrayItemDragStart(
                                                            event,
                                                            key,
                                                            index
                                                        )
                                                    }
                                                    onDragEnd={
                                                        handleArrayItemDragEnd
                                                    }
                                                    onClick={() => {
                                                        if (
                                                            suppressDragClickRef.current
                                                        ) {
                                                            suppressDragClickRef.current = false;
                                                            return;
                                                        }
                                                        toggleArrayItemExpanded(
                                                            itemPath,
                                                            false
                                                        );
                                                    }}
                                                >
                                                    <div className="min-w-0">
                                                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-secondary-700">
                                                            {`Row ${index + 1}`}
                                                        </div>
                                                        <div className="truncate text-sm font-semibold text-neutral-900">
                                                            {featureName}
                                                        </div>
                                                    </div>
                                                </button>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        title="Add row after"
                                                        className={
                                                            INLINE_ADD_BUTTON_CLASSNAME
                                                        }
                                                        onClick={() =>
                                                            handleAnimatedInsert(
                                                                {
                                                                    arrayKey:
                                                                        key,
                                                                    insertIndex:
                                                                        index +
                                                                        1,
                                                                    insert: () =>
                                                                        editor.components.nested.add(
                                                                            {
                                                                                file,
                                                                                key,
                                                                                index:
                                                                                    index +
                                                                                    1,
                                                                            }
                                                                        ),
                                                                }
                                                            )
                                                        }
                                                    >
                                                        <PlusIcon className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={
                                                            ROUND_DANGER_ICON_BUTTON_CLASSNAME
                                                        }
                                                        disabled={
                                                            rows.length <= 1
                                                        }
                                                        onClick={() =>
                                                            handleAnimatedDelete(
                                                                {
                                                                    arrayKey:
                                                                        key,
                                                                    itemPath,
                                                                    removeIndex:
                                                                        index,
                                                                    remove: () =>
                                                                        editor.components.nested.delete(
                                                                            {
                                                                                file,
                                                                                key: itemPath,
                                                                            }
                                                                        ),
                                                                }
                                                            )
                                                        }
                                                    >
                                                        <TrashIcon className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title={
                                                            isExpanded
                                                                ? 'Collapse row'
                                                                : 'Expand row'
                                                        }
                                                        className={
                                                            INLINE_ADD_BUTTON_CLASSNAME
                                                        }
                                                        aria-expanded={
                                                            isExpanded
                                                        }
                                                        aria-controls={
                                                            detailsId
                                                        }
                                                        onClick={() =>
                                                            toggleArrayItemExpanded(
                                                                itemPath,
                                                                false
                                                            )
                                                        }
                                                    >
                                                        <ChevronDownIcon
                                                            className={`h-3 w-3 ${
                                                                rowToneClasses.chevronText
                                                            } transition-transform duration-200 ${
                                                                isExpanded
                                                                    ? 'rotate-180'
                                                                    : ''
                                                            }`}
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                            {isExpanded ? (
                                                <div
                                                    id={detailsId}
                                                    className="bg-white px-3 pb-2 pt-3"
                                                >
                                                    {renderInput(
                                                        item &&
                                                            typeof item ===
                                                                'object'
                                                            ? item.Feature ?? ''
                                                            : '',
                                                        featurePath,
                                                        level + 1,
                                                        { skipButtons: true }
                                                    )}

                                                    {renderInput(
                                                        item &&
                                                            typeof item ===
                                                                'object' &&
                                                            Array.isArray(
                                                                item.Values
                                                            )
                                                            ? item.Values
                                                            : [],
                                                        `${itemPath}.Values`,
                                                        level + 1,
                                                        { skipButtons: true }
                                                    )}

                                                    {Object.entries(
                                                        item &&
                                                            typeof item ===
                                                                'object' &&
                                                            !Array.isArray(item)
                                                            ? item
                                                            : {}
                                                    )
                                                        .filter(
                                                            ([nestedKey]) =>
                                                                nestedKey !==
                                                                    'Feature' &&
                                                                nestedKey !==
                                                                    'Values'
                                                        )
                                                        .map(
                                                            ([
                                                                nestedKey,
                                                                nestedValue,
                                                            ]) => (
                                                                <React.Fragment
                                                                    key={`${itemPath}.${nestedKey}`}
                                                                >
                                                                    {renderInput(
                                                                        nestedValue,
                                                                        `${itemPath}.${nestedKey}`,
                                                                        level +
                                                                            1,
                                                                        {
                                                                            skipButtons: true,
                                                                        }
                                                                    )}
                                                                </React.Fragment>
                                                            )
                                                        )}
                                                </div>
                                            ) : null}
                                        </div>
                                        {showDropPlaceholderAfter
                                            ? rowDropPlaceholder
                                            : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }

            const isGroupedServicesArray =
                (componentName === 'Nav' || componentName === 'Footer') &&
                keyLeaf(key).toLowerCase() === 'services';

            if (isGroupedServicesArray) {
                const sectionToneClasses =
                    getCollapsibleArrayToneClasses('primary');
                const sectionExpanded = isArrayItemExpanded(key, false);
                const servicesHeaderPath = 'ServicesHeader';
                const servicesHeaderEditStateKey = `${key}.__section_header__`;
                const isServicesHeaderEditing = isArrayItemExpanded(
                    servicesHeaderEditStateKey,
                    false
                );
                const servicesHeaderValue = String(
                    readValueAtPath(component_props, servicesHeaderPath) ?? ''
                ).trim();
                const detailsId = `component-editor-${String(key).replace(
                    /[^a-zA-Z0-9_-]/g,
                    '-'
                )}`;
                const sectionLabel =
                    componentName === 'Nav' ? 'Main Dropdown' : 'Services';
                const expandTitle =
                    componentName === 'Nav'
                        ? 'main dropdown'
                        : 'services section';

                return (
                    <div key={key + level} className={containerClassName}>
                        <div
                            className={`mt-4 overflow-hidden rounded-xl border shadow-sm ${sectionToneClasses.wrapper}`}
                        >
                            {isServicesHeaderEditing ? (
                                <div
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left ${
                                        sectionExpanded
                                            ? `border-b ${sectionToneClasses.headerOpenBorder}`
                                            : ''
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div
                                            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${sectionToneClasses.metaText}`}
                                        >
                                            {sectionLabel}
                                        </div>
                                        <form
                                            onSubmit={(event) => {
                                                event.preventDefault();
                                                onChange({
                                                    file,
                                                    key: servicesHeaderPath,
                                                    value: readInlineFieldDraft(
                                                        servicesHeaderPath,
                                                        readValueAtPath(
                                                            component_props,
                                                            servicesHeaderPath
                                                        ) ?? ''
                                                    ),
                                                });
                                                setArrayItemExpanded(
                                                    servicesHeaderEditStateKey,
                                                    false
                                                );
                                                clearInlineFieldDraft(
                                                    servicesHeaderPath
                                                );
                                            }}
                                            className="mt-1 flex min-w-0 items-center gap-1.5"
                                        >
                                            <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                <input
                                                    type="text"
                                                    value={readInlineFieldDraft(
                                                        servicesHeaderPath,
                                                        readValueAtPath(
                                                            component_props,
                                                            servicesHeaderPath
                                                        ) ?? ''
                                                    )}
                                                    onChange={(event) =>
                                                        setInlineFieldDraft(
                                                            servicesHeaderPath,
                                                            event.target.value
                                                        )
                                                    }
                                                    placeholder="Services Header"
                                                    className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                    autoFocus
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                title="Save Services Header"
                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                            >
                                                <CheckIcon className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button"
                                                title="Cancel Services Header edit"
                                                onClick={() => {
                                                    setArrayItemExpanded(
                                                        servicesHeaderEditStateKey,
                                                        false
                                                    );
                                                    clearInlineFieldDraft(
                                                        servicesHeaderPath
                                                    );
                                                }}
                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                            >
                                                <XMarkIcon className="h-3 w-3" />
                                            </button>
                                        </form>
                                    </div>
                                    <button
                                        type="button"
                                        title={
                                            sectionExpanded
                                                ? `Collapse ${expandTitle}`
                                                : `Expand ${expandTitle}`
                                        }
                                        className={INLINE_ADD_BUTTON_CLASSNAME}
                                        aria-expanded={sectionExpanded}
                                        aria-controls={detailsId}
                                        onClick={() =>
                                            toggleArrayItemExpanded(key, false)
                                        }
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 ${
                                                sectionToneClasses.chevronText
                                            } transition-transform duration-200 ${
                                                sectionExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                                        sectionToneClasses.buttonHover
                                    } ${
                                        sectionExpanded
                                            ? `border-b ${sectionToneClasses.headerOpenBorder}`
                                            : ''
                                    }`}
                                    aria-expanded={sectionExpanded}
                                    aria-controls={detailsId}
                                    onClick={() =>
                                        toggleArrayItemExpanded(key, false)
                                    }
                                >
                                    <div className="min-w-0">
                                        <div
                                            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${sectionToneClasses.metaText}`}
                                        >
                                            {sectionLabel}
                                        </div>
                                        <div className="group mt-1 flex min-w-0 items-center gap-1">
                                            <div className="truncate text-sm font-semibold text-neutral-900">
                                                {servicesHeaderValue ||
                                                    'Services Header'}
                                            </div>
                                            <button
                                                type="button"
                                                title="Edit Services Header"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setInlineFieldDraft(
                                                        servicesHeaderPath,
                                                        readValueAtPath(
                                                            component_props,
                                                            servicesHeaderPath
                                                        ) ?? ''
                                                    );
                                                    setArrayItemExpanded(
                                                        servicesHeaderEditStateKey,
                                                        true
                                                    );
                                                }}
                                                className={
                                                    INLINE_EDIT_BUTTON_CLASSNAME
                                                }
                                            >
                                                <PencilSquareIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <span
                                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${sectionToneClasses.chevronText}`}
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 transition-transform duration-200 ${
                                                sectionExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </span>
                                </div>
                            )}

                            {sectionExpanded ? (
                                <div
                                    id={detailsId}
                                    className="bg-white px-3 pb-3 pt-2"
                                >
                                    {value.map((item, index) => {
                                        const itemPath = `${key}[${index}]`;
                                        return (
                                            <div
                                                key={itemPath}
                                                ref={(node) =>
                                                    registerArrayItemNode(
                                                        itemPath,
                                                        node
                                                    )
                                                }
                                            >
                                                {renderInput(
                                                    item,
                                                    itemPath,
                                                    level,
                                                    {
                                                        index,
                                                        parentLength:
                                                            value.length,
                                                        skipButtons,
                                                    }
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
            }

            return (
                <div key={key + level} className={containerClassName}>
                    {value.map((item, index) => {
                        const itemPath = `${key}[${index}]`;
                        return (
                            <div
                                key={itemPath}
                                ref={(node) =>
                                    registerArrayItemNode(itemPath, node)
                                }
                            >
                                {renderInput(item, itemPath, level, {
                                    index,
                                    parentLength: value.length,
                                    skipButtons,
                                })}
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (is_object) {
            const is_parent_array = /\[\d+\]$/.test(key);
            const rowIndex =
                typeof ctxIndex === 'number'
                    ? ctxIndex
                    : is_parent_array
                    ? Number(key.match(/\[(\d+)\]$/)[1])
                    : 0;

            const baseArrayLeaf = keyLeaf(
                String(key).replace(/\[\d+\]$/, '')
            ).toLowerCase();
            const nestedLeaf = keyLeaf(key).toLowerCase();
            const showTopControls =
                typeof parentLength === 'number' && parentLength > 1;
            const localKeys = Object.keys(value || {});
            const localButtonPairs = resolveButtonPairs(localKeys);
            const isNestedItem = is_parent_array || key.includes('.');
            const arrayEditorConfig = is_parent_array
                ? resolveArrayEditorConfig(componentName, key)
                : null;
            const mediaFieldKey = arrayEditorConfig?.useImageBackground
                ? localKeys.find((nestedKey) =>
                      (arrayEditorConfig.backgroundImageFields || []).some(
                          (field) =>
                              field.toLowerCase() === nestedKey.toLowerCase()
                      )
                  ) ||
                  localKeys.find((nestedKey) => looksLikeImageKey(nestedKey))
                : null;
            const mediaFieldPath =
                mediaFieldKey && is_parent_array
                    ? `${key}.${mediaFieldKey}`
                    : null;
            const mediaAltFieldKey =
                mediaFieldKey && arrayEditorConfig?.useImageBackground
                    ? localKeys.find((nestedKey) => {
                          const leaf = keyLeaf(nestedKey).toLowerCase();
                          return leaf === 'alttext' || leaf === 'alt';
                      }) || null
                    : null;
            const mediaCaptionFieldKey =
                mediaFieldKey && componentName === 'Gallery'
                    ? localKeys.find(
                          (nestedKey) =>
                              keyLeaf(nestedKey).toLowerCase() === 'caption'
                      ) || null
                    : null;
            const isMediaFilenamePillEditor = Boolean(
                is_parent_array &&
                    (arrayEditorConfig?.itemLabel === 'Media' ||
                        arrayEditorConfig?.mediaFilenamePill) &&
                    mediaFieldKey
            );
            const isCollapsibleArrayItem = Boolean(arrayEditorConfig);
            const isInlineEditArrayItem =
                isCollapsibleArrayItem &&
                arrayEditorConfig?.editorStyle === 'inline-edit';
            const pillHeaderEditableField = isCollapsibleArrayItem
                ? arrayEditorConfig?.headerEditableField ||
                  (isMediaFilenamePillEditor ? mediaFieldKey : null) ||
                  null
                : null;
            const pillHeaderEditPath =
                pillHeaderEditableField && is_parent_array
                    ? `${key}.${pillHeaderEditableField}`
                    : null;
            const pillHeaderEditStateKey = `${key}.__pill_header__`;
            const isPillHeaderEditing = pillHeaderEditPath
                ? !isMediaFilenamePillEditor &&
                  isArrayItemExpanded(pillHeaderEditStateKey, false)
                : false;
            let headerTitle = is_parent_array
                ? getArrayItemEditorFallbackTitle(
                      key,
                      rowIndex,
                      arrayEditorConfig
                  )
                : prettyLabel(key);
            if (
                is_parent_array &&
                componentName === 'ReviewHero' &&
                (baseArrayLeaf === 'reviews' ||
                    baseArrayLeaf === 'author' ||
                    nestedLeaf === 'author')
            ) {
                headerTitle = '';
            }
            const defaultExpanded = arrayEditorConfig?.defaultExpanded ?? true;
            const isExpanded = isCollapsibleArrayItem
                ? isArrayItemExpanded(key, defaultExpanded)
                : true;
            const itemDisplayTitle = isCollapsibleArrayItem
                ? isMediaFilenamePillEditor
                    ? getSiteImageByUrl(value?.[mediaFieldKey])?.filename ||
                      extractMediaFilename(value?.[mediaFieldKey]) ||
                      getArrayItemEditorTitle(
                          value,
                          arrayEditorConfig,
                          headerTitle
                      )
                    : getArrayItemEditorTitle(
                          value,
                          arrayEditorConfig,
                          headerTitle
                      )
                : headerTitle;
            const itemDisplaySubtitle = isInlineEditArrayItem
                ? getArrayItemEditorSubtitle(
                      value,
                      arrayEditorConfig,
                      itemDisplayTitle
                  )
                : '';
            const itemMetaLabel = isCollapsibleArrayItem
                ? getArrayItemEditorMetaLabel(rowIndex, arrayEditorConfig)
                : '';
            const detailsId = `component-editor-${String(key).replace(
                /[^a-zA-Z0-9_-]/g,
                '-'
            )}`;
            const collapsibleToneClasses = isCollapsibleArrayItem
                ? getCollapsibleArrayToneClasses(arrayEditorConfig?.tone)
                : null;
            const collapsibleBackgroundImage = isCollapsibleArrayItem
                ? getCollapsibleArrayBackgroundImage(value, arrayEditorConfig)
                : '';
            const hasImageBackedCollapsibleHeader = Boolean(
                collapsibleBackgroundImage
            );
            const effectiveCollapsibleToneClasses =
                hasImageBackedCollapsibleHeader
                    ? {
                          wrapper: 'border-primary-200 bg-white',
                          headerOpenBorder: 'border-white/15',
                          buttonHover: 'hover:bg-transparent',
                          metaText: 'text-white/80',
                          chevronText: 'text-white',
                      }
                    : arrayEditorConfig?.useImageBackground
                    ? getEmptyMediaCollapsibleToneClasses()
                    : collapsibleToneClasses ||
                      getCollapsibleArrayToneClasses('primary');
            const collapsibleHeaderStyle = hasImageBackedCollapsibleHeader
                ? {
                      backgroundImage: `linear-gradient(135deg, rgba(5, 12, 23, 0.78) 0%, rgba(5, 12, 23, 0.6) 55%, rgba(5, 12, 23, 0.42) 100%), url(${JSON.stringify(
                          collapsibleBackgroundImage
                      )})`,
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                  }
                : undefined;
            const collapsibleTitleClassName = hasImageBackedCollapsibleHeader
                ? 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]'
                : 'text-neutral-900';
            const collapsibleHeaderEditButtonClassName =
                hasImageBackedCollapsibleHeader
                    ? 'focus-visible:ring-white/60 group-focus-visible-within:opacity-100 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full bg-black/15 text-white/90 opacity-0 shadow-sm backdrop-blur-[2px] transition-all hover:bg-black/30 hover:text-white focus:outline-none focus-visible:ring-2 group-hover:opacity-100'
                    : INLINE_EDIT_BUTTON_CLASSNAME;
            const collapsibleHeaderAddButtonClassName =
                hasImageBackedCollapsibleHeader
                    ? 'focus-visible:ring-white/60 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full bg-black/15 text-white shadow-sm backdrop-blur-[2px] transition-all hover:bg-black/30 focus:outline-none focus-visible:ring-2 disabled:opacity-40'
                    : INLINE_ADD_BUTTON_CLASSNAME;
            const collapsibleHeaderDeleteButtonClassName =
                hasImageBackedCollapsibleHeader
                    ? 'focus-visible:ring-white/60 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full bg-black/15 text-white shadow-sm backdrop-blur-[2px] transition-all hover:bg-red-500/30 hover:text-white focus:outline-none focus-visible:ring-2 disabled:opacity-40'
                    : isInlineEditArrayItem
                    ? SMALL_DANGER_ICON_BUTTON_CLASSNAME
                    : ROUND_DANGER_ICON_BUTTON_CLASSNAME;
            const collapsibleHeaderChevronButtonClassName =
                hasImageBackedCollapsibleHeader
                    ? 'focus-visible:ring-white/60 inline-flex h-6 w-6 items-center justify-center self-center rounded-full bg-black/15 text-white shadow-sm backdrop-blur-[2px] transition-all hover:bg-black/30 focus:outline-none focus-visible:ring-2'
                    : `inline-flex h-6 w-6 items-center justify-center self-center rounded-full transition-colors ${effectiveCollapsibleToneClasses.chevronText} ${effectiveCollapsibleToneClasses.buttonHover}`;
            const inlineEditPrimaryKey = isInlineEditArrayItem
                ? findInlineEditPrimaryKey(value)
                : null;
            const inlineEditLinkKey = isInlineEditArrayItem
                ? findInlineEditLinkKey(
                      value,
                      arrayEditorConfig?.inlineEditLinkFields
                  )
                : null;
            const inlineEditPrimaryPath =
                inlineEditPrimaryKey && is_parent_array
                    ? `${key}.${inlineEditPrimaryKey}`
                    : null;
            const inlineEditLinkPath =
                inlineEditLinkKey && is_parent_array
                    ? `${key}.${inlineEditLinkKey}`
                    : null;
            const inlineEditPrimaryStateKey = `${key}.__inline_primary__`;
            const inlineEditLinkStateKey = `${key}.__inline_link__`;
            const isInlinePrimaryEditing = isInlineEditArrayItem
                ? isArrayItemExpanded(inlineEditPrimaryStateKey, false)
                : false;
            const isInlineLinkEditing = isInlineEditArrayItem
                ? isArrayItemExpanded(inlineEditLinkStateKey, false)
                : false;
            const isInlineEditing =
                isInlinePrimaryEditing || isInlineLinkEditing;
            const itemArrayKey = is_parent_array
                ? arrayPathFromItemKey(key)
                : '';
            const isDraggedItem =
                isCollapsibleArrayItem &&
                draggedArrayItem?.arrayKey === itemArrayKey &&
                draggedArrayItem?.fromIndex === rowIndex;
            const isDropTarget =
                isCollapsibleArrayItem &&
                draggedArrayItem?.arrayKey === itemArrayKey &&
                draggedArrayItem?.fromIndex !== rowIndex &&
                draggedArrayItem?.overIndex === rowIndex;
            const showDropPlaceholderBefore =
                isDropTarget &&
                draggedArrayItem?.overPosition === 'before' &&
                !isNoOpDropPosition(
                    draggedArrayItem?.fromIndex,
                    rowIndex,
                    'before'
                );
            const showDropPlaceholderAfter =
                isDropTarget &&
                draggedArrayItem?.overPosition === 'after' &&
                !isNoOpDropPosition(
                    draggedArrayItem?.fromIndex,
                    rowIndex,
                    'after'
                );
            const dropPlaceholder = (
                <div
                    className={`my-3 h-16 rounded-xl border-2 border-dashed ${getDropPlaceholderClasses(
                        arrayEditorConfig?.tone
                    )}`}
                    aria-hidden="true"
                />
            );
            const effectiveHiddenFieldPaths = [
                ...hiddenFieldPaths,
                ...(arrayEditorConfig?.hiddenFields || []),
                ...(pillHeaderEditableField && !isMediaFilenamePillEditor
                    ? [pillHeaderEditableField]
                    : []),
            ];
            const nestedColorSchemeKey =
                isNestedItem && componentName === 'Services'
                    ? localKeys.find((nestedKey) =>
                          isColorSchemeKey(nestedKey)
                      ) || null
                    : null;
            // Avoid rendering button fields twice when using the grouped block below
            const nestedEntries = Object.entries(value)
                .filter(
                    ([nestedKey]) =>
                        !isButtonKey(nestedKey) &&
                        !isButtonIconKey(nestedKey) &&
                        !isButtonIconVisibleKey(nestedKey) &&
                        nestedKey !== nestedColorSchemeKey &&
                        (isMediaFilenamePillEditor ||
                            nestedKey !== pillHeaderEditableField) &&
                        nestedKey !== mediaAltFieldKey &&
                        nestedKey !== mediaCaptionFieldKey &&
                        !effectiveHiddenFieldPaths.includes(nestedKey)
                )
                .sort(([aKey], [bKey]) => {
                    const fieldOrder = arrayEditorConfig?.fieldOrder || [];
                    const aIndex = fieldOrder.indexOf(aKey);
                    const bIndex = fieldOrder.indexOf(bKey);

                    if (aIndex === -1 && bIndex === -1) return 0;
                    if (aIndex === -1) return 1;
                    if (bIndex === -1) return -1;
                    return aIndex - bIndex;
                });

            const disableInlineAdd =
                (componentName === 'ReviewHero' &&
                    itemArrayKey &&
                    keyLeaf(itemArrayKey).toLowerCase() === 'reviews') ||
                (Number.isFinite(Number(arrayEditorConfig?.maxItems)) &&
                    typeof parentLength === 'number' &&
                    parentLength >= Number(arrayEditorConfig.maxItems));
            const disableArrayItemDelete =
                parentLength <= 1 && !arrayEditorConfig?.allowEmpty;
            const arrayItemControls = is_parent_array ? (
                <div className="flex items-center gap-1">
                    {mediaFieldPath ? (
                        <>
                            <button
                                type="button"
                                title="Upload a file"
                                className={collapsibleHeaderAddButtonClassName}
                                onClick={() =>
                                    mediaUploadInputRefs.current[
                                        mediaFieldPath
                                    ]?.click()
                                }
                            >
                                <ArrowUpTrayIcon
                                    className="h-3 w-3"
                                    aria-hidden="true"
                                />
                            </button>
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                ref={(node) => {
                                    if (node) {
                                        mediaUploadInputRefs.current[
                                            mediaFieldPath
                                        ] = node;
                                    } else {
                                        delete mediaUploadInputRefs.current[
                                            mediaFieldPath
                                        ];
                                    }
                                }}
                                onChange={(event) =>
                                    handleInlineMediaUpload(
                                        mediaFieldPath,
                                        event
                                    )
                                }
                            />
                        </>
                    ) : null}
                    {!disableInlineAdd ? (
                        <button
                            type="button"
                            title={`Add ${
                                arrayEditorConfig?.itemLabel || 'item'
                            } after`}
                            className={collapsibleHeaderAddButtonClassName}
                            onClick={() =>
                                handleAnimatedInsert({
                                    arrayKey: itemArrayKey,
                                    insertIndex: rowIndex + 1,
                                    insert: () =>
                                        editor.components.nested.add({
                                            file,
                                            key: itemArrayKey,
                                            index: rowIndex + 1,
                                        }),
                                })
                            }
                        >
                            <PlusIcon className="h-3 w-3" aria-hidden="true" />
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className={collapsibleHeaderDeleteButtonClassName}
                        disabled={disableArrayItemDelete}
                        onClick={() => {
                            if (disableArrayItemDelete) return;
                            handleAnimatedDelete({
                                arrayKey: arrayPathFromItemKey(key),
                                itemPath: key,
                                removeIndex: rowIndex,
                                remove: () =>
                                    editor.components.nested.delete({
                                        file,
                                        key,
                                        index: rowIndex,
                                    }),
                            });
                        }}
                    >
                        <TrashIcon className="h-3 w-3" aria-hidden="true" />
                    </button>
                </div>
            ) : null;

            const objectChildren = (() => {
                const nestedBeforeImageKey = localKeys.find((nestedKey) => {
                    const leaf = keyLeaf(nestedKey).toLowerCase();
                    return leaf === 'beforeimage' || leaf === 'beforeimageurl';
                });
                const nestedAfterImageKey = localKeys.find((nestedKey) => {
                    const leaf = keyLeaf(nestedKey).toLowerCase();
                    return leaf === 'afterimage' || leaf === 'afterimageurl';
                });
                const nestedBeforeAltKey = localKeys.find((nestedKey) => {
                    const leaf = keyLeaf(nestedKey).toLowerCase();
                    return leaf === 'beforealttext' || leaf === 'beforealt';
                });
                const nestedAfterAltKey = localKeys.find((nestedKey) => {
                    const leaf = keyLeaf(nestedKey).toLowerCase();
                    return leaf === 'afteralttext' || leaf === 'afteralt';
                });
                const nestedBeforeAfterAnchorKey =
                    nestedBeforeImageKey && nestedAfterImageKey
                        ? localKeys.indexOf(nestedBeforeImageKey) <=
                          localKeys.indexOf(nestedAfterImageKey)
                            ? nestedBeforeImageKey
                            : nestedAfterImageKey
                        : null;
                const nestedButtonsSectionKey = `${key}.__cta_buttons__`;
                const nestedButtonsExpanded = isArrayItemExpanded(
                    nestedButtonsSectionKey,
                    false
                );
                const nestedButtonToneClasses =
                    getCollapsibleArrayToneClasses('cta');
                const nestedButtonsDetailsId = `component-editor-${String(
                    nestedButtonsSectionKey
                ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                const nestedButtonSummary = localButtonPairs
                    .map(({ textKey }) =>
                        String(
                            readValueAtPath(
                                component_props,
                                `${key}.${textKey}`
                            ) ?? ''
                        ).trim()
                    )
                    .filter(Boolean)
                    .join(', ');
                const buttonsBlock =
                    isNestedItem && localButtonPairs.length > 0 ? (
                        <div className="mt-6" key={`${key}-buttons`}>
                            <div
                                className={`overflow-hidden rounded-xl border shadow-sm ${nestedButtonToneClasses.wrapper}`}
                            >
                                <button
                                    type="button"
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                                        nestedButtonToneClasses.buttonHover
                                    } ${
                                        nestedButtonsExpanded
                                            ? `border-b ${nestedButtonToneClasses.headerOpenBorder}`
                                            : ''
                                    }`}
                                    aria-expanded={nestedButtonsExpanded}
                                    aria-controls={nestedButtonsDetailsId}
                                    onClick={() =>
                                        toggleArrayItemExpanded(
                                            nestedButtonsSectionKey,
                                            false
                                        )
                                    }
                                >
                                    <div className="min-w-0">
                                        <div
                                            className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${nestedButtonToneClasses.metaText}`}
                                        >
                                            <CursorArrowRaysIcon
                                                className="h-4 w-4"
                                                aria-hidden="true"
                                            />
                                            <span>CTA Buttons</span>
                                        </div>
                                        {nestedButtonSummary ? (
                                            <div className="mt-1 truncate text-sm font-semibold text-white">
                                                {nestedButtonSummary}
                                            </div>
                                        ) : null}
                                    </div>
                                    <span
                                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${nestedButtonToneClasses.chevronText}`}
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 transition-transform duration-200 ${
                                                nestedButtonsExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </span>
                                </button>

                                {nestedButtonsExpanded ? (
                                    <div
                                        id={nestedButtonsDetailsId}
                                        className="bg-white px-3 pb-3 pt-2"
                                    >
                                        {localButtonPairs.map(
                                            ({
                                                textKey,
                                                linkKey,
                                                iconKey,
                                                iconVisibleKey,
                                                openInNewTabKey,
                                                label,
                                                linkLabel,
                                            }) => {
                                                const textPath = `${key}.${textKey}`;
                                                const linkPath = linkKey
                                                    ? `${key}.${linkKey}`
                                                    : null;
                                                const iconPath = iconKey
                                                    ? `${key}.${iconKey}`
                                                    : null;
                                                const iconVisiblePath =
                                                    iconVisibleKey
                                                        ? `${key}.${iconVisibleKey}`
                                                        : null;
                                                const textVal = String(
                                                    readValueAtPath(
                                                        component_props,
                                                        textPath
                                                    ) ?? ''
                                                );
                                                const iconVal = iconPath
                                                    ? String(
                                                          readValueAtPath(
                                                              component_props,
                                                              iconPath
                                                          ) ?? ''
                                                      )
                                                    : '';
                                                const iconVisible =
                                                    iconVisiblePath
                                                        ? coerceBoolean(
                                                              readValueAtPath(
                                                                  component_props,
                                                                  iconVisiblePath
                                                              ) ?? true
                                                          )
                                                        : true;
                                                const hasButtonText =
                                                    textVal.trim().length > 0;
                                                const showLink =
                                                    !!linkPath && hasButtonText;
                                                const textLeafLower =
                                                    keyLeaf(
                                                        textKey
                                                    ).toLowerCase();
                                                const linkVal = linkPath
                                                    ? String(
                                                          readValueAtPath(
                                                              component_props,
                                                              linkPath
                                                          ) ?? ''
                                                      )
                                                    : '';
                                                const canOpenInNewTab =
                                                    !!openInNewTabKey &&
                                                    showLink &&
                                                    (textLeafLower === 'cta' ||
                                                    textLeafLower === 'smallcta'
                                                        ? linkVal.trim()
                                                              .length > 0
                                                        : textLeafLower ===
                                                          'buttontext'
                                                        ? true
                                                        : textLeafLower.includes(
                                                              'secondary'
                                                          ) ||
                                                          textLeafLower.includes(
                                                              'outline'
                                                          ) ||
                                                          textLeafLower.includes(
                                                              'third'
                                                          ));
                                                const openInNewTabPath =
                                                    openInNewTabKey
                                                        ? `${key}.${openInNewTabKey}`
                                                        : null;
                                                const openInNewTab =
                                                    canOpenInNewTab &&
                                                    openInNewTabPath
                                                        ? coerceBoolean(
                                                              readValueAtPath(
                                                                  component_props,
                                                                  openInNewTabPath
                                                              ) ?? false
                                                          )
                                                        : false;
                                                const isCtaButton =
                                                    textLeafLower === 'cta' ||
                                                    textLeafLower ===
                                                        'smallcta';
                                                const isSecondaryButton =
                                                    textLeafLower.includes(
                                                        'secondary'
                                                    );
                                                const cardClassName =
                                                    isCtaButton
                                                        ? 'rounded-xl bg-primary-50 p-3'
                                                        : isSecondaryButton
                                                        ? 'rounded-xl bg-secondary-50 p-3'
                                                        : 'rounded-xl border border-neutral-300 bg-white p-3';

                                                return (
                                                    <div
                                                        key={`nested-btn-${textPath}`}
                                                        className={`mb-3 ${cardClassName}`}
                                                    >
                                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                            {label ||
                                                                prettyLabel(
                                                                    textKey
                                                                )}
                                                        </label>
                                                        <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <input
                                                                type="text"
                                                                value={textVal}
                                                                onChange={(e) =>
                                                                    onChange({
                                                                        file,
                                                                        key: textPath,
                                                                        value: e
                                                                            .target
                                                                            .value,
                                                                    })
                                                                }
                                                                className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                            />
                                                        </div>

                                                        {hasButtonText &&
                                                            iconPath && (
                                                                <div className="mt-3">
                                                                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                                        {prettyLabel(
                                                                            iconKey
                                                                        )}
                                                                    </label>
                                                                    <div
                                                                        className="mt-2"
                                                                        data-inline-icon-field={
                                                                            iconPath
                                                                        }
                                                                    >
                                                                        <FontAwesomeIconPicker
                                                                            value={
                                                                                iconVal
                                                                            }
                                                                            onChange={(
                                                                                next
                                                                            ) =>
                                                                                onChange(
                                                                                    {
                                                                                        file,
                                                                                        key: iconPath,
                                                                                        value: next,
                                                                                    }
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}

                                                        {hasButtonText &&
                                                            iconVisiblePath && (
                                                                <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1">
                                                                    <div className="text-[11px] font-semibold leading-4 text-primary-700">
                                                                        Show
                                                                        Icon
                                                                    </div>
                                                                    <Switch
                                                                        size="sm"
                                                                        checked={
                                                                            iconVisible
                                                                        }
                                                                        onChange={(
                                                                            enable
                                                                        ) =>
                                                                            onChange(
                                                                                {
                                                                                    file,
                                                                                    key: iconVisiblePath,
                                                                                    value: enable,
                                                                                }
                                                                            )
                                                                        }
                                                                        ariaLabel={`${prettyLabel(
                                                                            iconKey ||
                                                                                textKey
                                                                        )} visible`}
                                                                    />
                                                                </div>
                                                            )}

                                                        {showLink && (
                                                            <div className="mt-3">
                                                                <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                                    {linkLabel ||
                                                                        prettyLabel(
                                                                            linkKey
                                                                        )}
                                                                </label>
                                                                <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                                    <input
                                                                        type="text"
                                                                        value={
                                                                            linkVal
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            onChange(
                                                                                {
                                                                                    file,
                                                                                    key: linkPath,
                                                                                    value: e
                                                                                        .target
                                                                                        .value,
                                                                                }
                                                                            )
                                                                        }
                                                                        className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}

                                                        {canOpenInNewTab &&
                                                            openInNewTabPath && (
                                                                <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1">
                                                                    <div className="text-[11px] font-semibold leading-4 text-primary-700">
                                                                        Open in
                                                                        new tab
                                                                    </div>
                                                                    <Switch
                                                                        size="sm"
                                                                        checked={
                                                                            openInNewTab
                                                                        }
                                                                        onChange={(
                                                                            enable
                                                                        ) =>
                                                                            onChange(
                                                                                {
                                                                                    file,
                                                                                    key: openInNewTabPath,
                                                                                    value: enable,
                                                                                }
                                                                            )
                                                                        }
                                                                        ariaLabel={`${prettyLabel(
                                                                            textKey
                                                                        )} open in new tab`}
                                                                    />
                                                                </div>
                                                            )}
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : null;

                const nestedColorSchemeBlock = nestedColorSchemeKey ? (
                    <div
                        className="mt-6"
                        key={`${key}-${nestedColorSchemeKey}-color`}
                    >
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            Color Scheme
                        </label>
                        <div className="mt-1">
                            <ColorSchemeSelect
                                value={normalizeComponentColorScheme(
                                    String(
                                        value[nestedColorSchemeKey] ?? 'light'
                                    ),
                                    filterColorSchemeGroups(
                                        COMPONENT_COLOR_SCHEME_GROUPS,
                                        componentName
                                    )
                                )}
                                onChange={(val) =>
                                    onChange({
                                        file,
                                        key: `${key}.${nestedColorSchemeKey}`,
                                        value: val,
                                    })
                                }
                                groups={filterColorSchemeGroups(
                                    COMPONENT_COLOR_SCHEME_GROUPS,
                                    componentName
                                )}
                                swatchFn={componentSchemeToSwatch}
                            />
                        </div>
                    </div>
                ) : null;

                const children = [];
                let buttonsInserted = false;
                nestedEntries.forEach(([nestedKey, nestedValue], nestedIdx) => {
                    const leaf = keyLeaf(nestedKey).toLowerCase();
                    const isFeatureLike = leaf === 'features';
                    const isLast = nestedIdx === nestedEntries.length - 1;
                    const mediaAltValue = mediaAltFieldKey
                        ? value[mediaAltFieldKey]
                        : undefined;

                    if (
                        nestedBeforeAfterAnchorKey &&
                        nestedKey === nestedBeforeAfterAnchorKey &&
                        nestedBeforeImageKey &&
                        nestedAfterImageKey
                    ) {
                        const nestedBeforeAfterSectionKey = `${key}.__before_after__`;
                        const nestedBeforeAfterExpanded = isArrayItemExpanded(
                            nestedBeforeAfterSectionKey,
                            false
                        );
                        const nestedBeforeAfterDetailsId = `component-editor-${String(
                            nestedBeforeAfterSectionKey
                        ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                        const nestedBeforeImageValue =
                            value[nestedBeforeImageKey];
                        const nestedAfterImageValue =
                            value[nestedAfterImageKey];
                        const nestedBeforeAfterHasBothImages =
                            isSelectedEditorMedia(nestedBeforeImageValue) &&
                            isSelectedEditorMedia(nestedAfterImageValue);
                        const nestedBeforeAfterToneClasses =
                            nestedBeforeAfterHasBothImages
                                ? getCollapsibleArrayToneClasses('gradient')
                                : getEmptyMediaCollapsibleToneClasses();
                        const nestedBeforeAfterHeaderStyle =
                            nestedBeforeAfterHasBothImages
                                ? {
                                      backgroundImage: `linear-gradient(135deg, rgba(5, 12, 23, 0.76) 0%, rgba(5, 12, 23, 0.58) 55%, rgba(5, 12, 23, 0.42) 100%), url(${JSON.stringify(
                                          normalizeCollapsibleArrayBackgroundImageValue(
                                              nestedBeforeImageValue
                                          )
                                      )}), url(${JSON.stringify(
                                          normalizeCollapsibleArrayBackgroundImageValue(
                                              nestedAfterImageValue
                                          )
                                      )})`,
                                      backgroundSize:
                                          'cover, 50% 100%, 50% 100%',
                                      backgroundPosition:
                                          'center, left center, right center',
                                      backgroundRepeat:
                                          'no-repeat, no-repeat, no-repeat',
                                  }
                                : undefined;
                        const nestedBeforeAfterMetaClassName =
                            nestedBeforeAfterHasBothImages
                                ? 'text-white/80 drop-shadow-[0_1px_1px_rgba(0,0,0,0.55)]'
                                : nestedBeforeAfterToneClasses.metaText;
                        const nestedBeforeAfterTitleClassName =
                            nestedBeforeAfterHasBothImages
                                ? 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]'
                                : 'text-neutral-900';
                        const nestedBeforeAfterChevronClassName =
                            nestedBeforeAfterHasBothImages
                                ? 'inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full bg-black/15 text-white shadow-sm backdrop-blur-[2px] transition-all hover:bg-black/30'
                                : `inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${nestedBeforeAfterToneClasses.chevronText}`;
                        const nestedBeforeAfterUploadImageKey =
                            !isSelectedEditorMedia(nestedBeforeImageValue)
                                ? nestedBeforeImageKey
                                : !isSelectedEditorMedia(nestedAfterImageValue)
                                ? nestedAfterImageKey
                                : nestedBeforeImageKey;
                        const nestedBeforeAfterUploadPath =
                            nestedBeforeAfterUploadImageKey
                                ? `${key}.${nestedBeforeAfterUploadImageKey}`
                                : null;
                        const renderNestedBeforeAfterImageCard = ({
                            imageKey,
                            imageValue,
                            altKey,
                        }) => {
                            const imagePath = `${key}.${imageKey}`;

                            return (
                                <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                                    <div className="bg-white px-3 pb-3 pt-2">
                                        {renderInput(
                                            imageValue,
                                            imagePath,
                                            level + 1,
                                            {
                                                inlineMediaPanelProps: {
                                                    isOpen: true,
                                                    layout: 'media-row',
                                                },
                                                inlineMediaAltProps: altKey
                                                    ? {
                                                          key: `${key}.${altKey}`,
                                                          value:
                                                              readValueAtPath(
                                                                  component_props,
                                                                  `${key}.${altKey}`
                                                              ) ?? '',
                                                      }
                                                    : null,
                                                forceInlineMediaPanel: true,
                                                hideLabel: true,
                                                hideImagePreview: true,
                                                hideImageActionButtons: true,
                                            }
                                        )}
                                    </div>
                                </div>
                            );
                        };

                        children.push(
                            <div
                                key={`${key}.__before-after`}
                                className="mb-3 mt-4"
                            >
                                <div
                                    className={`overflow-hidden rounded-xl border shadow-sm ${nestedBeforeAfterToneClasses.wrapper}`}
                                >
                                    <div
                                        style={nestedBeforeAfterHeaderStyle}
                                        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                                            nestedBeforeAfterHasBothImages
                                                ? 'bg-neutral-900/70'
                                                : nestedBeforeAfterToneClasses.buttonHover
                                        } ${
                                            nestedBeforeAfterExpanded
                                                ? `border-b ${
                                                      nestedBeforeAfterHasBothImages
                                                          ? 'border-white/15'
                                                          : nestedBeforeAfterToneClasses.headerOpenBorder
                                                  }`
                                                : ''
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            className="min-w-0 flex-1 text-left focus-visible:outline-none"
                                            aria-expanded={
                                                nestedBeforeAfterExpanded
                                            }
                                            aria-controls={
                                                nestedBeforeAfterDetailsId
                                            }
                                            onClick={() =>
                                                toggleArrayItemExpanded(
                                                    nestedBeforeAfterSectionKey,
                                                    false
                                                )
                                            }
                                        >
                                            <div
                                                className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${nestedBeforeAfterMetaClassName}`}
                                            >
                                                Before &amp; After
                                            </div>
                                            <div
                                                className={`mt-1 text-sm font-semibold ${nestedBeforeAfterTitleClassName}`}
                                            >
                                                Before and After Images
                                            </div>
                                        </button>
                                        <span className="flex items-center gap-1">
                                            {nestedBeforeAfterUploadPath ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        title={
                                                            nestedBeforeAfterUploadImageKey ===
                                                            nestedAfterImageKey
                                                                ? 'Upload after image'
                                                                : 'Upload before image'
                                                        }
                                                        className={
                                                            nestedBeforeAfterChevronClassName
                                                        }
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            mediaUploadInputRefs.current[
                                                                nestedBeforeAfterUploadPath
                                                            ]?.click();
                                                        }}
                                                    >
                                                        <ArrowUpTrayIcon
                                                            className="h-3 w-3"
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        ref={(node) => {
                                                            if (node) {
                                                                mediaUploadInputRefs.current[
                                                                    nestedBeforeAfterUploadPath
                                                                ] = node;
                                                            } else {
                                                                delete mediaUploadInputRefs
                                                                    .current[
                                                                    nestedBeforeAfterUploadPath
                                                                ];
                                                            }
                                                        }}
                                                        onChange={(event) =>
                                                            handleInlineMediaUpload(
                                                                nestedBeforeAfterUploadPath,
                                                                event
                                                            )
                                                        }
                                                    />
                                                </>
                                            ) : null}
                                            <button
                                                type="button"
                                                aria-label="Toggle before and after images"
                                                aria-expanded={
                                                    nestedBeforeAfterExpanded
                                                }
                                                aria-controls={
                                                    nestedBeforeAfterDetailsId
                                                }
                                                className={
                                                    nestedBeforeAfterChevronClassName
                                                }
                                                onClick={() =>
                                                    toggleArrayItemExpanded(
                                                        nestedBeforeAfterSectionKey,
                                                        false
                                                    )
                                                }
                                            >
                                                <ChevronDownIcon
                                                    className={`h-3 w-3 ${
                                                        nestedBeforeAfterHasBothImages
                                                            ? 'text-white'
                                                            : nestedBeforeAfterToneClasses.chevronText
                                                    } transition-transform duration-200 ${
                                                        nestedBeforeAfterExpanded
                                                            ? 'rotate-180'
                                                            : ''
                                                    }`}
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </span>
                                    </div>

                                    {nestedBeforeAfterExpanded ? (
                                        <div
                                            id={nestedBeforeAfterDetailsId}
                                            className="bg-white px-3 pb-3 pt-2"
                                        >
                                            <div className="flex flex-col gap-4">
                                                {renderNestedBeforeAfterImageCard(
                                                    {
                                                        imageKey:
                                                            nestedBeforeImageKey,
                                                        imageValue:
                                                            nestedBeforeImageValue,
                                                        altKey: nestedBeforeAltKey,
                                                    }
                                                )}
                                                {renderNestedBeforeAfterImageCard(
                                                    {
                                                        imageKey:
                                                            nestedAfterImageKey,
                                                        imageValue:
                                                            nestedAfterImageValue,
                                                        altKey: nestedAfterAltKey,
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        );
                        return;
                    } else if (
                        nestedBeforeAfterAnchorKey &&
                        [
                            nestedBeforeImageKey,
                            nestedAfterImageKey,
                            nestedBeforeAltKey,
                            nestedAfterAltKey,
                        ]
                            .filter(Boolean)
                            .includes(nestedKey)
                    ) {
                        return;
                    }

                    children.push(
                        <React.Fragment key={`${key}.${nestedKey}`}>
                            {renderInput(
                                nestedValue,
                                `${key}.${nestedKey}`,
                                level + 1,
                                {
                                    skipButtons:
                                        skipButtons ||
                                        localButtonPairs.length > 0,
                                    parentLength: undefined,
                                    hiddenFieldPaths: effectiveHiddenFieldPaths
                                        .filter((path) =>
                                            path.startsWith(`${nestedKey}.`)
                                        )
                                        .map((path) =>
                                            path.slice(nestedKey.length + 1)
                                        ),
                                    hideImagePreview:
                                        mediaFieldKey === nestedKey,
                                    hideImageActionButtons:
                                        mediaFieldKey === nestedKey,
                                    inlineMediaPanelProps:
                                        mediaFieldKey === nestedKey
                                            ? {
                                                  isOpen: isExpanded,
                                                  layout: 'media-row',
                                              }
                                            : null,
                                    inlineMediaAltProps:
                                        mediaFieldKey === nestedKey &&
                                        mediaAltFieldKey
                                            ? {
                                                  key: `${key}.${mediaAltFieldKey}`,
                                                  value: mediaAltValue,
                                              }
                                            : null,
                                    inlineMediaCaptionProps:
                                        mediaFieldKey === nestedKey &&
                                        mediaCaptionFieldKey
                                            ? {
                                                  key: `${key}.${mediaCaptionFieldKey}`,
                                                  value:
                                                      value[
                                                          mediaCaptionFieldKey
                                                      ] ?? '',
                                              }
                                            : null,
                                    hideLabel: mediaFieldKey === nestedKey,
                                }
                            )}
                        </React.Fragment>
                    );

                    if (
                        !buttonsInserted &&
                        buttonsBlock &&
                        (isFeatureLike || isLast)
                    ) {
                        // Insert buttons immediately after Features when present; otherwise at the end.
                        children.push(buttonsBlock);
                        buttonsInserted = true;
                    }
                });

                if (buttonsBlock && !buttonsInserted) {
                    children.push(buttonsBlock);
                }
                if (nestedColorSchemeBlock) {
                    children.push(nestedColorSchemeBlock);
                }
                return children;
            })();

            if (isInlineEditArrayItem) {
                return (
                    <div
                        key={key}
                        className={containerClassName}
                        onDragOver={(event) =>
                            handleArrayItemDragOver(
                                event,
                                itemArrayKey,
                                rowIndex
                            )
                        }
                        onDrop={(event) =>
                            handleArrayItemDrop(event, itemArrayKey, rowIndex)
                        }
                    >
                        {showDropPlaceholderBefore ? dropPlaceholder : null}
                        <div
                            data-draggable-array-item="true"
                            className={`mt-4 ${
                                componentName === 'Services'
                                    ? 'overflow-visible'
                                    : 'overflow-hidden'
                            } rounded-xl border border-neutral-200 bg-white shadow-sm transition-all ${
                                isDraggedItem ? 'scale-[0.985] opacity-70' : ''
                            }`}
                        >
                            <div className="flex items-center gap-2 px-3 py-2">
                                <div
                                    draggable={!isInlineEditing}
                                    className={`flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 ${
                                        isInlineEditing
                                            ? 'cursor-default'
                                            : 'cursor-grab active:cursor-grabbing'
                                    }`}
                                    onDragStart={
                                        isInlineEditing
                                            ? undefined
                                            : (event) =>
                                                  handleArrayItemDragStart(
                                                      event,
                                                      itemArrayKey,
                                                      rowIndex
                                                  )
                                    }
                                    onDragEnd={
                                        isInlineEditing
                                            ? undefined
                                            : handleArrayItemDragEnd
                                    }
                                >
                                    <div
                                        id={detailsId}
                                        className="min-w-0 flex-1"
                                        onClick={(event) =>
                                            event.stopPropagation()
                                        }
                                    >
                                        {itemMetaLabel ? (
                                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">
                                                {itemMetaLabel}
                                            </div>
                                        ) : null}

                                        <div className="space-y-1">
                                            {inlineEditPrimaryPath ? (
                                                isInlinePrimaryEditing ? (
                                                    <form
                                                        onSubmit={(event) => {
                                                            event.preventDefault();
                                                            handleComponentPropChange(
                                                                {
                                                                    key: inlineEditPrimaryPath,
                                                                    value: readInlineFieldDraft(
                                                                        inlineEditPrimaryPath,
                                                                        readValueAtPath(
                                                                            component_props,
                                                                            inlineEditPrimaryPath
                                                                        ) ?? ''
                                                                    ),
                                                                }
                                                            );
                                                            setArrayItemExpanded(
                                                                inlineEditPrimaryStateKey,
                                                                false
                                                            );
                                                            clearInlineFieldDraft(
                                                                inlineEditPrimaryPath
                                                            );
                                                        }}
                                                        className="flex min-w-0 items-center gap-1.5"
                                                    >
                                                        <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <input
                                                                type="text"
                                                                value={readInlineFieldDraft(
                                                                    inlineEditPrimaryPath,
                                                                    readValueAtPath(
                                                                        component_props,
                                                                        inlineEditPrimaryPath
                                                                    ) ?? ''
                                                                )}
                                                                onChange={(e) =>
                                                                    setInlineFieldDraft(
                                                                        inlineEditPrimaryPath,
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                placeholder={prettyLabel(
                                                                    inlineEditPrimaryKey
                                                                )}
                                                                className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                                autoFocus
                                                            />
                                                        </div>
                                                        <button
                                                            type="submit"
                                                            title={`Save ${prettyLabel(
                                                                inlineEditPrimaryKey
                                                            )}`}
                                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                                        >
                                                            <CheckIcon className="h-3 w-3" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title={`Cancel ${prettyLabel(
                                                                inlineEditPrimaryKey
                                                            )} edit`}
                                                            onClick={() => {
                                                                setArrayItemExpanded(
                                                                    inlineEditPrimaryStateKey,
                                                                    false
                                                                );
                                                                clearInlineFieldDraft(
                                                                    inlineEditPrimaryPath
                                                                );
                                                            }}
                                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                                        >
                                                            <XMarkIcon className="h-3 w-3" />
                                                        </button>
                                                    </form>
                                                ) : (
                                                    <div className="group flex min-w-0 items-center gap-1">
                                                        <div className="truncate px-1 py-0.5 text-sm font-semibold text-neutral-900">
                                                            {itemDisplayTitle}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            title={`Edit ${prettyLabel(
                                                                inlineEditPrimaryKey
                                                            )}`}
                                                            onClick={() => {
                                                                setInlineFieldDraft(
                                                                    inlineEditPrimaryPath,
                                                                    readValueAtPath(
                                                                        component_props,
                                                                        inlineEditPrimaryPath
                                                                    ) ?? ''
                                                                );
                                                                setArrayItemExpanded(
                                                                    inlineEditPrimaryStateKey,
                                                                    true
                                                                );
                                                                setArrayItemExpanded(
                                                                    inlineEditLinkStateKey,
                                                                    false
                                                                );
                                                            }}
                                                            className={
                                                                INLINE_EDIT_BUTTON_CLASSNAME
                                                            }
                                                        >
                                                            <PencilSquareIcon
                                                                className="h-3.5 w-3.5"
                                                                aria-hidden="true"
                                                            />
                                                        </button>
                                                    </div>
                                                )
                                            ) : null}

                                            {inlineEditLinkPath ? (
                                                isInlineLinkEditing ? (
                                                    <form
                                                        onSubmit={(event) => {
                                                            event.preventDefault();
                                                            onChange({
                                                                file,
                                                                key: inlineEditLinkPath,
                                                                value: readInlineFieldDraft(
                                                                    inlineEditLinkPath,
                                                                    readValueAtPath(
                                                                        component_props,
                                                                        inlineEditLinkPath
                                                                    ) ?? ''
                                                                ),
                                                            });
                                                            setArrayItemExpanded(
                                                                inlineEditLinkStateKey,
                                                                false
                                                            );
                                                            clearInlineFieldDraft(
                                                                inlineEditLinkPath
                                                            );
                                                        }}
                                                        className="flex min-w-0 items-center gap-1.5"
                                                    >
                                                        <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <div className="flex h-6 items-stretch overflow-hidden rounded-[10px] bg-white">
                                                                <div className="flex w-7 shrink-0 items-center justify-center border-r border-neutral-200 text-neutral-500">
                                                                    <LinkIcon
                                                                        className="h-3 w-3"
                                                                        aria-hidden="true"
                                                                    />
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    value={readInlineFieldDraft(
                                                                        inlineEditLinkPath,
                                                                        readValueAtPath(
                                                                            component_props,
                                                                            inlineEditLinkPath
                                                                        ) ?? ''
                                                                    )}
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        setInlineFieldDraft(
                                                                            inlineEditLinkPath,
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    placeholder={prettyLabel(
                                                                        inlineEditLinkKey
                                                                    )}
                                                                    className="block h-full w-full border-0 px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                                    autoFocus
                                                                />
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="submit"
                                                            title={`Save ${prettyLabel(
                                                                inlineEditLinkKey
                                                            )}`}
                                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                                        >
                                                            <CheckIcon className="h-3 w-3" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title={`Cancel ${prettyLabel(
                                                                inlineEditLinkKey
                                                            )} edit`}
                                                            onClick={() => {
                                                                setArrayItemExpanded(
                                                                    inlineEditLinkStateKey,
                                                                    false
                                                                );
                                                                clearInlineFieldDraft(
                                                                    inlineEditLinkPath
                                                                );
                                                            }}
                                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                                        >
                                                            <XMarkIcon className="h-3 w-3" />
                                                        </button>
                                                    </form>
                                                ) : (
                                                    <div className="group flex min-w-0 items-center gap-1">
                                                        <div className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md px-1 py-0.5 text-xs text-neutral-500">
                                                            <LinkIcon
                                                                className="h-3 w-3 shrink-0"
                                                                aria-hidden="true"
                                                            />
                                                            <span className="truncate">
                                                                {itemDisplaySubtitle ||
                                                                    readValueAtPath(
                                                                        component_props,
                                                                        inlineEditLinkPath
                                                                    ) ||
                                                                    prettyLabel(
                                                                        inlineEditLinkKey
                                                                    )}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            title={`Edit ${prettyLabel(
                                                                inlineEditLinkKey
                                                            )}`}
                                                            onClick={() => {
                                                                setInlineFieldDraft(
                                                                    inlineEditLinkPath,
                                                                    readValueAtPath(
                                                                        component_props,
                                                                        inlineEditLinkPath
                                                                    ) ?? ''
                                                                );
                                                                setArrayItemExpanded(
                                                                    inlineEditLinkStateKey,
                                                                    true
                                                                );
                                                                setArrayItemExpanded(
                                                                    inlineEditPrimaryStateKey,
                                                                    false
                                                                );
                                                            }}
                                                            className={
                                                                INLINE_EDIT_BUTTON_CLASSNAME
                                                            }
                                                        >
                                                            <PencilSquareIcon
                                                                className="h-3.5 w-3.5"
                                                                aria-hidden="true"
                                                            />
                                                        </button>
                                                    </div>
                                                )
                                            ) : null}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1">
                                    {arrayItemControls}
                                </div>
                            </div>
                        </div>
                        {showDropPlaceholderAfter ? dropPlaceholder : null}
                    </div>
                );
            }

            if (isCollapsibleArrayItem) {
                return (
                    <div
                        key={key}
                        className={containerClassName}
                        onDragOver={(event) =>
                            handleArrayItemDragOver(
                                event,
                                itemArrayKey,
                                rowIndex
                            )
                        }
                        onDrop={(event) =>
                            handleArrayItemDrop(event, itemArrayKey, rowIndex)
                        }
                    >
                        {showDropPlaceholderBefore ? dropPlaceholder : null}
                        <div
                            data-draggable-array-item="true"
                            className={`mt-4 ${
                                componentName === 'Services'
                                    ? 'overflow-visible'
                                    : 'overflow-hidden'
                            } rounded-xl border shadow-sm transition-all ${
                                effectiveCollapsibleToneClasses.wrapper
                            } ${
                                isDraggedItem ? 'scale-[0.985] opacity-70' : ''
                            }`}
                        >
                            <div
                                style={collapsibleHeaderStyle}
                                className={`flex items-center gap-2 px-3 py-2 ${
                                    hasImageBackedCollapsibleHeader
                                        ? 'bg-neutral-900/70'
                                        : ''
                                } ${
                                    isExpanded
                                        ? `border-b ${effectiveCollapsibleToneClasses.headerOpenBorder}`
                                        : ''
                                }`}
                            >
                                {isPillHeaderEditing ? (
                                    <div className="flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1">
                                        <div className="min-w-0 flex-1">
                                            {itemMetaLabel ? (
                                                <div
                                                    className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${
                                                        effectiveCollapsibleToneClasses.metaText
                                                    } ${
                                                        hasImageBackedCollapsibleHeader
                                                            ? 'drop-shadow-[0_1px_1px_rgba(0,0,0,0.55)]'
                                                            : ''
                                                    }`}
                                                >
                                                    {itemMetaLabel}
                                                </div>
                                            ) : null}
                                            <form
                                                onSubmit={async (event) => {
                                                    event.preventDefault();
                                                    const currentHeaderValue =
                                                        readValueAtPath(
                                                            component_props,
                                                            pillHeaderEditPath
                                                        ) ?? '';
                                                    const currentSiteImage =
                                                        isMediaFilenamePillEditor
                                                            ? getSiteImageByUrl(
                                                                  currentHeaderValue
                                                              )
                                                            : null;
                                                    const currentFilename =
                                                        currentSiteImage?.filename ||
                                                        extractMediaFilename(
                                                            currentHeaderValue
                                                        );
                                                    const currentFilenameParts =
                                                        splitFilenameParts(
                                                            currentFilename
                                                        );
                                                    const nextHeaderDraft =
                                                        readInlineFieldDraft(
                                                            pillHeaderEditPath,
                                                            isMediaFilenamePillEditor
                                                                ? currentFilenameParts.name
                                                                : currentHeaderValue
                                                        );
                                                    try {
                                                        if (
                                                            isMediaFilenamePillEditor &&
                                                            currentSiteImage?.image_id
                                                        ) {
                                                            const nextFilename =
                                                                buildFilenameFromParts(
                                                                    nextHeaderDraft,
                                                                    currentFilenameParts.extension
                                                                );
                                                            if (
                                                                nextFilename &&
                                                                nextFilename !==
                                                                    currentFilename
                                                            ) {
                                                                const globalCompanyId =
                                                                    editor
                                                                        .global
                                                                        ?.data
                                                                        ?.company_id;
                                                                const companyId =
                                                                    globalCompanyId ||
                                                                    editor.site
                                                                        ?.data
                                                                        ?.company_id;
                                                                if (
                                                                    !companyId
                                                                ) {
                                                                    throw new Error(
                                                                        'Missing company ID.'
                                                                    );
                                                                }

                                                                const updatedImage =
                                                                    await renameImageAsset(
                                                                        {
                                                                            companyId,
                                                                            currentFilename,
                                                                            currentUrl:
                                                                                currentSiteImage.url,
                                                                            nextFilename,
                                                                        }
                                                                    );

                                                                editor.site.updateImage?.(
                                                                    {
                                                                        image_id:
                                                                            currentSiteImage.image_id,
                                                                        data: {
                                                                            filename:
                                                                                updatedImage?.filename ||
                                                                                nextFilename,
                                                                            url:
                                                                                updatedImage?.url ||
                                                                                currentSiteImage.url,
                                                                        },
                                                                    }
                                                                );
                                                            } else {
                                                                editor.site.updateImage?.(
                                                                    {
                                                                        image_id:
                                                                            currentSiteImage.image_id,
                                                                        data: {
                                                                            filename:
                                                                                currentFilename,
                                                                        },
                                                                    }
                                                                );
                                                            }
                                                        } else {
                                                            onChange({
                                                                file,
                                                                key: pillHeaderEditPath,
                                                                value: isMediaFilenamePillEditor
                                                                    ? replaceMediaFilename(
                                                                          currentHeaderValue,
                                                                          buildFilenameFromParts(
                                                                              nextHeaderDraft,
                                                                              currentFilenameParts.extension
                                                                          )
                                                                      )
                                                                    : nextHeaderDraft,
                                                            });
                                                        }
                                                        setArrayItemExpanded(
                                                            pillHeaderEditStateKey,
                                                            false
                                                        );
                                                        clearInlineFieldDraft(
                                                            pillHeaderEditPath
                                                        );
                                                    } catch (error) {
                                                        notifications.push({
                                                            title: 'Failed to save file name',
                                                            variant: 'error',
                                                            error,
                                                        });
                                                    }
                                                }}
                                                className="mt-1 flex min-w-0 items-center gap-1.5"
                                            >
                                                <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                    <input
                                                        type="text"
                                                        value={readInlineFieldDraft(
                                                            pillHeaderEditPath,
                                                            isMediaFilenamePillEditor
                                                                ? splitFilenameParts(
                                                                      getSiteImageByUrl(
                                                                          readValueAtPath(
                                                                              component_props,
                                                                              pillHeaderEditPath
                                                                          ) ??
                                                                              ''
                                                                      )
                                                                          ?.filename ||
                                                                          extractMediaFilename(
                                                                              readValueAtPath(
                                                                                  component_props,
                                                                                  pillHeaderEditPath
                                                                              ) ??
                                                                                  ''
                                                                          )
                                                                  ).name
                                                                : readValueAtPath(
                                                                      component_props,
                                                                      pillHeaderEditPath
                                                                  ) ?? ''
                                                        )}
                                                        onChange={(event) =>
                                                            setInlineFieldDraft(
                                                                pillHeaderEditPath,
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        placeholder={
                                                            isMediaFilenamePillEditor
                                                                ? 'File Name'
                                                                : prettyLabel(
                                                                      pillHeaderEditableField
                                                                  )
                                                        }
                                                        className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                        autoFocus
                                                    />
                                                </div>
                                                <button
                                                    type="submit"
                                                    title={`Save ${
                                                        isMediaFilenamePillEditor
                                                            ? 'file name'
                                                            : prettyLabel(
                                                                  pillHeaderEditableField
                                                              )
                                                    }`}
                                                    className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                                >
                                                    <CheckIcon className="h-3 w-3" />
                                                </button>
                                                <button
                                                    type="button"
                                                    title={`Cancel ${
                                                        isMediaFilenamePillEditor
                                                            ? 'file name'
                                                            : prettyLabel(
                                                                  pillHeaderEditableField
                                                              )
                                                    } edit`}
                                                    onClick={() => {
                                                        setArrayItemExpanded(
                                                            pillHeaderEditStateKey,
                                                            false
                                                        );
                                                        clearInlineFieldDraft(
                                                            pillHeaderEditPath
                                                        );
                                                    }}
                                                    className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                                >
                                                    <XMarkIcon className="h-3 w-3" />
                                                </button>
                                            </form>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        draggable
                                        className={`${effectiveCollapsibleToneClasses.buttonHover} flex min-w-0 flex-1 cursor-grab items-center gap-3 rounded-lg p-1 text-left transition-colors ease-in focus-visible:outline-none active:cursor-grabbing`}
                                        aria-expanded={isExpanded}
                                        aria-controls={detailsId}
                                        onDragStart={(event) =>
                                            handleArrayItemDragStart(
                                                event,
                                                itemArrayKey,
                                                rowIndex
                                            )
                                        }
                                        onDragEnd={handleArrayItemDragEnd}
                                        onClick={() => {
                                            if (suppressDragClickRef.current) {
                                                suppressDragClickRef.current = false;
                                                return;
                                            }
                                            toggleArrayItemExpanded(
                                                key,
                                                defaultExpanded
                                            );
                                        }}
                                    >
                                        <div className="min-w-0">
                                            {itemMetaLabel ? (
                                                <div
                                                    className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${
                                                        effectiveCollapsibleToneClasses.metaText
                                                    } ${
                                                        hasImageBackedCollapsibleHeader
                                                            ? 'drop-shadow-[0_1px_1px_rgba(0,0,0,0.55)]'
                                                            : ''
                                                    }`}
                                                >
                                                    {itemMetaLabel}
                                                </div>
                                            ) : null}
                                            <div className="group mt-1 flex min-w-0 items-center gap-1">
                                                <div
                                                    className={`truncate text-sm font-semibold ${collapsibleTitleClassName}`}
                                                >
                                                    {itemDisplayTitle}
                                                </div>
                                                {pillHeaderEditPath &&
                                                !isMediaFilenamePillEditor ? (
                                                    <button
                                                        type="button"
                                                        title={`Edit ${
                                                            isMediaFilenamePillEditor
                                                                ? 'file name'
                                                                : prettyLabel(
                                                                      pillHeaderEditableField
                                                                  )
                                                        }`}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setInlineFieldDraft(
                                                                pillHeaderEditPath,
                                                                isMediaFilenamePillEditor
                                                                    ? splitFilenameParts(
                                                                          getSiteImageByUrl(
                                                                              readValueAtPath(
                                                                                  component_props,
                                                                                  pillHeaderEditPath
                                                                              ) ??
                                                                                  ''
                                                                          )
                                                                              ?.filename ||
                                                                              extractMediaFilename(
                                                                                  readValueAtPath(
                                                                                      component_props,
                                                                                      pillHeaderEditPath
                                                                                  ) ??
                                                                                      ''
                                                                              )
                                                                      ).name
                                                                    : readValueAtPath(
                                                                          component_props,
                                                                          pillHeaderEditPath
                                                                      ) ?? ''
                                                            );
                                                            setArrayItemExpanded(
                                                                pillHeaderEditStateKey,
                                                                true
                                                            );
                                                        }}
                                                        className={
                                                            collapsibleHeaderEditButtonClassName
                                                        }
                                                    >
                                                        <PencilSquareIcon className="h-3.5 w-3.5" />
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center gap-1">
                                    {arrayItemControls}
                                    <button
                                        type="button"
                                        title={
                                            isExpanded
                                                ? 'Collapse item'
                                                : 'Expand item'
                                        }
                                        className={
                                            collapsibleHeaderChevronButtonClassName
                                        }
                                        aria-expanded={isExpanded}
                                        aria-controls={detailsId}
                                        onClick={() =>
                                            toggleArrayItemExpanded(
                                                key,
                                                defaultExpanded
                                            )
                                        }
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 ${
                                                effectiveCollapsibleToneClasses.chevronText
                                            } transition-transform duration-200 ${
                                                isExpanded ? 'rotate-180' : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            </div>

                            {isExpanded ? (
                                <div
                                    id={detailsId}
                                    className="bg-white px-3 pb-3 pt-2"
                                >
                                    {objectChildren}
                                </div>
                            ) : null}
                        </div>
                        {showDropPlaceholderAfter ? dropPlaceholder : null}
                    </div>
                );
            }

            return (
                <div key={key} className={containerClassName}>
                    {headerTitle ? (
                        <strong className="mb-2 mt-6 block text-sm font-semibold text-primary-700">
                            {headerTitle}
                        </strong>
                    ) : null}

                    {is_parent_array && showTopControls && (
                        <div className="flex items-center gap-2">
                            <div className="h-0.5 w-full bg-neutral-700 opacity-100" />
                            {arrayItemControls}
                            <div className="h-0.5 w-full bg-neutral-700" />
                        </div>
                    )}

                    {objectChildren}
                </div>
            );
        }

        const is_image = looksLikeImageKey(key);
        const isReviewComponent = [
            'Reviews',
            'ReviewHero',
            'ReviewSlider',
        ].includes(componentName);
        const isReviewAuthorSourceField =
            isReviewComponent &&
            keyLeaf(key).toLowerCase() === 'source' &&
            String(key).toLowerCase().includes('.author.');
        const isReviewAuthorIconField =
            isReviewComponent &&
            keyLeaf(key).toLowerCase() === 'icon' &&
            String(key).toLowerCase().includes('.author.');
        const reviewAuthorSource =
            isReviewAuthorIconField &&
            readValueAtPath(component_props, replaceLeafInPath(key, 'Source'));
        const normalizedImageVal =
            isReviewAuthorIconField &&
            value &&
            typeof value === 'object' &&
            'value' in value
                ? value.value
                : value;
        const reviewAuthorAutoIcon =
            isReviewAuthorIconField && !normalizedImageVal
                ? mapSourceToIconVal(reviewAuthorSource)
                : '';

        if (isButtonKey(key) && shouldHideButtonLinkField(key, component_props))
            return null;

        const isShowcaseSlider =
            componentName === 'ImageSlider' &&
            keyLeaf(key).toLowerCase() === 'showcase';
        const isReviewLayoutField =
            componentName === 'Reviews' &&
            keyLeaf(key).toLowerCase() === 'layout';
        const isReviewSourceField =
            componentName === 'Reviews' &&
            keyLeaf(key).toLowerCase() === 'reviewsource';
        const isNiceJobLayoutField =
            componentName === 'Reviews' &&
            keyLeaf(key).toLowerCase() === 'nicejoblayout';
        const isReviewFeatureIconField =
            isReviewComponent && keyLeaf(key).toLowerCase() === 'featureicon';
        const isReviewFeatureIconPackField =
            isReviewComponent &&
            keyLeaf(key).toLowerCase() === 'featureiconpack';
        const isReviewFeatureIconStyleField =
            isReviewComponent &&
            keyLeaf(key).toLowerCase() === 'featureiconstyle';
        const isItemGridFeatureIconField =
            componentName === 'ItemGrid' &&
            keyLeaf(key).toLowerCase() === 'icon' &&
            /features\[\d+\]\./i.test(String(key));
        const isNavIconPickerField =
            componentName === 'Nav' &&
            ['ctamobileicon', 'phoneicon', 'bannerbuttonicon'].includes(
                keyLeaf(key).toLowerCase()
            );
        const isFontAwesomeIconField =
            (keyLeaf(key).toLowerCase() === 'icon' || isNavIconPickerField) &&
            (!['Reviews', 'ReviewSlider', 'ReviewFloat'].includes(
                componentName
            ) ||
                isNavIconPickerField) &&
            !isReviewFeatureIconField &&
            !isReviewAuthorIconField &&
            !isItemGridFeatureIconField;
        const reviewLayoutVal = String(value || 'grid').toLowerCase();
        const reviewFeatureIconFallback =
            componentName === 'Reviews' ? 'quote' : 'stars';
        const reviewFeatureIconVal = String(
            component_props?.FeatureIcon || value || reviewFeatureIconFallback
        ).toLowerCase();
        const featureIconPackVal = (
            component_props?.FeatureIconPack || 'classic'
        ).toLowerCase();
        const featureIconStyleVal = (
            component_props?.FeatureIconStyle || 'solid'
        ).toLowerCase();
        const reviewPackOptions = [
            { key: 'classic', label: 'Classic' },
            { key: 'duotone', label: 'Duotone' },
            { key: 'sharp', label: 'Sharp' },
            { key: 'sharp-duotone', label: 'Sharp Duotone' },
        ];
        const reviewStyleOptions = [
            { key: 'solid', label: 'Solid' },
            { key: 'regular', label: 'Regular' },
            { key: 'light', label: 'Light' },
            { key: 'thin', label: 'Thin' },
        ];
        const sliderVal = (() => {
            const n = Number(value);
            if (!Number.isFinite(n)) return 2;
            return Math.min(4, Math.max(1, Math.round(n)));
        })();
        const schemaEntry = schemaEntryForKey(componentSchema, key);
        const enumOptions = Array.isArray(schemaEntry?.enum)
            ? schemaEntry.enum
            : undefined;
        const isEnumField =
            !isShowcaseSlider &&
            !isReviewLayoutField &&
            !isReviewSourceField &&
            !isNiceJobLayoutField &&
            !isNiceJobFiltersField &&
            !isReviewFeatureIconField &&
            !isReviewFeatureIconPackField &&
            !isReviewFeatureIconStyleField &&
            Array.isArray(enumOptions) &&
            enumOptions.length > 0;
        const isFloatingActionTypeField =
            componentName === 'FloatingAction' &&
            keyLeaf(key).toLowerCase() === 'type';
        const isHeroHeaderSizeField =
            HERO_SUB_HEADER_COMPONENTS.has(componentName) &&
            keyLeaf(key).toLowerCase() === 'headersize';
        const isHeroFullBGSubHeaderField =
            componentName === 'HeroFullBG' &&
            keyLeaf(key).toLowerCase() === 'subheader';
        const fieldContainerClassName =
            componentName === 'HeroFullBG' &&
            (isHeroHeaderSizeField || isHeroFullBGSubHeaderField)
                ? compactRow
                    ? 'mb-3 w-full'
                    : 'mb-4 w-full'
                : containerClassName;
        const isBannerColorSchemeField =
            componentName === 'Nav' &&
            keyLeaf(key).toLowerCase() === 'bannercolorscheme';
        const isPageSchemaMarkupField =
            isPageComponent &&
            keyLeaf(key).toLowerCase() === PAGE_SCHEMA_MARKUP_KEY.toLowerCase();
        const isPagePublishedField =
            isPageComponent &&
            keyLeaf(key).toLowerCase() === PAGE_PUBLISHED_KEY.toLowerCase();
        const isAltTextField = isAltTextFieldKey(key);
        const altTextSourceImage = isAltTextField
            ? inferAltTextSourceImageField(key, component_props)
            : null;
        const isAltTextGenerating = altTextGeneratingKey === key;
        const unpublishRedirectMessage =
            '(e.g. /contact). Leave blank for 404.';
        const isSchemaMarkupGenerating =
            isPageSchemaMarkupField && schemaMarkupGenerating;
        if (isReviewFeatureIconPackField || isReviewFeatureIconStyleField) {
            return null; // rendered inline with FeatureIcon field
        }
        if (isReviewAuthorIconField) {
            return null;
        }
        if (isNiceJobFiltersField && !useNiceJobReviews) {
            return null;
        }
        const schemaDefaultValue =
            typeof schemaEntry?.value === 'string' ||
            typeof schemaEntry?.value === 'number'
                ? String(schemaEntry.value)
                : '';
        const selectValue =
            typeof value === 'string' || typeof value === 'number'
                ? String(value)
                : schemaDefaultValue
                ? schemaDefaultValue
                : '';
        const showPrompt =
            Boolean(schemaEntry?.AI) &&
            isPromptableKey(key, componentName) &&
            !isPageSchemaMarkupField &&
            !isAltTextField &&
            !is_image &&
            !isShowcaseSlider &&
            !isReviewLayoutField &&
            !isReviewFeatureIconField &&
            !isNiceJobFiltersField;

        return (
            <div key={key} className={fieldContainerClassName}>
                {showPrompt ? (
                    <PromptInput
                        label={
                            <label className="block text-xs font-semibold leading-5 text-primary-700">
                                {prettyLabel(key)}
                            </label>
                        }
                        file={file}
                        componentName={componentName}
                        element={key}
                    />
                ) : isPageSchemaMarkupField ? (
                    <div className="mb-1 flex items-center justify-between gap-2">
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            {prettyLabel(key)}
                        </label>
                        <button
                            type="button"
                            onClick={() => handleGenerateSchemaMarkup({ key })}
                            disabled={isSchemaMarkupGenerating}
                            className="inline-flex items-center gap-1 rounded-md bg-primary-700 px-2 py-1 text-[10px] font-semibold text-white shadow-sm transition-colors duration-200 ease-in-out hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isSchemaMarkupGenerating ? (
                                <LoadingSpinner size="small" color="#FFFFFF" />
                            ) : (
                                <SparklesIcon className="h-4 w-4" />
                            )}
                            <span>Generate</span>
                        </button>
                    </div>
                ) : isPagePublishedField && !isPagePublished ? (
                    <div className="mb-1 grid grid-cols-[52px_minmax(0,1fr)] items-center gap-4">
                        <label className="w-[52px] shrink-0 whitespace-nowrap text-xs font-semibold leading-5 text-primary-700">
                            {prettyLabel(key)}
                        </label>
                        <label className="min-w-0 flex-1 text-xs font-semibold leading-5 text-primary-700">
                            {prettyLabel(PAGE_UNPUBLISH_REDIRECT_KEY)}
                        </label>
                    </div>
                ) : hideLabel ? null : (
                    <div className="mb-1 flex items-center justify-between gap-2">
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            {customLabel || prettyLabel(key)}
                        </label>
                        {isAltTextField ? (
                            <button
                                type="button"
                                onClick={() =>
                                    handleGenerateAltText({
                                        key,
                                        imageUrl: altTextSourceImage?.value,
                                    })
                                }
                                disabled={
                                    !ALT_TEXT_GENERATION_ENABLED ||
                                    isAltTextGenerating ||
                                    !altTextSourceImage
                                }
                                className={`${INLINE_GENERATE_BUTTON_CLASSNAME} ${
                                    ALT_TEXT_GENERATION_ENABLED
                                        ? ''
                                        : 'border-neutral-200 bg-neutral-100 text-neutral-400 hover:border-neutral-200 hover:bg-neutral-100'
                                }`}
                            >
                                {isAltTextGenerating ? (
                                    <LoadingSpinner
                                        size="small"
                                        color="#1D4ED8"
                                    />
                                ) : (
                                    <SparklesIcon className="h-3 w-3" />
                                )}
                                <span>Generate</span>
                            </button>
                        ) : null}
                    </div>
                )}

                {isItemGridFeatureIconField ? (
                    <div className="mt-2" data-inline-icon-field={key}>
                        <FontAwesomeIconPicker
                            value={typeof value === 'string' ? value : ''}
                            onChange={(next) =>
                                onChange({ file, key, value: next })
                            }
                        />
                    </div>
                ) : isFontAwesomeIconField ? (
                    <div className="mt-2" data-inline-icon-field={key}>
                        <FontAwesomeIconPicker
                            value={typeof value === 'string' ? value : ''}
                            collapseByDefault={
                                componentName === 'Nav' &&
                                keyLeaf(key).toLowerCase() === 'ctamobileicon'
                            }
                            onChange={(next) =>
                                onChange({ file, key, value: next })
                            }
                        />
                    </div>
                ) : isBannerColorSchemeField ? (
                    <div className="mt-1">
                        <div className="relative rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                            <Listbox
                                value={
                                    BANNER_COLOR_SCHEMES.some(
                                        (o) => o.value === selectValue
                                    )
                                        ? selectValue
                                        : BANNER_COLOR_SCHEMES[0].value
                                }
                                onChange={(val) =>
                                    onChange({ file, key, value: val })
                                }
                            >
                                <div className="relative">
                                    <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-xs leading-5 text-neutral-800 focus:outline-none md:text-sm">
                                        <span className="truncate">
                                            {BANNER_COLOR_SCHEMES.find(
                                                (o) => o.value === selectValue
                                            )?.label ||
                                                BANNER_COLOR_SCHEMES[0].label}
                                        </span>
                                        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                            <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                                        </span>
                                    </Listbox.Button>
                                    <Transition
                                        as={React.Fragment}
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100 scale-100"
                                        leaveTo="opacity-0 scale-95"
                                    >
                                        <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                            {BANNER_COLOR_SCHEMES.map((opt) => (
                                                <Listbox.Option
                                                    key={opt.value}
                                                    value={opt.value}
                                                    className={({
                                                        active,
                                                        selected,
                                                    }) =>
                                                        [
                                                            'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                            active
                                                                ? 'bg-primary-50 text-primary-700'
                                                                : 'text-neutral-800',
                                                            selected
                                                                ? 'font-medium'
                                                                : '',
                                                        ].join(' ')
                                                    }
                                                >
                                                    {({ selected }) => (
                                                        <>
                                                            <span className="truncate">
                                                                {opt.label}
                                                            </span>
                                                            {selected && (
                                                                <CheckIcon className="h-4 w-4 text-primary-600" />
                                                            )}
                                                        </>
                                                    )}
                                                </Listbox.Option>
                                            ))}
                                        </Listbox.Options>
                                    </Transition>
                                </div>
                            </Listbox>
                        </div>
                    </div>
                ) : isReviewAuthorSourceField ? (
                    <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                        {REVIEW_SOURCE_OPTIONS.map((option) => {
                            const sourceValue = String(value || '')
                                .toLowerCase()
                                .trim();
                            const reviewIconKey = replaceLeafInPath(
                                key,
                                'Icon'
                            );
                            const mappedSourceIcon =
                                mapSourceToIconVal(sourceValue);
                            const currentIconPath = String(
                                readValueAtPath(
                                    component_props,
                                    reviewIconKey
                                ) ?? ''
                            ).trim();
                            const customPreviewSrc =
                                option.value === 'custom' &&
                                currentIconPath &&
                                !REVIEW_SOURCE_OPTIONS.some(
                                    (preset) =>
                                        preset.icon &&
                                        preset.icon === currentIconPath
                                )
                                    ? currentIconPath
                                    : '';
                            const isCustomActive =
                                !['google', 'nicejob', 'facebook'].includes(
                                    sourceValue
                                ) ||
                                (currentIconPath &&
                                    currentIconPath !== mappedSourceIcon &&
                                    !REVIEW_SOURCE_OPTIONS.some(
                                        (preset) =>
                                            preset.icon &&
                                            preset.icon === currentIconPath
                                    ));
                            const isActive =
                                option.value === 'custom'
                                    ? isCustomActive
                                    : sourceValue === option.value;

                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => {
                                        onChange({
                                            file,
                                            key,
                                            value: option.value,
                                        });
                                        if (option.icon) {
                                            onChange({
                                                file,
                                                key: reviewIconKey,
                                                value: option.icon,
                                            });
                                        } else {
                                            handleOpenMediaPanel(
                                                reviewIconKey,
                                                currentIconPath
                                            );
                                        }
                                    }}
                                    className={`flex min-h-[52px] flex-col items-center justify-center rounded-xl border px-2 py-1.5 text-center transition-all ${
                                        isActive
                                            ? 'border-primary-300 bg-primary-50 shadow-sm ring-1 ring-primary-200'
                                            : 'hover:bg-primary-50/50 border-neutral-200 bg-white hover:border-primary-200'
                                    }`}
                                >
                                    {option.icon || customPreviewSrc ? (
                                        <img
                                            src={
                                                option.icon || customPreviewSrc
                                            }
                                            alt={option.label}
                                            className="mb-1 h-5 w-auto object-contain"
                                        />
                                    ) : (
                                        <div className="mb-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 bg-neutral-50 text-neutral-500">
                                            <RectangleGroupIcon
                                                className="h-3 w-3"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    )}
                                    <span className="leading-3.5 text-[10px] font-semibold text-neutral-800">
                                        {option.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                ) : typeof value === 'boolean' ||
                  (typeof value === 'string' &&
                      ['true', 'false'].includes(
                          value.trim().toLowerCase()
                      )) ? (
                    <div className="mt-1">
                        <div
                            className={
                                isPagePublishedField && !isPagePublished
                                    ? 'grid grid-cols-[52px_minmax(0,1fr)] items-center gap-4'
                                    : 'flex items-center'
                            }
                        >
                            {isPagePublishedField && !isPagePublished ? (
                                <div className="flex h-5 items-center">
                                    <Switch
                                        checked={
                                            typeof value === 'string'
                                                ? value.trim().toLowerCase() ===
                                                  'true'
                                                : !!value
                                        }
                                        onChange={(checked) =>
                                            handleBooleanToggle(key, checked)
                                        }
                                        ariaLabel={prettyLabel(key)}
                                    />
                                </div>
                            ) : (
                                <Switch
                                    checked={
                                        typeof value === 'string'
                                            ? value.trim().toLowerCase() ===
                                              'true'
                                            : !!value
                                    }
                                    onChange={(checked) =>
                                        handleBooleanToggle(key, checked)
                                    }
                                    ariaLabel={prettyLabel(key)}
                                />
                            )}
                            {componentName === 'Popup' &&
                                keyLeaf(key).toLowerCase() === 'enabled' && (
                                    <button
                                        type="button"
                                        onClick={handlePopupTest}
                                        className="ml-3 inline-flex h-5 items-center rounded-full bg-secondary-700 px-2.5 text-[11px] font-semibold leading-none text-white shadow-sm transition-colors hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-1"
                                    >
                                        Preview
                                    </button>
                                )}
                            {isPagePublishedField && !isPagePublished && (
                                <div className="min-w-0">
                                    <div className="rounded-lg bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                        <input
                                            type="text"
                                            value={unpublishRedirectValue}
                                            onChange={(e) =>
                                                onChange({
                                                    file,
                                                    key: PAGE_UNPUBLISH_REDIRECT_KEY,
                                                    value: e.target.value,
                                                })
                                            }
                                            onBlur={(e) => {
                                                const normalized =
                                                    normalizeUnpublishRedirectTarget(
                                                        e.target.value
                                                    );
                                                onChange({
                                                    file,
                                                    key: PAGE_UNPUBLISH_REDIRECT_KEY,
                                                    value: normalized,
                                                });
                                            }}
                                            placeholder={
                                                unpublishRedirectMessage
                                            }
                                            className="block h-5 w-full rounded-[7px] border-0 px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-[11px]"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : isHeroHeaderSizeField ? (
                    (() => {
                        const currentValue = HERO_HEADER_SIZE_OPTIONS.includes(
                            selectValue
                        )
                            ? selectValue
                            : 'large';
                        const currentIndex = Math.max(
                            0,
                            HERO_HEADER_SIZE_OPTIONS.indexOf(currentValue)
                        );
                        return (
                            <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                <div className="rounded-[10px] bg-white px-3 py-2">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="range"
                                            min={0}
                                            max={
                                                HERO_HEADER_SIZE_OPTIONS.length -
                                                1
                                            }
                                            step={1}
                                            value={currentIndex}
                                            onChange={(e) => {
                                                const nextIndex = Number(
                                                    e.target.value
                                                );
                                                onChange({
                                                    file,
                                                    key,
                                                    value:
                                                        HERO_HEADER_SIZE_OPTIONS[
                                                            nextIndex
                                                        ] || 'large',
                                                });
                                            }}
                                            className="tw-slider flex-1 cursor-pointer"
                                        />
                                        <span className="w-20 text-right text-xs font-semibold text-neutral-700">
                                            {
                                                HERO_HEADER_SIZE_LABELS[
                                                    currentValue
                                                ]
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })()
                ) : isFloatingActionTypeField ? (
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            {
                                value: 'book',
                                label: 'Book',
                                icon: 'fa-solid fa-calendar-check',
                            },
                            {
                                value: 'call',
                                label: 'Call',
                                icon: 'fa-solid fa-phone',
                            },
                            {
                                value: 'popup',
                                label: 'Popup',
                                icon: 'fa-solid fa-tag',
                            },
                            {
                                value: 'text',
                                label: 'Text',
                                icon: 'fa-solid fa-message-sms',
                            },
                            {
                                value: 'whatsapp',
                                label: 'WhatsApp',
                                icon: 'fa-brands fa-whatsapp',
                            },
                            {
                                value: 'messenger',
                                label: 'Messenger',
                                icon: 'fa-brands fa-facebook-messenger',
                            },
                            {
                                value: 'email',
                                label: 'Email',
                                icon: 'fa-solid fa-envelope',
                            },
                            {
                                value: 'directions',
                                label: 'Directions',
                                icon: 'fa-solid fa-location-dot',
                            },
                        ].map((option) => {
                            const selectedType = String(
                                component_props?.Type ||
                                    component_props?.Action ||
                                    'text'
                            )
                                .trim()
                                .toLowerCase();
                            const selected = selectedType === option.value;

                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    aria-pressed={selected}
                                    aria-label={`${option.label} action`}
                                    onClick={() =>
                                        onChange({
                                            file,
                                            key,
                                            value: option.value,
                                        })
                                    }
                                    className={`flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl border p-2 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
                                        selected
                                            ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-sm'
                                            : 'border-neutral-300 bg-white text-neutral-600 hover:border-primary-300 hover:text-primary-700'
                                    }`}
                                >
                                    <i
                                        className={`${option.icon} text-xl`}
                                        aria-hidden="true"
                                    />
                                    <span>{option.label}</span>
                                </button>
                            );
                        })}
                    </div>
                ) : isEnumField ? (
                    <div className="relative rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                        <Listbox
                            value={
                                enumOptions?.includes(selectValue)
                                    ? selectValue
                                    : enumOptions?.[0] ?? ''
                            }
                            onChange={(val) =>
                                onChange({ file, key, value: val })
                            }
                        >
                            <div className="relative">
                                <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-xs leading-5 text-neutral-800 focus:outline-none md:text-sm">
                                    <span className="truncate">
                                        {selectValue ||
                                            enumOptions?.[0] ||
                                            'Select an option'}
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                        <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                                    </span>
                                </Listbox.Button>
                                <Transition
                                    as={React.Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                        {enumOptions?.map((option) => (
                                            <Listbox.Option
                                                key={option}
                                                value={option}
                                                className={({
                                                    active,
                                                    selected,
                                                }) =>
                                                    [
                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                        active
                                                            ? 'bg-primary-50 text-primary-700'
                                                            : 'text-neutral-800',
                                                        selected
                                                            ? 'font-medium'
                                                            : '',
                                                    ].join(' ')
                                                }
                                            >
                                                {({ selected }) => (
                                                    <>
                                                        <span className="truncate">
                                                            {option}
                                                        </span>
                                                        {selected && (
                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        </Listbox>
                    </div>
                ) : is_image ? (
                    (() => {
                        const mediaPositionKey = getMediaPositionKey(
                            componentName,
                            key
                        );
                        const mediaLeaf = keyLeaf(key).toLowerCase();
                        const hideEmptyPreview =
                            mediaLeaf === 'beforeimage' ||
                            mediaLeaf === 'afterimage' ||
                            mediaLeaf === 'beforeimageurl' ||
                            mediaLeaf === 'afterimageurl';
                        const isBeforeAfterMediaField = hideEmptyPreview;
                        const showBeforeAfterUploadFallback =
                            isBeforeAfterMediaField &&
                            !hideImageActionButtons &&
                            !hideInlineMediaPanel;
                        const mediaPositionFallback =
                            componentName === 'Service'
                                ? component_props?.MediaPosition
                                : undefined;
                        const hasPositionControl = Boolean(mediaPositionKey);
                        const hasAltTextControl = Boolean(
                            inlineMediaAltProps?.key
                        );
                        const hasCaptionControl = Boolean(
                            inlineMediaCaptionProps?.key
                        );
                        const selectedSiteImage =
                            getSiteImageByUrl(normalizedImageVal);
                        const selectedSiteImageAltText =
                            readSiteImageAltText(selectedSiteImage);
                        const componentAltText = String(
                            inlineMediaAltProps?.value ?? ''
                        );
                        const inlineMediaShortcutAltValue =
                            componentAltText || selectedSiteImageAltText;
                        const handleInlineMediaAltShortcutChange = (
                            nextValue
                        ) => {
                            syncImageAltText({
                                imageUrl: normalizedImageVal,
                                altText: nextValue,
                                componentAltKey: inlineMediaAltProps?.key,
                            });
                        };
                        const positionControlNode = hasPositionControl ? (
                            <div className="min-w-0">
                                <label className="block text-xs font-semibold leading-5 text-primary-700">
                                    Position
                                </label>
                                <div className="mt-1">
                                    <ImageUpload
                                        company_id={
                                            editor.global.data.company_id
                                        }
                                        file={file}
                                        element={key}
                                        onChange={onChange}
                                        src={
                                            reviewAuthorAutoIcon ||
                                            normalizedImageVal
                                        }
                                        hideEmptyPreview={hideEmptyPreview}
                                        previewSize={previewSize}
                                        layout="stacked"
                                        showPreview={false}
                                        showActionButtons={false}
                                        positionValue={
                                            mediaPositionKey
                                                ? getByPath(
                                                      component_props,
                                                      mediaPositionKey
                                                  ) ?? mediaPositionFallback
                                                : undefined
                                        }
                                        onPositionChange={
                                            mediaPositionKey
                                                ? (val) =>
                                                      onChange({
                                                          file,
                                                          key: mediaPositionKey,
                                                          value: val,
                                                      })
                                                : undefined
                                        }
                                    />
                                </div>
                            </div>
                        ) : null;
                        const altTextControlNode = hasAltTextControl ? (
                            <div className="min-w-0">
                                <div className="min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Alt Text
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                inlineMediaAltProps?.key
                                                    ? handleGenerateAltText({
                                                          key: inlineMediaAltProps.key,
                                                          imageUrl:
                                                              normalizedImageVal,
                                                          applyAltText:
                                                              handleInlineMediaAltShortcutChange,
                                                      })
                                                    : undefined
                                            }
                                            disabled={
                                                !ALT_TEXT_GENERATION_ENABLED ||
                                                !inlineMediaAltProps?.key ||
                                                altTextGeneratingKey ===
                                                    inlineMediaAltProps.key ||
                                                !isSelectedEditorMedia(
                                                    normalizedImageVal
                                                )
                                            }
                                            className={`${INLINE_GENERATE_BUTTON_CLASSNAME} ${
                                                ALT_TEXT_GENERATION_ENABLED
                                                    ? ''
                                                    : 'border-neutral-200 bg-neutral-100 text-neutral-400 hover:border-neutral-200 hover:bg-neutral-100'
                                            }`}
                                        >
                                            {altTextGeneratingKey ===
                                            inlineMediaAltProps?.key ? (
                                                <LoadingSpinner
                                                    size="small"
                                                    color="#1D4ED8"
                                                />
                                            ) : (
                                                <SparklesIcon className="h-3 w-3" />
                                            )}
                                            <span>Generate</span>
                                        </button>
                                    </div>
                                    <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                        <textarea
                                            value={inlineMediaShortcutAltValue}
                                            onChange={(event) =>
                                                handleInlineMediaAltShortcutChange(
                                                    event.target.value
                                                )
                                            }
                                            rows={2}
                                            className="block min-h-[52px] w-full resize-y rounded-[10px] border-0 px-3 py-2 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : null;
                        const captionControlNode = hasCaptionControl ? (
                            <div className="min-w-0">
                                <label className="block text-xs font-semibold leading-5 text-primary-700">
                                    Caption
                                </label>
                                <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <textarea
                                        value={String(
                                            inlineMediaCaptionProps?.value ?? ''
                                        )}
                                        onChange={(event) =>
                                            onChange({
                                                file,
                                                key: inlineMediaCaptionProps.key,
                                                value: event.target.value,
                                            })
                                        }
                                        rows={2}
                                        placeholder="Add an image caption"
                                        className="block min-h-[52px] w-full resize-y rounded-[10px] border-0 px-3 py-2 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                    />
                                </div>
                            </div>
                        ) : null;
                        if (inlineMediaPanelProps?.layout === 'media-row') {
                            return (
                                <div className="flex flex-col gap-2.5 pb-2">
                                    {!hideInlineMediaPanel &&
                                    (!isBeforeAfterMediaField ||
                                        forceInlineMediaPanel) ? (
                                        <div className="pt-0.5">
                                            <MediaPanel
                                                inline
                                                hidePreview
                                                currentValue={
                                                    normalizedImageVal
                                                }
                                                inlineAltValue={
                                                    inlineMediaShortcutAltValue
                                                }
                                                onInlineAltChange={
                                                    handleInlineMediaAltShortcutChange
                                                }
                                                onSelectImage={(val) => {
                                                    onChange({
                                                        file,
                                                        key,
                                                        value: val,
                                                    });
                                                    if (
                                                        inlineMediaAltProps?.key
                                                    ) {
                                                        const selectedImage =
                                                            getSiteImageByUrl(
                                                                val
                                                            );
                                                        if (selectedImage) {
                                                            onChange({
                                                                file,
                                                                key: inlineMediaAltProps.key,
                                                                value: readSiteImageAltText(
                                                                    selectedImage
                                                                ),
                                                            });
                                                        }
                                                    }
                                                }}
                                                onSelectVideo={(val) => {
                                                    onChange({
                                                        file,
                                                        key,
                                                        value: val,
                                                    });
                                                }}
                                                onSelectCustom={(val) => {
                                                    onChange({
                                                        file,
                                                        key,
                                                        value: val,
                                                    });
                                                }}
                                                onPreviewSelect={() => {}}
                                            />
                                        </div>
                                    ) : null}
                                    {hasAltTextControl
                                        ? altTextControlNode
                                        : null}
                                    {hasCaptionControl
                                        ? captionControlNode
                                        : null}
                                    {hasPositionControl
                                        ? positionControlNode
                                        : null}
                                </div>
                            );
                        }
                        return (
                            <div className="flex flex-col justify-between gap-2">
                                {showBeforeAfterUploadFallback ? (
                                    <div className="flex items-center justify-end">
                                        <button
                                            type="button"
                                            title="Upload image"
                                            className={
                                                INLINE_ADD_BUTTON_CLASSNAME
                                            }
                                            onClick={() =>
                                                mediaUploadInputRefs.current[
                                                    key
                                                ]?.click()
                                            }
                                        >
                                            <ArrowUpTrayIcon
                                                className="h-3 w-3"
                                                aria-hidden="true"
                                            />
                                        </button>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={(node) => {
                                                if (node) {
                                                    mediaUploadInputRefs.current[
                                                        key
                                                    ] = node;
                                                } else {
                                                    delete mediaUploadInputRefs
                                                        .current[key];
                                                }
                                            }}
                                            onChange={(event) =>
                                                handleInlineMediaUpload(
                                                    key,
                                                    event
                                                )
                                            }
                                        />
                                    </div>
                                ) : null}
                                <ImageUpload
                                    company_id={editor.global.data.company_id}
                                    file={file}
                                    element={key}
                                    onChange={onChange}
                                    removeButtonIcon={
                                        componentName === 'Popup' &&
                                        keyLeaf(key).toLowerCase() === 'image'
                                            ? 'trash'
                                            : undefined
                                    }
                                    removeButtonLabel={
                                        componentName === 'Popup' &&
                                        keyLeaf(key).toLowerCase() === 'image'
                                            ? 'Remove popup image'
                                            : undefined
                                    }
                                    src={
                                        reviewAuthorAutoIcon ||
                                        normalizedImageVal
                                    }
                                    hideEmptyPreview={hideEmptyPreview}
                                    previewSize={previewSize}
                                    layout={layout}
                                    midContent={midContent}
                                    showPreview={!hideImagePreview}
                                    showActionButtons={
                                        !hideImageActionButtons &&
                                        !isBeforeAfterMediaField
                                    }
                                    onTogglePanel={() =>
                                        handleOpenMediaPanel(
                                            key,
                                            normalizedImageVal
                                        )
                                    }
                                    positionValue={
                                        mediaPositionKey
                                            ? getByPath(
                                                  component_props,
                                                  mediaPositionKey
                                              ) ?? mediaPositionFallback
                                            : undefined
                                    }
                                    onPositionChange={
                                        mediaPositionKey
                                            ? (val) =>
                                                  onChange({
                                                      file,
                                                      key: mediaPositionKey,
                                                      value: val,
                                                  })
                                            : undefined
                                    }
                                />
                            </div>
                        );
                    })()
                ) : isShowcaseSlider ? (
                    <div className="mt-2 inline-flex flex-wrap gap-2">
                        {[1, 2, 3, 4].map((num) => (
                            <VSChip
                                key={num}
                                active={sliderVal === num}
                                disabled={false}
                                srText={`Showcase ${num} images`}
                                onClick={() =>
                                    onChange({
                                        file,
                                        key,
                                        value: String(num),
                                    })
                                }
                            >
                                {num}
                            </VSChip>
                        ))}
                    </div>
                ) : (
                    <div className="mt-1">
                        {MARKDOWN_EDITOR_WHITELIST.some((v) =>
                            String(key).endsWith(v)
                        ) ? (
                            (() => {
                                const linkInfo = inferLinkedFieldKey(
                                    key,
                                    component_props
                                );
                                const linkValue = linkInfo?.value ?? '';
                                const hasLinkedField =
                                    linkValue.trim().length > 0;

                                return (
                                    <MDEditor
                                        value={value}
                                        fieldKey={key}
                                        componentName={componentName}
                                        onChange={(md) =>
                                            handleInputChange(
                                                { target: { value: md } },
                                                key
                                            )
                                        }
                                        linkValue={linkValue} // ← was: linkInfo?.value ?? ''
                                        hideLinkButton={hasLinkedField}
                                        disableLinking={hasLinkedField}
                                    />
                                );
                            })()
                        ) : isButtonKey(key) ? (
                            <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                <input
                                    type="text"
                                    value={value ?? ''}
                                    onChange={(e) => handleInputChange(e, key)}
                                    className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5
                  text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                />
                            </div>
                        ) : isReviewSourceField ? (
                            <div className="mt-2">
                                <div className="inline-flex flex-wrap gap-2">
                                    {[
                                        { value: 'manual', label: 'Manual' },
                                        { value: 'nicejob', label: 'Stories' },
                                    ].map((option) => (
                                        <VSChip
                                            key={option.value}
                                            active={
                                                reviewSourceValue ===
                                                option.value
                                            }
                                            disabled={false}
                                            srText={`Use ${option.label} reviews`}
                                            onClick={() =>
                                                onChange({
                                                    file,
                                                    key,
                                                    value: option.value,
                                                })
                                            }
                                        >
                                            {option.label}
                                        </VSChip>
                                    ))}
                                </div>
                                {useNiceJobReviews ? (
                                    <TagPillsInput
                                        value={
                                            component_props?.NiceJobFilters ??
                                            componentSchema?.NiceJobFilters
                                                ?.value ??
                                            []
                                        }
                                        onChange={(next) =>
                                            onChange({
                                                file,
                                                key: 'NiceJobFilters',
                                                value: next,
                                            })
                                        }
                                    />
                                ) : null}
                            </div>
                        ) : isNiceJobLayoutField ? (
                            <div className="mt-2 inline-flex flex-wrap gap-2">
                                {[
                                    { value: 'grid', label: 'Masonry' },
                                    {
                                        value: 'uniform-grid',
                                        label: 'Grid',
                                    },
                                    {
                                        value: 'single',
                                        label: 'Single Column',
                                    },
                                    { value: 'slider', label: 'Slider' },
                                ].map((option) => (
                                    <VSChip
                                        key={option.value}
                                        active={
                                            String(value || 'grid')
                                                .toLowerCase()
                                                .trim() === option.value
                                        }
                                        disabled={false}
                                        srText={`Nice Job layout ${option.label}`}
                                        onClick={() =>
                                            onChange({
                                                file,
                                                key,
                                                value: option.value,
                                            })
                                        }
                                    >
                                        {option.label}
                                    </VSChip>
                                ))}
                            </div>
                        ) : isReviewLayoutField ? (
                            <div className="mt-2 inline-flex flex-wrap gap-2">
                                {[
                                    'grid',
                                    'masonry',
                                    'bento',
                                    'square',
                                    'slider',
                                ].map((opt) => (
                                    <VSChip
                                        key={opt}
                                        active={reviewLayoutVal === opt}
                                        disabled={false}
                                        srText={`Layout ${opt}`}
                                        onClick={() =>
                                            onChange({
                                                file,
                                                key,
                                                value: opt,
                                            })
                                        }
                                    >
                                        {prettyLabel(opt)}
                                    </VSChip>
                                ))}
                            </div>
                        ) : isReviewFeatureIconField ? (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                <div className="inline-flex flex-wrap gap-2">
                                    {['quote', 'stars'].map((opt) => (
                                        <VSChip
                                            key={opt}
                                            active={
                                                reviewFeatureIconVal === opt
                                            }
                                            disabled={false}
                                            srText={`Feature icon ${opt}`}
                                            onClick={() =>
                                                onChange({
                                                    file,
                                                    key,
                                                    value: opt,
                                                })
                                            }
                                        >
                                            <span className="mr-1 inline-flex items-center justify-center">
                                                <i
                                                    className={`fa-solid ${
                                                        opt === 'quote'
                                                            ? 'fa-quote-left'
                                                            : 'fa-star'
                                                    } text-[13px]`}
                                                    aria-hidden="true"
                                                />
                                            </span>
                                            {prettyLabel(opt)}
                                        </VSChip>
                                    ))}
                                </div>
                                <div className="flex flex-nowrap items-center gap-2">
                                    <Listbox
                                        value={featureIconPackVal}
                                        onChange={(v) =>
                                            onChange({
                                                file,
                                                key: 'FeatureIconPack',
                                                value: v,
                                            })
                                        }
                                    >
                                        <div className="relative inline-flex w-[120px] rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                            <Listbox.Button className="relative flex h-9 w-full items-center justify-between rounded-[10px] bg-white px-3 text-left text-xs leading-5 text-neutral-800 focus:outline-none">
                                                <span className="flex items-center gap-2 truncate">
                                                    <i
                                                        className="fa-brands fa-font-awesome text-[13px] text-neutral-500"
                                                        aria-hidden="true"
                                                    />
                                                    <span className="font-semibold text-neutral-700">
                                                        Pack
                                                    </span>
                                                </span>
                                                <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                                            </Listbox.Button>
                                            <Transition
                                                as={React.Fragment}
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100 scale-100"
                                                leaveTo="opacity-0 scale-95"
                                            >
                                                <Listbox.Options className="absolute z-20 mt-1 w-full rounded-md border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                                    {reviewPackOptions.map(
                                                        (opt) => (
                                                            <Listbox.Option
                                                                key={opt.key}
                                                                value={opt.key}
                                                                className={({
                                                                    active,
                                                                }) =>
                                                                    [
                                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                                        active
                                                                            ? 'bg-primary-50 text-primary-700'
                                                                            : 'text-neutral-800',
                                                                    ].join(' ')
                                                                }
                                                            >
                                                                {({
                                                                    selected,
                                                                }) => (
                                                                    <>
                                                                        <span>
                                                                            {
                                                                                opt.label
                                                                            }
                                                                        </span>
                                                                        {selected && (
                                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Listbox.Option>
                                                        )
                                                    )}
                                                </Listbox.Options>
                                            </Transition>
                                        </div>
                                    </Listbox>

                                    <Listbox
                                        value={featureIconStyleVal}
                                        onChange={(v) =>
                                            onChange({
                                                file,
                                                key: 'FeatureIconStyle',
                                                value: v,
                                            })
                                        }
                                    >
                                        <div className="relative inline-flex w-[120px] rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                            <Listbox.Button className="relative flex h-9 w-full items-center justify-between rounded-[10px] bg-white px-3 text-left text-xs leading-5 text-neutral-800 focus:outline-none">
                                                <span className="flex items-center gap-2 truncate">
                                                    <i
                                                        className="fa-solid fa-palette text-[13px] text-neutral-500"
                                                        aria-hidden="true"
                                                    />
                                                    <span className="font-semibold text-neutral-700">
                                                        Style
                                                    </span>
                                                </span>
                                                <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                                            </Listbox.Button>
                                            <Transition
                                                as={React.Fragment}
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100 scale-100"
                                                leaveTo="opacity-0 scale-95"
                                            >
                                                <Listbox.Options className="absolute z-20 mt-1 w-full rounded-md border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                                    {reviewStyleOptions.map(
                                                        (opt) => (
                                                            <Listbox.Option
                                                                key={opt.key}
                                                                value={opt.key}
                                                                className={({
                                                                    active,
                                                                }) =>
                                                                    [
                                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                                        active
                                                                            ? 'bg-primary-50 text-primary-700'
                                                                            : 'text-neutral-800',
                                                                    ].join(' ')
                                                                }
                                                            >
                                                                {({
                                                                    selected,
                                                                }) => (
                                                                    <>
                                                                        <span>
                                                                            {
                                                                                opt.label
                                                                            }
                                                                        </span>
                                                                        {selected && (
                                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Listbox.Option>
                                                        )
                                                    )}
                                                </Listbox.Options>
                                            </Transition>
                                        </div>
                                    </Listbox>
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                {keyLeaf(key).toLowerCase() === 'googlemap' ? (
                                    <input
                                        type="text"
                                        value={value ?? ''}
                                        onChange={(e) =>
                                            handleCleanChange(e, key)
                                        } // clean on type
                                        onPaste={(e) => {
                                            const pasted =
                                                e.clipboardData?.getData(
                                                    'text'
                                                ) ?? '';
                                            const clean =
                                                normalizeMapEmbed(pasted);
                                            if (clean) {
                                                e.preventDefault(); // block raw iframe paste
                                                onChange({
                                                    file,
                                                    key,
                                                    value: clean,
                                                }); // clean on paste
                                            }
                                        }}
                                        className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                        placeholder="Paste Google Maps iframe or URL"
                                    />
                                ) : (
                                    <>
                                        {isPageSchemaMarkupField ? (
                                            <textarea
                                                rows={8}
                                                className="block min-h-[140px] w-full resize-y rounded-[10px] border-0 px-3 py-1.5 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                value={value ?? ''}
                                                onChange={(e) =>
                                                    handleInputChange(e, key)
                                                }
                                                placeholder={`{
  "@context": "https://schema.org",
  "@type": "LocalBusiness"
}`}
                                            />
                                        ) : (
                                            <TextArea
                                                className="block w-full rounded-[10px] border-0 py-1.5 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                value={value ?? ''}
                                                onChange={(e) =>
                                                    handleInputChange(e, key)
                                                }
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const contentNodes = (() => {
        const nodes = [];
        const skipped = new Set();
        const heroFullBGReorderableLeafSet = new Set(
            HERO_FULL_BG_REORDERABLE_LEAVES
        );
        const heroFullBGReorderableLeaves =
            componentName === 'HeroFullBG'
                ? contentKeysForNodes
                      .map((candidateKey) =>
                          isTopLevelKey(candidateKey)
                              ? keyLeaf(candidateKey).toLowerCase()
                              : null
                      )
                      .filter((leaf) => heroFullBGReorderableLeafSet.has(leaf))
                : [];
        const readDraggedTopLevelField = (event) => {
            if (draggedTopLevelField) return draggedTopLevelField;
            const payloads = [
                event?.dataTransfer?.getData('application/json'),
                event?.dataTransfer?.getData('text/plain'),
            ].filter(Boolean);
            for (const raw of payloads) {
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed && typeof parsed.fieldLeaf === 'string') {
                        return parsed;
                    }
                } catch (error) {
                    continue;
                }
            }
            return null;
        };
        const reorderHeroFullBGFields = (fromLeaf, overLeaf, overPosition) => {
            const currentLeaves = heroFullBGReorderableLeaves;
            const fromIndex = currentLeaves.indexOf(fromLeaf);
            const overIndex = currentLeaves.indexOf(overLeaf);
            if (fromIndex === -1 || overIndex === -1) return;

            const insertionIndex =
                overPosition === 'after' ? overIndex + 1 : overIndex;
            const nextLeaves = [...currentLeaves];
            const [movedLeaf] = nextLeaves.splice(fromIndex, 1);
            if (!movedLeaf) return;
            const adjustedInsertionIndex =
                fromIndex < insertionIndex
                    ? insertionIndex - 1
                    : insertionIndex;
            if (fromIndex === adjustedInsertionIndex) return;
            nextLeaves.splice(adjustedInsertionIndex, 0, movedLeaf);
            onChange({
                file,
                key: 'ContentOrder',
                value: nextLeaves.join(','),
            });
        };
        const isNoOpHeroFullBGDropPosition = (
            fromLeaf,
            overLeaf,
            overPosition
        ) => {
            const currentLeaves = heroFullBGReorderableLeaves;
            const fromIndex = currentLeaves.indexOf(fromLeaf);
            const overIndex = currentLeaves.indexOf(overLeaf);
            if (fromIndex === -1 || overIndex === -1) return true;
            const insertionIndex =
                overPosition === 'after' ? overIndex + 1 : overIndex;
            const adjustedInsertionIndex =
                fromIndex < insertionIndex
                    ? insertionIndex - 1
                    : insertionIndex;
            return fromIndex === adjustedInsertionIndex;
        };
        const getHeroFullBGDropPosition = (targetLeaf, event) => {
            const targetRect = event.currentTarget.getBoundingClientRect();
            const pointerY = event.clientY - targetRect.top;
            const ratio =
                targetRect.height > 0 ? pointerY / targetRect.height : 0.5;
            if (targetLeaf === 'subheader') {
                return ratio < 0.7 ? 'before' : 'after';
            }
            return ratio < 0.5 ? 'before' : 'after';
        };
        const getHeroFullBGDropTarget = (targetLeaf, event) => {
            const overPosition = getHeroFullBGDropPosition(targetLeaf, event);
            if (targetLeaf !== 'header' || overPosition !== 'after') {
                return { overLeaf: targetLeaf, overPosition };
            }

            const headerIndex = heroFullBGReorderableLeaves.indexOf('header');
            const nextLeaf = heroFullBGReorderableLeaves[headerIndex + 1];
            if (!nextLeaf) return { overLeaf: targetLeaf, overPosition };

            return { overLeaf: nextLeaf, overPosition: 'before' };
        };
        const getHeroFullBGTerminalDropLeaf = () => {
            const lastIndex = heroFullBGReorderableLeaves.length - 1;
            return lastIndex >= 0
                ? heroFullBGReorderableLeaves[lastIndex]
                : null;
        };
        const wrapHeroFullBGFieldNode = (nodeKey, node) => {
            if (componentName !== 'HeroFullBG') return node;
            if (!isTopLevelKey(nodeKey)) return node;

            const leaf = keyLeaf(nodeKey).toLowerCase();
            if (!heroFullBGReorderableLeafSet.has(leaf)) return node;

            const isDraggedItem = draggedTopLevelField?.fieldLeaf === leaf;
            const isDropTarget =
                draggedTopLevelField?.fieldLeaf &&
                draggedTopLevelField?.fieldLeaf !== leaf &&
                draggedTopLevelField?.overLeaf === leaf;
            const showDropPlaceholderBefore =
                isDropTarget &&
                draggedTopLevelField?.overPosition === 'before' &&
                !isNoOpHeroFullBGDropPosition(
                    draggedTopLevelField?.fieldLeaf,
                    leaf,
                    'before'
                );
            const showDropPlaceholderAfter =
                isDropTarget &&
                draggedTopLevelField?.overPosition === 'after' &&
                !isNoOpHeroFullBGDropPosition(
                    draggedTopLevelField?.fieldLeaf,
                    leaf,
                    'after'
                );
            const dropPlaceholder = (
                <div
                    className={`my-3 h-16 rounded-xl border-2 border-dashed ${getDropPlaceholderClasses(
                        'primary'
                    )}`}
                    aria-hidden="true"
                />
            );

            return (
                <div
                    key={`hero-field-wrap-${nodeKey}`}
                    onDragOver={(event) => {
                        const payload = readDraggedTopLevelField(event);
                        if (!payload) return;
                        if (payload.fieldLeaf === leaf) {
                            setDraggedTopLevelField({
                                fieldLeaf: payload.fieldLeaf,
                                overLeaf: null,
                                overPosition: null,
                            });
                            return;
                        }
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        const { overLeaf, overPosition } =
                            getHeroFullBGDropTarget(leaf, event);
                        if (overLeaf === payload.fieldLeaf) {
                            setDraggedTopLevelField({
                                fieldLeaf: payload.fieldLeaf,
                                overLeaf: null,
                                overPosition: null,
                            });
                            return;
                        }
                        setDraggedTopLevelField((prev) => {
                            if (
                                isNoOpHeroFullBGDropPosition(
                                    payload.fieldLeaf,
                                    overLeaf,
                                    overPosition
                                )
                            ) {
                                return {
                                    fieldLeaf: payload.fieldLeaf,
                                    overLeaf: null,
                                    overPosition: null,
                                };
                            }
                            if (
                                prev &&
                                prev.fieldLeaf === payload.fieldLeaf &&
                                prev.overLeaf === overLeaf &&
                                prev.overPosition === overPosition
                            ) {
                                return prev;
                            }
                            return {
                                fieldLeaf: payload.fieldLeaf,
                                overLeaf,
                                overPosition,
                            };
                        });
                    }}
                    onDrop={(event) => {
                        const payload = readDraggedTopLevelField(event);
                        if (!payload || payload.fieldLeaf === leaf) return;
                        event.preventDefault();
                        const { overLeaf, overPosition } =
                            getHeroFullBGDropTarget(leaf, event);
                        if (overLeaf === payload.fieldLeaf) {
                            handleTopLevelFieldDragEnd();
                            return;
                        }
                        if (
                            isNoOpHeroFullBGDropPosition(
                                payload.fieldLeaf,
                                overLeaf,
                                overPosition
                            )
                        ) {
                            handleTopLevelFieldDragEnd();
                            return;
                        }
                        reorderHeroFullBGFields(
                            payload.fieldLeaf,
                            overLeaf,
                            overPosition
                        );
                        handleTopLevelFieldDragEnd();
                    }}
                >
                    {showDropPlaceholderBefore ? dropPlaceholder : null}
                    <div
                        data-draggable-hero-field="true"
                        className={
                            isDraggedItem ? 'scale-[0.985] opacity-70' : ''
                        }
                    >
                        {node}
                    </div>
                    {showDropPlaceholderAfter ? dropPlaceholder : null}
                </div>
            );
        };
        const wrapHeroFullBGTerminalDropNode = (nodeKey, node) => {
            if (componentName !== 'HeroFullBG') return node;
            if (!isTopLevelKey(nodeKey)) return node;

            const leaf = keyLeaf(nodeKey).toLowerCase();
            if (heroFullBGReorderableLeafSet.has(leaf)) return node;

            return (
                <div
                    key={`hero-terminal-drop-${nodeKey}`}
                    onDragOver={(event) => {
                        const payload = readDraggedTopLevelField(event);
                        const terminalLeaf = getHeroFullBGTerminalDropLeaf();
                        if (!payload || !terminalLeaf) return;

                        event.preventDefault();
                        event.stopPropagation();
                        event.dataTransfer.dropEffect = 'move';
                        if (
                            payload.fieldLeaf === terminalLeaf ||
                            isNoOpHeroFullBGDropPosition(
                                payload.fieldLeaf,
                                terminalLeaf,
                                'after'
                            )
                        ) {
                            setDraggedTopLevelField({
                                fieldLeaf: payload.fieldLeaf,
                                overLeaf: null,
                                overPosition: null,
                            });
                            return;
                        }

                        setDraggedTopLevelField((prev) => {
                            if (
                                prev &&
                                prev.fieldLeaf === payload.fieldLeaf &&
                                prev.overLeaf === terminalLeaf &&
                                prev.overPosition === 'after'
                            ) {
                                return prev;
                            }
                            return {
                                fieldLeaf: payload.fieldLeaf,
                                overLeaf: terminalLeaf,
                                overPosition: 'after',
                            };
                        });
                    }}
                    onDrop={(event) => {
                        const payload = readDraggedTopLevelField(event);
                        const terminalLeaf = getHeroFullBGTerminalDropLeaf();
                        if (!payload || !terminalLeaf) return;

                        event.preventDefault();
                        event.stopPropagation();
                        if (
                            payload.fieldLeaf !== terminalLeaf &&
                            !isNoOpHeroFullBGDropPosition(
                                payload.fieldLeaf,
                                terminalLeaf,
                                'after'
                            )
                        ) {
                            reorderHeroFullBGFields(
                                payload.fieldLeaf,
                                terminalLeaf,
                                'after'
                            );
                        }
                        handleTopLevelFieldDragEnd();
                    }}
                >
                    {node}
                </div>
            );
        };
        const ensureKeyedContentNode = (nodeKey, node) => {
            const stableKey = `content-node-${String(nodeKey)}`;
            if (node == null) return node;
            if (Array.isArray(node)) {
                return <React.Fragment key={stableKey}>{node}</React.Fragment>;
            }
            if (React.isValidElement(node)) {
                return node.key == null
                    ? React.cloneElement(node, { key: stableKey })
                    : node;
            }
            return <React.Fragment key={stableKey}>{node}</React.Fragment>;
        };
        const pushContentNode = (nodeKey, node) => {
            const wrappedNode = wrapHeroFullBGFieldNode(nodeKey, node);
            nodes.push(
                ensureKeyedContentNode(
                    nodeKey,
                    wrapHeroFullBGTerminalDropNode(nodeKey, wrappedNode)
                )
            );
        };
        for (const key of contentKeysForNodes) {
            if (skipped.has(key)) continue;
            if (componentName === 'HeroFullBG' && key === 'CTAButtons') {
                const ctaButtonsSection =
                    renderComponentCTAButtonsSection(componentName);
                if (ctaButtonsSection) {
                    pushContentNode(key, ctaButtonsSection);
                }
                continue;
            }
            const isStandaloneMediaField =
                isTopLevelKey(key) &&
                looksLikeImageKey(key) &&
                key !== beforeImageKey &&
                key !== afterImageKey;
            if (isStandaloneMediaField) {
                const mediaSectionKey = `${key}.__media__`;
                const mediaSectionExpanded = isArrayItemExpanded(
                    mediaSectionKey,
                    false
                );
                const mediaLabel =
                    isPageComponent && keyLeaf(key).toLowerCase() === 'imageurl'
                        ? 'Open Graph Image'
                        : componentName === 'CTAHero' &&
                          keyLeaf(key).toLowerCase() === 'imageurl'
                        ? 'Background Image'
                        : componentName === 'HeroFullBG' &&
                          keyLeaf(key).toLowerCase() === 'imageurl'
                        ? 'Background Image'
                        : componentName === 'ItemGrid' &&
                          keyLeaf(key).toLowerCase() === 'imageurl'
                        ? 'Background Image'
                        : prettyLabel(key);
                const pageOpenGraphTitleKey =
                    isPageComponent && keyLeaf(key).toLowerCase() === 'imageurl'
                        ? contentKeysForNodes.find(
                              (candidateKey) =>
                                  keyLeaf(candidateKey).toLowerCase() ===
                                  'opengraphtitle'
                          ) || null
                        : null;
                const mediaPositionKey = getMediaPositionKey(
                    componentName,
                    key
                );
                const mediaBackgroundFixedKey = [
                    'imageurl',
                    'backgroundimage',
                    'backgroundimageurl',
                ].includes(keyLeaf(key).toLowerCase())
                    ? replaceLeafInPath(key, 'BackgroundFixed')
                    : null;
                const hasMediaBackgroundFixedControl =
                    Boolean(mediaBackgroundFixedKey) &&
                    getByPath(component_props, mediaBackgroundFixedKey) !==
                        undefined;
                const isMediaBackgroundFixedActive =
                    hasMediaBackgroundFixedControl &&
                    coerceBoolean(
                        getByPath(component_props, mediaBackgroundFixedKey)
                    );
                const mediaAltInfo = inferAltTextFieldForImage(
                    key,
                    component_props
                );
                const mediaVariantInfo = inferStandaloneMediaVariantField(
                    componentName,
                    key,
                    component_props
                );
                const mediaRadiusInfo = inferStandaloneMediaRadiusField(
                    componentName,
                    key,
                    component_props
                );
                const mediaValue = component_props[key];
                const mediaBackgroundImage =
                    normalizeCollapsibleArrayBackgroundImageValue(mediaValue);
                const hasImageBackedHeader = isSelectedEditorMedia(mediaValue);
                const mediaDisplayFilename = hasImageBackedHeader
                    ? getSiteImageByUrl(mediaValue)?.filename ||
                      extractMediaFilename(mediaValue)
                    : '';
                const mediaFilenameClassName = hasImageBackedHeader
                    ? 'text-sm font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]'
                    : 'text-sm font-semibold text-neutral-900';
                const mediaToneClasses = hasImageBackedHeader
                    ? {
                          wrapper: 'border-primary-200 bg-white',
                          headerOpenBorder: 'border-white/15',
                          buttonHover: 'hover:bg-transparent',
                          metaText: 'text-white/80',
                          chevronText: 'text-white',
                      }
                    : getEmptyMediaCollapsibleToneClasses();
                const mediaHeaderStyle = hasImageBackedHeader
                    ? {
                          backgroundImage: `linear-gradient(135deg, rgba(5, 12, 23, 0.78) 0%, rgba(5, 12, 23, 0.6) 55%, rgba(5, 12, 23, 0.42) 100%), url(${JSON.stringify(
                              mediaBackgroundImage
                          )})`,
                          backgroundPosition: 'center',
                          backgroundSize: 'cover',
                      }
                    : undefined;
                const mediaHeaderUploadButtonClassName = hasImageBackedHeader
                    ? 'focus-visible:ring-white/60 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full bg-black/15 text-white shadow-sm backdrop-blur-[2px] transition-all hover:bg-black/30 focus:outline-none focus-visible:ring-2 disabled:opacity-40'
                    : INLINE_ADD_BUTTON_CLASSNAME;
                const mediaHeaderFixedButtonClassName = hasImageBackedHeader
                    ? `focus-visible:ring-white/60 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full shadow-sm backdrop-blur-[2px] transition-all focus:outline-none focus-visible:ring-2 ${
                          isMediaBackgroundFixedActive
                              ? 'bg-primary-600 text-white hover:bg-primary-500'
                              : 'bg-black/15 text-white hover:bg-black/30'
                      }`
                    : `inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${
                          isMediaBackgroundFixedActive
                              ? 'bg-primary-600 text-white hover:bg-primary-500'
                              : 'text-neutral-500 hover:bg-primary-100 hover:text-primary-700'
                      }`;
                const mediaHeaderChevronButtonClassName = hasImageBackedHeader
                    ? 'focus-visible:ring-white/60 inline-flex h-6 w-6 items-center justify-center self-center rounded-full bg-black/15 text-white shadow-sm backdrop-blur-[2px] transition-all hover:bg-black/30 focus:outline-none focus-visible:ring-2'
                    : `inline-flex h-6 w-6 items-center justify-center self-center rounded-full transition-colors ${mediaToneClasses.chevronText} ${mediaToneClasses.buttonHover}`;
                const mediaDetailsId = `component-editor-${String(
                    mediaSectionKey
                ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                const isHeroFullBGLeadMedia =
                    componentName === 'HeroFullBG' &&
                    keyLeaf(key).toLowerCase() === 'leadimageurl';
                skipped.add(key);
                if (pageOpenGraphTitleKey) {
                    skipped.add(pageOpenGraphTitleKey);
                }
                if (mediaAltInfo?.key) skipped.add(mediaAltInfo.key);
                if (mediaVariantInfo?.key) skipped.add(mediaVariantInfo.key);
                if (mediaRadiusInfo?.key) skipped.add(mediaRadiusInfo.key);
                if (mediaPositionKey) skipped.add(mediaPositionKey);
                if (mediaBackgroundFixedKey) {
                    skipped.add(mediaBackgroundFixedKey);
                }

                pushContentNode(
                    key,
                    <div key={mediaSectionKey} className="mb-3 mt-4">
                        <div
                            data-hero-lead-media-card={
                                isHeroFullBGLeadMedia ? true : undefined
                            }
                            className={`overflow-hidden rounded-xl border shadow-sm ${mediaToneClasses.wrapper}`}
                        >
                            <div
                                style={mediaHeaderStyle}
                                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in ${
                                    hasImageBackedHeader
                                        ? 'bg-neutral-900/70'
                                        : mediaToneClasses.buttonHover
                                } ${
                                    mediaSectionExpanded
                                        ? `border-b ${mediaToneClasses.headerOpenBorder}`
                                        : ''
                                }`}
                            >
                                <div
                                    role="button"
                                    tabIndex={0}
                                    draggable={isHeroFullBGLeadMedia}
                                    className={`${mediaToneClasses.buttonHover} flex min-w-0 flex-1 cursor-grab items-start gap-3 rounded-lg p-1 text-left transition-colors ease-in focus-visible:outline-none active:cursor-grabbing`}
                                    aria-expanded={mediaSectionExpanded}
                                    aria-controls={mediaDetailsId}
                                    onDragStart={
                                        isHeroFullBGLeadMedia
                                            ? (event) =>
                                                  handleTopLevelFieldDragStart(
                                                      event,
                                                      'leadimageurl'
                                                  )
                                            : undefined
                                    }
                                    onDragEnd={
                                        isHeroFullBGLeadMedia
                                            ? handleTopLevelFieldDragEnd
                                            : undefined
                                    }
                                    onClick={() => {
                                        if (suppressDragClickRef.current) {
                                            suppressDragClickRef.current = false;
                                            return;
                                        }
                                        toggleArrayItemExpanded(
                                            mediaSectionKey,
                                            false
                                        );
                                    }}
                                    onKeyDown={(event) => {
                                        if (
                                            event.key === 'Enter' ||
                                            event.key === ' '
                                        ) {
                                            event.preventDefault();
                                            toggleArrayItemExpanded(
                                                mediaSectionKey,
                                                false
                                            );
                                        }
                                    }}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div
                                            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${mediaToneClasses.metaText}`}
                                        >
                                            {mediaLabel}
                                        </div>
                                        {mediaDisplayFilename ? (
                                            <div
                                                className={`mt-1 truncate ${mediaFilenameClassName}`}
                                                title={mediaDisplayFilename}
                                            >
                                                {mediaDisplayFilename}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    {hasMediaBackgroundFixedControl ? (
                                        <button
                                            type="button"
                                            title={
                                                isMediaBackgroundFixedActive
                                                    ? 'Disable fixed background'
                                                    : 'Enable fixed background'
                                            }
                                            className={
                                                mediaHeaderFixedButtonClassName
                                            }
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onChange({
                                                    file,
                                                    key: mediaBackgroundFixedKey,
                                                    value: !isMediaBackgroundFixedActive,
                                                });
                                            }}
                                        >
                                            <ArrowsUpDownIcon
                                                className="h-3 w-3"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    ) : null}
                                    <button
                                        type="button"
                                        title="Upload a file"
                                        className={
                                            mediaHeaderUploadButtonClassName
                                        }
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            mediaUploadInputRefs.current[
                                                key
                                            ]?.click();
                                        }}
                                    >
                                        <ArrowUpTrayIcon
                                            className="h-3 w-3"
                                            aria-hidden="true"
                                        />
                                    </button>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        ref={(node) => {
                                            if (node) {
                                                mediaUploadInputRefs.current[
                                                    key
                                                ] = node;
                                            } else {
                                                delete mediaUploadInputRefs
                                                    .current[key];
                                            }
                                        }}
                                        onChange={(event) =>
                                            handleInlineMediaUpload(key, event)
                                        }
                                    />
                                    <span
                                        className={
                                            mediaHeaderChevronButtonClassName
                                        }
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 transition-transform duration-200 ${
                                                mediaSectionExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </span>
                                </div>
                            </div>

                            {mediaSectionExpanded ? (
                                <div
                                    id={mediaDetailsId}
                                    className="bg-white px-3 pb-3 pt-2"
                                >
                                    {pageOpenGraphTitleKey ? (
                                        <React.Fragment>
                                            {renderInput(
                                                component_props[
                                                    pageOpenGraphTitleKey
                                                ],
                                                pageOpenGraphTitleKey
                                            )}
                                        </React.Fragment>
                                    ) : null}
                                    {renderInput(component_props[key], key, 0, {
                                        inlineMediaPanelProps: {
                                            isOpen: true,
                                            layout: 'media-row',
                                        },
                                        inlineMediaAltProps: mediaAltInfo?.key
                                            ? {
                                                  key: mediaAltInfo.key,
                                                  value:
                                                      readValueAtPath(
                                                          component_props,
                                                          mediaAltInfo.key
                                                      ) ?? '',
                                              }
                                            : null,
                                        customLabel: mediaLabel,
                                        hideImagePreview: true,
                                        hideImageActionButtons: true,
                                    })}
                                    {mediaVariantInfo?.key ? (
                                        <div className="mt-3">
                                            <ImageVariantControls
                                                value={String(
                                                    mediaVariantInfo.value ??
                                                        'original'
                                                )}
                                                allowedDisplays={
                                                    mediaVariantInfo.allowedDisplays
                                                }
                                                enableVerticalAlign={false}
                                                enableShadow={false}
                                                onChange={(next) =>
                                                    onChange({
                                                        file,
                                                        key: mediaVariantInfo.key,
                                                        value: next,
                                                    })
                                                }
                                            />
                                        </div>
                                    ) : null}
                                    {mediaRadiusInfo?.key ? (
                                        <div className="mt-3 flex items-center justify-between gap-3">
                                            <div>
                                                <div className="text-xs font-semibold leading-5 text-primary-700">
                                                    {mediaRadiusInfo.label}
                                                </div>
                                                <div className="text-[11px] leading-4 text-neutral-500">
                                                    Applies the site shape
                                                    radius to this image.
                                                </div>
                                            </div>
                                            <Switch
                                                checked={coerceBoolean(
                                                    mediaRadiusInfo.value
                                                )}
                                                onChange={(checked) =>
                                                    onChange({
                                                        file,
                                                        key: mediaRadiusInfo.key,
                                                        value: checked,
                                                    })
                                                }
                                                ariaLabel="Toggle lead image corner radius"
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
                continue;
            }
            if (
                componentName === 'Footer' &&
                footerLinksKey &&
                key === footerLinksKey
            ) {
                const sectionToneClasses =
                    getCollapsibleArrayToneClasses('primary');
                const sectionExpanded = isArrayItemExpanded(key, false);
                const linksHeaderPath = 'LinksHeader';
                const linksHeaderEditStateKey = `${key}.__section_header__`;
                const isLinksHeaderEditing = isArrayItemExpanded(
                    linksHeaderEditStateKey,
                    false
                );
                const linksHeaderValue = String(
                    readValueAtPath(component_props, linksHeaderPath) ?? ''
                ).trim();
                const detailsId = `component-editor-${String(key).replace(
                    /[^a-zA-Z0-9_-]/g,
                    '-'
                )}`;

                skipped.add(key);
                nodes.push(
                    <div key={key} className="mt-4">
                        <div
                            className={`overflow-hidden rounded-xl border shadow-sm ${sectionToneClasses.wrapper}`}
                        >
                            {isLinksHeaderEditing ? (
                                <div
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left ${
                                        sectionExpanded
                                            ? `border-b ${sectionToneClasses.headerOpenBorder}`
                                            : ''
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div
                                            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${sectionToneClasses.metaText}`}
                                        >
                                            Links
                                        </div>
                                        <form
                                            onSubmit={(event) => {
                                                event.preventDefault();
                                                onChange({
                                                    file,
                                                    key: linksHeaderPath,
                                                    value: readInlineFieldDraft(
                                                        linksHeaderPath,
                                                        readValueAtPath(
                                                            component_props,
                                                            linksHeaderPath
                                                        ) ?? ''
                                                    ),
                                                });
                                                setArrayItemExpanded(
                                                    linksHeaderEditStateKey,
                                                    false
                                                );
                                                clearInlineFieldDraft(
                                                    linksHeaderPath
                                                );
                                            }}
                                            className="mt-1 flex min-w-0 items-center gap-1.5"
                                        >
                                            <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                <input
                                                    type="text"
                                                    value={readInlineFieldDraft(
                                                        linksHeaderPath,
                                                        readValueAtPath(
                                                            component_props,
                                                            linksHeaderPath
                                                        ) ?? ''
                                                    )}
                                                    onChange={(event) =>
                                                        setInlineFieldDraft(
                                                            linksHeaderPath,
                                                            event.target.value
                                                        )
                                                    }
                                                    placeholder="Links Header"
                                                    className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                    autoFocus
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                title="Save Links Header"
                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                            >
                                                <CheckIcon className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button"
                                                title="Cancel Links Header edit"
                                                onClick={() => {
                                                    setArrayItemExpanded(
                                                        linksHeaderEditStateKey,
                                                        false
                                                    );
                                                    clearInlineFieldDraft(
                                                        linksHeaderPath
                                                    );
                                                }}
                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                            >
                                                <XMarkIcon className="h-3 w-3" />
                                            </button>
                                        </form>
                                    </div>
                                    <button
                                        type="button"
                                        title={
                                            sectionExpanded
                                                ? 'Collapse links section'
                                                : 'Expand links section'
                                        }
                                        className={INLINE_ADD_BUTTON_CLASSNAME}
                                        aria-expanded={sectionExpanded}
                                        aria-controls={detailsId}
                                        onClick={() =>
                                            toggleArrayItemExpanded(key, false)
                                        }
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 ${
                                                sectionToneClasses.chevronText
                                            } transition-transform duration-200 ${
                                                sectionExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                                        sectionToneClasses.buttonHover
                                    } ${
                                        sectionExpanded
                                            ? `border-b ${sectionToneClasses.headerOpenBorder}`
                                            : ''
                                    }`}
                                    aria-expanded={sectionExpanded}
                                    aria-controls={detailsId}
                                    onClick={() =>
                                        toggleArrayItemExpanded(key, false)
                                    }
                                >
                                    <div className="min-w-0">
                                        <div
                                            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${sectionToneClasses.metaText}`}
                                        >
                                            Links
                                        </div>
                                        <div className="group mt-1 flex min-w-0 items-center gap-1">
                                            <div className="truncate text-sm font-semibold text-neutral-900">
                                                {linksHeaderValue ||
                                                    'Links Header'}
                                            </div>
                                            <button
                                                type="button"
                                                title="Edit Links Header"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setInlineFieldDraft(
                                                        linksHeaderPath,
                                                        readValueAtPath(
                                                            component_props,
                                                            linksHeaderPath
                                                        ) ?? ''
                                                    );
                                                    setArrayItemExpanded(
                                                        linksHeaderEditStateKey,
                                                        true
                                                    );
                                                }}
                                                className={
                                                    INLINE_EDIT_BUTTON_CLASSNAME
                                                }
                                            >
                                                <PencilSquareIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <span
                                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${sectionToneClasses.chevronText}`}
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 transition-transform duration-200 ${
                                                sectionExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </span>
                                </div>
                            )}

                            {sectionExpanded ? (
                                <div
                                    id={detailsId}
                                    className="bg-white px-3 pb-3 pt-2"
                                >
                                    {renderInput(component_props[key], key)}
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
                continue;
            }
            if (
                componentName === 'Footer' &&
                footerInfoAnchorKey &&
                key === footerInfoAnchorKey
            ) {
                const sectionKey = 'Footer.__info__';
                const sectionToneClasses =
                    getCollapsibleArrayToneClasses('primary');
                const sectionExpanded = isArrayItemExpanded(sectionKey, false);
                const infoHeaderPath = 'InfoHeader';
                const infoHeaderEditStateKey = `${sectionKey}.__section_header__`;
                const isInfoHeaderEditing = isArrayItemExpanded(
                    infoHeaderEditStateKey,
                    false
                );
                const infoHeaderValue = String(
                    readValueAtPath(component_props, infoHeaderPath) ?? ''
                ).trim();
                const detailsId = 'component-editor-footer-info-section';

                footerInfoKeys.forEach((infoKey) => skipped.add(infoKey));
                nodes.push(
                    <div key={sectionKey} className="mt-4">
                        <div
                            className={`overflow-hidden rounded-xl border shadow-sm ${sectionToneClasses.wrapper}`}
                        >
                            {isInfoHeaderEditing ? (
                                <div
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left ${
                                        sectionExpanded
                                            ? `border-b ${sectionToneClasses.headerOpenBorder}`
                                            : ''
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div
                                            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${sectionToneClasses.metaText}`}
                                        >
                                            Info
                                        </div>
                                        <form
                                            onSubmit={(event) => {
                                                event.preventDefault();
                                                onChange({
                                                    file,
                                                    key: infoHeaderPath,
                                                    value: readInlineFieldDraft(
                                                        infoHeaderPath,
                                                        readValueAtPath(
                                                            component_props,
                                                            infoHeaderPath
                                                        ) ?? ''
                                                    ),
                                                });
                                                setArrayItemExpanded(
                                                    infoHeaderEditStateKey,
                                                    false
                                                );
                                                clearInlineFieldDraft(
                                                    infoHeaderPath
                                                );
                                            }}
                                            className="mt-1 flex min-w-0 items-center gap-1.5"
                                        >
                                            <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                <input
                                                    type="text"
                                                    value={readInlineFieldDraft(
                                                        infoHeaderPath,
                                                        readValueAtPath(
                                                            component_props,
                                                            infoHeaderPath
                                                        ) ?? ''
                                                    )}
                                                    onChange={(event) =>
                                                        setInlineFieldDraft(
                                                            infoHeaderPath,
                                                            event.target.value
                                                        )
                                                    }
                                                    placeholder="Info Header"
                                                    className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                    autoFocus
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                title="Save Info Header"
                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                            >
                                                <CheckIcon className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button"
                                                title="Cancel Info Header edit"
                                                onClick={() => {
                                                    setArrayItemExpanded(
                                                        infoHeaderEditStateKey,
                                                        false
                                                    );
                                                    clearInlineFieldDraft(
                                                        infoHeaderPath
                                                    );
                                                }}
                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                            >
                                                <XMarkIcon className="h-3 w-3" />
                                            </button>
                                        </form>
                                    </div>
                                    <button
                                        type="button"
                                        title={
                                            sectionExpanded
                                                ? 'Collapse info section'
                                                : 'Expand info section'
                                        }
                                        className={INLINE_ADD_BUTTON_CLASSNAME}
                                        aria-expanded={sectionExpanded}
                                        aria-controls={detailsId}
                                        onClick={() =>
                                            toggleArrayItemExpanded(
                                                sectionKey,
                                                false
                                            )
                                        }
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 ${
                                                sectionToneClasses.chevronText
                                            } transition-transform duration-200 ${
                                                sectionExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                                        sectionToneClasses.buttonHover
                                    } ${
                                        sectionExpanded
                                            ? `border-b ${sectionToneClasses.headerOpenBorder}`
                                            : ''
                                    }`}
                                    aria-expanded={sectionExpanded}
                                    aria-controls={detailsId}
                                    onClick={() =>
                                        toggleArrayItemExpanded(
                                            sectionKey,
                                            false
                                        )
                                    }
                                >
                                    <div className="min-w-0">
                                        <div
                                            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${sectionToneClasses.metaText}`}
                                        >
                                            Info
                                        </div>
                                        <div className="group mt-1 flex min-w-0 items-center gap-1">
                                            <div className="truncate text-sm font-semibold text-neutral-900">
                                                {infoHeaderValue ||
                                                    'Info Header'}
                                            </div>
                                            <button
                                                type="button"
                                                title="Edit Info Header"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setInlineFieldDraft(
                                                        infoHeaderPath,
                                                        readValueAtPath(
                                                            component_props,
                                                            infoHeaderPath
                                                        ) ?? ''
                                                    );
                                                    setArrayItemExpanded(
                                                        infoHeaderEditStateKey,
                                                        true
                                                    );
                                                }}
                                                className={
                                                    INLINE_EDIT_BUTTON_CLASSNAME
                                                }
                                            >
                                                <PencilSquareIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <span
                                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${sectionToneClasses.chevronText}`}
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 transition-transform duration-200 ${
                                                sectionExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </span>
                                </div>
                            )}

                            {sectionExpanded ? (
                                <div
                                    id={detailsId}
                                    className="bg-white px-3 pb-3 pt-2"
                                >
                                    {footerInfoKeys.map((infoKey) => (
                                        <React.Fragment key={infoKey}>
                                            {renderInput(
                                                component_props[infoKey],
                                                infoKey
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
                continue;
            }
            if (componentName === 'Nav' && navPhoneKey && key === navPhoneKey) {
                const navPhoneEditStateKey = `${navPhoneKey}.__inline_primary__`;
                const isNavPhoneEditing = isArrayItemExpanded(
                    navPhoneEditStateKey,
                    false
                );
                const navPhoneValue = String(
                    readValueAtPath(component_props, navPhoneKey) ?? ''
                ).trim();

                nodes.push(
                    <div key={navPhoneKey} className="mt-4">
                        <div
                            data-draggable-array-item="true"
                            className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm transition-all"
                        >
                            <div className="flex items-center gap-2 px-3 py-2">
                                <div className="min-w-0 flex-1">
                                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">
                                        Phone
                                    </div>
                                    {isNavPhoneEditing ? (
                                        <form
                                            onSubmit={(event) => {
                                                event.preventDefault();
                                                handleComponentPropChange({
                                                    key: navPhoneKey,
                                                    value: readInlineFieldDraft(
                                                        navPhoneKey,
                                                        readValueAtPath(
                                                            component_props,
                                                            navPhoneKey
                                                        ) ?? ''
                                                    ),
                                                });
                                                setArrayItemExpanded(
                                                    navPhoneEditStateKey,
                                                    false
                                                );
                                                clearInlineFieldDraft(
                                                    navPhoneKey
                                                );
                                            }}
                                            className="flex min-w-0 items-center gap-1.5"
                                        >
                                            <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                <input
                                                    type="text"
                                                    value={readInlineFieldDraft(
                                                        navPhoneKey,
                                                        readValueAtPath(
                                                            component_props,
                                                            navPhoneKey
                                                        ) ?? ''
                                                    )}
                                                    onChange={(event) =>
                                                        setInlineFieldDraft(
                                                            navPhoneKey,
                                                            event.target.value
                                                        )
                                                    }
                                                    placeholder="Phone"
                                                    className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                    autoFocus
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                title="Save Phone"
                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                            >
                                                <CheckIcon className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button"
                                                title="Cancel Phone edit"
                                                onClick={() => {
                                                    setArrayItemExpanded(
                                                        navPhoneEditStateKey,
                                                        false
                                                    );
                                                    clearInlineFieldDraft(
                                                        navPhoneKey
                                                    );
                                                }}
                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                            >
                                                <XMarkIcon className="h-3 w-3" />
                                            </button>
                                        </form>
                                    ) : (
                                        <div className="group flex min-w-0 items-center gap-1">
                                            <div className="truncate px-1 py-0.5 text-sm font-semibold text-neutral-900">
                                                {navPhoneValue || 'Phone'}
                                            </div>
                                            <button
                                                type="button"
                                                title="Edit Phone"
                                                onClick={() => {
                                                    setInlineFieldDraft(
                                                        navPhoneKey,
                                                        readValueAtPath(
                                                            component_props,
                                                            navPhoneKey
                                                        ) ?? ''
                                                    );
                                                    setArrayItemExpanded(
                                                        navPhoneEditStateKey,
                                                        true
                                                    );
                                                }}
                                                className={
                                                    INLINE_EDIT_BUTTON_CLASSNAME
                                                }
                                            >
                                                <PencilSquareIcon
                                                    className="h-3.5 w-3.5"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
                continue;
            }
            if (
                componentName === 'Nav' &&
                navBannerAnchorKey &&
                key === navBannerAnchorKey
            ) {
                navBannerKeys.forEach((bannerKey) => skipped.add(bannerKey));
                const bannerSectionKey = 'Nav.__banner__';
                const bannerExpanded = isArrayItemExpanded(
                    bannerSectionKey,
                    false
                );
                const bannerToneClasses =
                    getCollapsibleArrayToneClasses('primary');
                const bannerDetailsId = 'component-editor-nav-banner-section';
                const bannerTitlePath = navBannerTitleKey || 'BannerTitle';
                const bannerTitleEditStateKey = `${bannerSectionKey}.__section_header__`;
                const bannerLinkPath = navBannerKeys.find(
                    (bannerKey) =>
                        keyLeaf(bannerKey).toLowerCase() === 'bannerbuttonlink'
                );
                const bannerLinkEditStateKey = `${bannerSectionKey}.__section_link__`;
                const isBannerTitleEditing = isArrayItemExpanded(
                    bannerTitleEditStateKey,
                    false
                );
                const isBannerLinkEditing = bannerLinkPath
                    ? isArrayItemExpanded(bannerLinkEditStateKey, false)
                    : false;
                const bannerSummary = String(
                    (navBannerTitleKey &&
                        readValueAtPath(component_props, navBannerTitleKey)) ||
                        (navBannerTextKey &&
                            readValueAtPath(
                                component_props,
                                navBannerTextKey
                            )) ||
                        ''
                ).trim();

                nodes.push(
                    <div key={bannerSectionKey} className="mt-4">
                        <div
                            className={`relative overflow-visible rounded-xl border shadow-sm ${bannerToneClasses.wrapper}`}
                        >
                            {isBannerTitleEditing || isBannerLinkEditing ? (
                                <div
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left ${
                                        bannerExpanded
                                            ? `border-b ${bannerToneClasses.headerOpenBorder}`
                                            : ''
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div
                                            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${bannerToneClasses.metaText}`}
                                        >
                                            Banner
                                        </div>
                                        <div className="mt-1 space-y-1">
                                            {isBannerTitleEditing ? (
                                                <form
                                                    onSubmit={(event) => {
                                                        event.preventDefault();
                                                        onChange({
                                                            file,
                                                            key: bannerTitlePath,
                                                            value: readInlineFieldDraft(
                                                                bannerTitlePath,
                                                                readValueAtPath(
                                                                    component_props,
                                                                    bannerTitlePath
                                                                ) ?? ''
                                                            ),
                                                        });
                                                        setArrayItemExpanded(
                                                            bannerTitleEditStateKey,
                                                            false
                                                        );
                                                        clearInlineFieldDraft(
                                                            bannerTitlePath
                                                        );
                                                    }}
                                                    className="flex min-w-0 items-center gap-1.5"
                                                >
                                                    <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                        <input
                                                            type="text"
                                                            value={readInlineFieldDraft(
                                                                bannerTitlePath,
                                                                readValueAtPath(
                                                                    component_props,
                                                                    bannerTitlePath
                                                                ) ?? ''
                                                            )}
                                                            onChange={(event) =>
                                                                setInlineFieldDraft(
                                                                    bannerTitlePath,
                                                                    event.target
                                                                        .value
                                                                )
                                                            }
                                                            placeholder="Banner Title"
                                                            className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                            autoFocus
                                                        />
                                                    </div>
                                                    <button
                                                        type="submit"
                                                        title="Save Banner Title"
                                                        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                                    >
                                                        <CheckIcon className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Cancel Banner Title edit"
                                                        onClick={() => {
                                                            setArrayItemExpanded(
                                                                bannerTitleEditStateKey,
                                                                false
                                                            );
                                                            clearInlineFieldDraft(
                                                                bannerTitlePath
                                                            );
                                                        }}
                                                        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                                    >
                                                        <XMarkIcon className="h-3 w-3" />
                                                    </button>
                                                </form>
                                            ) : (
                                                <div className="group flex min-w-0 items-center gap-1">
                                                    <div className="truncate text-sm font-semibold text-neutral-900">
                                                        {bannerSummary ||
                                                            'Banner Title'}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        title="Edit Banner Title"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setInlineFieldDraft(
                                                                bannerTitlePath,
                                                                readValueAtPath(
                                                                    component_props,
                                                                    bannerTitlePath
                                                                ) ?? ''
                                                            );
                                                            setArrayItemExpanded(
                                                                bannerTitleEditStateKey,
                                                                true
                                                            );
                                                            setArrayItemExpanded(
                                                                bannerLinkEditStateKey,
                                                                false
                                                            );
                                                        }}
                                                        className={
                                                            INLINE_EDIT_BUTTON_CLASSNAME
                                                        }
                                                    >
                                                        <PencilSquareIcon className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            )}

                                            {bannerLinkPath ? (
                                                isBannerLinkEditing ? (
                                                    <form
                                                        onSubmit={(event) => {
                                                            event.preventDefault();
                                                            onChange({
                                                                file,
                                                                key: bannerLinkPath,
                                                                value: readInlineFieldDraft(
                                                                    bannerLinkPath,
                                                                    readValueAtPath(
                                                                        component_props,
                                                                        bannerLinkPath
                                                                    ) ?? ''
                                                                ),
                                                            });
                                                            setArrayItemExpanded(
                                                                bannerLinkEditStateKey,
                                                                false
                                                            );
                                                            clearInlineFieldDraft(
                                                                bannerLinkPath
                                                            );
                                                        }}
                                                        className="flex min-w-0 items-center gap-1.5"
                                                    >
                                                        <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <div className="flex h-6 items-stretch overflow-hidden rounded-[10px] bg-white">
                                                                <div className="flex w-7 shrink-0 items-center justify-center border-r border-neutral-200 text-neutral-500">
                                                                    <LinkIcon
                                                                        className="h-3 w-3"
                                                                        aria-hidden="true"
                                                                    />
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    value={readInlineFieldDraft(
                                                                        bannerLinkPath,
                                                                        readValueAtPath(
                                                                            component_props,
                                                                            bannerLinkPath
                                                                        ) ?? ''
                                                                    )}
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        setInlineFieldDraft(
                                                                            bannerLinkPath,
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    placeholder="Banner Link"
                                                                    className="block h-full w-full border-0 px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                                    autoFocus
                                                                />
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="submit"
                                                            title="Save Banner Link"
                                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                                        >
                                                            <CheckIcon className="h-3 w-3" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Cancel Banner Link edit"
                                                            onClick={() => {
                                                                setArrayItemExpanded(
                                                                    bannerLinkEditStateKey,
                                                                    false
                                                                );
                                                                clearInlineFieldDraft(
                                                                    bannerLinkPath
                                                                );
                                                            }}
                                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                                        >
                                                            <XMarkIcon className="h-3 w-3" />
                                                        </button>
                                                    </form>
                                                ) : (
                                                    <div className="group flex min-w-0 items-center gap-1">
                                                        <div className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md px-1 py-0.5 text-xs text-neutral-500">
                                                            <LinkIcon
                                                                className="h-3 w-3 shrink-0"
                                                                aria-hidden="true"
                                                            />
                                                            <span className="truncate">
                                                                {readValueAtPath(
                                                                    component_props,
                                                                    bannerLinkPath
                                                                ) ||
                                                                    'Banner Link'}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            title="Edit Banner Link"
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation();
                                                                setInlineFieldDraft(
                                                                    bannerLinkPath,
                                                                    readValueAtPath(
                                                                        component_props,
                                                                        bannerLinkPath
                                                                    ) ?? ''
                                                                );
                                                                setArrayItemExpanded(
                                                                    bannerLinkEditStateKey,
                                                                    true
                                                                );
                                                                setArrayItemExpanded(
                                                                    bannerTitleEditStateKey,
                                                                    false
                                                                );
                                                            }}
                                                            className={
                                                                INLINE_EDIT_BUTTON_CLASSNAME
                                                            }
                                                        >
                                                            <PencilSquareIcon className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                )
                                            ) : null}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        title={
                                            bannerExpanded
                                                ? 'Collapse banner'
                                                : 'Expand banner'
                                        }
                                        className={INLINE_ADD_BUTTON_CLASSNAME}
                                        aria-expanded={bannerExpanded}
                                        aria-controls={bannerDetailsId}
                                        onClick={() =>
                                            toggleArrayItemExpanded(
                                                bannerSectionKey,
                                                false
                                            )
                                        }
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 ${
                                                bannerToneClasses.chevronText
                                            } transition-transform duration-200 ${
                                                bannerExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                                        bannerToneClasses.buttonHover
                                    } ${
                                        bannerExpanded
                                            ? `border-b ${bannerToneClasses.headerOpenBorder}`
                                            : ''
                                    }`}
                                    aria-expanded={bannerExpanded}
                                    aria-controls={bannerDetailsId}
                                    onClick={() =>
                                        toggleArrayItemExpanded(
                                            bannerSectionKey,
                                            false
                                        )
                                    }
                                >
                                    <div className="min-w-0">
                                        <div
                                            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${bannerToneClasses.metaText}`}
                                        >
                                            Banner
                                        </div>
                                        <div className="group mt-1 flex min-w-0 items-center gap-1">
                                            <div className="truncate text-sm font-semibold text-neutral-900">
                                                {bannerSummary ||
                                                    'Banner Title'}
                                            </div>
                                            <button
                                                type="button"
                                                title="Edit Banner Title"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setInlineFieldDraft(
                                                        bannerTitlePath,
                                                        readValueAtPath(
                                                            component_props,
                                                            bannerTitlePath
                                                        ) ?? ''
                                                    );
                                                    setArrayItemExpanded(
                                                        bannerTitleEditStateKey,
                                                        true
                                                    );
                                                    setArrayItemExpanded(
                                                        bannerLinkEditStateKey,
                                                        false
                                                    );
                                                }}
                                                className={
                                                    INLINE_EDIT_BUTTON_CLASSNAME
                                                }
                                            >
                                                <PencilSquareIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                        {bannerLinkPath ? (
                                            <div className="group mt-1 flex min-w-0 items-center gap-1">
                                                <div className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md px-1 py-0.5 text-xs text-neutral-500">
                                                    <LinkIcon
                                                        className="h-3 w-3 shrink-0"
                                                        aria-hidden="true"
                                                    />
                                                    <span className="truncate">
                                                        {readValueAtPath(
                                                            component_props,
                                                            bannerLinkPath
                                                        ) || 'Banner Link'}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    title="Edit Banner Link"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setInlineFieldDraft(
                                                            bannerLinkPath,
                                                            readValueAtPath(
                                                                component_props,
                                                                bannerLinkPath
                                                            ) ?? ''
                                                        );
                                                        setArrayItemExpanded(
                                                            bannerLinkEditStateKey,
                                                            true
                                                        );
                                                        setArrayItemExpanded(
                                                            bannerTitleEditStateKey,
                                                            false
                                                        );
                                                    }}
                                                    className={
                                                        INLINE_EDIT_BUTTON_CLASSNAME
                                                    }
                                                >
                                                    <PencilSquareIcon className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                    <span
                                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${bannerToneClasses.chevronText}`}
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 transition-transform duration-200 ${
                                                bannerExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </span>
                                </div>
                            )}
                            {bannerExpanded ? (
                                <div
                                    id={bannerDetailsId}
                                    className="rounded-b-xl bg-white px-3 pb-3 pt-2"
                                >
                                    {navBannerKeys
                                        .filter(
                                            (bannerKey) =>
                                                bannerKey !==
                                                    navBannerTitleKey &&
                                                bannerKey !== bannerLinkPath
                                        )
                                        .map((bannerKey) => (
                                            <React.Fragment key={bannerKey}>
                                                {renderInput(
                                                    component_props[bannerKey],
                                                    bannerKey
                                                )}
                                            </React.Fragment>
                                        ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
                continue;
            }
            if (
                beforeAfterAnchorKey &&
                key === beforeAfterAnchorKey &&
                beforeImageKey &&
                afterImageKey
            ) {
                skipped.add(beforeImageKey);
                skipped.add(afterImageKey);
                if (beforeAltKey) skipped.add(beforeAltKey);
                if (afterAltKey) skipped.add(afterAltKey);
                const beforeAfterSectionKey = `${
                    componentName || 'component'
                }.__before_after__`;
                const beforeAfterExpanded = isArrayItemExpanded(
                    beforeAfterSectionKey,
                    false
                );
                const beforeAfterDetailsId = `component-editor-${String(
                    beforeAfterSectionKey
                ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                const beforeImageValue = component_props[beforeImageKey];
                const afterImageValue = component_props[afterImageKey];
                const beforeAfterHasBothImages =
                    isSelectedEditorMedia(beforeImageValue) &&
                    isSelectedEditorMedia(afterImageValue);
                const beforeAfterToneClasses = beforeAfterHasBothImages
                    ? getCollapsibleArrayToneClasses('gradient')
                    : getEmptyMediaCollapsibleToneClasses();
                const beforeAfterHeaderStyle = beforeAfterHasBothImages
                    ? {
                          backgroundImage: `linear-gradient(135deg, rgba(5, 12, 23, 0.76) 0%, rgba(5, 12, 23, 0.58) 55%, rgba(5, 12, 23, 0.42) 100%), url(${JSON.stringify(
                              normalizeCollapsibleArrayBackgroundImageValue(
                                  beforeImageValue
                              )
                          )}), url(${JSON.stringify(
                              normalizeCollapsibleArrayBackgroundImageValue(
                                  afterImageValue
                              )
                          )})`,
                          backgroundSize: 'cover, 50% 100%, 50% 100%',
                          backgroundPosition:
                              'center, left center, right center',
                          backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
                      }
                    : undefined;
                const beforeAfterMetaClassName = beforeAfterHasBothImages
                    ? 'text-white/80 drop-shadow-[0_1px_1px_rgba(0,0,0,0.55)]'
                    : beforeAfterToneClasses.metaText;
                const beforeAfterTitleClassName = beforeAfterHasBothImages
                    ? 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]'
                    : 'text-neutral-900';
                const beforeAfterChevronClassName = beforeAfterHasBothImages
                    ? 'inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full bg-black/15 text-white shadow-sm backdrop-blur-[2px] transition-all hover:bg-black/30'
                    : `inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${beforeAfterToneClasses.chevronText}`;
                const beforeAfterUploadImageKey = !isSelectedEditorMedia(
                    beforeImageValue
                )
                    ? beforeImageKey
                    : !isSelectedEditorMedia(afterImageValue)
                    ? afterImageKey
                    : beforeImageKey;
                const renderBeforeAfterImageCard = ({
                    imageKey,
                    imageValue,
                    altKey,
                }) => {
                    const imagePath = imageKey;

                    return (
                        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                            <div className="flex items-center justify-between gap-2 px-3 py-2">
                                <div className="min-w-0 flex-1">
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">
                                        {imageKey === beforeImageKey
                                            ? 'Before Image'
                                            : 'After Image'}
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white px-3 pb-3 pt-2">
                                {renderInput(imageValue, imagePath, 0, {
                                    inlineMediaPanelProps: {
                                        isOpen: true,
                                        layout: 'media-row',
                                    },
                                    inlineMediaAltProps: altKey
                                        ? {
                                              key: altKey,
                                              value:
                                                  readValueAtPath(
                                                      component_props,
                                                      altKey
                                                  ) ?? '',
                                          }
                                        : null,
                                    forceInlineMediaPanel: true,
                                    hideLabel: true,
                                    hideImagePreview: true,
                                    hideImageActionButtons: true,
                                })}
                            </div>
                        </div>
                    );
                };
                nodes.push(
                    <div key="before-after-pair" className="mb-3 mt-4">
                        <div
                            className={`overflow-hidden rounded-xl border shadow-sm ${beforeAfterToneClasses.wrapper}`}
                        >
                            <div
                                style={beforeAfterHeaderStyle}
                                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                                    beforeAfterHasBothImages
                                        ? 'bg-neutral-900/70'
                                        : beforeAfterToneClasses.buttonHover
                                } ${
                                    beforeAfterExpanded
                                        ? `border-b ${
                                              beforeAfterHasBothImages
                                                  ? 'border-white/15'
                                                  : beforeAfterToneClasses.headerOpenBorder
                                          }`
                                        : ''
                                }`}
                            >
                                <button
                                    type="button"
                                    className="min-w-0 flex-1 text-left focus-visible:outline-none"
                                    aria-expanded={beforeAfterExpanded}
                                    aria-controls={beforeAfterDetailsId}
                                    onClick={() =>
                                        toggleArrayItemExpanded(
                                            beforeAfterSectionKey,
                                            false
                                        )
                                    }
                                >
                                    <div
                                        className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${beforeAfterMetaClassName}`}
                                    >
                                        Before &amp; After
                                    </div>
                                    <div
                                        className={`mt-1 text-sm font-semibold ${beforeAfterTitleClassName}`}
                                    >
                                        Before and After Images
                                    </div>
                                </button>
                                <span className="flex items-center gap-1">
                                    {beforeAfterUploadImageKey ? (
                                        <>
                                            <button
                                                type="button"
                                                title={
                                                    beforeAfterUploadImageKey ===
                                                    afterImageKey
                                                        ? 'Upload after image'
                                                        : 'Upload before image'
                                                }
                                                className={
                                                    beforeAfterChevronClassName
                                                }
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    mediaUploadInputRefs.current[
                                                        beforeAfterUploadImageKey
                                                    ]?.click();
                                                }}
                                            >
                                                <ArrowUpTrayIcon
                                                    className="h-3 w-3"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                ref={(node) => {
                                                    if (node) {
                                                        mediaUploadInputRefs.current[
                                                            beforeAfterUploadImageKey
                                                        ] = node;
                                                    } else {
                                                        delete mediaUploadInputRefs
                                                            .current[
                                                            beforeAfterUploadImageKey
                                                        ];
                                                    }
                                                }}
                                                onChange={(event) =>
                                                    handleInlineMediaUpload(
                                                        beforeAfterUploadImageKey,
                                                        event
                                                    )
                                                }
                                            />
                                        </>
                                    ) : null}
                                    <button
                                        type="button"
                                        aria-label="Toggle before and after images"
                                        aria-expanded={beforeAfterExpanded}
                                        aria-controls={beforeAfterDetailsId}
                                        className={beforeAfterChevronClassName}
                                        onClick={() =>
                                            toggleArrayItemExpanded(
                                                beforeAfterSectionKey,
                                                false
                                            )
                                        }
                                    >
                                        <ChevronDownIcon
                                            className={`h-3 w-3 ${
                                                beforeAfterHasBothImages
                                                    ? 'text-white'
                                                    : beforeAfterToneClasses.chevronText
                                            } transition-transform duration-200 ${
                                                beforeAfterExpanded
                                                    ? 'rotate-180'
                                                    : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </span>
                            </div>

                            {beforeAfterExpanded ? (
                                <div
                                    id={beforeAfterDetailsId}
                                    className="bg-white px-3 pb-3 pt-2"
                                >
                                    <div className="flex flex-col gap-4">
                                        {renderBeforeAfterImageCard({
                                            imageKey: beforeImageKey,
                                            imageValue:
                                                component_props[beforeImageKey],
                                            altKey: beforeAltKey,
                                        })}
                                        {renderBeforeAfterImageCard({
                                            imageKey: afterImageKey,
                                            imageValue:
                                                component_props[afterImageKey],
                                            altKey: afterAltKey,
                                        })}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
                continue;
            }
            if (
                beforeAfterAnchorKey &&
                (key === beforeImageKey ||
                    key === afterImageKey ||
                    key === beforeAltKey ||
                    key === afterAltKey)
            ) {
                continue;
            }
            const fieldValue =
                componentName === 'Footer' &&
                key === 'Medias' &&
                component_props[key] === undefined
                    ? []
                    : component_props[key];
            pushContentNode(key, renderInput(fieldValue, key));
            if (
                componentName === 'ReviewHero' &&
                keyLeaf(key).toLowerCase() === 'reviews'
            ) {
                pushContentNode(key, renderReviewHeroCTAButtonsSection());
            }
        }
        return nodes;
    })();

    function renderNavPhoneButtonCard(phonePair, buttonsSectionKey) {
        if (!phonePair || phonePair.kind !== 'nav-phone') return null;

        const readPhoneValue = (key, fallback = '') => {
            const rawValue = component_props[key];
            const value =
                rawValue && typeof rawValue === 'object' && 'value' in rawValue
                    ? rawValue.value
                    : rawValue;
            return value ?? fallback;
        };
        const phoneValue = String(readPhoneValue(phonePair.textKey));
        const showPhoneNumber = coerceBoolean(
            readPhoneValue(phonePair.showTextKey, false)
        );
        const showDesktopIcon = coerceBoolean(
            readPhoneValue(phonePair.iconVisibleKey, false)
        );
        const desktopIconValue = String(
            readPhoneValue(phonePair.iconKey, 'fa-solid fa-phone')
        );
        const mobileIconValue = String(
            readPhoneValue(
                phonePair.mobileIconKey,
                desktopIconValue || 'fa-solid fa-phone'
            )
        );
        const iconPosition =
            String(readPhoneValue(phonePair.iconPositionKey, 'before'))
                .trim()
                .toLowerCase() === 'after'
                ? 'after'
                : 'before';

        return (
            <div
                key={`${buttonsSectionKey}-${phonePair.textKey}`}
                className="mb-3 rounded-xl bg-secondary-50 p-3"
                data-nav-phone-button-controls="true"
            >
                <div className="text-xs font-semibold leading-5 text-primary-700">
                    Phone Button
                </div>
                <div className="text-[11px] leading-4 text-neutral-600">
                    Uses the navigation’s secondary button style.
                </div>

                <label className="mt-3 block text-xs font-semibold leading-5 text-primary-700">
                    Phone Number
                </label>
                <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                    <input
                        type="text"
                        value={phoneValue}
                        onChange={(event) =>
                            onChange({
                                file,
                                key: phonePair.textKey,
                                value: event.target.value,
                            })
                        }
                        placeholder="(000) 000-0000"
                        className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                    />
                </div>

                <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2">
                    <div>
                        <div className="text-xs font-semibold leading-5 text-primary-700">
                            Show Number on Desktop
                        </div>
                        <div className="text-[11px] leading-4 text-neutral-600">
                            When off, the desktop button displays only its icon.
                        </div>
                    </div>
                    <Switch
                        size="sm"
                        checked={showPhoneNumber}
                        onChange={(enabled) =>
                            onChange({
                                file,
                                key: phonePair.showTextKey,
                                value: enabled,
                            })
                        }
                        ariaLabel="Show phone number on desktop"
                    />
                </div>

                <div className="mt-3">
                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                        Desktop Icon
                    </label>
                    <div className="text-[11px] leading-4 text-neutral-600">
                        {showPhoneNumber
                            ? 'Can appear before or after the phone number.'
                            : 'Displayed by itself when the phone number is hidden.'}
                    </div>
                    <div
                        className="mt-2"
                        data-inline-icon-field={phonePair.iconKey}
                    >
                        <FontAwesomeIconPicker
                            value={desktopIconValue}
                            onChange={(next) =>
                                onChange({
                                    file,
                                    key: phonePair.iconKey,
                                    value: next,
                                })
                            }
                        />
                    </div>
                </div>

                {showPhoneNumber ? (
                    <>
                        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2">
                            <div>
                                <div className="text-xs font-semibold leading-5 text-primary-700">
                                    Show Icon with Number
                                </div>
                                <div className="text-[11px] leading-4 text-neutral-600">
                                    Applies to the desktop button only.
                                </div>
                            </div>
                            <Switch
                                size="sm"
                                checked={showDesktopIcon}
                                onChange={(enabled) =>
                                    onChange({
                                        file,
                                        key: phonePair.iconVisibleKey,
                                        value: enabled,
                                    })
                                }
                                ariaLabel="Show icon beside phone number on desktop"
                            />
                        </div>

                        {showDesktopIcon ? (
                            <div className="mt-3">
                                <div className="text-xs font-semibold leading-5 text-primary-700">
                                    Icon Position
                                </div>
                                <div className="mt-1 grid grid-cols-2 gap-1 rounded-lg bg-neutral-200 p-1">
                                    {[
                                        ['before', 'Before Number'],
                                        ['after', 'After Number'],
                                    ].map(([value, label]) => (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() =>
                                                onChange({
                                                    file,
                                                    key: phonePair.iconPositionKey,
                                                    value,
                                                })
                                            }
                                            className={`rounded-md px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                                                iconPosition === value
                                                    ? 'bg-white text-primary-700 shadow-sm'
                                                    : 'text-neutral-600 hover:bg-white/60'
                                            }`}
                                            aria-pressed={
                                                iconPosition === value
                                            }
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </>
                ) : null}

                <div className="mt-3 border-t border-secondary-100 pt-3">
                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                        Mobile Icon
                    </label>
                    <div className="text-[11px] leading-4 text-neutral-600">
                        Mobile always displays this icon without the phone
                        number.
                    </div>
                    <div
                        className="mt-2"
                        data-inline-icon-field={phonePair.mobileIconKey}
                    >
                        <FontAwesomeIconPicker
                            value={mobileIconValue}
                            onChange={(next) =>
                                onChange({
                                    file,
                                    key: phonePair.mobileIconKey,
                                    value: next,
                                })
                            }
                        />
                    </div>
                </div>
            </div>
        );
    }

    function renderComponentCTAButtonsSection(
        targetComponentName = componentName
    ) {
        if (targetComponentName === 'ReviewHero') return null;
        const buttonPairs = resolveButtonPairs(allKeys, targetComponentName);
        if (buttonPairs.length === 0) return null;

        const buttonsSectionKey = `${
            targetComponentName || 'component'
        }.__cta_buttons__`;
        const buttonsExpanded = isArrayItemExpanded(buttonsSectionKey, false);
        const buttonToneClasses = getCollapsibleArrayToneClasses('cta');
        const buttonsDetailsId = `component-editor-${String(
            buttonsSectionKey
        ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
        const buttonSummary = buttonPairs
            .map(({ textKey }) => {
                const rawValue =
                    component_props[textKey] &&
                    typeof component_props[textKey] === 'object' &&
                    'value' in component_props[textKey]
                        ? component_props[textKey].value
                        : component_props[textKey];
                const textValue = String(rawValue ?? '').trim();
                return textValue;
            })
            .filter(Boolean)
            .join(', ');

        return (
            <div className="mt-8">
                <div
                    className={`overflow-hidden rounded-xl border shadow-sm ${buttonToneClasses.wrapper}`}
                >
                    <button
                        type="button"
                        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                            buttonToneClasses.buttonHover
                        } ${
                            buttonsExpanded
                                ? `border-b ${buttonToneClasses.headerOpenBorder}`
                                : ''
                        }`}
                        aria-expanded={buttonsExpanded}
                        aria-controls={buttonsDetailsId}
                        onClick={() =>
                            toggleArrayItemExpanded(buttonsSectionKey, false)
                        }
                    >
                        <div className="min-w-0">
                            <div
                                className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${buttonToneClasses.metaText}`}
                            >
                                <CursorArrowRaysIcon
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                                <span>CTA Buttons</span>
                            </div>
                            {buttonSummary ? (
                                <div className="mt-1 truncate text-sm font-semibold text-white">
                                    {buttonSummary}
                                </div>
                            ) : null}
                        </div>
                        <span
                            className={`inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${buttonToneClasses.chevronText}`}
                        >
                            <ChevronDownIcon
                                className={`h-3 w-3 transition-transform duration-200 ${
                                    buttonsExpanded ? 'rotate-180' : ''
                                }`}
                                aria-hidden="true"
                            />
                        </span>
                    </button>

                    {buttonsExpanded ? (
                        <div
                            id={buttonsDetailsId}
                            className="bg-white px-3 pb-3 pt-2"
                        >
                            {buttonPairs.map(
                                ({
                                    textKey,
                                    linkKey,
                                    iconKey,
                                    iconVisibleKey,
                                    openInNewTabKey,
                                    label,
                                    linkLabel,
                                }) => {
                                    const readVal = (k) =>
                                        k == null
                                            ? ''
                                            : String(
                                                  (component_props[k] &&
                                                  typeof component_props[k] ===
                                                      'object' &&
                                                  'value' in component_props[k]
                                                      ? component_props[k].value
                                                      : component_props[k]) ??
                                                      ''
                                              );
                                    const textVal = readVal(textKey);
                                    const hasButtonText =
                                        textVal.trim().length > 0;
                                    const iconVal = readVal(iconKey);
                                    const iconVisible = iconVisibleKey
                                        ? coerceBoolean(
                                              (component_props[
                                                  iconVisibleKey
                                              ] &&
                                              typeof component_props[
                                                  iconVisibleKey
                                              ] === 'object' &&
                                              'value' in
                                                  component_props[
                                                      iconVisibleKey
                                                  ]
                                                  ? component_props[
                                                        iconVisibleKey
                                                    ].value
                                                  : component_props[
                                                        iconVisibleKey
                                                    ]) ?? true
                                          )
                                        : true;
                                    const linkVal = readVal(linkKey);
                                    const showLink = !!linkKey && hasButtonText;
                                    const canOpenInNewTab =
                                        !!openInNewTabKey &&
                                        showLink &&
                                        linkVal.trim().length > 0;
                                    const textLeafLower =
                                        keyLeaf(textKey).toLowerCase();
                                    const isCtaButton =
                                        textLeafLower === 'cta' ||
                                        textLeafLower === 'smallcta';
                                    const isSecondaryButton =
                                        textLeafLower.includes('secondary');
                                    const cardClassName = isCtaButton
                                        ? 'rounded-xl bg-primary-50 p-3'
                                        : isSecondaryButton
                                        ? 'rounded-xl bg-secondary-50 p-3'
                                        : 'rounded-xl border border-neutral-300 bg-white p-3';
                                    const openInNewTabVal = canOpenInNewTab
                                        ? coerceBoolean(
                                              (component_props[
                                                  openInNewTabKey
                                              ] &&
                                              typeof component_props[
                                                  openInNewTabKey
                                              ] === 'object' &&
                                              'value' in
                                                  component_props[
                                                      openInNewTabKey
                                                  ]
                                                  ? component_props[
                                                        openInNewTabKey
                                                    ].value
                                                  : component_props[
                                                        openInNewTabKey
                                                    ]) ?? false
                                          )
                                        : false;

                                    return (
                                        <div
                                            key={`${buttonsSectionKey}-${textKey}`}
                                            className={`mb-3 ${cardClassName}`}
                                        >
                                            <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                {label || prettyLabel(textKey)}
                                            </label>
                                            <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                <input
                                                    type="text"
                                                    value={textVal}
                                                    onChange={(e) =>
                                                        onChange({
                                                            file,
                                                            key: textKey,
                                                            value: e.target
                                                                .value,
                                                        })
                                                    }
                                                    className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                />
                                            </div>

                                            {hasButtonText && iconKey && (
                                                <div className="mt-3">
                                                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                        {prettyLabel(iconKey)}
                                                    </label>
                                                    <div
                                                        className="mt-2"
                                                        data-inline-icon-field={
                                                            iconKey
                                                        }
                                                    >
                                                        <FontAwesomeIconPicker
                                                            value={iconVal}
                                                            onChange={(next) =>
                                                                onChange({
                                                                    file,
                                                                    key: iconKey,
                                                                    value: next,
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {hasButtonText &&
                                                iconVisibleKey && (
                                                    <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1">
                                                        <div className="text-[11px] font-semibold leading-4 text-primary-700">
                                                            Show Icon
                                                        </div>
                                                        <Switch
                                                            size="sm"
                                                            checked={
                                                                iconVisible
                                                            }
                                                            onChange={(
                                                                enable
                                                            ) =>
                                                                onChange({
                                                                    file,
                                                                    key: iconVisibleKey,
                                                                    value: enable,
                                                                })
                                                            }
                                                            ariaLabel={`${prettyLabel(
                                                                iconKey ||
                                                                    textKey
                                                            )} visible`}
                                                        />
                                                    </div>
                                                )}

                                            {showLink && (
                                                <div className="mt-3">
                                                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                        {linkLabel ||
                                                            prettyLabel(
                                                                linkKey
                                                            )}
                                                    </label>
                                                    <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                        <input
                                                            type="text"
                                                            value={linkVal}
                                                            onChange={(e) =>
                                                                onChange({
                                                                    file,
                                                                    key: linkKey,
                                                                    value: e
                                                                        .target
                                                                        .value,
                                                                })
                                                            }
                                                            className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {canOpenInNewTab && (
                                                <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1">
                                                    <div className="text-[11px] font-semibold leading-4 text-primary-700">
                                                        Open in new tab
                                                    </div>
                                                    <Switch
                                                        size="sm"
                                                        checked={
                                                            openInNewTabVal
                                                        }
                                                        onChange={(enable) =>
                                                            onChange({
                                                                file,
                                                                key: openInNewTabKey,
                                                                value: enable,
                                                            })
                                                        }
                                                        ariaLabel={`${prettyLabel(
                                                            textKey
                                                        )} open in new tab`}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    function renderReviewHeroCTAButtonsSection() {
        if (componentName !== 'ReviewHero') return null;
        const buttonPairs = resolveButtonPairs(allKeys, componentName);
        if (buttonPairs.length === 0) return null;

        const buttonsSectionKey = `${componentName}.__cta_buttons__`;
        const buttonsExpanded = isArrayItemExpanded(buttonsSectionKey, false);
        const buttonToneClasses = getCollapsibleArrayToneClasses('cta');
        const buttonsDetailsId = `component-editor-${String(
            buttonsSectionKey
        ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
        const readButtonValue = (buttonKey) => {
            const raw = component_props[buttonKey];
            return String(
                raw && typeof raw === 'object' && 'value' in raw
                    ? raw.value ?? ''
                    : raw ?? ''
            );
        };
        const buttonSummary = buttonPairs
            .map(({ textKey }) => readButtonValue(textKey).trim())
            .filter(Boolean)
            .join(', ');

        return (
            <div className="mt-8">
                <div
                    className={`overflow-hidden rounded-xl border shadow-sm ${buttonToneClasses.wrapper}`}
                >
                    <button
                        type="button"
                        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                            buttonToneClasses.buttonHover
                        } ${
                            buttonsExpanded
                                ? `border-b ${buttonToneClasses.headerOpenBorder}`
                                : ''
                        }`}
                        aria-expanded={buttonsExpanded}
                        aria-controls={buttonsDetailsId}
                        onClick={() =>
                            toggleArrayItemExpanded(buttonsSectionKey, false)
                        }
                    >
                        <div className="min-w-0">
                            <div
                                className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${buttonToneClasses.metaText}`}
                            >
                                <CursorArrowRaysIcon
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                                <span>CTA Buttons</span>
                            </div>
                            {buttonSummary ? (
                                <div className="mt-1 truncate text-sm font-semibold text-white">
                                    {buttonSummary}
                                </div>
                            ) : null}
                        </div>
                        <span
                            className={`inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${buttonToneClasses.chevronText}`}
                        >
                            <ChevronDownIcon
                                className={`h-3 w-3 transition-transform duration-200 ${
                                    buttonsExpanded ? 'rotate-180' : ''
                                }`}
                                aria-hidden="true"
                            />
                        </span>
                    </button>

                    {buttonsExpanded ? (
                        <div
                            id={buttonsDetailsId}
                            className="bg-white px-3 pb-3 pt-2"
                        >
                            {buttonPairs.map(
                                ({
                                    textKey,
                                    linkKey,
                                    label,
                                    linkLabel,
                                    openInNewTabKey,
                                }) => {
                                    const textVal = readButtonValue(textKey);
                                    const hasButtonText =
                                        textVal.trim().length > 0;
                                    const linkVal = linkKey
                                        ? readButtonValue(linkKey)
                                        : '';
                                    const openInNewTabVal = openInNewTabKey
                                        ? coerceBoolean(
                                              component_props[
                                                  openInNewTabKey
                                              ] &&
                                                  typeof component_props[
                                                      openInNewTabKey
                                                  ] === 'object' &&
                                                  'value' in
                                                      component_props[
                                                          openInNewTabKey
                                                      ]
                                                  ? component_props[
                                                        openInNewTabKey
                                                    ].value
                                                  : component_props[
                                                        openInNewTabKey
                                                    ] ?? false
                                          )
                                        : false;
                                    const textLeafLower =
                                        keyLeaf(textKey).toLowerCase();
                                    const isCtaButton =
                                        textLeafLower === 'cta' ||
                                        textLeafLower === 'smallcta';
                                    const isSecondaryButton =
                                        textLeafLower.includes('secondary');
                                    const cardClassName = isCtaButton
                                        ? 'rounded-xl bg-primary-50 p-3'
                                        : isSecondaryButton
                                        ? 'rounded-xl bg-secondary-50 p-3'
                                        : 'rounded-xl border border-neutral-300 bg-white p-3';

                                    return (
                                        <div
                                            key={`reviewhero-btn-${textKey}`}
                                            className={`mb-3 ${cardClassName}`}
                                        >
                                            <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                {label || prettyLabel(textKey)}
                                            </label>
                                            <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                <input
                                                    type="text"
                                                    value={textVal}
                                                    onChange={(e) =>
                                                        onChange({
                                                            file,
                                                            key: textKey,
                                                            value: e.target
                                                                .value,
                                                        })
                                                    }
                                                    className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                />
                                            </div>

                                            {linkKey && hasButtonText ? (
                                                <div className="mt-3">
                                                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                        {linkLabel ||
                                                            prettyLabel(
                                                                linkKey
                                                            )}
                                                    </label>
                                                    <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                        <input
                                                            type="text"
                                                            value={linkVal}
                                                            onChange={(e) =>
                                                                onChange({
                                                                    file,
                                                                    key: linkKey,
                                                                    value: e
                                                                        .target
                                                                        .value,
                                                                })
                                                            }
                                                            className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            ) : null}

                                            {openInNewTabKey &&
                                            linkKey &&
                                            hasButtonText ? (
                                                <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1">
                                                    <div className="text-[11px] font-semibold leading-4 text-primary-700">
                                                        Open in new tab
                                                    </div>
                                                    <Switch
                                                        size="sm"
                                                        checked={
                                                            openInNewTabVal
                                                        }
                                                        onChange={(enable) =>
                                                            onChange({
                                                                file,
                                                                key: openInNewTabKey,
                                                                value: enable,
                                                            })
                                                        }
                                                        ariaLabel={`${prettyLabel(
                                                            textKey
                                                        )} open in new tab`}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <EditorDrawer
            storageKey="globalEditorDrawerv2"
            bodyRef={editorScrollRef}
            title={
                activeMediaField ? (
                    <button
                        onClick={handleCloseMediaPanel}
                        className="flex items-center gap-2 text-white transition-colors hover:text-white/80"
                    >
                        <ArrowLeftIcon className="h-5 w-5" />
                        <span>Back to Editor</span>
                    </button>
                ) : (componentName || '').toLowerCase() === 'page' ? (
                    'Page Settings'
                ) : (
                    'Component Editor'
                )
            }
            open={open}
            onClose={activeMediaField ? handleCloseMediaPanel : onClose}
            onCancel={activeMediaField ? handleCloseMediaPanel : onCancel}
            onSave={() => {
                onSave();
                onClose();
            }}
            hideFooter={!!activeMediaField}
        >
            {activeMediaField ? (
                <div className="relative flex h-full flex-col">
                    <div className="relative flex-1">
                        <MediaPanel
                            currentValue={activeMediaField.value}
                            onSelectImage={handleMediaSelect}
                            onSelectVideo={handleMediaSelect}
                            onSelectCustom={handleMediaSelect}
                            onPreviewSelect={handleMediaPreview}
                            showBackgroundVideoWarning={[
                                'HeroFullBG',
                                'CTAHero',
                            ].includes(componentName)}
                        />
                    </div>
                </div>
            ) : componentName === 'JobPricingCalculator' ? (
                <JobPricingCalculatorEditor
                    componentProps={component_props}
                    file={file}
                    onChange={onChange}
                    onChooseBackground={() =>
                        setActiveMediaField({
                            key: 'BackgroundImage',
                            value: component_props?.BackgroundImage || '',
                        })
                    }
                    onChooseResultImage={() =>
                        setActiveMediaField({
                            key: 'ResultImage',
                            value: component_props?.ResultImage || '',
                        })
                    }
                />
            ) : (
                <>
                    {/* Header / prompt for component */}
                    <PromptInput
                        label={
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <RectangleGroupIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>
                                    {formatComponentName(componentName)}
                                </span>
                            </div>
                        }
                        file={file}
                        componentName={componentName}
                    />

                    {componentName === 'Privacy' && (
                        <PrivacyDocumentImport
                            file={file}
                            componentName={componentName}
                            onChange={onChange}
                        />
                    )}

                    {componentName === 'TermsAndConditions' && (
                        <TermsDocumentImport
                            file={file}
                            componentName={componentName}
                            onChange={onChange}
                        />
                    )}

                    {/* Shape Divider controls */}
                    {isShapeDivider && (
                        <div className="mt-4">
                            <ShapeDividerControls
                                shape={String(component_props.Shape ?? 'tilt')}
                                colorFill={String(
                                    component_props.ColorFill ?? 'primary'
                                )}
                                flip={String(component_props.Flip ?? '')}
                                onRemove={onDeleteComponent}
                                onChange={(key, value) =>
                                    onChange({ file, key, value })
                                }
                            />
                        </div>
                    )}

                    {/* Content */}
                    {contentNodes}

                    {isPageComponent && pageCustomCodeKeys.length > 0 && (
                        <div className="mt-6">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <CodeBracketIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>Custom Code</span>
                            </div>

                            {pageCustomCodeKeys.map((codeKey) => (
                                <React.Fragment
                                    key={`page-custom-code-${codeKey}`}
                                >
                                    {renderInput(
                                        component_props[codeKey],
                                        codeKey
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}

                    {/* Image Position */}
                    {imagePosKey &&
                        !MEDIA_POSITION_COMPONENTS.has(componentName) &&
                        !shouldHideProp(componentName, imagePosKey) && (
                            <div className="mb-4">
                                <label className="block text-xs font-semibold leading-5 text-primary-700">
                                    Image Position
                                </label>
                                <div className="mt-1">
                                    {(() => {
                                        const allowedPositions =
                                            componentName === 'Popup' ||
                                            IMAGE_POSITION_CENTER_COMPONENTS.has(
                                                componentName
                                            )
                                                ? componentName === 'Popup'
                                                    ? [
                                                          'left',
                                                          'center',
                                                          'right',
                                                          'full',
                                                      ]
                                                    : [
                                                          'left',
                                                          'center',
                                                          'right',
                                                      ]
                                                : ['left', 'right'];
                                        const rawPos =
                                            component_props[imagePosKey];
                                        const rawPosStr = String(
                                            rawPos &&
                                                typeof rawPos === 'object' &&
                                                'value' in rawPos
                                                ? rawPos.value
                                                : rawPos ?? ''
                                        )
                                            .toLowerCase()
                                            .trim();
                                        const displayPos =
                                            componentName === 'Popup' &&
                                            rawPosStr === 'top'
                                                ? 'center'
                                                : normalizeImagePosition(
                                                      component_props[
                                                          imagePosKey
                                                      ],
                                                      allowedPositions
                                                  );
                                        return (
                                            <ImagePositionButtons
                                                allowed={allowedPositions}
                                                hideUnsupported={true}
                                                value={displayPos}
                                                onChange={(val) =>
                                                    onChange({
                                                        file,
                                                        key: imagePosKey,
                                                        value: val,
                                                    })
                                                }
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                    {showStickyMediaToggle && (
                        <div className="mb-4">
                            <div className="flex items-center gap-3">
                                <div>
                                    <div className="text-xs font-semibold leading-5 text-primary-700">
                                        Sticky Media
                                    </div>
                                    <div className="text-[11px] leading-4 text-neutral-500">
                                        Pins the media while the content
                                        scrolls.
                                    </div>
                                </div>
                                <Switch
                                    checked={coerceBoolean(
                                        component_props[stickyMediaKey]
                                    )}
                                    onChange={(checked) =>
                                        handleBooleanToggle(
                                            stickyMediaKey,
                                            checked
                                        )
                                    }
                                    ariaLabel="Toggle sticky media"
                                />
                            </div>
                        </div>
                    )}

                    {/* Image Variant */}
                    {imageVariantKey && (
                        <div className="mb-4 mt-1">
                            <ImageVariantControls
                                value={String(
                                    component_props[imageVariantKey] ??
                                        imageVariantDisplays?.[0] ??
                                        'cover'
                                )}
                                allowedDisplays={imageVariantDisplays}
                                enableVerticalAlign={imageVariantVerticalAlign}
                                disableVerticalAlign={
                                    disableImageVariantVerticalAlign
                                }
                                enableShadow={imageVariantShadow}
                                onChange={(next) =>
                                    onChange({
                                        file,
                                        key: imageVariantKey,
                                        value: next,
                                    })
                                }
                            />
                        </div>
                    )}

                    {/* Buttons */}
                    {componentName !== 'ReviewHero' &&
                        componentName !== 'HeroFullBG' &&
                        (() => {
                            const buttonPairs = resolveButtonPairs(
                                allKeys,
                                componentName
                            );
                            const navPhoneButtonPair =
                                resolveNavPhoneButtonPair(
                                    allKeys,
                                    componentName
                                );
                            if (componentName === 'Nav') {
                                const primaryCtaPair = buttonPairs.find(
                                    ({ textKey }) =>
                                        keyLeaf(textKey).toLowerCase() === 'cta'
                                );
                                if (primaryCtaPair) {
                                    // Selecting or clearing the CTA icon is the
                                    // visibility control for Nav. Keep the legacy
                                    // saved value working in the renderer, but do
                                    // not expose the redundant editor toggle.
                                    primaryCtaPair.iconVisibleKey = null;
                                    primaryCtaPair.mobileIconKey =
                                        findKeyByLeaf(
                                            allKeys,
                                            'ctamobileicon'
                                        ) ||
                                        replaceLeafInPath(
                                            primaryCtaPair.textKey,
                                            'CTAMobileIcon'
                                        );
                                }
                            }
                            if (navPhoneButtonPair) {
                                const ctaButtonIndex = buttonPairs.findIndex(
                                    ({ textKey }) =>
                                        keyLeaf(textKey).toLowerCase() === 'cta'
                                );
                                buttonPairs.splice(
                                    ctaButtonIndex >= 0
                                        ? ctaButtonIndex + 1
                                        : 0,
                                    0,
                                    navPhoneButtonPair
                                );
                            }
                            if (buttonPairs.length === 0) return null;
                            const buttonsSectionKey = `${
                                componentName || 'component'
                            }.__cta_buttons__`;
                            const buttonsExpanded = isArrayItemExpanded(
                                buttonsSectionKey,
                                false
                            );
                            const buttonToneClasses =
                                getCollapsibleArrayToneClasses('cta');
                            const buttonsDetailsId = `component-editor-${String(
                                buttonsSectionKey
                            ).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                            const buttonSummary = buttonPairs
                                .map(({ textKey }) => {
                                    const rawValue =
                                        component_props[textKey] &&
                                        typeof component_props[textKey] ===
                                            'object' &&
                                        'value' in component_props[textKey]
                                            ? component_props[textKey].value
                                            : component_props[textKey];
                                    const textValue = String(
                                        rawValue ?? ''
                                    ).trim();
                                    return textValue;
                                })
                                .filter(Boolean)
                                .join(', ');

                            return (
                                <div className="mt-8">
                                    <div
                                        className={`overflow-hidden rounded-xl border shadow-sm ${buttonToneClasses.wrapper}`}
                                    >
                                        <button
                                            type="button"
                                            className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ease-in focus-visible:outline-none ${
                                                buttonToneClasses.buttonHover
                                            } ${
                                                buttonsExpanded
                                                    ? `border-b ${buttonToneClasses.headerOpenBorder}`
                                                    : ''
                                            }`}
                                            aria-expanded={buttonsExpanded}
                                            aria-controls={buttonsDetailsId}
                                            onClick={() =>
                                                toggleArrayItemExpanded(
                                                    buttonsSectionKey,
                                                    false
                                                )
                                            }
                                        >
                                            <div className="min-w-0">
                                                <div
                                                    className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${buttonToneClasses.metaText}`}
                                                >
                                                    <CursorArrowRaysIcon
                                                        className="h-4 w-4"
                                                        aria-hidden="true"
                                                    />
                                                    <span>CTA Buttons</span>
                                                </div>
                                                {buttonSummary ? (
                                                    <div className="mt-1 truncate text-sm font-semibold text-white">
                                                        {buttonSummary}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <span
                                                className={`inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full ${buttonToneClasses.chevronText}`}
                                            >
                                                <ChevronDownIcon
                                                    className={`h-3 w-3 transition-transform duration-200 ${
                                                        buttonsExpanded
                                                            ? 'rotate-180'
                                                            : ''
                                                    }`}
                                                    aria-hidden="true"
                                                />
                                            </span>
                                        </button>

                                        {buttonsExpanded ? (
                                            <div
                                                id={buttonsDetailsId}
                                                className="bg-white px-3 pb-3 pt-2"
                                            >
                                                {buttonPairs.map(
                                                    (buttonPair) => {
                                                        if (
                                                            buttonPair.kind ===
                                                            'nav-phone'
                                                        ) {
                                                            return renderNavPhoneButtonCard(
                                                                buttonPair,
                                                                buttonsSectionKey
                                                            );
                                                        }
                                                        const {
                                                            textKey,
                                                            linkKey,
                                                            iconKey,
                                                            mobileIconKey,
                                                            iconVisibleKey,
                                                            openInNewTabKey,
                                                            label,
                                                            linkLabel,
                                                        } = buttonPair;
                                                        const textVal = String(
                                                            (component_props[
                                                                textKey
                                                            ] &&
                                                            typeof component_props[
                                                                textKey
                                                            ] === 'object' &&
                                                            'value' in
                                                                component_props[
                                                                    textKey
                                                                ]
                                                                ? component_props[
                                                                      textKey
                                                                  ].value
                                                                : component_props[
                                                                      textKey
                                                                  ]) ?? ''
                                                        );
                                                        const iconVal = iconKey
                                                            ? String(
                                                                  (component_props[
                                                                      iconKey
                                                                  ] &&
                                                                  typeof component_props[
                                                                      iconKey
                                                                  ] ===
                                                                      'object' &&
                                                                  'value' in
                                                                      component_props[
                                                                          iconKey
                                                                      ]
                                                                      ? component_props[
                                                                            iconKey
                                                                        ].value
                                                                      : component_props[
                                                                            iconKey
                                                                        ]) ?? ''
                                                              )
                                                            : '';
                                                        const mobileIconVal =
                                                            mobileIconKey
                                                                ? String(
                                                                      (component_props[
                                                                          mobileIconKey
                                                                      ] &&
                                                                      typeof component_props[
                                                                          mobileIconKey
                                                                      ] ===
                                                                          'object' &&
                                                                      'value' in
                                                                          component_props[
                                                                              mobileIconKey
                                                                          ]
                                                                          ? component_props[
                                                                                mobileIconKey
                                                                            ]
                                                                                .value
                                                                          : component_props[
                                                                                mobileIconKey
                                                                            ]) ??
                                                                          ''
                                                                  )
                                                                : '';
                                                        const iconVisible =
                                                            iconVisibleKey
                                                                ? coerceBoolean(
                                                                      (component_props[
                                                                          iconVisibleKey
                                                                      ] &&
                                                                      typeof component_props[
                                                                          iconVisibleKey
                                                                      ] ===
                                                                          'object' &&
                                                                      'value' in
                                                                          component_props[
                                                                              iconVisibleKey
                                                                          ]
                                                                          ? component_props[
                                                                                iconVisibleKey
                                                                            ]
                                                                                .value
                                                                          : component_props[
                                                                                iconVisibleKey
                                                                            ]) ??
                                                                          true
                                                                  )
                                                                : true;
                                                        const hasButtonText =
                                                            textVal.trim()
                                                                .length > 0;
                                                        const linkVal =
                                                            linkKey == null
                                                                ? ''
                                                                : String(
                                                                      (component_props[
                                                                          linkKey
                                                                      ] &&
                                                                      typeof component_props[
                                                                          linkKey
                                                                      ] ===
                                                                          'object' &&
                                                                      'value' in
                                                                          component_props[
                                                                              linkKey
                                                                          ]
                                                                          ? component_props[
                                                                                linkKey
                                                                            ]
                                                                                .value
                                                                          : component_props[
                                                                                linkKey
                                                                            ]) ??
                                                                          ''
                                                                  );
                                                        const textLeafLower =
                                                            keyLeaf(
                                                                textKey
                                                            ).toLowerCase();
                                                        const isCtaButton =
                                                            textLeafLower ===
                                                                'cta' ||
                                                            textLeafLower ===
                                                                'smallcta';
                                                        const popupHideCTAButtonKey =
                                                            componentName ===
                                                                'Popup' &&
                                                            isCtaButton
                                                                ? findKeyByLeaf(
                                                                      allKeys,
                                                                      'hidectabutton'
                                                                  ) ||
                                                                  'HideCTAButton'
                                                                : null;
                                                        const popupHideCTAButtonEnabled =
                                                            popupHideCTAButtonKey
                                                                ? coerceBoolean(
                                                                      (component_props[
                                                                          popupHideCTAButtonKey
                                                                      ] &&
                                                                      typeof component_props[
                                                                          popupHideCTAButtonKey
                                                                      ] ===
                                                                          'object' &&
                                                                      'value' in
                                                                          component_props[
                                                                              popupHideCTAButtonKey
                                                                          ]
                                                                          ? component_props[
                                                                                popupHideCTAButtonKey
                                                                            ]
                                                                                .value
                                                                          : component_props[
                                                                                popupHideCTAButtonKey
                                                                            ]) ??
                                                                          false
                                                                  )
                                                                : false;
                                                        const hidePrimaryPopupButton =
                                                            componentName ===
                                                                'Popup' &&
                                                            isCtaButton &&
                                                            popupHideCTAButtonEnabled;
                                                        const showLink =
                                                            !!linkKey &&
                                                            (hasButtonText ||
                                                                hidePrimaryPopupButton);
                                                        const canOpenInNewTab =
                                                            !!openInNewTabKey &&
                                                            showLink &&
                                                            (isCtaButton
                                                                ? linkVal.trim()
                                                                      .length >
                                                                  0
                                                                : textLeafLower ===
                                                                  'buttontext'
                                                                ? true
                                                                : textLeafLower.includes(
                                                                      'secondary'
                                                                  ) ||
                                                                  textLeafLower.includes(
                                                                      'outline'
                                                                  ) ||
                                                                  textLeafLower.includes(
                                                                      'third'
                                                                  ));
                                                        const openInNewTab =
                                                            canOpenInNewTab
                                                                ? coerceBoolean(
                                                                      (component_props[
                                                                          openInNewTabKey
                                                                      ] &&
                                                                      typeof component_props[
                                                                          openInNewTabKey
                                                                      ] ===
                                                                          'object' &&
                                                                      'value' in
                                                                          component_props[
                                                                              openInNewTabKey
                                                                          ]
                                                                          ? component_props[
                                                                                openInNewTabKey
                                                                            ]
                                                                                .value
                                                                          : component_props[
                                                                                openInNewTabKey
                                                                            ]) ??
                                                                          false
                                                                  )
                                                                : false;
                                                        const isSecondaryButton =
                                                            textLeafLower.includes(
                                                                'secondary'
                                                            );
                                                        const cardClassName =
                                                            isCtaButton
                                                                ? 'rounded-xl bg-primary-50 p-3'
                                                                : isSecondaryButton
                                                                ? 'rounded-xl bg-secondary-50 p-3'
                                                                : 'rounded-xl border border-neutral-300 bg-white p-3';

                                                        return (
                                                            <div
                                                                key={`btn-${textKey}`}
                                                                className={`mb-3 ${cardClassName}`}
                                                            >
                                                                {!hidePrimaryPopupButton && (
                                                                    <>
                                                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                                            {label ||
                                                                                prettyLabel(
                                                                                    textKey
                                                                                )}
                                                                        </label>
                                                                        <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                                            <input
                                                                                type="text"
                                                                                value={
                                                                                    textVal
                                                                                }
                                                                                onChange={(
                                                                                    e
                                                                                ) =>
                                                                                    onChange(
                                                                                        {
                                                                                            file,
                                                                                            key: textKey,
                                                                                            value: e
                                                                                                .target
                                                                                                .value,
                                                                                        }
                                                                                    )
                                                                                }
                                                                                className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                                            />
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {hasButtonText &&
                                                                    iconKey && (
                                                                        <div className="mt-3">
                                                                            <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                                                {prettyLabel(
                                                                                    iconKey
                                                                                )}
                                                                            </label>
                                                                            <div
                                                                                className="mt-2"
                                                                                data-inline-icon-field={
                                                                                    iconKey
                                                                                }
                                                                            >
                                                                                <FontAwesomeIconPicker
                                                                                    value={
                                                                                        iconVal
                                                                                    }
                                                                                    onChange={(
                                                                                        next
                                                                                    ) =>
                                                                                        onChange(
                                                                                            {
                                                                                                file,
                                                                                                key: iconKey,
                                                                                                value: next,
                                                                                            }
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                {isCtaButton &&
                                                                    mobileIconKey && (
                                                                        <div className="mt-3 border-t border-primary-100 pt-3">
                                                                            <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                                                {
                                                                                    'CTA Mobile Icon'
                                                                                }
                                                                            </label>
                                                                            <div className="text-[11px] leading-4 text-neutral-600">
                                                                                {
                                                                                    'Shown as the icon-only primary CTA on mobile. Clear it to hide the mobile CTA.'
                                                                                }
                                                                            </div>
                                                                            <div
                                                                                className="mt-2"
                                                                                data-inline-icon-field={
                                                                                    mobileIconKey
                                                                                }
                                                                            >
                                                                                <FontAwesomeIconPicker
                                                                                    value={
                                                                                        mobileIconVal
                                                                                    }
                                                                                    collapseByDefault
                                                                                    onChange={(
                                                                                        next
                                                                                    ) =>
                                                                                        onChange(
                                                                                            {
                                                                                                file,
                                                                                                key: mobileIconKey,
                                                                                                value: next,
                                                                                            }
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                {hasButtonText &&
                                                                    iconVisibleKey && (
                                                                        <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1">
                                                                            <div className="text-[11px] font-semibold leading-4 text-primary-700">
                                                                                Show
                                                                                Icon
                                                                            </div>
                                                                            <Switch
                                                                                size="sm"
                                                                                checked={
                                                                                    iconVisible
                                                                                }
                                                                                onChange={(
                                                                                    enable
                                                                                ) =>
                                                                                    onChange(
                                                                                        {
                                                                                            file,
                                                                                            key: iconVisibleKey,
                                                                                            value: enable,
                                                                                        }
                                                                                    )
                                                                                }
                                                                                ariaLabel={`${prettyLabel(
                                                                                    iconKey ||
                                                                                        textKey
                                                                                )} visible`}
                                                                            />
                                                                        </div>
                                                                    )}

                                                                {showLink && (
                                                                    <div className="mt-3">
                                                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                                            {linkLabel ||
                                                                                prettyLabel(
                                                                                    linkKey
                                                                                )}
                                                                        </label>
                                                                        <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                                            <input
                                                                                type="text"
                                                                                value={
                                                                                    linkVal
                                                                                }
                                                                                onChange={(
                                                                                    e
                                                                                ) =>
                                                                                    onChange(
                                                                                        {
                                                                                            file,
                                                                                            key: linkKey,
                                                                                            value: e
                                                                                                .target
                                                                                                .value,
                                                                                        }
                                                                                    )
                                                                                }
                                                                                className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {componentName ===
                                                                    'Popup' &&
                                                                    isCtaButton &&
                                                                    popupHideCTAButtonKey && (
                                                                        <div className="mt-3 flex items-center justify-between rounded-lg bg-neutral-100 px-3 py-2">
                                                                            <div className="pr-3">
                                                                                <div className="text-xs font-semibold leading-5 text-primary-700">
                                                                                    Hide
                                                                                    CTA
                                                                                    Button
                                                                                </div>
                                                                                <div className="text-[11px] leading-4 text-neutral-600">
                                                                                    Makes
                                                                                    the
                                                                                    popup
                                                                                    area
                                                                                    clickable
                                                                                    with
                                                                                    the
                                                                                    CTA
                                                                                    link.
                                                                                </div>
                                                                            </div>
                                                                            <Switch
                                                                                checked={
                                                                                    popupHideCTAButtonEnabled
                                                                                }
                                                                                onChange={(
                                                                                    enable
                                                                                ) =>
                                                                                    onChange(
                                                                                        {
                                                                                            file,
                                                                                            key: popupHideCTAButtonKey,
                                                                                            value: enable,
                                                                                        }
                                                                                    )
                                                                                }
                                                                                ariaLabel="Toggle popup CTA button visibility"
                                                                            />
                                                                        </div>
                                                                    )}

                                                                {canOpenInNewTab && (
                                                                    <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1">
                                                                        <div className="text-[11px] font-semibold leading-4 text-primary-700">
                                                                            Open
                                                                            in
                                                                            new
                                                                            tab
                                                                        </div>
                                                                        <Switch
                                                                            size="sm"
                                                                            checked={
                                                                                openInNewTab
                                                                            }
                                                                            onChange={(
                                                                                enable
                                                                            ) =>
                                                                                onChange(
                                                                                    {
                                                                                        file,
                                                                                        key: openInNewTabKey,
                                                                                        value: enable,
                                                                                    }
                                                                                )
                                                                            }
                                                                            ariaLabel={`${prettyLabel(
                                                                                textKey
                                                                            )} open in new tab`}
                                                                        />
                                                                    </div>
                                                                )}

                                                                {componentName ===
                                                                    'Nav' &&
                                                                    embedLinkKey &&
                                                                    keyLeaf(
                                                                        textKey
                                                                    ).toLowerCase() ===
                                                                        'cta' && (
                                                                        <div className="mt-3 flex items-center justify-between rounded-lg bg-neutral-100 px-3 py-2">
                                                                            <div className="pr-3">
                                                                                <div className="text-xs font-semibold leading-5 text-primary-700">
                                                                                    Open
                                                                                    CTA
                                                                                    in
                                                                                    popup
                                                                                </div>
                                                                                <div className="text-[11px] leading-4 text-neutral-600">
                                                                                    Load
                                                                                    the
                                                                                    CTA
                                                                                    link
                                                                                    inside
                                                                                    a
                                                                                    popup
                                                                                    instead
                                                                                    of
                                                                                    a
                                                                                    new
                                                                                    tab.
                                                                                </div>
                                                                            </div>
                                                                            <Switch
                                                                                checked={(() => {
                                                                                    const raw =
                                                                                        component_props[
                                                                                            embedLinkKey
                                                                                        ] &&
                                                                                        typeof component_props[
                                                                                            embedLinkKey
                                                                                        ] ===
                                                                                            'object' &&
                                                                                        'value' in
                                                                                            component_props[
                                                                                                embedLinkKey
                                                                                            ]
                                                                                            ? component_props[
                                                                                                  embedLinkKey
                                                                                              ]
                                                                                                  .value
                                                                                            : component_props[
                                                                                                  embedLinkKey
                                                                                              ];
                                                                                    if (
                                                                                        typeof raw ===
                                                                                        'string'
                                                                                    ) {
                                                                                        return (
                                                                                            raw
                                                                                                .toLowerCase()
                                                                                                .trim() ===
                                                                                            'true'
                                                                                        );
                                                                                    }
                                                                                    return !!raw;
                                                                                })()}
                                                                                onChange={(
                                                                                    enable
                                                                                ) =>
                                                                                    onChange(
                                                                                        {
                                                                                            file,
                                                                                            key: embedLinkKey,
                                                                                            value: enable,
                                                                                        }
                                                                                    )
                                                                                }
                                                                                ariaLabel="Toggle CTA popup"
                                                                            />
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })()}

                    {/* Styling */}
                    {(componentName || '').toLowerCase() !== 'page' &&
                        (ctaHeroLayoutKey ||
                            colorKey ||
                            behaviorKey ||
                            alignKey ||
                            vspaceKey ||
                            iconSizeKey ||
                            hasAnimationControls) && (
                            <div className="mt-8">
                                <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                    <SwatchIcon
                                        className="h-5 w-5 text-primary-700"
                                        aria-hidden="true"
                                    />
                                    <span>Styling</span>
                                </div>
                                {ctaHeroLayoutKey &&
                                    renderInput(
                                        component_props[ctaHeroLayoutKey],
                                        ctaHeroLayoutKey
                                    )}
                                {/* Component Color scheme */}
                                {colorKey && (
                                    <div className="mb-4">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Color Scheme
                                        </label>
                                        <div className="mt-1">
                                            {(() => {
                                                const isMap =
                                                    componentName ===
                                                    'GoogleMap';
                                                const raw = String(
                                                    component_props[colorKey] ??
                                                        (isMap
                                                            ? 'light'
                                                            : 'white')
                                                );

                                                let displayGroups = isMap
                                                    ? MAP_COLOR_SCHEME_GROUPS
                                                    : COMPONENT_COLOR_SCHEME_GROUPS;

                                                if (!isMap) {
                                                    displayGroups =
                                                        filterColorSchemeGroups(
                                                            displayGroups,
                                                            componentName,
                                                            component_props
                                                        );
                                                }

                                                const flatValues = displayGroups
                                                    .flatMap(
                                                        (g) => g.options || []
                                                    )
                                                    .map((o) => o.value);

                                                const normalizeFn = isMap
                                                    ? normalizeMapColorScheme
                                                    : normalizeComponentColorScheme;

                                                const swatchFn = isMap
                                                    ? mapSchemeToSwatch
                                                    : componentSchemeToSwatch;

                                                const isNavComponent =
                                                    componentName === 'Nav';
                                                const hasOverflowProp =
                                                    isNavComponent &&
                                                    logoOverflowKey &&
                                                    Object.prototype.hasOwnProperty.call(
                                                        component_props,
                                                        logoOverflowKey
                                                    );
                                                const overflowPropRaw =
                                                    hasOverflowProp &&
                                                    component_props[
                                                        logoOverflowKey
                                                    ];
                                                const overflowFromProp =
                                                    isNavComponent &&
                                                    (typeof overflowPropRaw ===
                                                    'string'
                                                        ? overflowPropRaw
                                                              .toLowerCase()
                                                              .trim() === 'true'
                                                        : Boolean(
                                                              overflowPropRaw
                                                          ));
                                                const overflowFromColor =
                                                    isNavComponent &&
                                                    isOverflowSchemeValue(raw);
                                                const overflowEnabled =
                                                    isNavComponent &&
                                                    (overflowFromProp ||
                                                        (!hasOverflowProp &&
                                                            overflowFromColor));
                                                const valueForDisplay =
                                                    isNavComponent &&
                                                    overflowFromColor &&
                                                    !hasOverflowProp
                                                        ? fallbackFromOverflow(
                                                              raw
                                                          )
                                                        : raw;

                                                let normalized = normalizeFn(
                                                    valueForDisplay,
                                                    displayGroups
                                                );

                                                if (
                                                    valueForDisplay.trim() !==
                                                        '' &&
                                                    !flatValues.includes(
                                                        valueForDisplay
                                                    )
                                                ) {
                                                    normalized =
                                                        valueForDisplay;
                                                    displayGroups = [
                                                        ...displayGroups,
                                                        {
                                                            label: 'Current',
                                                            options: [
                                                                {
                                                                    value: valueForDisplay,
                                                                    label: valueForDisplay,
                                                                },
                                                            ],
                                                        },
                                                    ];
                                                }

                                                const handleOverflowToggle = (
                                                    enable
                                                ) => {
                                                    onChange({
                                                        file,
                                                        key:
                                                            logoOverflowKey ||
                                                            'LogoOverflow',
                                                        value: enable,
                                                    });
                                                };
                                                const showGradientDirection =
                                                    !isMap &&
                                                    !isNavComponent &&
                                                    isGradientColorScheme(
                                                        normalized
                                                    );
                                                const gradientDirectionValue =
                                                    clampGradientAngle(
                                                        component_props[
                                                            gradientDirectionKey
                                                        ]
                                                    );
                                                const rotateGradientDirection =
                                                    (delta) => {
                                                        const nextAngle =
                                                            normalizeAngleValue(
                                                                gradientDirectionValue +
                                                                    delta
                                                            );
                                                        onChange({
                                                            file,
                                                            key: gradientDirectionKey,
                                                            value: String(
                                                                nextAngle
                                                            ),
                                                        });
                                                    };

                                                return (
                                                    <div className="space-y-2">
                                                        <ColorSchemeSelect
                                                            value={normalized}
                                                            onChange={(val) =>
                                                                onChange({
                                                                    file,
                                                                    key: colorKey,
                                                                    value: val,
                                                                })
                                                            }
                                                            groups={
                                                                displayGroups
                                                            }
                                                            swatchFn={swatchFn}
                                                        />
                                                        {showGradientDirection && (
                                                            <AngleStepControl
                                                                label="Angle"
                                                                value={
                                                                    gradientDirectionValue
                                                                }
                                                                onStep={
                                                                    rotateGradientDirection
                                                                }
                                                            />
                                                        )}
                                                        {isNavComponent && (
                                                            <div className="mt-2 flex items-center justify-between rounded-lg bg-neutral-100 px-3 py-2">
                                                                <div className="pr-3">
                                                                    <div className="text-xs font-semibold leading-5 text-primary-700">
                                                                        Shadow
                                                                    </div>
                                                                </div>
                                                                <Switch
                                                                    checked={coerceBoolean(
                                                                        component_props[
                                                                            'Shadow'
                                                                        ]
                                                                    )}
                                                                    onChange={(
                                                                        checked
                                                                    ) =>
                                                                        onChange(
                                                                            {
                                                                                file,
                                                                                key: 'Shadow',
                                                                                value: checked,
                                                                            }
                                                                        )
                                                                    }
                                                                    ariaLabel="Toggle shadow"
                                                                />
                                                            </div>
                                                        )}
                                                        {isNavComponent && (
                                                            <div className="flex items-center justify-between rounded-lg bg-neutral-100 px-3 py-2">
                                                                <div className="pr-3">
                                                                    <div className="text-xs font-semibold leading-5 text-primary-700">
                                                                        Overflow
                                                                    </div>
                                                                </div>
                                                                <Switch
                                                                    checked={
                                                                        overflowEnabled
                                                                    }
                                                                    onChange={(
                                                                        checked
                                                                    ) =>
                                                                        handleOverflowToggle(
                                                                            checked
                                                                        )
                                                                    }
                                                                    ariaLabel="Toggle overflow background"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                )}
                                {componentName === 'Nav' && (
                                    <div className="mb-4">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Behavior
                                        </label>
                                        <div className="mt-1">
                                            <div className="relative rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                <Listbox
                                                    value={navBehaviorValue}
                                                    onChange={(val) =>
                                                        onChange({
                                                            file,
                                                            key:
                                                                behaviorKey ||
                                                                'Behavior',
                                                            value: val,
                                                        })
                                                    }
                                                >
                                                    <div className="relative">
                                                        <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-xs leading-5 text-neutral-800 focus:outline-none md:text-sm">
                                                            <span className="truncate">
                                                                {NAV_BEHAVIOR_OPTIONS.find(
                                                                    (opt) =>
                                                                        opt.value ===
                                                                        navBehaviorValue
                                                                )?.label ||
                                                                    'Select behavior'}
                                                            </span>
                                                            <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                                                <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                                                            </span>
                                                        </Listbox.Button>
                                                        <Transition
                                                            as={React.Fragment}
                                                            leave="transition ease-in duration-100"
                                                            leaveFrom="opacity-100 scale-100"
                                                            leaveTo="opacity-0 scale-95"
                                                        >
                                                            <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                                                {NAV_BEHAVIOR_OPTIONS.map(
                                                                    (opt) => (
                                                                        <Listbox.Option
                                                                            key={
                                                                                opt.value
                                                                            }
                                                                            value={
                                                                                opt.value
                                                                            }
                                                                            className={({
                                                                                active,
                                                                                selected,
                                                                            }) =>
                                                                                [
                                                                                    'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                                                    active
                                                                                        ? 'bg-primary-50 text-primary-700'
                                                                                        : 'text-neutral-800',
                                                                                    selected
                                                                                        ? 'font-medium'
                                                                                        : '',
                                                                                ].join(
                                                                                    ' '
                                                                                )
                                                                            }
                                                                        >
                                                                            {({
                                                                                selected,
                                                                            }) => (
                                                                                <>
                                                                                    <span className="truncate">
                                                                                        {
                                                                                            opt.label
                                                                                        }
                                                                                    </span>
                                                                                    {selected && (
                                                                                        <CheckIcon className="h-4 w-4 text-primary-600" />
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </Listbox.Option>
                                                                    )
                                                                )}
                                                            </Listbox.Options>
                                                        </Transition>
                                                    </div>
                                                </Listbox>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {componentName === 'Nav' && (
                                    <div className="mb-4">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Height
                                        </label>
                                        <div className="mt-1">
                                            <div className="relative rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                <Listbox
                                                    value={navHeightValue}
                                                    onChange={(val) =>
                                                        onChange({
                                                            file,
                                                            key:
                                                                navHeightKey ||
                                                                'NavHeight',
                                                            value: val,
                                                        })
                                                    }
                                                >
                                                    <div className="relative">
                                                        <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-xs leading-5 text-neutral-800 focus:outline-none md:text-sm">
                                                            <span className="truncate">
                                                                {NAV_HEIGHT_OPTIONS.find(
                                                                    (opt) =>
                                                                        opt.value ===
                                                                        navHeightValue
                                                                )?.label ||
                                                                    'Select height'}
                                                            </span>
                                                            <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                                                <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                                                            </span>
                                                        </Listbox.Button>
                                                        <Transition
                                                            as={React.Fragment}
                                                            leave="transition ease-in duration-100"
                                                            leaveFrom="opacity-100 scale-100"
                                                            leaveTo="opacity-0 scale-95"
                                                        >
                                                            <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                                                {NAV_HEIGHT_OPTIONS.map(
                                                                    (opt) => (
                                                                        <Listbox.Option
                                                                            key={
                                                                                opt.value
                                                                            }
                                                                            value={
                                                                                opt.value
                                                                            }
                                                                            className={({
                                                                                active,
                                                                                selected,
                                                                            }) =>
                                                                                [
                                                                                    'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                                                    active
                                                                                        ? 'bg-primary-50 text-primary-700'
                                                                                        : 'text-neutral-800',
                                                                                    selected
                                                                                        ? 'font-medium'
                                                                                        : '',
                                                                                ].join(
                                                                                    ' '
                                                                                )
                                                                            }
                                                                        >
                                                                            {({
                                                                                selected,
                                                                            }) => (
                                                                                <>
                                                                                    <span className="truncate">
                                                                                        {
                                                                                            opt.label
                                                                                        }
                                                                                    </span>
                                                                                    {selected && (
                                                                                        <CheckIcon className="h-4 w-4 text-primary-600" />
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </Listbox.Option>
                                                                    )
                                                                )}
                                                            </Listbox.Options>
                                                        </Transition>
                                                    </div>
                                                </Listbox>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* Popup position */}
                                {componentName === 'Popup' &&
                                    popupPositionKey && (
                                        <div className="mb-4">
                                            <label className="block text-xs font-semibold leading-5 text-primary-700">
                                                Popup Position
                                            </label>
                                            <div className="mt-1">
                                                <PopupPositionButtons
                                                    value={normalizePopupPosition(
                                                        component_props[
                                                            popupPositionKey
                                                        ]
                                                    )}
                                                    onChange={(val) =>
                                                        onChange({
                                                            file,
                                                            key: popupPositionKey,
                                                            value: val,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                    )}
                                {iconSizeKey && (
                                    <div className="mb-4">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Icon Size
                                        </label>
                                        <div className="mt-1">
                                            <div className="relative rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                <Listbox
                                                    value={String(
                                                        component_props[
                                                            iconSizeKey
                                                        ] || 'default'
                                                    )
                                                        .toLowerCase()
                                                        .trim()}
                                                    onChange={(val) =>
                                                        onChange({
                                                            file,
                                                            key: iconSizeKey,
                                                            value: val,
                                                        })
                                                    }
                                                >
                                                    <div className="relative">
                                                        <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-xs leading-5 text-neutral-800 focus:outline-none md:text-sm">
                                                            <span className="truncate">
                                                                {COMPONENT_ICON_SIZE_OPTIONS.find(
                                                                    (opt) =>
                                                                        opt.key ===
                                                                        String(
                                                                            component_props[
                                                                                iconSizeKey
                                                                            ] ||
                                                                                'default'
                                                                        )
                                                                            .toLowerCase()
                                                                            .trim()
                                                                )?.label ||
                                                                    'Default'}
                                                            </span>
                                                            <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                                                <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                                                            </span>
                                                        </Listbox.Button>
                                                        <Transition
                                                            as={React.Fragment}
                                                            leave="transition ease-in duration-100"
                                                            leaveFrom="opacity-100 scale-100"
                                                            leaveTo="opacity-0 scale-95"
                                                        >
                                                            <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                                                {COMPONENT_ICON_SIZE_OPTIONS.map(
                                                                    (opt) => (
                                                                        <Listbox.Option
                                                                            key={
                                                                                opt.key
                                                                            }
                                                                            value={
                                                                                opt.key
                                                                            }
                                                                            className={({
                                                                                active,
                                                                                selected,
                                                                            }) =>
                                                                                [
                                                                                    'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                                                    active
                                                                                        ? 'bg-primary-50 text-primary-700'
                                                                                        : 'text-neutral-800',
                                                                                    selected
                                                                                        ? 'font-medium'
                                                                                        : '',
                                                                                ].join(
                                                                                    ' '
                                                                                )
                                                                            }
                                                                        >
                                                                            {({
                                                                                selected,
                                                                            }) => (
                                                                                <>
                                                                                    <span className="truncate">
                                                                                        {
                                                                                            opt.label
                                                                                        }
                                                                                    </span>
                                                                                    {selected && (
                                                                                        <CheckIcon className="h-4 w-4 text-primary-600" />
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </Listbox.Option>
                                                                    )
                                                                )}
                                                            </Listbox.Options>
                                                        </Transition>
                                                    </div>
                                                </Listbox>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* Cards Color Scheme (ItemGrid) */}
                                {cardsColorKey && (
                                    <div className="mb-4">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Cards Color Scheme
                                        </label>
                                        <div className="mt-1">
                                            <ColorSchemeSelect
                                                value={(() => {
                                                    const raw =
                                                        (component_props[
                                                            cardsColorKey
                                                        ] &&
                                                        typeof component_props[
                                                            cardsColorKey
                                                        ] === 'object' &&
                                                        'value' in
                                                            component_props[
                                                                cardsColorKey
                                                            ]
                                                            ? component_props[
                                                                  cardsColorKey
                                                              ].value
                                                            : component_props[
                                                                  cardsColorKey
                                                              ]) ?? '';
                                                    const str = String(raw);
                                                    return str.trim() === ''
                                                        ? ''
                                                        : normalizeCardColorScheme(
                                                              str
                                                          );
                                                })()}
                                                onChange={(val) =>
                                                    onChange({
                                                        file,
                                                        key: cardsColorKey,
                                                        value: val,
                                                    })
                                                }
                                                groups={[
                                                    ...CARD_COLOR_SCHEME_GROUPS,
                                                ]}
                                                swatchFn={
                                                    componentSchemeToSwatch
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                                {/* Map Color Scheme */}
                                {mapColorKey && hasMap && (
                                    <div className="mb-4">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Map Color Scheme
                                        </label>
                                        <div className="mt-1">
                                            <ColorSchemeSelect
                                                value={normalizeMapColorScheme(
                                                    String(
                                                        component_props[
                                                            mapColorKey
                                                        ] ?? 'light'
                                                    )
                                                )}
                                                onChange={(val) =>
                                                    onChange({
                                                        file,
                                                        key: mapColorKey,
                                                        value: val,
                                                    })
                                                }
                                                groups={MAP_COLOR_SCHEME_GROUPS}
                                                swatchFn={mapSchemeToSwatch}
                                            />
                                        </div>
                                    </div>
                                )}
                                {/* Animation + Duration */}
                                {hasAnimationControls && (
                                    <AnimationControls
                                        component_props={component_props}
                                        file={file}
                                        componentName={componentName}
                                        onChange={onChange}
                                    />
                                )}
                                {/* Alignment */}
                                {supportsAlignment && (
                                    <div className="mb-4">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Alignment
                                        </label>
                                        <div className="mt-1">
                                            <AlignmentButtons
                                                allowed={allowedAlignments}
                                                hideUnsupported={
                                                    !showOverflowAlignment &&
                                                    !isCTAHeroBoxLayout
                                                }
                                                includeOverflow={
                                                    showOverflowAlignment
                                                }
                                                value={alignmentValue}
                                                onChange={(val) =>
                                                    onChange({
                                                        file,
                                                        key: alignKey,
                                                        value: val,
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                                {/* Vertical spacing */}
                                {vspaceKey && componentName !== 'GoogleMap' && (
                                    <div className="mb-4">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Vertical Spacing
                                        </label>
                                        {(() => {
                                            const rawSpacing =
                                                component_props[vspaceKey] ??
                                                (componentName === 'CustomCode'
                                                    ? ''
                                                    : 'medium');
                                            const overflowKeys = [
                                                'none-overflow',
                                                'medium-overflow',
                                                'large-overflow',
                                                'overflow',
                                            ];
                                            const isItemGridOverflow =
                                                componentName === 'ItemGrid' &&
                                                overflowKeys.includes(
                                                    String(rawSpacing)
                                                );
                                            const normalizedSpacing =
                                                isItemGridOverflow
                                                    ? 'medium'
                                                    : rawSpacing;
                                            const spacingDisabled =
                                                componentName ===
                                                    'ImageSlider' &&
                                                imageVariantValue.startsWith(
                                                    'full'
                                                );
                                            return (
                                                <>
                                                    <div className="mt-1">
                                                        <VerticalSpacingRow
                                                            value={
                                                                normalizedSpacing
                                                            }
                                                            disabled={
                                                                spacingDisabled
                                                            }
                                                            onChange={(
                                                                newKey
                                                            ) =>
                                                                onChange({
                                                                    file,
                                                                    key: vspaceKey,
                                                                    value: newKey,
                                                                })
                                                            }
                                                            hideBottom={
                                                                componentName ===
                                                                'Footer'
                                                            }
                                                            bottomFixed={
                                                                componentName ===
                                                                'Footer'
                                                                    ? 'medium'
                                                                    : undefined
                                                            }
                                                            allowFull={
                                                                componentName ===
                                                                'CustomCode'
                                                            }
                                                        />
                                                    </div>
                                                    {componentName ===
                                                        'Footer' &&
                                                        footerBottomSpacingKey && (
                                                            <div className="mt-4">
                                                                <VerticalSpacingRow
                                                                    value={
                                                                        component_props[
                                                                            footerBottomSpacingKey
                                                                        ] ??
                                                                        'none'
                                                                    }
                                                                    onChange={(
                                                                        newKey
                                                                    ) =>
                                                                        onChange(
                                                                            {
                                                                                file,
                                                                                key: footerBottomSpacingKey,
                                                                                value: newKey,
                                                                            }
                                                                        )
                                                                    }
                                                                    hideTop
                                                                    topFixed="none"
                                                                />
                                                            </div>
                                                        )}
                                                    {componentName ===
                                                        'ItemGrid' && (
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            {[
                                                                {
                                                                    value: 'medium-overflow',
                                                                    label: 'Medium Overflow',
                                                                },
                                                                {
                                                                    value: 'large-overflow',
                                                                    label: 'Large Overflow',
                                                                },
                                                                {
                                                                    value: 'overflow',
                                                                    label: 'Full Overflow',
                                                                },
                                                            ].map((opt) => (
                                                                <VSChip
                                                                    key={
                                                                        opt.value
                                                                    }
                                                                    active={
                                                                        isItemGridOverflow &&
                                                                        String(
                                                                            rawSpacing
                                                                        ) ===
                                                                            opt.value
                                                                    }
                                                                    disabled={
                                                                        spacingDisabled
                                                                    }
                                                                    onClick={() =>
                                                                        onChange(
                                                                            {
                                                                                file,
                                                                                key: vspaceKey,
                                                                                value: opt.value,
                                                                            }
                                                                        )
                                                                    }
                                                                    srText={
                                                                        opt.label
                                                                    }
                                                                >
                                                                    {opt.label}
                                                                </VSChip>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}
                                {/* Engage in footer only */}
                                {engageKey && componentName === 'Footer' && (
                                    <div className="mb-4">
                                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                                            Engage
                                        </label>
                                        <div className="mt-1">
                                            <EngageButtons
                                                allowed={getAllowedEngageFromProps(
                                                    component_props
                                                )}
                                                hideUnsupported={false}
                                                value={normalizeEngage(
                                                    component_props[engageKey]
                                                )}
                                                onChange={(val) =>
                                                    onChange({
                                                        file,
                                                        key: engageKey,
                                                        value: val,
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                </>
            )}
        </EditorDrawer>
    );
}
